import React, { useEffect, useState, useRef, useMemo, useCallback, useLayoutEffect, lazy, Suspense, startTransition } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
    Autocomplete,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Popover,
    Select,
    Skeleton,
    Slider,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import axios from 'axios';
import SftGlobalData from '../../library/SftGlobalData';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import { LanguagesProvider, parseLangCsv, toLangCsv, orderLangs, ALL_LANGUAGE_CODES } from './WebshopLanguagesContext';
import { usePermissions } from '../../library/Permissions';
import { ConfigCard } from './style/cards';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PaletteIcon from '@mui/icons-material/Palette';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import WebIcon from '@mui/icons-material/Web';
import BrushIcon from '@mui/icons-material/Brush';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GridViewIcon from '@mui/icons-material/GridView';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import LockIcon from '@mui/icons-material/Lock';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import SyncIcon from '@mui/icons-material/Sync';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import UnlayerEditor from '../../components/UnlayerEditor';
import Colors from '../../library/Colors';
import NavigationModal from './style/modals/NavigationModal';
import FooterModal from './style/modals/FooterModal';
import HeaderLogoModal from './style/modals/HeaderLogoModal';
import ProductDetailInformationModal from './style/modals/ProductDetailInformationModal';
import {
    parseTemplateToChips, serializeChipsToTemplate,
    parseCsvToChips,      serializeChipsToCsv,
} from './style/cards/ProductTitleAndUrlCard';
import GroupsFilterModal, { DEFAULT_GROUPS_FILTER_CONFIG } from './style/modals/GroupsFilterModal';
import ProductCardModal, { DEFAULT_PRODUCT_CARD_CONFIG } from './style/modals/ProductCardModal';
import ProductGridModal, { DEFAULT_PRODUCT_GRID_CONFIG } from './style/modals/ProductGridModal';
import ImageGalleryModal from './style/modals/ImageGalleryModal';
import ExtraFooterModal from './style/modals/ExtraFooterModal';
import GlobalWidthSettingsModal from './style/modals/GlobalWidthSettingsModal';
import { CadeaubonTab, ShopTheLookTab, MerkenTab, BannersTab, OverigeTab } from './features';
import { BRANDS_CONFIG_DEFAULT } from './features/MerkenTab';
import { STL_CONFIG_DEFAULT } from './features/ShopTheLookTab';
import { OVERIGE_CONFIG_DEFAULT } from './features/OverigeTab';
import { CADEAUBON_CONFIG_DEFAULT } from './features/CadeaubonTab';
import SyncSettingsTab, { SYNC_CONFIG_DEFAULT } from './sync/SyncSettingsTab';
// DynamicPageTab / StaticPageTab / AboutPageTab / CartPageTab are lazy-loaded
// below (heavy rich-text editors) — see the lazy() block after the imports.
import { storeRowToForm, storeFormToColumns, detectHoursSeparator, DEFAULT_HOURS_SEPARATOR } from './data/stores';
import { MAP_SETTINGS_DEFAULT } from './data/mapStyles';
import { NoWebshopState, WebshopErrorState } from './_webshopNotice';
import CarouselConfigModal, { DEFAULT_CAROUSEL_CONFIG } from './pages/CarouselConfigModal';
import ImageGridConfigModal, { DEFAULT_IMAGE_GRID_CONFIG } from './pages/ImageGridConfigModal';
// ImageGridConfigModalV2 is lazy-loaded below (see the lazy() block).
import ProcessingTab, { PROCESSING_CONFIG_DEFAULT } from './processing/ProcessingTab';
import i18n from '../../i18n';
import { useWebshopSearch, WebshopSearchBar } from './search/WebshopConfigSearch';
import { registerUnsavedCheck, unregisterUnsavedCheck } from '../../library/UnsavedChangesGuard';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragOverlay,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Lazy-load the heaviest page editors/modal so their code (rich-text / Unlayer
// editors) is fetched on demand instead of bundled into the config-screen chunk.
// Their usages are wrapped in <Suspense> below.
const DynamicPageTab        = lazy(() => import('./pages/DynamicPageTab'));
const StaticPageTab         = lazy(() => import('./pages/StaticPageTab'));
const AboutPageTab          = lazy(() => import('./pages/AboutPageTab'));
const CartPageTab           = lazy(() => import('./pages/CartPageTab'));
const ImageGridConfigModalV2 = lazy(() => import('./pages/ImageGridConfigModalV2'));

const TabPanel = ({ children, value, index, sx }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 3, ...sx }}>{children}</Box>}
    </div>
);
TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number.isRequired, index: PropTypes.number.isRequired, sx: PropTypes.object };

const DEFAULT_AVAILABLE = [
    { id: 'sales', name: 'Sales products', i18nKey: 'cfg_block_sales' },
    // 'text' is offered as an Available block only on the 404 page tab.
    { id: 'text', name: 'Text', i18nKey: 'cfg_block_text', onlyOn: 'page404', configurable: true },
];

// Style settings: which color paths in `styleSettings` map to which legacy
// setting key. Used both on load (to seed the originalStyleSettingsRef) and
// on save (to diff + patch). Module-scope so we declare it once.
const STYLE_COLOR_PATHS = [
    { setting: 'COLOR_BODY_BACKGROUND',         path: ['global','background'] },
    { setting: 'COLOR_BODY_TEXT',               path: ['global','text']       },
    { setting: 'COLOR_PRIMARY',                 path: ['global','primary']    },
    { setting: 'COLOR_BACKGROUND_LANG',         path: ['sections','language','bgColor']     },
    { setting: 'COLOR_TEXT_LANG',               path: ['sections','language','textColor']   },
    { setting: 'COLOR_TEXT_ACTIVE_LANG',        path: ['sections','language','activeColor'] },
    { setting: 'COLOR_BACKGROUND_HEADER',       path: ['sections','header','bgColor']       },
    { setting: 'COLOR_TEXT_HEADER',             path: ['sections','header','textColor']     },
    { setting: 'COLOR_SECONDARY',               path: ['sections','menu','bgColor']         },
    { setting: 'COLOR_SECONDARY_INVERTED',      path: ['sections','menu','textColor']       },
    { setting: 'COLOR_IN_CART_BACKGROUND',      path: ['sections','menu','accentColor']     },
    // Custom hover colours — gated by the COLOR_USE_CUSTOM_HOVER toggle (a
    // boolean handled separately); the colour values themselves round-trip here.
    { setting: 'COLOR_PRIMARY_HOVER',           path: ['sections','menu','primaryHover']       },
    { setting: 'COLOR_PRIMARY_HOVER_TEXT',      path: ['sections','menu','primaryHoverText']   },
    { setting: 'COLOR_SECONDARY_HOVER',         path: ['sections','menu','secondaryHover']     },
    { setting: 'COLOR_SECONDARY_HOVER_TEXT',    path: ['sections','menu','secondaryHoverText'] },
    { setting: 'COLOR_SALES_VALUE_PRIMARY',     path: ['sections','price','discountColor']  },
    { setting: 'COLOR_BORDERS',                 path: ['sections','sideFilter','borderColor'] },
    { setting: 'COLOR_BACKGROUND_FOOTER',       path: ['sections','footer','bgColor']         },
    { setting: 'COLOR_TEXT_FOOTER',             path: ['sections','footer','textColor']       },
    { setting: 'COLOR_BACKGROUND_FOOTER_EXTRA', path: ['sections','footerExtra','bgColor']    },
    { setting: 'COLOR_TEXT_FOOTER_EXTRA',       path: ['sections','footerExtra','textColor']  },
];
const readByPath = (obj, path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

// The webshop URL lives on `selectedWebshop.display_name` (lic_clients_webshop)
// and frequently has NO protocol (e.g. "testshop.fashionretail.be") — Header.js
// prepends https:// for exactly this reason. Image/asset URLs and the
// generate_statics iframe are built from this base, so a protocol-less value
// yields a RELATIVE url that 404s against the admin domain (broken logo →
// placeholder SVG, blank carousel/grid tiles). Normalise once, everywhere:
// strip the mini-api suffix + trailing slash, then guarantee a protocol.
const shopImageBase = (raw) => {
    let u = (raw || '').replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/$/, '');
    if (u && !/^https?:\/\//i.test(u)) u = `https://${u}`;
    return u;
};

// Pure settings-coercion helpers. Hoisted to module scope so they're available
// throughout the load effect regardless of where in the effect body they're
// first referenced (a local `const` declaration mid-effect created a temporal
// dead zone: the font block read `readBool` before its declaration → the whole
// load threw and logo/carousel/grid never initialised).
const readNum  = (v, fb) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };
const readBool = (v, fb) => {
    if (v === true || v === 1 || v === '1') return true;
    if (v === false || v === 0 || v === '0') return false;
    return fb;
};
const readStr  = (v, fb) => (v == null || v === '' ? fb : String(v));

// Shared "Save" button for the config dialogs (style config/colour + Pages block
// gear). Runs the global saveConfig, shows a spinner + "saving…" while in flight,
// and closes the dialog on success (only when a save wasn't already running when
// it was clicked). Previously this exact block was copy-pasted 3×.
const ConfigSaveButton = ({ saving, onSave, onClose, t }) => (
    <Button
        variant="contained"
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        onClick={async () => {
            const wasSaving = saving;
            await onSave();
            if (!wasSaving && onClose) onClose();
        }}
    >
        {saving ? (t ? t('cfg_saving', 'Bezig met opslaan…') : 'Bezig met opslaan…') : (t ? t('save', 'Save') : 'Save')}
    </Button>
);
ConfigSaveButton.propTypes = { saving: PropTypes.bool, onSave: PropTypes.func, onClose: PropTypes.func, t: PropTypes.func };

// Canonical front-page block catalog. IDs MUST match what the storefront
// stores in HOME_SECTIONS_ORDER (camelCase where applicable). Product
// containers are added to this list dynamically once fetched, with
// `pd_cnt-<dbId>` ids — matching the legacy admin convention. The `name`
// field is the technical fallback; the visible label comes from `i18nKey`
// so admins see "Welkom" in NL, "Welcome" in EN, etc.
// `onlyOn` scopes a block to one page: 'home' blocks (the landing-page hero /
// product sections) are not offered on the 404 page, which only supports a
// text block + product containers. 'page404' scopes a block to the 404 page.
// Product containers carry no `onlyOn`, so they're available on both.
const STATIC_BLOCKS = [
    { id: 'carousel',    name: 'Carousel',       i18nKey: 'cfg_block_carousel',     onlyOn: 'home', configurable: true },
    { id: 'imageGrid',   name: 'Image grid',     i18nKey: 'cfg_block_image_grid',   onlyOn: 'home', configurable: true },
    { id: 'intro',       name: 'Welcome',        i18nKey: 'cfg_block_intro',        onlyOn: 'home', configurable: true },
    { id: 'newProducts', name: 'New products',   i18nKey: 'cfg_block_new_products', onlyOn: 'home' },
    { id: 'brands',      name: 'Brands',         i18nKey: 'cfg_block_brands',       onlyOn: 'home' },
    { id: 'sales',       name: 'Sales products', i18nKey: 'cfg_block_sales',        onlyOn: 'home' },
    { id: 'text',        name: 'Text',           i18nKey: 'cfg_block_text',         onlyOn: 'page404', configurable: true },
];

// System / web-safe font stacks — rendered as a "default" group at the top
// of the font dropdown. These don't need a Google Fonts <link> injection
// because the browser resolves them from the OS.
const DEFAULT_FONTS = [
    { family: '"Noto Serif", serif',                          category: 'default' },
    { family: 'Roboto, "Helvetica Neue", sans-serif',         category: 'default' },
    { family: 'Georgia, serif',                               category: 'default' },
    { family: 'Arial, Helvetica, sans-serif',                 category: 'default' },
    { family: '"Comic Sans MS", "Comic Sans", cursive',       category: 'default' },
    { family: 'Verdana, Geneva, sans-serif',                  category: 'default' },
];

// Compute a readable text color (black/white) against a given hex background.
const getContrastText = (hex) => {
    try {
        const h = String(hex).replace('#', '').trim();
        if (h.length !== 6) return '#000000';
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.55 ? '#000000' : '#ffffff';
    } catch { return '#000000'; }
};

// Standalone component so it keeps a stable identity across parent re-renders —
// critical because the native OS color picker closes if its <input> gets
// unmounted/remounted while the user is dragging. By defining this at module
// scope, React reconciles the same DOM node across renders and the picker stays
// open.
const ColorField = React.memo(function ColorField({ label, value, onChange, mutedText, borderColor, markPickerInteraction, compact = false }) {
    const contrastColor = getContrastText(value);
    // Compact mode shrinks the swatch + hex input for use in the sidebar
    // panels, where vertical space is at a premium.
    const swatchHeight = compact ? 30 : 44;
    const hexFontSize  = compact ? '0.78rem' : '0.95rem';
    const hexWidth     = compact ? 96 : 120;
    return (
        <Box>
            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: compact ? 0.25 : 0.5, fontSize: compact ? '0.72rem' : undefined }}>
                {label}
            </Typography>
            <Box
                component="label"
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: swatchHeight,
                    backgroundColor: value,
                    borderRadius: 1,
                    border: `1px solid ${borderColor}`,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    '&:hover .hex-input, & .hex-input:focus': {
                        color: contrastColor,
                    },
                }}
                onMouseDown={markPickerInteraction}
            >
                <input
                    type="color"
                    value={value}
                    onChange={(e) => { markPickerInteraction && markPickerInteraction(); onChange(e.target.value); }}
                    onInput={markPickerInteraction}
                    onBlur={markPickerInteraction}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 1 }}
                />
                <Box
                    component="input"
                    className="hex-input"
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.target.select()}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: value,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        fontSize: hexFontSize,
                        letterSpacing: 1,
                        textAlign: 'center',
                        width: hexWidth,
                        cursor: 'text',
                        transition: 'color 150ms',
                    }}
                />
            </Box>
        </Box>
    );
});

const DEFAULT_CONTAINER = {
    reference: '',
    link: '',
    maxArticles: 12,
    maxWidth: 1200,
    topMargin: 0,
    bottomMargin: 0,
    viewMethod: 'horizontal',
    scrollMethod: 'arrows_large',
    active: true,
    keepVisible: true,
    titleNL: '',
    titleEN: '',
    titleFR: '',
    buttonNL: '',
    buttonEN: '',
    buttonFR: '',
    titleFontSize: 1,
    buttonColor: 'primary',
    titlePosition: 'left',
    useIframe: true,
    designNL: null,
    designEN: null,
    designFR: null,
};

// Translate a raw `product_containers` DB row into the form-state shape the
// configure modal uses. Anything we can confidently map gets transferred;
// fields we don't (yet) understand cleanly keep their DEFAULT value, so the
// form still renders in a sensible state. The `body_*` HTML columns are
// intentionally NOT mapped onto designNL/EN/FR — those are unlayer JSON
// designs, a different shape from raw HTML; importing legacy HTML into
// unlayer is a separate task.
const containerRowToFormData = (row) => {
    if (!row) return DEFAULT_CONTAINER;
    const num = (v, fallback) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    };
    const bool = (v) => Number(v) === 1 || v === true || v === '1';
    const [marginTop, marginBottom] = (() => {
        const raw = String(row.margin || '').trim();
        if (!raw) return [DEFAULT_CONTAINER.topMargin, DEFAULT_CONTAINER.bottomMargin];
        const [t, b] = raw.split(',').map(s => Number(s.trim()));
        return [
            Number.isFinite(t) ? t : DEFAULT_CONTAINER.topMargin,
            Number.isFinite(b) ? b : DEFAULT_CONTAINER.bottomMargin,
        ];
    })();
    return {
        ...DEFAULT_CONTAINER,
        reference:     row.description    || '',
        titleNL:       row.loc_nl         || '',
        titleEN:       row.loc_en         || '',
        titleFR:       row.loc_fr         || '',
        buttonNL:      row.button_nl      || '',
        buttonEN:      row.button_en      || '',
        buttonFR:      row.button_fr      || '',
        buttonColor:   row.button_type    || DEFAULT_CONTAINER.buttonColor,
        link:          row.explore_url    || '',
        maxArticles:   num(row.max_items,  DEFAULT_CONTAINER.maxArticles),
        maxWidth:      num(row.max_width,  DEFAULT_CONTAINER.maxWidth),
        topMargin:     marginTop,
        bottomMargin:  marginBottom,
        viewMethod:    row.display_style  || DEFAULT_CONTAINER.viewMethod,
        scrollMethod:  row.scroll_style   || DEFAULT_CONTAINER.scrollMethod,
        titleFontSize: num(row.title_size, DEFAULT_CONTAINER.titleFontSize),
        titlePosition: (row.title_position || DEFAULT_CONTAINER.titlePosition).toLowerCase(),
        active:        bool(row.is_active),
        keepVisible:   bool(row.show_when_no_items),
        useIframe:     bool(row.use_iframe),
    };
};

const DEFAULT_ACTIVE = [
    { id: 'carousel',    name: 'Carousel',     i18nKey: 'cfg_block_carousel',     configurable: true },
    { id: 'intro',       name: 'Welcome',      i18nKey: 'cfg_block_intro',        configurable: true },
    { id: 'imagegrid',   name: 'Image grid',   i18nKey: 'cfg_block_image_grid',   configurable: true },
    { id: 'newproducts', name: 'New products', i18nKey: 'cfg_block_new_products' },
    { id: 'brands',      name: 'Brands',       i18nKey: 'cfg_block_brands' },
];

// 404 page starts empty — admins drag product containers onto it from Available.
const DEFAULT_ACTIVE_404 = [];

// Login/account cards shown on the checkout page, pre-populated with the
// two blocks that used to live in the legacy config screen.
const DEFAULT_CARDS = [
    {
        id: 'existing_account',
        group: 'account',
        label: 'Sign in with an existing account',
        useIframe: false,
        contentNL: '<h3>Aanmelden</h3><p>Vul je e-mailadres en wachtwoord in om je bestelling snel af te ronden.</p>',
        contentEN: '<h3>Sign in</h3><p>Enter your email and password to finish your order quickly.</p>',
        contentFR: '<h3>Se connecter</h3><p>Saisissez votre e-mail et votre mot de passe pour finaliser votre commande.</p>',
    },
    {
        id: 'create_account',
        group: 'account',
        label: 'Create a new account',
        useIframe: false,
        contentNL: '<h3>Account aanmaken</h3><p>Maak een account aan om je bestellingen te volgen.</p>',
        contentEN: '<h3>Create account</h3><p>Create an account so you can track your orders.</p>',
        contentFR: '<h3>Créer un compte</h3><p>Créez un compte pour suivre vos commandes.</p>',
    },
    {
        id: 'without_account',
        group: 'account',
        label: 'Continue without an account',
        useIframe: false,
        contentNL: '<h3>Zonder account bestellen</h3><p>Rond je bestelling af zonder een account aan te maken.</p>',
        contentEN: '<h3>Order without an account</h3><p>Complete your order without creating an account.</p>',
        contentFR: '<h3>Commander sans compte</h3><p>Finalisez votre commande sans créer de compte.</p>',
    },
    {
        id: 'pickup',
        group: 'delivery',
        label: 'Order - Pickup',
        useIframe: false,
        contentNL: '<h3>Afhaling</h3><p>Haal je bestelling op in de winkel.</p>',
        contentEN: '<h3>Pickup</h3><p>Collect your order in the store.</p>',
        contentFR: '<h3>Retrait</h3><p>Retirez votre commande en magasin.</p>',
    },
    {
        id: 'delivery',
        group: 'delivery',
        label: 'Order - Delivery',
        useIframe: false,
        contentNL: '<h3>Verzending</h3><p>Laat je bestelling thuis leveren.</p>',
        contentEN: '<h3>Delivery</h3><p>Have your order delivered to your home.</p>',
        contentFR: '<h3>Livraison</h3><p>Faites livrer votre commande à domicile.</p>',
    },
];

// Cart-page cards ↔ `contents` rows. The cart flow has account blocks (login,
// register, without) and delivery-method blocks (pickup, delivery); each is a
// wysiwyg content row. Loaded from the settings dump (s.contents_*), saved via
// PATCH /admin/contents/{attr}.
const CARD_CONTENT = {
    existing_account: { attr: 'TEXT_ORDER_ACCOUNT_LOGIN',    field: 'contents_order_login' },
    create_account:   { attr: 'TEXT_ORDER_ACCOUNT_REGISTER', field: 'contents_order_register' },
    without_account:  { attr: 'TEXT_ORDER_ACCOUNT_WITHOUT',  field: 'contents_order_without' },
    pickup:           { attr: 'TEXT_ORDER_PICKUP',           field: 'contents_text_order_pickup' },
    delivery:         { attr: 'TEXT_ORDER_DELIVERY',         field: 'contents_text_order_delivery' },
};

// Brands page layout ↔ the storefront's BRANDS_PAGE_STYLE value.
const BRANDS_LAYOUT_TO_STYLE = { continuous: 'list', grouped: 'alphabetical-list', multiCol: 'alphabetical-columns' };
const BRANDS_STYLE_TO_LAYOUT = { 'list': 'continuous', 'alphabetical-list': 'grouped', 'alphabetical-columns': 'multiCol' };

// Dynamic-page position ↔ the backend `dynamic_pages.position` column.
const DYN_POS_TO_BACKEND   = { hidden: 'HIDDEN', header: 'HEADER', footer: 'FOOTER', both: 'BOTH' };
const DYN_POS_FROM_BACKEND = { HIDDEN: 'hidden', HEADER: 'header', FOOTER: 'footer', BOTH: 'both' };
const DYN_LANGS = ['nl', 'en', 'fr'];

// Information banner position ↔ backend `information_banners.position`. UI
// 'top' = above the navbar (backend 'top'); 'below' = below the menu/navbar
// (backend 'navbar', per mig147).
const BANNER_POS_TO_BACKEND   = { top: 'top', below: 'navbar' };
const BANNER_POS_FROM_BACKEND = { top: 'top', navbar: 'below' };

// Cookie banner style ↔ vanilla-cookieconsent layout (cookie.js guiOptions).
const OVERIGE_STYLE_TO_LAYOUT      = { floating: 'cloud', modal: 'box', bottom: 'bar' };
const LAYOUT_TO_OVERIGE_STYLE      = { cloud: 'floating', box: 'modal', bar: 'bottom' };

// Cadeaubon (gift voucher) — the CHEQUES_* settings are stored as strings
// ('1'/'0' for booleans, CSV for amounts, enums for types). These helpers map
// the flat settings dump to the CadeaubonTab config shape and back.
const chq01   = (v) => v === true || v === 1 || v === '1';
const chqNum  = (v, dflt) => { const n = Number(v); return Number.isFinite(n) ? n : dflt; };
const chqAmts = (v) => String(v == null ? '' : v).split(',').map(x => parseInt(x.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
// Voucher-type enum for the personalization selects (matches the old admin).
const CHEQUE_TYPE = ['NONE', 'PHYSICAL', 'DIGITAL', 'BOTH'];
const chqType = (v, dflt) => { const u = String(v == null ? '' : v).toUpperCase(); return CHEQUE_TYPE.includes(u) ? u : dflt; };

const settingsToCadeaubon = (s, dflt) => {
    const types = String(s.CHEQUES_AVAILABLE_TYPES || 'BOTH').toUpperCase();
    const presets = chqAmts(s.CHEQUES_AVAILABLE_AMOUNTS);
    return {
        availability: {
            showInNavigation: chq01(s.SHOW_HEADER_CHEQUES),
            allowPhysical:    types === 'BOTH' || types === 'PHYSICAL',
            allowDigital:     types === 'BOTH' || types === 'DIGITAL',
        },
        amounts: {
            presets:     presets.length ? presets : dflt.amounts.presets,
            allowCustom: chq01(s.CHEQUES_ALLOW_CUSTOM_AMOUNT),
            customMin:   chqNum(s.CHEQUES_MIN_AMOUNT, dflt.amounts.customMin),
            customMax:   chqNum(s.CHEQUES_MAX_AMOUNT, dflt.amounts.customMax),
        },
        discounts: {
            countTowardTotals:  chq01(s.CHEQUES_ADD_TO_CUSTOMER_DISCOUNT),
            applyToVoucherOnly: chq01(s.CHEQUES_ALLOW_CUSTOMER_DISC),
            applyToMixedOrders: chq01(s.CHEQUES_ALLOW_CUSTOMER_DISC_MIX),
        },
        shipping: {
            cost:     chqNum(s.CHEQUES_SHIPMENT_COSTS, dflt.shipping.cost),
            freeFrom: chqNum(s.CHEQUES_SHIPMENT_FREE_AMOUNT, dflt.shipping.freeFrom),
        },
        personalization: {
            infoOnType:          chqType(s.CHEQUES_ALLOW_INFO_ON_TYPE, dflt.personalization.infoOnType),
            sendDigitalDirectly: chq01(s.CHEQUES_ALLOW_SEND_TO_RECEIVER),
            messageOnType:       chqType(s.CHEQUES_ALLOW_MESSAGE_ON_TYPE, dflt.personalization.messageOnType),
        },
    };
};

// CadeaubonTab config → flat { SETTING_KEY: storedStringValue } map. Booleans as
// '1'/'0', amounts as a ', '-joined CSV, enums for types/message — matching what
// the backend stores so the dirty-diff compares like-for-like.
const cadeaubonToSettings = (c) => {
    const a = c.availability || {}, am = c.amounts || {}, d = c.discounts || {}, sh = c.shipping || {}, p = c.personalization || {};
    const types = (a.allowPhysical && a.allowDigital) ? 'BOTH' : a.allowDigital ? 'DIGITAL' : a.allowPhysical ? 'PHYSICAL' : 'BOTH';
    return {
        SHOW_HEADER_CHEQUES:              a.showInNavigation ? '1' : '0',
        CHEQUES_AVAILABLE_TYPES:          types,
        CHEQUES_AVAILABLE_AMOUNTS:        (am.presets || []).join(', '),
        CHEQUES_ALLOW_CUSTOM_AMOUNT:      am.allowCustom ? '1' : '0',
        CHEQUES_MIN_AMOUNT:               String(am.customMin ?? ''),
        CHEQUES_MAX_AMOUNT:               String(am.customMax ?? ''),
        CHEQUES_SHIPMENT_COSTS:           String(sh.cost ?? ''),
        CHEQUES_SHIPMENT_FREE_AMOUNT:     String(sh.freeFrom ?? ''),
        CHEQUES_ALLOW_INFO_ON_TYPE:       chqType(p.infoOnType, 'NONE'),
        CHEQUES_ALLOW_MESSAGE_ON_TYPE:    chqType(p.messageOnType, 'NONE'),
        CHEQUES_ALLOW_SEND_TO_RECEIVER:   p.sendDigitalDirectly ? '1' : '0',
        CHEQUES_ADD_TO_CUSTOMER_DISCOUNT: d.countTowardTotals ? '1' : '0',
        CHEQUES_ALLOW_CUSTOMER_DISC:      d.applyToVoucherOnly ? '1' : '0',
        CHEQUES_ALLOW_CUSTOMER_DISC_MIX:  d.applyToMixedOrders ? '1' : '0',
    };
};

// Backend `cheques_placeholders` row → the Translations-table row shape. The UI
// edits loc_*/description; `key` is server-generated and shown read-only.
const placeholderRowToForm = (row) => ({
    id:          row.id,
    key:         row.key || '',
    description: row.description || '',
    layout:      row.layout || 'GLOBAL',
    nl:          row.loc_nl || '',
    en:          row.loc_en || '',
    fr:          row.loc_fr || '',
});

// Cheque/voucher layout (cheques_layouts) row → UI shape. `imgBase` is the
// resolved `<webshopBase><img_path>/cheque-layouts/` URL prefix for previews.
// `cheque_type` ≠ null marks a built-in system default (not deletable). For
// PHYSICAL layouts `layout_file` is the uploaded preview image; for DIGITAL it's
// the `<id>.html` design file (not an image), so we only surface it as an image
// for physical layouts.
const layoutRowToForm = (row, imgBase) => {
    const type = String(row.type || 'DIGITAL').toUpperCase();
    const isImageFile = (fn) => fn && !/\.html?$/i.test(String(fn));
    return {
        id:          `lay_${row.id}`,
        serverId:    row.id,
        type,
        description: row.description || '',
        visible:     !!Number(row.visible),
        isSystem:    !!(row.cheque_type != null && String(row.cheque_type).trim() !== ''),
        chequeType:  row.cheque_type || null,
        image:       row.image ? { name: row.image, url: `${imgBase}${row.image}` } : null,
        layoutImage: (type === 'PHYSICAL' && isImageFile(row.layout_file))
            ? { name: row.layout_file, url: `${imgBase}${row.layout_file}` } : null,
        // pending pickers + design dirty (set in the tab; cleared on save/refetch)
        imageFile: null, layoutImageFile: null,
        designDirty: false, design: null, html: null, designLoaded: false,
        copyFrom: null,
    };
};

// Stable signature for the layouts dirty-diff (covers metadata, pending image
// picks, and a dirty Unlayer design).
const layoutCompareJson = (arr) => JSON.stringify((arr || []).map(l => ({
    id:          l.serverId ?? null,
    type:        l.type,
    description: l.description || '',
    visible:     !!l.visible,
    copyFrom:    l.copyFrom ?? null,
    img:         l.imageFile ? `PENDING:${l.imageFile.name}` : (l.image?.name || ''),
    layoutImg:   l.layoutImageFile ? `PENDING:${l.layoutImageFile.name}` : (l.layoutImage?.name || ''),
    design:      l.designDirty ? 'PENDING' : '',
})));

// Custom customer fields = `customers_categories_groups` rows (CRUD). The panel
// uses {id, codeGroup(=sft_id), labels{nl,en,fr}, showOn{registration,account,newsletter}}.
const customerGroupRowToForm = (row) => ({
    id:        row.id,
    codeGroup: String(row.sft_id || ''),
    labels:    { nl: row.loc_nl || '', en: row.loc_en || '', fr: row.loc_fr || '' },
    showOn: {
        registration: chq01(row.show_on_signup),
        account:      chq01(row.show_on_account),
        newsletter:   chq01(row.show_on_newsletter),
    },
});
const customerGroupToColumns = (g) => ({
    sft_id:             g.codeGroup || '',
    loc_nl:             g.labels?.nl || '',
    loc_en:             g.labels?.en || '',
    loc_fr:             g.labels?.fr || '',
    show_on_signup:     g.showOn?.registration ? 1 : 0,
    show_on_account:    g.showOn?.account ? 1 : 0,
    show_on_newsletter: g.showOn?.newsletter ? 1 : 0,
});

// ── Verwerking (order processing) — settings mapping ──────────────────────
// Only fields with a confirmed settings-key backend are wired here. Fields
// without one (general discount type, negative-cart, pickup cost, country
// preset/rates, per-store pickup, payment provider/type split, the "no
// checkout" payment option) stay in the UI as pending and are intentionally
// excluded from this map so they neither save nor flag dirty.
const PROC_PAY_LOC_TO_BACKEND   = { inStore: 'IN_STORE', onlineOnly: 'ONLINE', onlineAndStore: 'BOTH', noCheckout: 'NOCHECKOUT' };
const PROC_PAY_LOC_FROM_BACKEND = { IN_STORE: 'inStore', ONLINE: 'onlineOnly', BOTH: 'onlineAndStore' };
const PROC_SHIP_TO_BACKEND      = { pickupOnly: 'PICKUP', shippingOnly: 'SHIPMENT', pickupAndShipping: 'BOTH' };
const PROC_SHIP_FROM_BACKEND    = { PICKUP: 'pickupOnly', SHIPMENT: 'shippingOnly', BOTH: 'pickupAndShipping' };
// PICKUP_NO_STOCK_ICON stores a FontAwesome class string ("fas fa-truck-ramp-box");
// the IconPicker works with the dash-case name ("truck-ramp-box").
const iconClassToName = (cls) => { const m = String(cls || '').match(/fa-([a-z0-9-]+)/i); return m ? m[1] : ''; };
const iconNameToClass = (name) => (name ? `fas fa-${name}` : '');
// Discount/voucher type codes: the UI rows use 6-digit codes, the CHEQUES_*
// JSON settings key them as 8-digit ("99" + the 6-digit code).
const DISC_CODE8 = (code6) => `99${code6}`;

// POS-betaalmiddelen (kas_betaal1..9) zijn per winkel/kassa gesegmenteerd via
// kasvar.code: '0000' = globaal, '00'+winkel = winkel-specifiek, de volledige
// POS-id (bv. '0101') = kassa-specifiek. Per slot kiezen we de meest specifieke
// rij voor de gekozen POS-id (PROCESS_RECEIPT_POS_ID_PROCESS), met fallback
// kassa → winkel → globaal — dezelfde volgorde als de backend
// (ProgVariable::scopeForLocation: code IN ('0000','00'+store, pos)). Rijen van
// andere winkels/kassa's worden genegeerd. `rows` = [{n, value, code}].
const computePaymentTypeOptions = (rows, posIdRaw) => {
    const posId = String(posIdRaw || '').trim();
    const storeCode = posId.length >= 2 ? '00' + posId.slice(0, 2) : '';
    const rank = (code) => {
        const c = String(code == null ? '' : code).trim();
        if (posId && c === posId) return 3;          // kassa-specifiek
        if (storeCode && c === storeCode) return 2;  // winkel-specifiek
        if (c === '0000') return 1;                  // globaal
        return 0;                                    // andere winkel/kassa
    };
    const best = {};
    for (const r of (rows || [])) {
        const score = rank(r.code);
        if (score === 0) continue;
        if (!best[r.n] || score > best[r.n].score) best[r.n] = { value: r.value, score };
    }
    return Object.keys(best)
        .map(Number)
        .sort((a, b) => a - b)
        .map(n => ({ value: String(n), label: `${n} — ${best[n].value}` }));
};

// Read a File as a data URL (base64). Uploads travel as base64 in the JSON
// proxy body; the mini-api's UploadStore::saveBase64 strips the data-URL prefix.
const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
});

const settingsToProcessing = (s, dflt) => ({
    access: {
        ...dflt.access,
        posCode:          s.CUSTOMER_CREATION_POS_ID != null && s.CUSTOMER_CREATION_POS_ID !== '' ? String(s.CUSTOMER_CREATION_POS_ID) : dflt.access.posCode,
        guestCustomerId:  s.PROCESS_NO_ACCOUNT_ID != null ? String(s.PROCESS_NO_ACCOUNT_ID) : dflt.access.guestCustomerId,
        allowGuestPickup: chq01(s.PROCESS_ALLOW_WITHOUT_ACCOUNT),
    },
    warehouse: s.PROCESS_STORE != null && s.PROCESS_STORE !== '' ? String(s.PROCESS_STORE) : dflt.warehouse,
    receiptPos: {
        process: s.PROCESS_RECEIPT_POS_ID_PROCESS != null ? String(s.PROCESS_RECEIPT_POS_ID_PROCESS) : dflt.receiptPos.process,
        print:   s.PROCESS_RECEIPT_POS_ID_RECEIPT != null ? String(s.PROCESS_RECEIPT_POS_ID_RECEIPT) : dflt.receiptPos.print,
    },
    discountTypes: (() => {
        const allowed = (s.CHEQUES_ALLOWED && typeof s.CHEQUES_ALLOWED === 'object') ? s.CHEQUES_ALLOWED : {};
        const asPay   = (s.CHEQUES_AS_PAYMENT && typeof s.CHEQUES_AS_PAYMENT === 'object') ? s.CHEQUES_AS_PAYMENT : {};
        const receipt = (s.CHEQUES_ADD_TO_RECEIPT && typeof s.CHEQUES_ADD_TO_RECEIPT === 'object') ? s.CHEQUES_ADD_TO_RECEIPT : {};
        const out = {};
        Object.keys(dflt.discountTypes).forEach((code6) => {
            const k8 = DISC_CODE8(code6);
            out[code6] = { enabled: !!allowed[k8], acceptAsPayment: !!asPay[k8], showInEmail: !!receipt[k8] };
        });
        return out;
    })(),
    discountBarcode: s.ITEM_DISCOUNT != null ? String(s.ITEM_DISCOUNT) : '',
    cart: {
        ...dflt.cart,
        allowDiscountCodes:   chq01(s.ENABLE_VOUCHERS),
        allowNegativeTotals:  chq01(s.CHEQUE_USAGE_VALUE_BIGGER),
        limitQuantityPerItem: chq01(s.CART_MAX_QTY_LIMIT),
        maxQuantityPerItem:   chqNum(s.CART_MAX_QTY,   dflt.cart.maxQuantityPerItem),
        maxUniqueItemsInCart: chqNum(s.CART_MAX_ITEMS, dflt.cart.maxUniqueItemsInCart),
    },
    registration: {
        ...dflt.registration,
        phoneRequired:        chq01(s.CUSTOMER_CREATION_PHONE_REQ),
        defaultSalutation:    s.CUSTOMER_DEFAULT_TITLE != null && s.CUSTOMER_DEFAULT_TITLE !== '' ? String(s.CUSTOMER_DEFAULT_TITLE) : dflt.registration.defaultSalutation,
        defaultCountry:       s.CUSTOMER_DEFAULT_COUNTRY != null ? String(s.CUSTOMER_DEFAULT_COUNTRY) : dflt.registration.defaultCountry,
        showPurchaseHistory:  chq01(s.ACCOUNT_SHOW_ORDERS),
        showCustomerDiscount: chq01(s.ACCOUNT_SHOW_DISCOUNT),
    },
    externalStock: {
        iconName:  iconClassToName(s.PICKUP_NO_STOCK_ICON) || dflt.externalStock.iconName,
        iconColor: s.PICKUP_NO_STOCK_COLOR || dflt.externalStock.iconColor,
    },
    ticketPrinting: {
        regular: { pickup: chq01(s.PROCESS_PRINT_TICKET_PICK),     shipped: chq01(s.PROCESS_PRINT_TICKET_SHIP) },
        compact: { pickup: chq01(s.PROCESS_PRINT_TICKET_MIN_PICK), shipped: chq01(s.PROCESS_PRINT_TICKET_MIN_SHIP) },
        message: { pickup: chq01(s.PROCESS_PRINT_MESSAGE_PICK),    shipped: chq01(s.PROCESS_PRINT_MESSAGE_SHIP) },
    },
    payments: {
        ...dflt.payments,
        // LOOKBOOK (catalogue) mode = "no checkout"; otherwise map the online/in-store choice.
        location:     String(s.PROCESS_TICKET_MODULE || '').toUpperCase() === 'LOOKBOOK'
            ? 'noCheckout'
            : (PROC_PAY_LOC_FROM_BACKEND[String(s.PROCESS_PAYMENT_METHODS || '').toUpperCase()] || dflt.payments.location),
        allowInvoice: chq01(s.CHECKOUT_ALLOW_INVOICE),
        provider:     s.PAYMENT_PROVIDER ? String(s.PAYMENT_PROVIDER) : dflt.payments.provider,
        paymentType:  s.PROCESS_PAYMENT_TYPE != null && s.PROCESS_PAYMENT_TYPE !== '' ? String(s.PROCESS_PAYMENT_TYPE) : dflt.payments.paymentType,
    },
    shipping: {
        ...dflt.shipping,
        method:                   PROC_SHIP_FROM_BACKEND[String(s.PROCESS_TICKET_DELIVERY_TYPE || '').toUpperCase()] || dflt.shipping.method,
        routeExternalThroughShop: chq01(s.SEND_EXTERNAL_ORDER_TO_STORE),
        externalStockEnabled:     chq01(s.PRODUCT_USE_EXTERNAL_STOCK), // read-only gate (set in Data sync)
        defaultCost:              chqNum(s.SHIPMENT_COST,        dflt.shipping.defaultCost),
        defaultPickupCost:        chqNum(s.PICKUP_POINT_COST,    dflt.shipping.defaultPickupCost),
        freeFromAmount:           chqNum(s.SHIPMENT_FREE_AMOUNT, dflt.shipping.freeFromAmount),
        shippingCostItem:         s.ITEM_SHIPMENT != null ? String(s.ITEM_SHIPMENT) : dflt.shipping.shippingCostItem,
    },
    customerFields: dflt.customerFields, // pending (separate customers_categories_groups table)
});

// Processing config → flat { SETTING_KEY: storedString } for the dirty-diff and
// scalar PATCHes. `__`-prefixed keys are dirty-only signals (e.g. the discount
// JSON cells) and are skipped when building the actual PATCH list. The discount
// JSON objects themselves are rebuilt+patched separately (to preserve any codes
// not in the UI row list).
const processingToSettings = (c) => {
    const ac = c.access || {}, ca = c.cart || {}, rg = c.registration || {}, es = c.externalStock || {}, tp = c.ticketPrinting || {}, pm = c.payments || {}, sh = c.shipping || {}, dt = c.discountTypes || {};
    const out = {
        CUSTOMER_CREATION_POS_ID:      String(ac.posCode ?? ''),
        PROCESS_NO_ACCOUNT_ID:         String(ac.guestCustomerId ?? ''),
        PROCESS_ALLOW_WITHOUT_ACCOUNT: ac.allowGuestPickup ? '1' : '0',
        PROCESS_STORE:                 String(c.warehouse ?? ''),
        PROCESS_RECEIPT_POS_ID_PROCESS: String(c.receiptPos?.process ?? ''),
        PROCESS_RECEIPT_POS_ID_RECEIPT: String(c.receiptPos?.print   ?? ''),
        ITEM_DISCOUNT:                 String(c.discountBarcode ?? ''),
        ENABLE_VOUCHERS:               ca.allowDiscountCodes ? '1' : '0',
        CHEQUE_USAGE_VALUE_BIGGER:     ca.allowNegativeTotals ? '1' : '0',
        CART_MAX_QTY_LIMIT:            ca.limitQuantityPerItem ? '1' : '0',
        CART_MAX_QTY:                  String(ca.maxQuantityPerItem ?? ''),
        CART_MAX_ITEMS:                String(ca.maxUniqueItemsInCart ?? ''),
        CUSTOMER_CREATION_PHONE_REQ:   rg.phoneRequired ? '1' : '0',
        CUSTOMER_DEFAULT_TITLE:        String(rg.defaultSalutation ?? ''),
        CUSTOMER_DEFAULT_COUNTRY:      String(rg.defaultCountry ?? ''),
        ACCOUNT_SHOW_ORDERS:           rg.showPurchaseHistory ? '1' : '0',
        ACCOUNT_SHOW_DISCOUNT:         rg.showCustomerDiscount ? '1' : '0',
        PICKUP_NO_STOCK_ICON:          iconNameToClass(es.iconName),
        PICKUP_NO_STOCK_COLOR:         String(es.iconColor ?? ''),
        PROCESS_PRINT_TICKET_PICK:     tp.regular?.pickup  ? '1' : '0',
        PROCESS_PRINT_TICKET_SHIP:     tp.regular?.shipped ? '1' : '0',
        PROCESS_PRINT_TICKET_MIN_PICK: tp.compact?.pickup  ? '1' : '0',
        PROCESS_PRINT_TICKET_MIN_SHIP: tp.compact?.shipped ? '1' : '0',
        PROCESS_PRINT_MESSAGE_PICK:    tp.message?.pickup  ? '1' : '0',
        PROCESS_PRINT_MESSAGE_SHIP:    tp.message?.shipped ? '1' : '0',
        PROCESS_PAYMENT_METHODS:       PROC_PAY_LOC_TO_BACKEND[pm.location] || 'BOTH',
        CHECKOUT_ALLOW_INVOICE:        pm.allowInvoice ? '1' : '0',
        PAYMENT_PROVIDER:              pm.provider || 'msp',
        PROCESS_PAYMENT_TYPE:          String(pm.paymentType ?? '1'),
        PROCESS_TICKET_DELIVERY_TYPE:  PROC_SHIP_TO_BACKEND[sh.method] || 'BOTH',
        SEND_EXTERNAL_ORDER_TO_STORE:  sh.routeExternalThroughShop ? '1' : '0',
        SHIPMENT_COST:                 String(sh.defaultCost ?? ''),
        PICKUP_POINT_COST:             String(sh.defaultPickupCost ?? ''),
        SHIPMENT_FREE_AMOUNT:          String(sh.freeFromAmount ?? ''),
        ITEM_SHIPMENT:                 String(sh.shippingCostItem ?? ''),
    };
    // Discount/voucher cells — dirty-only signals (real save rebuilds the JSON).
    Object.keys(dt).forEach((code) => {
        const r = dt[code] || {};
        out[`__DISC_ALLOWED_${code}`] = r.enabled         ? '1' : '0';
        out[`__DISC_ASPAY_${code}`]   = r.acceptAsPayment ? '1' : '0';
        out[`__DISC_RECEIPT_${code}`] = r.showInEmail     ? '1' : '0';
    });
    return out;
};

// ── Synchronisatie (product sync) — settings mapping ──────────────────────
// All controls map onto existing storefront `settings` keys (FETCH_*, MAP_*,
// PHOTO_*), seeded in the storefront migrations. No CRUD, no deploy.
//
// PHOTO_PADDING_COLOR is stored as an "r,g,b" string; the colour input is hex.
const hexToRgbStr = (hex) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '').trim());
    if (!m) return '255,255,255';
    return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
};
const rgbStrToHex = (rgb) => {
    const parts = String(rgb || '').split(',').map(x => parseInt(x.trim(), 10));
    if (parts.length < 3 || parts.slice(0, 3).some(n => Number.isNaN(n))) return '#ffffff';
    const h = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${h(parts[0])}${h(parts[1])}${h(parts[2])}`;
};

// `allStoreCodes` (the full store universe) lets FETCH_FOR_STORES round-trip:
// '00'/'' = all → expanded to every code; an all-selected set → back to '00'.
const settingsToSync = (s, dflt, allStoreCodes = []) => ({
    sourceWebshop: s.FETCH_FOR_ONLINE != null && s.FETCH_FOR_ONLINE !== '' ? String(s.FETCH_FOR_ONLINE) : dflt.sourceWebshop,
    storesIncluded: (() => {
        const raw = s.FETCH_FOR_STORES != null ? String(s.FETCH_FOR_STORES) : '00';
        if (raw === '' || raw === '00') return [...allStoreCodes];
        return raw.split(',').map(x => x.trim()).filter(Boolean);
    })(),
    stockFiltering: {
        onlySyncWithStock:        chq01(s.FETCH_ONLY_WITH_STOCK),
        useExternalStockProvider: chq01(s.PRODUCT_USE_EXTERNAL_STOCK),
        showExternalOnlyProducts: chq01(s.OVERRULE_ONLY_IN_STOCK_FOR_EXT),
    },
    categoryHierarchy: [1, 2, 3, 4, 5, 6, 7].map((n) => {
        const v = s[`MAP_CATEGORY_${n}`];
        return { level: n, source: v != null && v !== '' ? String(v) : String(n).padStart(2, '0') };
    }),
    productFields: {
        name:      s.MAP_PRODUCT_NAME      != null && s.MAP_PRODUCT_NAME      !== '' ? String(s.MAP_PRODUCT_NAME)      : dflt.productFields.name,
        reference: s.MAP_PRODUCT_REFERENCE != null && s.MAP_PRODUCT_REFERENCE !== '' ? String(s.MAP_PRODUCT_REFERENCE) : dflt.productFields.reference,
    },
    languageMapping: {
        nl: s.MAP_LOCALE_NL != null && s.MAP_LOCALE_NL !== '' ? String(s.MAP_LOCALE_NL) : dflt.languageMapping.nl,
        en: s.MAP_LOCALE_EN != null && s.MAP_LOCALE_EN !== '' ? String(s.MAP_LOCALE_EN) : dflt.languageMapping.en,
        fr: s.MAP_LOCALE_FR != null && s.MAP_LOCALE_FR !== '' ? String(s.MAP_LOCALE_FR) : dflt.languageMapping.fr,
    },
    imageSourceTypes: {
        primary:     s.PHOTO_MAIN_TYPE   != null && s.PHOTO_MAIN_TYPE   !== '' ? String(s.PHOTO_MAIN_TYPE)   : dflt.imageSourceTypes.primary,
        alternative: s.PHOTO_SECOND_TYPE != null && s.PHOTO_SECOND_TYPE !== '' ? String(s.PHOTO_SECOND_TYPE) : dflt.imageSourceTypes.alternative,
    },
    imageProcessing: {
        useV2: chq01(s.USE_IMAGES_CRON_V2),
        sizes: {
            thumb:     { width: chqNum(s.PHOTO_SQUARE_SIZE_THUMB,   100), height: chqNum(s.PHOTO_RATIO_HEIGHT_THUMB,   160)  },
            preview:   { width: chqNum(s.PHOTO_SQUARE_SIZE_PREVIEW, 300), height: chqNum(s.PHOTO_RATIO_HEIGHT_PREVIEW, 500)  },
            processed: { width: chqNum(s.PHOTO_SQUARE_SIZE_FULL,    800), height: chqNum(s.PHOTO_RATIO_HEIGHT_FULL,    1300) },
        },
        backgroundFillColor: rgbStrToHex(s.PHOTO_PADDING_COLOR),
    },
});

const syncToSettings = (c, allStoreCodes = []) => {
    const sf = c.stockFiltering || {}, ip = c.imageProcessing || {}, sz = ip.sizes || {};
    const sel = Array.isArray(c.storesIncluded) ? c.storesIncluded : [];
    const allSelected = allStoreCodes.length > 0 && allStoreCodes.every(code => sel.includes(code));
    const out = {
        FETCH_FOR_ONLINE:               String(c.sourceWebshop ?? '1'),
        FETCH_FOR_STORES:               allSelected ? '00' : sel.join(','),
        FETCH_ONLY_WITH_STOCK:          sf.onlySyncWithStock        ? '1' : '0',
        PRODUCT_USE_EXTERNAL_STOCK:     sf.useExternalStockProvider ? '1' : '0',
        OVERRULE_ONLY_IN_STOCK_FOR_EXT: sf.showExternalOnlyProducts ? '1' : '0',
        MAP_PRODUCT_NAME:               String(c.productFields?.name ?? ''),
        MAP_PRODUCT_REFERENCE:          String(c.productFields?.reference ?? ''),
        MAP_LOCALE_NL:                  String(c.languageMapping?.nl ?? ''),
        MAP_LOCALE_EN:                  String(c.languageMapping?.en ?? ''),
        MAP_LOCALE_FR:                  String(c.languageMapping?.fr ?? ''),
        PHOTO_MAIN_TYPE:                String(c.imageSourceTypes?.primary ?? ''),
        PHOTO_SECOND_TYPE:              String(c.imageSourceTypes?.alternative ?? ''),
        USE_IMAGES_CRON_V2:             ip.useV2 ? '1' : '0',
        PHOTO_SQUARE_SIZE_THUMB:        String(sz.thumb?.width      ?? ''),
        PHOTO_SQUARE_SIZE_PREVIEW:      String(sz.preview?.width    ?? ''),
        PHOTO_SQUARE_SIZE_FULL:         String(sz.processed?.width  ?? ''),
        PHOTO_RATIO_HEIGHT_THUMB:       String(sz.thumb?.height     ?? ''),
        PHOTO_RATIO_HEIGHT_PREVIEW:     String(sz.preview?.height   ?? ''),
        PHOTO_RATIO_HEIGHT_FULL:        String(sz.processed?.height ?? ''),
        PHOTO_PADDING_COLOR:            hexToRgbStr(ip.backgroundFillColor || '#ffffff'),
    };
    (c.categoryHierarchy || []).forEach((lvl) => {
        out[`MAP_CATEGORY_${lvl.level}`] = String(lvl.source ?? '');
    });
    return out;
};

const bannerRowToForm = (row) => ({
    id:            `srv_${row.id}`,
    serverId:      row.id,
    referenceName: row.name || '',
    isActive:      !!Number(row.active),
    activeFrom:    row.date_from  ? String(row.date_from).slice(0, 10)  : '',
    activeTo:      row.date_until ? String(row.date_until).slice(0, 10) : '',
    position:      BANNER_POS_FROM_BACKEND[String(row.position || '').toLowerCase()] || 'top',
    bgColor:       row.background_color || '#000000',
    textColor:     row.foreground_color || '#ffffff',
    height:        row.height != null ? Number(row.height) : 30,
    scrollText:    !!Number(row.is_animated),
    multipleItems: !!Number(row.is_item_list),
    editor:        'wysiwyg',
    // Single-banner content lives in loc_*; the item-list variant uses items_*.
    // We surface whichever side matches the banner's current `is_item_list`.
    content: Number(row.is_item_list)
        ? { nl: row.items_nl || '', en: row.items_en || '', fr: row.items_fr || '' }
        : { nl: row.loc_nl   || '', en: row.loc_en   || '', fr: row.loc_fr   || '' },
});

// Backend `dynamic_pages` row → the editor's page shape. `content` starts empty
// (designs are lazy-loaded into the editor) so merely viewing a page can't flag
// the screen dirty; only edited languages land in `content`.
const dynRowToPage = (row) => ({
    id:       `srv_${row.id}`,
    serverId: row.id,
    slug:     row.route || '',
    position: DYN_POS_FROM_BACKEND[String(row.position || '').toUpperCase()] || 'hidden',
    isActive: !!row.active,
    iframe:   !!row.use_iframe,
    titles:   { nl: row.loc_nl || '', en: row.loc_en || '', fr: row.loc_fr || '' },
    content:  {},
});

const WebshopConfigScreen = (props) => {
    const { can } = usePermissions();
    const [tabValue, setTabValue] = useState(0); // Default to "Pages" (now first tab; opens on Front page sub-tab)
    // Permission key per main config tab index (0..4). Drives both which tab
    // buttons render and which tab is allowed to be active.
    const CONFIG_TAB_PERMS = ['webshop.config.tab.pages', 'webshop.config.tab.style', 'webshop.config.tab.features', 'webshop.config.tab.processing', 'webshop.config.tab.sync'];
    // If the active tab isn't allowed for the current role, fall back to the
    // first allowed one. Re-runs when the (simulated) role changes (`can`).
    useEffect(() => {
        if (!can(CONFIG_TAB_PERMS[tabValue])) {
            const first = CONFIG_TAB_PERMS.findIndex(p => can(p));
            if (first !== -1 && first !== tabValue) setTabValue(first);
        }
    }, [tabValue, can]); // eslint-disable-line react-hooks/exhaustive-deps
    const [pagesTab, setPagesTab] = useState(0); // 0=Front page, 1=404, 2=Cart, 3=About, 4=Disclaimer, 5=Privacy, 6=Dynamic
    const [styleTab, setStyleTab]   = useState(0); // 0=Explorer, 1=Product detail
    const [syncTab,  setSyncTab]    = useState(0); // 0=General, 1=Field mapping, 2=Images
    const [procTab,  setProcTab]    = useState(0); // 0=General, 1=Payments, 2=Shipping, 3=Customer fields

    const [featuresTab, setFeaturesTab] = useState(0); // 0=Cadeaubon 1=Shop the look 2=Banners 3=Overige

    // Bumped after every successful save. Each "…Dirty" useMemo includes it in
    // its deps so they all recompute once post-save — otherwise mutating the
    // baseline refs (originalXxxRef.current = …) alone wouldn't change a memo's
    // state dep, leaving ref-only sections (e.g. stores) stuck showing dirty and
    // the Save button stuck active until the next edit. MUST be declared before
    // the first dirty useMemo below (else a TDZ "before initialization" error).
    const [savedTick, setSavedTick] = useState(0);

    // Navigation modal state. All fields map 1:1 to a legacy SHOW_HEADER_*
    // / CENTER_* / HEADER_FILTER_* / HEADER_TYPE / HEADER_ITEM_BREAK_AMOUNT
    // / HOME_* setting.
    const DEFAULT_NAVIGATION_CONFIG = {
        headerContent: {
            contact: true, brands: true, shopTheLook: true,
            outlet: false,
            giftVoucher: false, search: true,
            account: true, cart: true,
            searchExpandedByDefault: false,
            showHomeButton: false,   // HOME_ENABLED
            homeUrl: '',             // HOME_URL — link target for the home button above
        },
        outletMode: {
            minDiscount: 10,
            hideFromExplore: false,
        },
        layout: {
            menuStyle: 'menuRight',              // HEADER_TYPE ('menuRight' | 'menuBelow')
            actionButtonsPosition: 'end',        // HEADER_ACCOUNT_POSITION ('start' | 'end') — only when menuStyle === 'menuRight'
            centerTopHeaderButtons: false,       // CENTER_HEADER ("Center all header items horizontally")
            centerTopButtonInMiddle: false,      // CENTER_LOGO   ("Center the logo horizontally")
            alignActionButtonsRight: true,       // HEADER_FILTER_SPACE_ACTIONS
        },
        navigationMenu: {
            categoriesShown: [],
            itemsPerRow: 6,
            menuHeight: 50,
            menuTextSize: 14,
        },
        behaviour: {
            keepMenuVisibleWhileScrolling: true,
        },
    };
    const [navigationConfig, setNavigationConfig] = useState(DEFAULT_NAVIGATION_CONFIG);
    const originalNavigationConfigRef = useRef(DEFAULT_NAVIGATION_CONFIG);
    const navigationDirty = useMemo(
        () => JSON.stringify(navigationConfig) !== JSON.stringify(originalNavigationConfigRef.current),
        [navigationConfig, savedTick]
    );
    const updateNavigation = (section, field, val) => {
        setNavigationConfig(prev => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: val },
        }));
    };
    // Navbar filter ORDER — `category` rows of the main-filter level
    // (NAVBAR_ITEMS_TO_LIST), each with an editable order_index. Loaded via
    // GET /admin/navbar-order?group={level}; saved as PATCH per changed row.
    const [navbarOrder, setNavbarOrder] = useState([]);
    const originalNavbarOrderRef = useRef([]);
    // Last level the list was fetched for — drives the reload-on-level-switch
    // effect without refetching on every render.
    const navbarOrderLevelRef = useRef('0');
    const navbarOrderCompare = (rows) => JSON.stringify((rows || []).map(r => ({ id: r.id, o: Number(r.order_index) || 0 })));
    const navbarOrderDirty = useMemo(
        () => navbarOrderCompare(navbarOrder) !== navbarOrderCompare(originalNavbarOrderRef.current),
        [navbarOrder, savedTick]
    );
    // FILTER_STYLE (read-only here) gates which ORDER_SIDE_FILTER_BY options
    // apply: NAME_BUT_MATCH_HEADER needs the tree style, MOST_ITEMS excludes it.
    const [navFilterStyle, setNavFilterStyle] = useState('');
    // Reload the order list live when the admin switches the main-filter
    // category in the modal (the initial load happens in the big load effect).
    const navCatLevel = (navigationConfig.navigationMenu?.categoriesShown && navigationConfig.navigationMenu.categoriesShown[0]) || '0';
    useEffect(() => {
        if (navCatLevel === navbarOrderLevelRef.current) return undefined;
        navbarOrderLevelRef.current = navCatLevel;
        if (navCatLevel === '0') {
            setNavbarOrder([]);
            originalNavbarOrderRef.current = [];
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: props.selectedWebshop?.id,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'GET',
                    path:       '/admin/navbar-order',
                    query:      { group: navCatLevel },
                });
                if (cancelled) return;
                const rows = (r?.success && Array.isArray(r.data)) ? r.data : [];
                setNavbarOrder(rows);
                originalNavbarOrderRef.current = rows;
            } catch { /* keep the previous list */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navCatLevel]);

    // Footer modal state. Wiring status per section:
    //   newsletter   ↔ SHOW_NEWSLETTER / NEWSLETTER_POPUP / NEWSLETTER_POPUP_FIRST_VISIT
    //   customLinks  ↔ external_links rows (position != 'SOCIAL'), CRUD via
    //                  /admin/external-links/{id}
    //   socialMedia  ↔ external_links rows (position == 'SOCIAL'), same CRUD
    //   width        — overlap with GlobalWidthSettingsModal (canonical there)
    //   pages        ↔ SF_LINK_* (one per page slug). The URL field is an
    //                  external-link override; an empty value means "use
    //                  the internal page". The "Edit page" button on each
    //                  row navigates to the dedicated page editor in the
    //                  Pages tab (handled via `onEditPage` in the modal
    //                  JSX further down).
    //   contact      ↔ SF_INFO_NAME / _ADDRESS / _CITY / _VAT / _PHONE /
    //                  _EMAIL / _WEBSITE (all plain string settings).
    const DEFAULT_FOOTER_CONFIG = {
        width: { maxContentWidth: 1500, backgroundFullWidth: true },
        pages: { about: '', terms: '', privacy: '', disclaimer: '' },
        customLinks: [],
        socialMedia: [],
        contact: {
            // Backed by SF_INFO_* settings. See ContactInformationCard's
            // doc-block for the field-to-key mapping.
            shopName: '', addressLine: '', city: '',
            vatNumber: '', phone: '', email: '', website: '',
        },
        newsletter: { enabled: false, showInPopup: false, openPopupAutomatically: false },
    };
    const [footerConfig, setFooterConfig] = useState(DEFAULT_FOOTER_CONFIG);
    const originalFooterConfigRef = useRef(DEFAULT_FOOTER_CONFIG);
    // Drop the client-side `id` field on customLinks/socialMedia entries
    // before comparing — those ids are re-generated each load and shouldn't
    // count as "dirty". Tracks serverId so re-saves don't double-POST.
    const normalizeFooterLink = (l) => ({
        serverId: l.serverId ?? null,
        url:      l.url || '',
        labels:   l.labels ? { nl: l.labels.nl || '', en: l.labels.en || '', fr: l.labels.fr || '' } : null,
    });
    const normalizeFooterForCompare = (cfg) => ({
        ...cfg,
        customLinks: (cfg.customLinks || []).map(normalizeFooterLink),
        socialMedia: (cfg.socialMedia || []).map(normalizeFooterLink),
    });
    const footerDirty = useMemo(
        () => JSON.stringify(normalizeFooterForCompare(footerConfig)) !==
              JSON.stringify(normalizeFooterForCompare(originalFooterConfigRef.current)),
        [footerConfig, savedTick]
    );
    const updateFooter = (section, field, val) => {
        // field === null signals a list replacement (customLinks / socialMedia).
        setFooterConfig(prev => field === null
            ? { ...prev, [section]: val }
            : { ...prev, [section]: { ...(prev[section] || {}), [field]: val } }
        );
    };

    // Header / Logo modal state. Image URLs are stored as plain strings; the
    // upload pipeline will land somewhere down the line — for now the cards
    // emit the file via onUploadImage and the URL is set by the parent once
    // we wire the actual upload.
    const DEFAULT_HEADER_LOGO_CONFIG = {
        size: { logoBarHeight: 100, maxContentWidth: 1500, backgroundFullWidth: true },
        images: { logoUrl: '', backgroundUrl: '', faviconUrl: '' },
    };
    const [headerLogoConfig, setHeaderLogoConfig] = useState(DEFAULT_HEADER_LOGO_CONFIG);
    const originalHeaderLogoConfigRef = useRef(DEFAULT_HEADER_LOGO_CONFIG);
    // Only `logoBarHeight` is wired to a unique backend key (HEADER_HEIGHT).
    // The size's maxContentWidth + backgroundFullWidth overlap with
    // globalWidths.perElement.header and are saved through that path; the
    // image URLs need a separate upload endpoint that isn't built yet.
    // Image signature for the dirty check: a pending upload (has `${field}__file`)
    // reads as 'PENDING' so picking/replacing a logo/header/favicon flags dirty.
    const headerImgSig = (img = {}) => ['logoUrl', 'backgroundUrl', 'faviconUrl']
        .map(f => (img[`${f}__file`] ? 'PENDING' : (img[f] || '')))
        .join('|');
    const headerLogoDirty = useMemo(
        () => (headerLogoConfig.size?.logoBarHeight ?? 0) !==
                 (originalHeaderLogoConfigRef.current.size?.logoBarHeight ?? 0)
              || headerImgSig(headerLogoConfig.images) !== headerImgSig(originalHeaderLogoConfigRef.current.images),
        [headerLogoConfig, savedTick]
    );
    const updateHeaderLogo = (section, field, val) => {
        setHeaderLogoConfig(prev => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: val },
        }));
    };
    const uploadHeaderImage = (field, file) => {
        // Stash a data URL for instant preview AND keep the raw File under
        // `${field}__file` so the save flow uploads it (base64 → general/{TYPE}).
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setHeaderLogoConfig(prev => ({
            ...prev,
            images: { ...(prev.images || {}), [field]: String(reader.result || ''), [`${field}__file`]: file },
        }));
        reader.readAsDataURL(file);
    };

    // Product Detail Information modal — 8 cards. Five are settings-backed:
    //   layout       ↔ PRODUCT_DETAILS_ORDER (CSV; block presence drives visibility,
    //                   CSV position drives storefront flex `order`)
    //   sectionsOpen ↔ PRODUCT_DETAILS_CONTAINER_STATES + PRODUCT_MOBILE_CONTAINER_STATES (JSON)
    //   detailsList  ↔ SHOW_FIELDS_ON_DETAIL_PAGE (CSV of 24 legacy field
    //                   keys: id, brand, name, reference, color, color_brand,
    //                   season, category_1..7, description_1..5, detail_1..5)
    //   related      ↔ PRODUCT_RELATED_ARTICLES_AMOUNT + DETAILS_COLOR_DISPLAY +
    //                   DETAILS_COLOR_DISPLAY_BORDER + DETAILS_COLOR_SHOW_OUT_OF_STOCK
    //   stock        ↔ SHOW_AVAILABLE_STOCK_STORES + HIDE_STORE_IF_NO_RECORD
    // The other three (descriptionTexts, titleAndUrl, breadcrumb) use
    // abstract modal field names that don't match the legacy CSV
    // vocabulary. Those stay frontend-only until the field-name translation
    // lands.
    //
    // layout.blocks shape mirrors what PageLayoutCard expects: a flat
    // `{ key: {used: bool} }` map. The `order` array preserves the CSV
    // sequence loaded from PRODUCT_DETAILS_ORDER so reorder-via-chip-DnD
    // can land later without losing the existing position.
    //
    // NICE-TO-HAVE: a 'washInstructions' block. The storefront's
    // detail.component.html only ships 8 ordered <section>s today; adding
    // wash instructions requires storefront work first, then re-adding the
    // chip in PageLayoutCard's DEFAULT_BLOCKS + here.
    const DEFAULT_PRODUCT_DETAIL_INFO_CONFIG = {
        layout: {
            blocks: {
                header:           { used: true },
                sizeSelector:     { used: true },
                addToCart:        { used: true },
                stockByLocation:  { used: true },
                variants:         { used: true },
                relatedProducts:  { used: true },
                productInfo:      { used: true },
                extraText:        { used: true },
            },
            order: ['header','sizeSelector','addToCart','stockByLocation','variants','relatedProducts','productInfo','extraText'],
        },
        sectionsOpen: {
            desktop: { productDetails: true,  variants: true,  relatedProducts: true,  articleInfo: true,  washInstructions: true,  stockByStore: false },
            mobile:  { productDetails: false, variants: false, relatedProducts: false, articleInfo: false, washInstructions: false, stockByStore: true  },
        },
        // detailsList groups mirror ProductDetailsListCard's GROUPS. Each
        // field is a legacy CSV key in SHOW_FIELDS_ON_DETAIL_PAGE; the
        // boolean is whether it's currently shown. Defaults match the
        // legacy install default: id,brand,name,reference,color,color_brand,
        // season + all category/description/detail slots on.
        detailsList: {
            identity: {
                id: true, brand: true, name: true, reference: true,
                color: true, color_brand: true, season: true,
            },
            categories: {
                category_1: true, category_2: true, category_3: true,
                category_4: true, category_5: true, category_6: true,
                category_7: true,
            },
            descriptions: {
                description_1: true, description_2: true, description_3: true,
                description_4: true, description_5: true,
                detail_1: true, detail_2: true, detail_3: true,
                detail_4: true, detail_5: true,
            },
        },
        descriptionTexts: {
            nl: { shortDescription: true, longDescription: true, marketingText: false, bsciDescription: false, careLabel: true, origin: false, eanCode: false },
            en: { shortDescription: true, longDescription: true, marketingText: false, bsciDescription: false, careLabel: true, origin: false, eanCode: false },
            fr: { shortDescription: true, longDescription: true, marketingText: false, bsciDescription: false, careLabel: true, origin: false, eanCode: false },
        },
        // 4 slots backed by free-form template strings (browserTab /
        // pageHeading / pageSubheading) and a CSV (urlSlug). Each slot's
        // `used` array is a sequence of token chips ({id, key}) and text
        // chips ({id, text}); see ProductTitleAndUrlCard for parse/serialize.
        titleAndUrl: {
            browserTab:     { used: [] },
            pageHeading:    { used: [] },
            pageSubheading: { used: [] },
            urlSlug:        { used: [] },
        },
        // Breadcrumb levels are picked from brand + category_1..7. `used` is
        // the ordered list of legacy field keys; the storefront splits
        // DETAIL_PAGE_NAV_SECTION on `,` to get the same array.
        breadcrumb: { used: ['category_2', 'category_3'] },
        related: { maxToShow: 8, variantsDisplay: 'colorImage', showOutOfStock: false, highlightSelected: false },
        stock: { showStockByStore: true, hideWhenSizeUnknown: false },
    };
    const [productDetailInfoConfig, setProductDetailInfoConfig] = useState(DEFAULT_PRODUCT_DETAIL_INFO_CONFIG);
    const originalProductDetailInfoConfigRef = useRef(DEFAULT_PRODUCT_DETAIL_INFO_CONFIG);
    // Title/URL chips carry generated `id`s that get regenerated on every
    // load (parseTemplateToChips assigns fresh ones). Strip them before
    // comparing so reordering the same fields with the same labels isn't
    // mistaken for a real edit. Each chip flattens to either its key
    // (token) or its text (literal).
    const normalizeChipForCompare = (c) =>
        c && c.text !== undefined ? `T:${c.text}` : `K:${c?.key || ''}`;
    const normalizePdiForCompare = (cfg) => ({
        ...cfg,
        titleAndUrl: cfg.titleAndUrl ? Object.fromEntries(
            Object.entries(cfg.titleAndUrl).map(([slot, slotData]) => [
                slot,
                { used: (slotData?.used || []).map(normalizeChipForCompare) },
            ])
        ) : {},
    });
    const productDetailInfoDirty = useMemo(
        () => JSON.stringify(normalizePdiForCompare(productDetailInfoConfig)) !==
              JSON.stringify(normalizePdiForCompare(originalProductDetailInfoConfigRef.current)),
        [productDetailInfoConfig, savedTick]
    );
    const updateProductDetailInfo = (section, field, val) => {
        setProductDetailInfoConfig(prev => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: val },
        }));
    };

    // Carousel modal — slides are rows in the `carousel` table (CRUD via
    // /admin/carousel/*), grouped by `lang`. Settings are local-only for now
    // (the ~25 HOME_GALLERY_*/GALLERY_HOVER_* settings keys land in a later
    // round). Image upload also deferred — selecting a file shows a local
    // blob preview but the file isn't persisted yet; save flow strips image
    // fields so existing server-side images aren't disturbed.
    const [carouselConfig, setCarouselConfig] = useState(DEFAULT_CAROUSEL_CONFIG);
    const originalCarouselConfigRef = useRef(DEFAULT_CAROUSEL_CONFIG);
    // Image-URL prefixes captured at load time (webshop base + img_path dirs).
    // The post-save refetches use these so they can hit the cheap dedicated
    // list endpoints instead of re-pulling the whole /admin/settings dump.
    const carouselImgBaseRef = useRef('');
    const gridImgBaseRef     = useRef('');
    const layoutImgBaseRef   = useRef('');
    // Normalising drops modal-only chip ids (slides get re-generated IDs on
    // load) so a JSON-compare is honest about what actually changed.
    // Settings tab: only the tier-1 fields that round-trip to the backend
    // are part of the compare (display/hover/behavior). TextOverlay is
    // local-only for now (see project_carousel_modal_status memory).
    const normalizeCarouselForCompare = (cfg) => ({
        slides: Object.fromEntries(
            Object.entries(cfg.slides || {}).map(([lang, list]) => [
                lang,
                // pendingDelete rows are excluded so flagging one reads as a
                // removal (dirty flips); undo restores the clean state.
                (list || []).filter(s => !s.pendingDelete).map(s => ({
                    serverId: s.serverId ?? null,
                    order:    s.order,
                    active:   !!s.active,
                    linkUrl:  s.linkUrl  || '',
                    hoverDesktop: s.hoverDesktop || '',
                    hoverMobile:  s.hoverMobile  || '',
                    // Image identity — a pending upload (has `file`) reads as a
                    // distinct value so picking/removing an image flags dirty.
                    desktopImage: s.desktopImage ? (s.desktopImage.file ? 'PENDING:' + s.desktopImage.name : (s.desktopImage.name || '')) : '',
                    altImage:     s.alternativeImage ? (s.alternativeImage.file ? 'PENDING:' + s.alternativeImage.name : (s.alternativeImage.name || '')) : '',
                })),
            ])
        ),
        // Settings are PER LANGUAGE ({ nl, en, fr }) since 2026-06-04.
        settings: Object.fromEntries(['nl', 'en', 'fr'].map((cl) => {
            const ls = cfg.settings?.[cl] || {};
            // Inline overlay-device normaliser (kept local to avoid a TDZ
            // reference to the image-grid helper declared later in the file).
            const od = (d) => ({
                background: (d && d.background) || '',
                textColor:  (d && d.textColor)  || '',
                position:   (d && d.position)   || '',
                size:       Number(d && d.size)  || 0,
                width:      Number(d && d.width) || 0,
                bold:       !!(d && d.bold),
                italic:     !!(d && d.italic),
                underline:  !!(d && d.underline),
                strike:     !!(d && d.strike),
            });
            const to = ls.textOverlay || {};
            return [cl, {
                display: {
                    fullWidth:     !!ls.display?.fullWidth,
                    maxHeight:     Number(ls.display?.maxHeight) || 0,
                    maxHeightUnit: ls.display?.maxHeightUnit || 'px',
                    spacingTop:    Number(ls.display?.spacingTop) || 0,
                    spacingBottom: Number(ls.display?.spacingBottom) || 0,
                    imageFit:      ls.display?.imageFit || 'scale',
                },
                hover: {
                    effect:         ls.hover?.effect || 'none',
                    zoomLevel:      Number(ls.hover?.zoomLevel) || 0,
                    animationSpeed: Number(ls.hover?.animationSpeed) || 0,
                },
                behavior: {
                    showArrows:       !!ls.behavior?.showArrows,
                    arrowsOnHover:    !!ls.behavior?.arrowsOnHover,
                    arrowsBackground: !!ls.behavior?.arrowsBackground,
                    autoAdvance:      !!ls.behavior?.autoAdvance,
                    timePerSlide:     Number(ls.behavior?.timePerSlide) || 0,
                    transition:       ls.behavior?.transition || 'fade',
                },
                textOverlay: {
                    desktop: od(to.desktop),
                    mobile:  od(to.mobile),
                    onlyOnHover:        !!to.onlyOnHover,
                    alwaysVisible:      !!to.alwaysVisible,
                    useDesktopOnMobile: !!to.useDesktopOnMobile,
                },
            }];
        })),
    });
    const carouselDirty = useMemo(
        () => JSON.stringify(normalizeCarouselForCompare(carouselConfig)) !==
              JSON.stringify(normalizeCarouselForCompare(originalCarouselConfigRef.current)),
        [carouselConfig, savedTick]
    );

    // Image grid modal — same pattern as Carousel. Cells = rows in the
    // `image_grid` table (CRUD via /admin/image-grid/*), grouped by `lang`.
    // Settings + image upload deferred for the same reasons (see carousel).
    const [imageGridConfig, setImageGridConfig] = useState(DEFAULT_IMAGE_GRID_CONFIG);
    const originalImageGridConfigRef = useRef(DEFAULT_IMAGE_GRID_CONFIG);
    // Toggle between the classic image-grid modal and the experimental V2
    // (live CSS-grid preview). State is local-only — both modals share the
    // same `imageGridConfig`, so flipping doesn't lose edits or trigger any
    // backend write. Default to classic so the existing UX is unchanged
    // until the user opts in.
    const [useImageGridV2, setUseImageGridV2] = useState(false);
    // Per-device overlay slice used by the dirty-compare. Each modal device
    // editor (desktop / mobile) carries the same field set; we coerce the
    // values so a missing key on one side doesn't false-positive as dirty.
    const normalizeOverlayDevice = (d) => ({
        background: (d && d.background) || '',
        textColor:  (d && d.textColor)  || '',
        position:   (d && d.position)   || '',
        size:       Number(d && d.size)  || 0,
        width:      Number(d && d.width) || 0,
        bold:       !!(d && d.bold),
        italic:     !!(d && d.italic),
        underline:  !!(d && d.underline),
        strike:     !!(d && d.strike),
    });
    const normalizeImageGridForCompare = (cfg) => ({
        cells: Object.fromEntries(
            Object.entries(cfg.cells || {}).map(([lang, list]) => [
                lang,
                // pendingDelete rows are excluded so flagging one reads as a
                // removal (dirty flips); undo restores the clean state.
                (list || []).filter(c => !c.pendingDelete).map(c => ({
                    serverId: c.serverId ?? null,
                    order:    c.order,
                    active:   !!c.active,
                    width:    Math.max(1, Number(c.width)  || 1),
                    height:   Math.max(1, Number(c.height) || 1),
                    linkUrl:  c.linkUrl  || '',
                    hoverDesktop: c.hoverDesktop || '',
                    hoverMobile:  c.hoverMobile  || '',
                    // Image identity — pending upload (has `file`) reads as a
                    // distinct value so picking/removing an image flags dirty.
                    desktopImage: c.desktopImage ? (c.desktopImage.file ? 'PENDING:' + c.desktopImage.name : (c.desktopImage.name || '')) : '',
                    altImage:     c.alternativeImage ? (c.alternativeImage.file ? 'PENDING:' + c.alternativeImage.name : (c.alternativeImage.name || '')) : '',
                })),
            ])
        ),
        // Settings are PER LANGUAGE ({ nl, en, fr }) since 2026-06-04.
        settings: Object.fromEntries(['nl', 'en', 'fr'].map((gl) => {
            const ls = cfg.settings?.[gl] || {};
            return [gl, {
                layout: {
                    columns:          Number(ls.layout?.columns) || 0,
                    gap:              Number(ls.layout?.gap) || 0,
                    keepGridOnMobile: !!ls.layout?.keepGridOnMobile,
                    margin:           Array.isArray(ls.layout?.margin)
                        ? [Number(ls.layout.margin[0]) || 0, Number(ls.layout.margin[1]) || 0]
                        : [0, 0],
                },
                hover: {
                    effect:         ls.hover?.effect || 'none',
                    zoomLevel:      Number(ls.hover?.zoomLevel) || 0,
                    animationSpeed: Number(ls.hover?.animationSpeed) || 0,
                },
                // Stable shape for the textOverlay block — mirrors the dual
                // desktop+mobile structure that overlay.desktop/.mobile use in
                // IMAGE_GRID_CONFIG_{lang} JSON (mig166).
                textOverlay: {
                    desktop: normalizeOverlayDevice(ls.textOverlay?.desktop),
                    mobile:  normalizeOverlayDevice(ls.textOverlay?.mobile),
                    onlyOnHover:        !!ls.textOverlay?.onlyOnHover,
                    alwaysVisible:      !!ls.textOverlay?.alwaysVisible,
                    useDesktopOnMobile: !!ls.textOverlay?.useDesktopOnMobile,
                },
            }];
        })),
    });
    const imageGridDirty = useMemo(
        () => JSON.stringify(normalizeImageGridForCompare(imageGridConfig)) !==
              JSON.stringify(normalizeImageGridForCompare(originalImageGridConfigRef.current)),
        [imageGridConfig, savedTick]
    );

    // Product card config — held here so the Verkenner preview (product
    // grid) can read the live values (line composition, divider, alignment).
    // Backed by legacy admin settings (PRODUCT_DISPLAY_INFO, PRODUCT_*, etc.);
    // see the load effect below for the per-field mapping.
    const [productCardConfig, setProductCardConfig] = useState(DEFAULT_PRODUCT_CARD_CONFIG);
    const originalProductCardConfigRef = useRef(DEFAULT_PRODUCT_CARD_CONFIG);
    // PRODUCT_HOVER_MAX_SIZES is "Y,X" CSV (lengtematen, breedtematen).
    // The modal's "Maximum sizes shown" control edits [0] (Y / lengtematen
    // / number of sizes in the hover badge). We stash [1] (X / breedtematen
    // / widths per size — the only axis the legacy admin exposed) so save
    // can round-trip it untouched.
    const productHoverMaxWidthsRef = useRef(5);
    // Normalising drops chip ids (they're regenerated on load and don't carry
    // user intent) so a JSON compare can be honest about what actually changed.
    // Each chip flattens to either its key (token) or its text (literal).
    const normalizeChip = (c) => (c && c.text !== undefined ? `T:${c.text}` : `K:${c?.key || ''}`);
    const normalizeCardForCompare = (cfg) => ({
        productInfo: {
            line1: (cfg.productInfo?.line1 || []).map(normalizeChip),
            line2: (cfg.productInfo?.line2 || []).map(normalizeChip),
            line3: (cfg.productInfo?.line3 || []).map(normalizeChip),
            showDivider: !!cfg.productInfo?.showDivider,
            align:        cfg.productInfo?.align || 'left',
        },
        image:       cfg.image       || {},
        sizePreview: cfg.sizePreview || {},
    });
    const productCardDirty = useMemo(
        () => JSON.stringify(normalizeCardForCompare(productCardConfig)) !==
              JSON.stringify(normalizeCardForCompare(originalProductCardConfigRef.current)),
        [productCardConfig, savedTick]
    );

    // Groups & sorting config — opened from the cog on the GROEPEN sidebar
    // in the Verkenner mockup. Backed by FILTER_*, SIDE_FILTER_*, and
    // EXPLORE_SORT_* legacy settings; see the load effect for the per-key
    // mapping.
    const [groupsFilterConfig, setGroupsFilterConfig] = useState(DEFAULT_GROUPS_FILTER_CONFIG);
    const originalGroupsFilterConfigRef = useRef(DEFAULT_GROUPS_FILTER_CONFIG);
    // The sort-rule entries carry React-only `id`s — strip them before
    // comparing so reordering the same fields doesn't trigger dirty if the
    // backend mapping is identical.
    const normalizeGroupsForCompare = (cfg) => ({
        ...cfg,
        defaultSortRule: (cfg.defaultSortRule || []).map(r => `${r.field}:${r.direction}`),
    });
    const groupsFilterDirty = useMemo(
        () => JSON.stringify(normalizeGroupsForCompare(groupsFilterConfig)) !==
              JSON.stringify(normalizeGroupsForCompare(originalGroupsFilterConfigRef.current)),
        [groupsFilterConfig, savedTick]
    );

    // Style settings ref + dirty are computed after `styleSettings` itself is
    // declared (further down). The table mapping lives at module scope so it
    // can be referenced from both the load effect and the save handler.
    const originalStyleSettingsRef = useRef(null);

    // Product grid config — opened from the cog between cards 2 and 3 in
    // the Verkenner preview. Drives column count, pagination position and
    // variant grouping behaviour. Backed by mini-api settings GRID_* — the
    // load effect populates both the working state and the ref below; save
    // diffs the two so an unchanged save is a true no-op.
    const [productGridConfig, setProductGridConfig] = useState(DEFAULT_PRODUCT_GRID_CONFIG);
    const originalProductGridConfigRef = useRef(DEFAULT_PRODUCT_GRID_CONFIG);
    const productGridDirty = useMemo(
        () => JSON.stringify(productGridConfig) !== JSON.stringify(originalProductGridConfigRef.current),
        [productGridConfig, savedTick]
    );

    // Image gallery modal — flat object, single card. Default position is
    // "left" so the preview matches the schema example (3 thumbs vertical
    // column on the left of a large main image). Wired to:
    //   thumbnailPosition       ↔ SHOW_GALLERY_THUMBS + GALLERY_THUMBS_POSITION
    //                             (hidden = thumbs off; otherwise position string)
    //   useGrid                 ↔ DETAIL_USE_NEW_GALLERY
    //   maxThumbnails           ↔ NEW_GALLERY_THUMBS_COUNT
    //   showArrows + colors     ↔ NEW_GALLERY_CONFIG.main_arrows  (JSON)
    //   showFullscreen + colors ↔ NEW_GALLERY_CONFIG.thumb_arrows (JSON)
    const DEFAULT_IMAGE_GALLERY_CONFIG = {
        thumbnailPosition: 'left',
        useGrid: true,
        maxThumbnails: 3,
        showArrows: true,
        arrowColor: '#000000',
        arrowBgColor: '#ffffff',
        showFullscreen: true,
        fullscreenArrowColor: '#000000',
        fullscreenArrowBgColor: '#ffffff',
    };
    const [imageGalleryConfig, setImageGalleryConfig] = useState(DEFAULT_IMAGE_GALLERY_CONFIG);
    const originalImageGalleryConfigRef = useRef(DEFAULT_IMAGE_GALLERY_CONFIG);
    const imageGalleryDirty = useMemo(
        () => JSON.stringify(imageGalleryConfig) !== JSON.stringify(originalImageGalleryConfigRef.current),
        [imageGalleryConfig, savedTick]
    );
    const updateImageGallery = (field, val) => {
        setImageGalleryConfig(prev => ({ ...prev, [field]: val }));
    };

    // Extra Footer modal state. Each column independently picks its editor
    // mode (wysiwyg default, switchable to unlayer). Both content shapes
    // are kept side-by-side per column so a mode switch doesn't lose work.
    const blankColumn = () => ({
        editor: 'wysiwyg',
        html:   { nl: '', en: '', fr: '' },
        design: { nl: null, en: null, fr: null },
    });
    // Extra footer config:
    //   showPaymentMethods    ↔ FOOTER_EXTRA_PAYMENT_METHODS (bool setting)
    //   columns.{1,2,3}       ↔ contents row TEXT_FOOTER_COL_{1,2,3}, via
    //                            the /admin/contents/{attribute} PATCH
    //
    // Each column carries both a wysiwyg HTML body and an unlayer design
    // side by side so the user can switch editors without losing work.
    // Loading from the backend only sees the HTML side (body_nl/en/fr in
    // the contents row); the unlayer design lives on disk and isn't read
    // back yet — switching to unlayer after a reload starts blank.
    const DEFAULT_EXTRA_FOOTER_CONFIG = {
        showPaymentMethods: true,
        columns: { 1: blankColumn(), 2: blankColumn(), 3: blankColumn() },
    };
    const [extraFooterConfig, setExtraFooterConfig] = useState(DEFAULT_EXTRA_FOOTER_CONFIG);
    const originalExtraFooterConfigRef = useRef(DEFAULT_EXTRA_FOOTER_CONFIG);
    // Dirty compare: include the editor mode (switching unlayer → wysiwyg
    // means the next save will delete the .json sidecar files server-side)
    // AND the design content (unlayer edits typically change the design
    // before they reach the rendered HTML, so a presence-only flag would
    // miss them). Stringifying multi-KB design blobs per render is fine
    // — the modal isn't a hot path.
    const normalizeExtraFooterColumn = (col) => ({
        editor: col?.editor || 'wysiwyg',
        html:   {
            nl: col?.html?.nl || '',
            en: col?.html?.en || '',
            fr: col?.html?.fr || '',
        },
        design: {
            nl: col?.design?.nl ?? null,
            en: col?.design?.en ?? null,
            fr: col?.design?.fr ?? null,
        },
    });
    const normalizeExtraFooterForCompare = (cfg) => ({
        showPaymentMethods: !!cfg.showPaymentMethods,
        columns: {
            1: normalizeExtraFooterColumn(cfg.columns?.[1]),
            2: normalizeExtraFooterColumn(cfg.columns?.[2]),
            3: normalizeExtraFooterColumn(cfg.columns?.[3]),
        },
    });
    const extraFooterDirty = useMemo(
        () => JSON.stringify(normalizeExtraFooterForCompare(extraFooterConfig)) !==
              JSON.stringify(normalizeExtraFooterForCompare(originalExtraFooterConfigRef.current)),
        [extraFooterConfig, savedTick]
    );

    // Static text pages (Pages tab → About / Disclaimer / Privacy). Each maps
    // to a `contents` row (TEXT_ABOUT / TEXT_DISCLAIMER / TEXT_PRIVACY),
    // exposed in the settings dump as s.contents_{about,disclaimer,privacy}.
    // Same content-PATCH save path as the Extra footer columns.
    const STATIC_PAGE_DEFAULT = {
        iframe: false, editor: 'wysiwyg',
        html:    { nl: '', en: '', fr: '' },
        designs: { nl: null, en: null, fr: null },
        isCustomHtml: false,
    };
    const [staticPages, setStaticPages] = useState({
        about: STATIC_PAGE_DEFAULT, disclaimer: STATIC_PAGE_DEFAULT, privacy: STATIC_PAGE_DEFAULT,
        // page404 = the 404 page's "text" block (contents_404 / TEXT_404),
        // edited via the block's gear dialog on the Pages → 404 sub-tab.
        page404: STATIC_PAGE_DEFAULT,
        // intro = the landing page's "Welcome" block (contents_intro / TEXT_INTRO),
        // edited via that block's gear dialog on the Pages → Front page sub-tab.
        intro: STATIC_PAGE_DEFAULT,
    });
    const originalStaticPagesRef = useRef(staticPages);
    // Apply a StaticPageTab updater (function or object) against the latest
    // page config — lets the controlled child push functional updates without
    // stale-closure risk (the unlayer auto-sync relies on this).
    const setStaticPage = (key) => (updater) => setStaticPages(prev => ({
        ...prev,
        [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
    }));
    const staticPagesDirty = useMemo(
        () => JSON.stringify(staticPages) !== JSON.stringify(originalStaticPagesRef.current),
        [staticPages, savedTick]
    );

    // Physical retail stores (Pages tab → About). Loaded from the mini-api
    // `GET /stores` and saved per-store via `PATCH /stores/{id}`. Opening hours
    // round-trip through the legacy per-day string columns (hours_mon..sun) via
    // storeRowToForm / storeFormToColumns.
    const [stores, setStores] = useState([]);
    const originalStoresRef = useRef([]);
    const storesDirty = useMemo(
        () => JSON.stringify(stores) !== JSON.stringify(originalStoresRef.current),
        [stores, savedTick]
    );
    // Break separator for multi-range opening hours (global for the About page,
    // applied to every store on save). Detected from the loaded hours strings so
    // it round-trips without a backend key. Tracked separately from `stores`
    // because changing only the separator doesn't touch the structured hours
    // state — but it does change the strings we write, so it needs its own dirty.
    const [hoursSeparator, setHoursSeparator] = useState(DEFAULT_HOURS_SEPARATOR);
    const originalHoursSeparatorRef = useRef(DEFAULT_HOURS_SEPARATOR);
    const separatorDirty = hoursSeparator !== originalHoursSeparatorRef.current;
    // Google Maps key — the storefront and the legacy super-user admin both use
    // the `GOOGLE_MAPS_TOKEN` setting (NOT `GMAPS_API_KEY`, which the new
    // Connections tab mistakenly reads — hence it shows empty there). Read-only
    // here; the About page uses it to geocode store addresses.
    const [gmapsApiKey, setGmapsApiKey] = useState('');

    // About-page store-locations map settings. Mode/position/zoom map to the
    // ABOUT_US_MAP_* settings; the style maps to the mapStyle.json file (read
    // from the settings dump as s.mapStyle, saved via POST /admin/maps-style).
    const [mapSettings, setMapSettings] = useState(MAP_SETTINGS_DEFAULT);
    const originalMapSettingsRef = useRef(MAP_SETTINGS_DEFAULT);
    const mapSettingsDirty = useMemo(
        () => JSON.stringify(mapSettings) !== JSON.stringify(originalMapSettingsRef.current),
        [mapSettings, savedTick]
    );
    // Global width settings — centralised max-content-width values shared
    // across the storefront. Path-style updates: 'applyToWholeSite',
    // 'productPagesWidth', 'perElement.<key>.width', 'perElement.<key>.bgFullWidth'.
    const DEFAULT_GLOBAL_WIDTHS = {
        applyToWholeSite: false,
        productPagesWidth: 1500,
        // BUTTON_BORDER_RADIUS (px) — new-admin-only key (created on first
        // save via the mini-api creatable whitelist; no legacy migration).
        buttonBorderRadius: 4,
        perElement: {
            languageBar:     { width: 2500, bgFullWidth: true },
            header:          { width: 2500, bgFullWidth: true },
            infoBannerBelow: { width: 1200, bgFullWidth: true },
            infoBannerTop:   { width: 0,    bgFullWidth: true },
            brands:          { width: 1500 },
            explore:         { width: 1500 },
            recentlyViewed:  { width: 1500, bgFullWidth: true },
            footer:          { width: 1500, bgFullWidth: true },
        },
    };
    const [globalWidths, setGlobalWidths] = useState(DEFAULT_GLOBAL_WIDTHS);
    const originalGlobalWidthsRef = useRef(DEFAULT_GLOBAL_WIDTHS);
    const globalWidthsDirty = useMemo(
        () => JSON.stringify(globalWidths) !== JSON.stringify(originalGlobalWidthsRef.current),
        [globalWidths, savedTick]
    );
    const updateGlobalWidths = (field, val) => {
        if (field === 'applyToWholeSite' || field === 'productPagesWidth' || field === 'buttonBorderRadius') {
            setGlobalWidths(prev => ({ ...prev, [field]: val }));
            return;
        }
        const m = /^perElement\.([a-zA-Z]+)\.(width|bgFullWidth)$/.exec(field);
        if (m) {
            const key = m[1];
            const sub = m[2];
            setGlobalWidths(prev => ({
                ...prev,
                perElement: {
                    ...prev.perElement,
                    [key]: { ...(prev.perElement[key] || {}), [sub]: val },
                },
            }));
        }
    };

    const updateExtraFooter = (field, val) => {
        // Supported path shapes:
        //   'showPaymentMethods'
        //   'columnN.editor'
        //   'columnN.html.<lang>'
        //   'columnN.design.<lang>'
        if (field === 'showPaymentMethods') {
            setExtraFooterConfig(prev => ({ ...prev, showPaymentMethods: val }));
            return;
        }
        const editorMatch = /^column([123])\.editor$/.exec(field);
        if (editorMatch) {
            const col = editorMatch[1];
            setExtraFooterConfig(prev => ({
                ...prev,
                columns: { ...prev.columns, [col]: { ...(prev.columns[col] || blankColumn()), editor: val } },
            }));
            return;
        }
        const contentMatch = /^column([123])\.(html|design)\.(nl|en|fr)$/.exec(field);
        if (contentMatch) {
            const col   = contentMatch[1];
            const kind  = contentMatch[2];   // 'html' | 'design'
            const lang  = contentMatch[3];
            setExtraFooterConfig(prev => {
                const colState = prev.columns[col] || blankColumn();
                return {
                    ...prev,
                    columns: {
                        ...prev.columns,
                        [col]: {
                            ...colState,
                            [kind]: { ...(colState[kind] || {}), [lang]: val },
                        },
                    },
                };
            });
        }
    };
    const [available, setAvailable] = useState(DEFAULT_AVAILABLE);
    const [active, setActive] = useState(DEFAULT_ACTIVE);
    // 404-page active list + its OWN available catalog. The 404 page and the
    // Front page (landing) are independent: activating a block on one must NOT
    // remove it from the other's Available column, so each tab gets its own
    // available list (a block can sit in both). Per-entry .config stores per-tab
    // settings.
    const [active404, setActive404] = useState(DEFAULT_ACTIVE_404);
    const [available404, setAvailable404] = useState(DEFAULT_AVAILABLE);
    const [cards, setCards] = useState(DEFAULT_CARDS);
    const originalCardsRef = useRef(DEFAULT_CARDS);
    const cardsDirty = useMemo(
        () => JSON.stringify(cards) !== JSON.stringify(originalCardsRef.current),
        [cards, savedTick]
    );
    // Brands page (Merken). Flat keys BRANDS_ONLY_ONLINE / _CATEGORY_FILTER /
    // _PAGE_STYLE + the BRANDS_PAGE_CONFIG JSON blob (short keys lm/am/cols/max/
    // sul/d/m). originalBrandsRawJsonRef keeps the raw loaded blob so save can
    // merge over it and preserve keys the tab doesn't surface (idx, divider…).
    const [brandsConfig, setBrandsConfig] = useState(BRANDS_CONFIG_DEFAULT);
    const originalBrandsConfigRef = useRef(BRANDS_CONFIG_DEFAULT);
    const originalBrandsRawJsonRef = useRef({});
    const brandsConfigDirty = useMemo(
        () => JSON.stringify(brandsConfig) !== JSON.stringify(originalBrandsConfigRef.current),
        [brandsConfig, savedTick]
    );
    // Dynamic pages (Pages → Dynamic page). Loaded from GET /dynamic-pages,
    // saved per page via POST/PATCH/DELETE /dynamic-pages. Designs are lazy-
    // loaded into the editor via the proxy; only edited langs sit in `content`.
    const [dynamicPages, setDynamicPages] = useState([]);
    const originalDynamicPagesRef = useRef([]);
    const dynamicPagesDirty = useMemo(
        () => JSON.stringify(dynamicPages) !== JSON.stringify(originalDynamicPagesRef.current),
        [dynamicPages, savedTick]
    );
    // Information banners (Features → Banners). Loaded from the settings dump
    // (s.informationBanners includes ALL non-deleted rows, unlike the public
    // GET /information-banner which filters by active+date). Saved per-banner
    // via POST/PATCH/soft-DELETE.
    const [banners, setBanners] = useState([]);
    const originalBannersRef = useRef([]);
    // `editor` is a UI-only mode (wysiwyg/html) and isn't persisted, so it
    // shouldn't flag the screen dirty — drop it from the comparison.
    const bannerCompareJson = (arr) => JSON.stringify((arr || []).map(({ editor, ...rest }) => rest));
    const bannersDirty = useMemo(
        () => bannerCompareJson(banners) !== bannerCompareJson(originalBannersRef.current),
        [banners, savedTick]
    );

    // Shop the look (Features → Shop the look). Intro text ↔ TEXT_LOOKS content
    // row; the rest ↔ LOOKS_* / LOOK_* / SHOW_FIELDS_ON_LOOK_* settings.
    // `introEditor` is UI-only and `showInIframe`/`ctaText` have no confirmed
    // backend yet — all excluded from the dirty compare / save.
    const [looksConfig, setLooksConfig] = useState(STL_CONFIG_DEFAULT);
    const originalLooksConfigRef = useRef(STL_CONFIG_DEFAULT);
    // The look CTA button label is the shared i18n string GLOBAL.LOOKS_DETAILS;
    // we keep its i18n-table row id so the save can PATCH /localization/{id}.
    const looksCtaIdRef = useRef(null);
    const looksCompareJson = (c) => {
        if (!c) return '';
        const { introEditor, ...rest } = c;
        return JSON.stringify(rest);
    };
    const looksConfigDirty = useMemo(
        () => looksCompareJson(looksConfig) !== looksCompareJson(originalLooksConfigRef.current),
        [looksConfig, savedTick]
    );

    // Overige (Features → Overige). Only the parts with a confirmed backend are
    // persisted: custom code (head/body → _scriptsHeader/_scriptsBody) and the
    // gtm/metaPixel tracking-tool ids (→ GTM_ID / PIXEL_ID). Cookie consent,
    // the ga4/tiktok/hotjar tools and the per-tool "active" flag stay in the UI
    // but aren't saved yet (pending backend), so they're excluded from dirty.
    const [overigeConfig, setOverigeConfig] = useState(OVERIGE_CONFIG_DEFAULT);
    const originalOverigeConfigRef = useRef(OVERIGE_CONFIG_DEFAULT);
    const toolId = (cfg, key) => {
        const t = (cfg.trackingTools || []).find(x => x.key === key);
        return t ? (t.id || '') : '';
    };
    const overigePersistJson = (c) => JSON.stringify({
        head: c.customCode?.head || '',
        body: c.customCode?.body || '',
        gtm:  toolId(c, 'gtm'),
        pixel: toolId(c, 'metaPixel'),
        cookieStyle:    c.cookies?.bannerStyle || '',
        cookiePos:      c.cookies?.position || '',
        cookieAnalytics: !!c.cookies?.categories?.analytics,
        cookieMarketing: !!c.cookies?.categories?.marketing,
    });
    const overigeConfigDirty = useMemo(
        () => overigePersistJson(overigeConfig) !== overigePersistJson(originalOverigeConfigRef.current),
        [overigeConfig, savedTick]
    );

    // Cadeaubon (Features → Cadeaubon). Settings map to the CHEQUES_* settings
    // rows; the Translations table maps to the cheques_placeholders CRUD list.
    // Voucher layouts stay UI-only (need the multipart image upload + designer).
    const [cadeaubonConfig, setCadeaubonConfig] = useState(CADEAUBON_CONFIG_DEFAULT);
    const originalCadeaubonConfigRef = useRef(CADEAUBON_CONFIG_DEFAULT);
    const cadeaubonConfigDirty = useMemo(
        () => JSON.stringify(cadeaubonToSettings(cadeaubonConfig)) !== JSON.stringify(cadeaubonToSettings(originalCadeaubonConfigRef.current)),
        [cadeaubonConfig, savedTick]
    );
    const [cadeaubonPlaceholders, setCadeaubonPlaceholders] = useState([]);
    const originalPlaceholdersRef = useRef([]);
    const placeholderCompareJson = (arr) => JSON.stringify((arr || []).map(p => ({
        id: p.id ?? null, description: p.description || '', layout: p.layout || '', nl: p.nl || '', en: p.en || '', fr: p.fr || '',
    })));
    const cadeaubonPlaceholdersDirty = useMemo(
        () => placeholderCompareJson(cadeaubonPlaceholders) !== placeholderCompareJson(originalPlaceholdersRef.current),
        [cadeaubonPlaceholders, savedTick]
    );
    // Cadeaubon voucher layouts (cheques_layouts CRUD + image uploads + Unlayer
    // design). Loaded via GET /cheques-layouts (not in the settings dump).
    const [cadeaubonLayouts, setCadeaubonLayouts] = useState([]);
    const originalCadeaubonLayoutsRef = useRef([]);
    const cadeaubonLayoutsDirty = useMemo(
        () => layoutCompareJson(cadeaubonLayouts) !== layoutCompareJson(originalCadeaubonLayoutsRef.current),
        [cadeaubonLayouts, savedTick]
    );

    // Verwerking (order processing). Only the confirmed-backend settings are
    // persisted; the rest stay in the UI as pending (see settingsToProcessing).
    const [processingConfig, setProcessingConfig] = useState(PROCESSING_CONFIG_DEFAULT);
    const originalProcessingConfigRef = useRef(PROCESSING_CONFIG_DEFAULT);
    // Raw CHEQUES_* discount JSON objects as loaded — kept so a save can rebuild
    // them over the originals without dropping codes the UI doesn't list.
    const originalProcChequeRef = useRef({ allowed: {}, asPayment: {}, addToReceipt: {} });
    // Raw PROCESS_TICKET_MODULE as loaded (LOOKBOOK|RESERVATION|RECEIPT). The
    // "no checkout" payment-location tile maps to LOOKBOOK; we keep the original
    // here so switching back to a checkout location preserves RESERVATION/RECEIPT.
    const originalTicketModuleRef = useRef('');
    const processingConfigDirty = useMemo(
        () => JSON.stringify(processingToSettings(processingConfig)) !== JSON.stringify(processingToSettings(originalProcessingConfigRef.current)),
        [processingConfig, savedTick]
    );

    // Custom customer fields (Verwerking → Customer fields) — CRUD list backed by
    // `customers_categories_groups`, separate from the settings config.
    const [customerCategoryGroups, setCustomerCategoryGroups] = useState([]);
    const originalCustomerGroupsRef = useRef([]);
    const customerGroupCompareJson = (arr) => JSON.stringify((arr || []).map(g => ({
        id: g.id ?? null,
        sft_id: g.codeGroup || '',
        nl: g.labels?.nl || '', en: g.labels?.en || '', fr: g.labels?.fr || '',
        reg: !!g.showOn?.registration, acc: !!g.showOn?.account, news: !!g.showOn?.newsletter,
    })));
    const customerGroupsDirty = useMemo(
        () => customerGroupCompareJson(customerCategoryGroups) !== customerGroupCompareJson(originalCustomerGroupsRef.current),
        [customerCategoryGroups, savedTick]
    );
    // POS + store/warehouse + salutation + country lists for the Verwerking dropdowns.
    const [posOptions, setPosOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [salutationOptions, setSalutationOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    // Which online payment providers have credentials set up (disables the rest).
    const [paymentProviderAvailability, setPaymentProviderAvailability] = useState({});
    // POS payment-type labels (kas_betaal1..9), kept raw with their kasvar.code
    // so the dropdown can be filtered to the receipt POS id (per-store labels).
    const [paymentTypeRows, setPaymentTypeRows] = useState([]); // [{n, value, code}]
    const receiptProcessPosId = processingConfig.receiptPos?.process;
    const paymentTypeOptions = useMemo(
        () => computePaymentTypeOptions(paymentTypeRows, receiptProcessPosId),
        [paymentTypeRows, receiptProcessPosId]
    );

    // Synchronisatie (product sync) — all controls map onto existing settings
    // keys (FETCH_*, MAP_*, PHOTO_*). No CRUD, no deploy. The store universe
    // (warehouseOptions) lets FETCH_FOR_STORES round-trip '00' ↔ all-selected.
    const [syncConfig, setSyncConfig] = useState(SYNC_CONFIG_DEFAULT);
    const originalSyncConfigRef = useRef(SYNC_CONFIG_DEFAULT);
    const allStoreCodesForSync = useMemo(() => warehouseOptions.map(o => o.value), [warehouseOptions]);
    const syncConfigDirty = useMemo(
        () => JSON.stringify(syncToSettings(syncConfig, allStoreCodesForSync)) !== JSON.stringify(syncToSettings(originalSyncConfigRef.current, allStoreCodesForSync)),
        [syncConfig, allStoreCodesForSync, savedTick]
    );
    // Validate a guest customer id against the POS customer DB (auth backend,
    // sftData type "customer" → exact lookup on klant.klnr).
    const validateCustomerId = (id) => {
        const trimmed = String(id || '').trim();
        if (!trimmed) return Promise.resolve({ status: 'idle' });
        return SftGlobalData.post('sftData', { key: props.api, type: 'customer', code: trimmed })
            .then((r) => {
                const rows = (r?.data?.data && Array.isArray(r.data.data)) ? r.data.data : [];
                const row = rows.find(x => String(x.klnr).trim() === trimmed) || rows[0] || null;
                if (!row) return { status: 'invalid' };
                const person = [row.firstname, row.naam].map(s => String(s || '').trim()).filter(Boolean).join(' ');
                const name = [String(row.firmanaam || '').trim(), person].filter(Boolean).join(' — ');
                return { status: 'valid', name: name || trimmed };
            })
            .catch(() => ({ status: 'error' }));
    };

    // Bulk-apply a country preset (allOff/allOn/default/belgium/benelux/europe).
    // This is a direct, destructive action on the shared countries table (resets
    // visible/prefered on ALL countries), NOT a saved setting — so it runs
    // immediately, not via Save. Reloads countryOptions afterwards. Returns bool.
    const applyCountryPreset = async (template) => {
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return false;
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key: props.api, webshop_id: webshopId, sftUserId: props.login?.sft_user_id, user: props.login?.login,
                method: 'POST', path: '/countries/default-values', body: { template },
            });
            if (!r || !r.success || (r.data && r.data.status === false)) {
                if (props.notify) props.notify('Could not apply country preset', 4000, 'top-right', props.theme, 1);
                return false;
            }
            // Refresh the country dropdown from the now-mutated table.
            try {
                const cr = await SftGlobalData.post('webshop&action=proxy', {
                    key: props.api, webshop_id: webshopId, sftUserId: props.login?.sft_user_id, user: props.login?.login,
                    method: 'GET', path: '/countries',
                });
                if (cr?.success && Array.isArray(cr.data)) {
                    const adminLang = String(i18n.language || 'nl').slice(0, 2).toLowerCase();
                    const locKey = ['nl', 'en', 'fr'].includes(adminLang) ? `loc_${adminLang}` : 'loc_nl';
                    setCountryOptions(cr.data
                        .filter(x => x && x.iso != null && String(x.iso) !== '')
                        .map(x => ({ value: String(x.iso), label: String(x[locKey] || x.loc_nl || x.name || x.iso) })));
                }
            } catch { /* dropdown refresh is best-effort */ }
            if (props.notify) props.notify('Country preset applied', 3000, 'top-right', props.theme, 2);
            return true;
        } catch {
            if (props.notify) props.notify('Could not apply country preset', 4000, 'top-right', props.theme, 1);
            return false;
        }
    };

    // Lazy-load a saved unlayer design for a dynamic page+language. The static
    // sidecars aren't reachable through the proxy (the mini-api router doesn't
    // serve /statics), so we use the dedicated `/dynamic-pages/{id}/design`
    // route which returns { design }. Returns the design object or null.
    const fetchDynDesign = async (serverId, lang) => {
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop?.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       `/dynamic-pages/${serverId}/design?lang=${lang}`,
            });
            if (r && r.success && r.data) {
                const d = r.data.design;
                if (d && typeof d === 'object') return d;
                if (typeof d === 'string') { try { return JSON.parse(d); } catch { return null; } }
            }
        } catch { /* ignore — editor falls back to blank */ }
        return null;
    };
    const [activeId, setActiveId] = useState(null);
    const [configBlock, setConfigBlock] = useState(null);
    const [containerModalOpen, setContainerModalOpen] = useState(false);
    const [containerData, setContainerData] = useState(DEFAULT_CONTAINER);
    const [editingContainerId, setEditingContainerId] = useState(null);
    const [editorLang, setEditorLang] = useState(0); // 0=NL, 1=EN, 2=FR
    const [editorFullscreen, setEditorFullscreen] = useState(false);
    const [editorInlineSlot, setEditorInlineSlot] = useState(null);
    const [slotRect, setSlotRect] = useState(null);
    const editorRefNL = useRef(null);
    const editorRefEN = useRef(null);
    const editorRefFR = useRef(null);
    const dialogContentRef = useRef(null);
    const savedScrollRef = useRef(0);

    // Front-page composition data fetched from the webshop's mini-api. We
    // load the full /admin/settings dump on webshop change and use:
    //   - `productContainers`     → mapped to `pd_cnt-<id>` blocks
    //   - `HOME_SECTIONS_ORDER`   → comma-separated list of active block ids
    // to populate the Active/Available columns. Saving back lands later.
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    // Scroll-aware save: show a floating save button when the header save button
    // scrolls out of view on long pages, and hide it again once it's back in
    // view. This null-root IntersectionObserver is the version confirmed working
    // in Chrome. (Firefox handling is being worked on separately.) A callback
    // ref keeps the observer attached across the screen's conditional renders.
    const [headerSaveVisible, setHeaderSaveVisible] = useState(true);
    const saveObserverRef = useRef(null);
    const headerSaveRef = useCallback((node) => {
        if (saveObserverRef.current) { saveObserverRef.current.disconnect(); saveObserverRef.current = null; }
        if (!node) { setHeaderSaveVisible(true); return; }
        if (typeof IntersectionObserver === 'undefined') return;
        saveObserverRef.current = new IntersectionObserver(
            ([entry]) => setHeaderSaveVisible(entry.isIntersecting),
            { threshold: 0 },
        );
        saveObserverRef.current.observe(node);
    }, []);
    const [configError, setConfigError] = useState(null);
    const [productContainers, setProductContainers] = useState([]);
    // Stores the saved HOME_SECTIONS_ORDER value as it sits on the server.
    // We diff this against the live `active` list to know when the order is
    // dirty and what to send in the PATCH.
    const [homeSectionsOrder, setHomeSectionsOrder] = useState('');
    // Same for the 404 page — its own active order (PAGE_404_SECTIONS_ORDER,
    // default 'text'), diffed against the live `active404` list.
    const [page404SectionsOrder, setPage404SectionsOrder] = useState('');
    // Per-shop labels keyed by legacy backend field name. Source of truth
    // is the customer's `kasvars` table reached via the central Auth
    // backend's `sftData` action. Known mappings:
    //   category_1..7 ↔ Code1Oms..Code7Oms
    //   detail_1..5   ↔ Levart1Oms..Levart5Oms
    // Populated by the parallel fetch in the load effect; cards fall back
    // to their cfg_pdi_details_* i18n keys when a slot has no value here.
    const [shopFieldLabels, setShopFieldLabels] = useState({});

    const toggleEditorFullscreen = () => {
        if (!editorFullscreen && dialogContentRef.current) {
            savedScrollRef.current = dialogContentRef.current.scrollTop;
        }
        setEditorFullscreen((v) => !v);
    };

    // Restore scroll position after exiting fullscreen
    useEffect(() => {
        if (!editorFullscreen && dialogContentRef.current && savedScrollRef.current) {
            // Wait for layout to settle after portal content returns
            requestAnimationFrame(() => {
                if (dialogContentRef.current) {
                    dialogContentRef.current.scrollTop = savedScrollRef.current;
                }
            });
        }
    }, [editorFullscreen]);

    // Attempt to call unlayer.init() as early as possible so features config
    // is registered before createEditor runs inside the wrapper.
    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 200; // ~10s
        const tryInit = () => {
            if (cancelled) return;
            if (window.unlayer && typeof window.unlayer.init === 'function') {
                try {
                    window.unlayer.init({
                        features: {
                            preview: false, // DIAGNOSTIC
                            textEditor: {
                                cleanPaste: false,
                                customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            console.log(data.text); // Log or manipulate text editor data
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                            },
                        },
                    });
                    // eslint-disable-next-line no-console
                    console.log('[unlayer] init called early with features.textEditor.cleanPaste: false + preview:false');
                } catch (e) { /* ignore */ }
                return;
            }
            if (++attempts < maxAttempts) {
                setTimeout(tryInit, 50);
            }
        };
        tryInit();
        return () => { cancelled = true; };
    }, []);

    // ── Load front-page composition from the webshop's mini-api ─────────────
    // Fetches `/admin/settings`, extracts product containers and
    // HOME_SECTIONS_ORDER, and partitions the block catalog into
    // Active (in saved order) + Available (everything else). Re-runs on
    // webshop switch.
    useEffect(() => {
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return;

        let cancelled = false;
        (async () => {
            setConfigLoading(true);
            setConfigError(null);
            try {
                const lang = (props.lang || 'nl').toLowerCase();
                // Parallel fetch: storefront settings (via the webshop proxy)
                // AND the customer's kasvars (via auth/sftData). The kasvars
                // call returns labels for category_1..7 (vars Code1Oms..
                // Code7Oms); we use those as the per-shop labels on the
                // detail-page cards. If the kasvars call fails the cards
                // fall back to the generic cfg_pdi_details_category_N i18n.
                // The old kitchen-sink GET /admin/settings is split into four
                // focused bundles fetched IN PARALLEL; the dump-shaped `s`
                // object is reassembled below so none of the field mappings
                // change. The legacy endpoint still exists as a fallback.
                const bundleGet = (path) => SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: webshopId,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'GET',
                    path,
                }).catch(() => null);
                const [coreResult, assetsResult, contentsResult, collectionsResult, kasvarsResult, storesResult, dynPagesResult, localizationResult, placeholdersResult, posResult, warehouseResult, customerCodesResult, countriesResult, layoutsResult] = await Promise.all([
                    bundleGet('/admin/settings-core'),
                    bundleGet('/admin/settings-assets'),
                    bundleGet('/admin/settings-contents'),
                    bundleGet('/admin/settings-collections'),
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'kasvars',
                        code: [
                            'Code1Oms','Code2Oms','Code3Oms','Code4Oms','Code5Oms','Code6Oms','Code7Oms',
                            'Levart1Oms','Levart2Oms','Levart3Oms','Levart4Oms','Levart5Oms',
                            // POS payment-means labels for the Verwerking payment-type dropdown.
                            'kas_betaal1','kas_betaal2','kas_betaal3','kas_betaal4','kas_betaal5',
                            'kas_betaal6','kas_betaal7','kas_betaal8','kas_betaal9',
                        ],
                    }).catch(() => null),
                    // Stores list — all non-deleted rows (no render=list/visible
                    // filter, so the admin sees invisible stores too).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/stores',
                    }).catch(() => null),
                    // Dynamic pages (custom /extra/* pages).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/dynamic-pages',
                    }).catch(() => null),
                    // i18n strings — used for the look CTA label (GLOBAL.LOOKS_DETAILS).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/localization',
                    }).catch(() => null),
                    // Cheque (gift-voucher) placeholders — the Cadeaubon Translations table.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheque-placeholders',
                    }).catch(() => null),
                    // POS list (auth backend) — for the Verwerking "register under POS" dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'pos',
                    }).catch(() => null),
                    // Store/warehouse list (auth backend) — for the Verwerking "order warehouse" dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'stores',
                    }).catch(() => null),
                    // Customer codes (kl_codes) — group 90 = salutations for the Verwerking dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'customerCodes',
                    }).catch(() => null),
                    // Webshop countries (managed in the Data tab) — visible ones feed
                    // the Verwerking "default country" dropdown.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/countries',
                    }).catch(() => null),
                    // Cheque/voucher layouts — the Cadeaubon "Voucher layouts" tab.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheques-layouts',
                    }).catch(() => null),
                ]);
                if (cancelled) return;
                // Per-bundle resilience: use every bundle that loaded, and only
                // fall back to the legacy kitchen-sink call for the parts that
                // failed. A single failing bundle (e.g. collections 500) must
                // not blank the whole screen — core (logo/colours) still works.
                const allBundlesOk = coreResult?.success && assetsResult?.success
                    && contentsResult?.success && collectionsResult?.success;
                let s = {
                    ...(coreResult?.success        ? (coreResult.data        || {}) : {}),
                    ...(assetsResult?.success      ? (assetsResult.data      || {}) : {}),
                    ...(contentsResult?.success    ? (contentsResult.data    || {}) : {}),
                    ...(collectionsResult?.success ? (collectionsResult.data || {}) : {}),
                };
                if (!allBundlesOk) {
                    // Pull the legacy dump as a base for whatever is missing,
                    // then overlay the fresh bundles that DID succeed.
                    const legacy = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings',
                    }).catch(() => null);
                    if (cancelled) return;
                    if (legacy?.success && legacy.data) {
                        s = { ...legacy.data, ...s };
                    } else if (Object.keys(s).length === 0) {
                        const failed = legacy || coreResult || assetsResult || contentsResult || collectionsResult;
                        setConfigError({
                            message: failed?.message || 'Failed to load settings',
                            code:    failed?.translation_key,
                        });
                        return;
                    }
                    // else: some bundles loaded — degrade gracefully with those.
                }

                // shopFieldLabels — `{var, value}` rows from kasvars.
                //   "CodeNOms"   → "category_N"   (1..7)
                //   "LevartNOms" → "detail_N"     (1..5)
                const kasvarRows = (kasvarsResult?.data?.data && Array.isArray(kasvarsResult.data.data))
                    ? kasvarsResult.data.data
                    : [];
                const nextFieldLabels = {};
                const payTypeRows = [];
                for (const row of kasvarRows) {
                    const v = String(row?.var   || '');
                    const value = row?.value;
                    if (!value) continue;
                    let m;
                    if ((m = /^Code([1-7])Oms$/i.exec(v))) {
                        nextFieldLabels[`category_${m[1]}`] = String(value);
                    } else if ((m = /^Levart([1-5])Oms$/i.exec(v))) {
                        nextFieldLabels[`detail_${m[1]}`] = String(value);
                    } else if ((m = /^kas_betaal([1-9])$/i.exec(v))) {
                        // Keep the kasvar.code so the dropdown can pick the row matching
                        // the receipt POS id (kassa → winkel → globaal fallback).
                        payTypeRows.push({ n: Number(m[1]), value: String(value), code: row?.code });
                    }
                }
                setShopFieldLabels(nextFieldLabels);
                // Payment types — raw rows; the POS-id filtering happens in a memo.
                setPaymentTypeRows(payTypeRows);

                // Stores — map the mini-api rows to the admin editor shape.
                const storeRows = (storesResult?.success && Array.isArray(storesResult.data))
                    ? storesResult.data
                    : [];
                const loadedStores = storeRows.map(storeRowToForm);
                setStores(loadedStores);
                originalStoresRef.current = loadedStores;
                const detectedSep = detectHoursSeparator(storeRows);
                setHoursSeparator(detectedSep);
                originalHoursSeparatorRef.current = detectedSep;
                setGmapsApiKey(s.GOOGLE_MAPS_TOKEN || s.GMAPS_API_KEY || '');

                // Store-locations map settings (ABOUT_US_MAP_* + mapStyle.json).
                const loadedMap = {
                    multi:    !!Number(s.ABOUT_US_MAP_MULTI),
                    position: s.ABOUT_US_MAP_POSITION || 'bottom',
                    zoom:     s.ABOUT_US_MAP_ZOOM     != null && s.ABOUT_US_MAP_ZOOM     !== '' ? Number(s.ABOUT_US_MAP_ZOOM)     : 14,
                    zoomMax:  s.ABOUT_US_MAP_ZOOM_MAX != null && s.ABOUT_US_MAP_ZOOM_MAX !== '' ? Number(s.ABOUT_US_MAP_ZOOM_MAX) : 16,
                    style:    (s.mapStyle != null ? String(s.mapStyle) : '').trim() || '[]',
                };
                setMapSettings(loadedMap);
                originalMapSettingsRef.current = loadedMap;

                // Cart-page account cards ← contents rows (wysiwyg only). Keep
                // the static label/id from DEFAULT_CARDS, override the body +
                // iframe flag from the backend content row.
                const loadedCards = DEFAULT_CARDS.map(base => {
                    const meta = CARD_CONTENT[base.id];
                    const row  = meta ? s[meta.field] : null;
                    if (!meta) return base;
                    return {
                        ...base,
                        useIframe: !!(row && row.use_iframe),
                        contentNL: (row && row.body_nl) || '',
                        contentEN: (row && row.body_en) || '',
                        contentFR: (row && row.body_fr) || '',
                    };
                });
                setCards(loadedCards);
                originalCardsRef.current = loadedCards;

                // Brands page (Merken). Flat keys + the BRANDS_PAGE_CONFIG blob.
                const rawBpc = (s.BRANDS_PAGE_CONFIG && typeof s.BRANDS_PAGE_CONFIG === 'object') ? s.BRANDS_PAGE_CONFIG : {};
                originalBrandsRawJsonRef.current = rawBpc;
                const D = BRANDS_CONFIG_DEFAULT;
                const styleFromShort = (o, def) => ({
                    size:      o && o.s != null ? Number(o.s) : def.size,
                    color:     o && o.c ? o.c : def.color,
                    bold:      o ? !!o.b : def.bold,
                    italic:    o ? !!o.i : def.italic,
                    underline: o ? !!o.u : def.underline,
                });
                const rd = rawBpc.d || {}, rm = rawBpc.m || {};
                const loadedBrands = {
                    whatToShow: {
                        onlyOnline: !!s.BRANDS_ONLY_ONLINE,
                        navigationCategory: (s.BRANDS_CATEGORY_FILTER && s.BRANDS_CATEGORY_FILTER !== 'none') ? String(s.BRANDS_CATEGORY_FILTER) : '',
                    },
                    layout: BRANDS_STYLE_TO_LAYOUT[s.BRANDS_PAGE_STYLE] || 'grouped',
                    settings: {
                        displayContinuous:        rawBpc.lm || D.settings.displayContinuous,
                        displayGrouped:           rawBpc.am || D.settings.displayGrouped,
                        showLettersWithoutBrands: !!rawBpc.sul,
                        letterColumns:            rawBpc.cols != null ? Number(rawBpc.cols) : D.settings.letterColumns,
                        brandsPerLetter:          rawBpc.max  != null ? Number(rawBpc.max)  : D.settings.brandsPerLetter,
                    },
                    textStyles: {
                        desktop: {
                            brandName:     styleFromShort(rd.bn, D.textStyles.desktop.brandName),
                            alphabetNav:   styleFromShort(rd.ai, D.textStyles.desktop.alphabetNav),
                            letterHeading: styleFromShort(rd.al, D.textStyles.desktop.letterHeading),
                        },
                        mobile: {
                            brandName:     styleFromShort(rm.bn, D.textStyles.mobile.brandName),
                            alphabetNav:   styleFromShort(rm.ai, D.textStyles.mobile.alphabetNav),
                            letterHeading: styleFromShort(rm.al, D.textStyles.mobile.letterHeading),
                        },
                    },
                };
                setBrandsConfig(loadedBrands);
                originalBrandsConfigRef.current = loadedBrands;

                // Dynamic pages.
                const dynRows = (dynPagesResult?.success && Array.isArray(dynPagesResult.data)) ? dynPagesResult.data : [];
                const loadedDynPages = dynRows.map(dynRowToPage);
                setDynamicPages(loadedDynPages);
                originalDynamicPagesRef.current = loadedDynPages;

                // Information banners — comes embedded in the settings dump.
                const bannerRows = Array.isArray(s.informationBanners) ? s.informationBanners : [];
                const loadedBanners = bannerRows.map(bannerRowToForm);
                setBanners(loadedBanners);
                originalBannersRef.current = loadedBanners;

                // Shop the look — intro from TEXT_LOOKS content row, rest from
                // LOOKS_*/LOOK_*/SHOW_FIELDS_ON_LOOK_* settings.
                const looksRow = s.contents_looks || null;
                const DL = STL_CONFIG_DEFAULT;
                const toFieldArr = (v) => String(v || '').split(/\s+/).map(x => x.trim()).filter(Boolean);
                // Look CTA label — the GLOBAL.LOOKS_DETAILS i18n row.
                const i18nRows = (localizationResult?.success && Array.isArray(localizationResult.data)) ? localizationResult.data : [];
                const ctaRow = i18nRows.find(r => String(r.group) === 'GLOBAL' && String(r.item) === 'LOOKS_DETAILS') || null;
                looksCtaIdRef.current = ctaRow ? ctaRow.id : null;
                const ctaText = ctaRow
                    ? { nl: ctaRow.nl || ctaRow.default || '', en: ctaRow.en || ctaRow.default || '', fr: ctaRow.fr || ctaRow.default || '' }
                    : { ...DL.overlay.ctaText };
                const loadedLooks = {
                    introText: {
                        nl: (looksRow && looksRow.body_nl) || '',
                        en: (looksRow && looksRow.body_en) || '',
                        fr: (looksRow && looksRow.body_fr) || '',
                    },
                    introEditor: 'visual',
                    looksGrid: {
                        navigationCategory: s.LOOKS_NAVIGATION_CODE != null && s.LOOKS_NAVIGATION_CODE !== '' ? String(s.LOOKS_NAVIGATION_CODE) : DL.looksGrid.navigationCategory,
                        displayOrder:       s.LOOKS_LIST_SORTING || DL.looksGrid.displayOrder,
                        looksPerRow:        s.MAX_COLUMNS_LOOKS != null && s.MAX_COLUMNS_LOOKS !== '' ? Number(s.MAX_COLUMNS_LOOKS) : DL.looksGrid.looksPerRow,
                        // The looks page renders its intro via <app-content-display>,
                        // which uses an iframe when the TEXT_LOOKS content row's
                        // use_iframe flag is set (same mechanism as About).
                        showInIframe:       !!(looksRow && Number(looksRow.use_iframe)),
                    },
                    overlay: {
                        onlyOnHover:          !!Number(s.LOOKS_OVERLAY_ONLY_ON_HOVER),
                        bandBg:               s.LOOKS_OVERLAY_BGC || DL.overlay.bandBg,
                        bandHeightPct:        s.LOOKS_OVERLAY_SIZE != null && s.LOOKS_OVERLAY_SIZE !== '' ? Number(s.LOOKS_OVERLAY_SIZE) : DL.overlay.bandHeightPct,
                        bandVerticalPosition: s.LOOKS_OVERLAY_POSITION || DL.overlay.bandVerticalPosition,
                        showTitle:            s.LOOKS_OVERLAY_SHOW_TITLE != null ? !!Number(s.LOOKS_OVERLAY_SHOW_TITLE) : DL.overlay.showTitle,
                        titleColor:           s.LOOKS_OVERLAY_FGC || DL.overlay.titleColor,
                        titleFontSize:        s.LOOKS_OVERLAY_TITLE_FS != null && s.LOOKS_OVERLAY_TITLE_FS !== '' ? Number(s.LOOKS_OVERLAY_TITLE_FS) : DL.overlay.titleFontSize,
                        showCta:              s.LOOKS_OVERLAY_SHOW_BUTTON != null ? !!Number(s.LOOKS_OVERLAY_SHOW_BUTTON) : DL.overlay.showCta,
                        ctaText:              ctaText,
                        ctaBg:                s.LOOKS_OVERLAY_BUTTON_BGC || DL.overlay.ctaBg,
                        ctaTextColor:         s.LOOKS_OVERLAY_BUTTON_FGC || DL.overlay.ctaTextColor,
                        ctaFontSize:          s.LOOKS_OVERLAY_BUTTON_FS != null && s.LOOKS_OVERLAY_BUTTON_FS !== '' ? Number(s.LOOKS_OVERLAY_BUTTON_FS) : DL.overlay.ctaFontSize,
                    },
                    detailPage: {
                        descriptionPosition:        s.LOOKS_DETAIL_TEXT_LOCATION || DL.detailPage.descriptionPosition,
                        showDividerBetweenProducts: !!Number(s.LOOK_SHOW_DIVIDER),
                        showOutOfStock:             !!Number(s.LOOK_SHOW_OUT_OF_STOCK),
                    },
                    productTitle:    toFieldArr(s.SHOW_FIELDS_ON_LOOK_TITLE),
                    productSubtitle: toFieldArr(s.SHOW_FIELDS_ON_LOOK_SUBHEADING),
                };
                setLooksConfig(loadedLooks);
                originalLooksConfigRef.current = loadedLooks;

                // Overige — custom code from the script-include files, the
                // GTM/Meta-Pixel tools from their settings, and the cookie
                // banner from cookie.js (s.cookie_consent). GA4/TikTok/Hotjar +
                // the per-tool active flag stay pending.
                const trackingTools = [];
                if (s.GTM_ID)   trackingTools.push({ key: 'gtm',       id: String(s.GTM_ID),   active: true });
                if (s.PIXEL_ID) trackingTools.push({ key: 'metaPixel', id: String(s.PIXEL_ID), active: true });
                // Cookie consent (layout cloud/box/bar ↔ our floating/modal/bottom).
                const cc       = (s.cookie_consent && typeof s.cookie_consent === 'object') ? s.cookie_consent : null;
                const ccModal  = cc?.guiOptions?.consentModal || {};
                const ccCats   = cc?.categories || {};
                const ccStyle  = LAYOUT_TO_OVERIGE_STYLE[ccModal.layout] || OVERIGE_CONFIG_DEFAULT.cookies.bannerStyle;
                const loadedCookies = {
                    categories: {
                        necessary: true,
                        analytics: Object.prototype.hasOwnProperty.call(ccCats, 'analytics'),
                        marketing: Object.prototype.hasOwnProperty.call(ccCats, 'marketing'),
                    },
                    bannerStyle: ccStyle,
                    position:    ccModal.position || OVERIGE_CONFIG_DEFAULT.cookies.position,
                };
                const loadedOverige = {
                    ...OVERIGE_CONFIG_DEFAULT,
                    cookies: loadedCookies,
                    trackingTools,
                    customCode: {
                        ...OVERIGE_CONFIG_DEFAULT.customCode,
                        head: s.scriptsHeader || '',
                        body: s.scriptsBody || '',
                    },
                };
                setOverigeConfig(loadedOverige);
                originalOverigeConfigRef.current = loadedOverige;

                // Cadeaubon — settings from the CHEQUES_* keys in the dump,
                // translations from the separate /cheque-placeholders list.
                const loadedCadeaubon = settingsToCadeaubon(s, CADEAUBON_CONFIG_DEFAULT);
                setCadeaubonConfig(loadedCadeaubon);
                originalCadeaubonConfigRef.current = loadedCadeaubon;

                const placeholderRows = (placeholdersResult?.success && Array.isArray(placeholdersResult.data)) ? placeholdersResult.data : [];
                const loadedPlaceholders = placeholderRows.map(placeholderRowToForm);
                setCadeaubonPlaceholders(loadedPlaceholders);
                originalPlaceholdersRef.current = loadedPlaceholders;

                // Cadeaubon voucher layouts (GET /cheques-layouts). Preview images
                // live under <img_path>/cheque-layouts/; prefix with the webshop
                // base so they resolve in the admin app.
                const layoutImgBase = `${shopImageBase(props.selectedWebshop?.display_name)}${s.img_path || ''}/cheque-layouts/`;
                layoutImgBaseRef.current = layoutImgBase;
                const layoutRows = (layoutsResult?.success && Array.isArray(layoutsResult.data)) ? layoutsResult.data : [];
                const loadedLayouts = layoutRows.map(r => layoutRowToForm(r, layoutImgBase));
                setCadeaubonLayouts(loadedLayouts);
                originalCadeaubonLayoutsRef.current = loadedLayouts;

                // Verwerking — settings from the dump (incl. the 3 CHEQUES_* JSON
                // discount objects, kept raw for round-tripping unknown codes).
                const loadedProcessing = settingsToProcessing(s, PROCESSING_CONFIG_DEFAULT);
                setProcessingConfig(loadedProcessing);
                originalProcessingConfigRef.current = loadedProcessing;
                originalTicketModuleRef.current = String(s.PROCESS_TICKET_MODULE || '').toUpperCase();
                // Online payment provider availability (disables providers without
                // credentials). Only MultiSafePay is offered now.
                setPaymentProviderAvailability({ msp: !!(s.MSP_API_TOKEN || s.MSP_API_TOKEN_TEST) });
                originalProcChequeRef.current = {
                    allowed:      (s.CHEQUES_ALLOWED        && typeof s.CHEQUES_ALLOWED        === 'object') ? s.CHEQUES_ALLOWED        : {},
                    asPayment:    (s.CHEQUES_AS_PAYMENT     && typeof s.CHEQUES_AS_PAYMENT     === 'object') ? s.CHEQUES_AS_PAYMENT     : {},
                    addToReceipt: (s.CHEQUES_ADD_TO_RECEIPT && typeof s.CHEQUES_ADD_TO_RECEIPT === 'object') ? s.CHEQUES_ADD_TO_RECEIPT : {},
                };

                // Custom customer fields (customers_categories_groups, from the dump).
                const ccgRows = Array.isArray(s.customerCategoryGroups) ? s.customerCategoryGroups : [];
                const loadedCustomerGroups = ccgRows.map(customerGroupRowToForm);
                setCustomerCategoryGroups(loadedCustomerGroups);
                originalCustomerGroupsRef.current = loadedCustomerGroups;

                // POS list (auth backend) → [{ value: code, label: "code — oms" }].
                const posRows = (posResult?.data?.data && Array.isArray(posResult.data.data)) ? posResult.data.data : [];
                setPosOptions(posRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => ({ value: String(r.code), label: r.oms ? `${r.code} — ${r.oms}` : String(r.code) })));

                // Store/warehouse list (auth backend) → same shape.
                const warehouseRows = (warehouseResult?.data?.data && Array.isArray(warehouseResult.data.data)) ? warehouseResult.data.data : [];
                const warehouseCodes = warehouseRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => String(r.code));
                setWarehouseOptions(warehouseRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => ({ value: String(r.code), label: r.oms ? `${r.code} — ${r.oms}` : String(r.code) })));

                // Synchronisatie — scalar settings from the dump. FETCH_FOR_STORES
                // '00'/'' expands to every store code (the universe just built).
                const loadedSync = settingsToSync(s, SYNC_CONFIG_DEFAULT, warehouseCodes);
                setSyncConfig(loadedSync);
                originalSyncConfigRef.current = loadedSync;

                // Salutations = customer codes (kl_codes) in group 90 → [{ value: totcode, label: "totcode — codeoms" }].
                const custCodeRows = (customerCodesResult?.data?.data && Array.isArray(customerCodesResult.data.data)) ? customerCodesResult.data.data : [];
                setSalutationOptions(custCodeRows
                    .filter(r => r && Number(r.codegroep) === 90 && r.totcode != null && String(r.totcode) !== '')
                    .map(r => {
                        const oms = String(r.codeoms || r.oms || '').trim();
                        return { value: String(r.totcode), label: oms ? `${r.totcode} — ${oms}` : String(r.totcode) };
                    }));

                // Countries (webshop DB, managed in the Data tab). The default-country
                // dropdown mirrors the old admin: ALL countries, prefered first (the
                // mini-api already orders prefered DESC, name). Value = iso, label
                // localized to the admin language.
                const countryRows = (countriesResult?.success && Array.isArray(countriesResult.data)) ? countriesResult.data : [];
                const adminLang = String(i18n.language || 'nl').slice(0, 2).toLowerCase();
                const locKey = ['nl', 'en', 'fr'].includes(adminLang) ? `loc_${adminLang}` : 'loc_nl';
                setCountryOptions(countryRows
                    .filter(r => r && r.iso != null && String(r.iso) !== '')
                    .map(r => ({ value: String(r.iso), label: String(r[locKey] || r.loc_nl || r.name || r.iso) })));

                const containers = Array.isArray(s.productContainers) ? s.productContainers : [];
                const orderStr = s.HOME_SECTIONS_ORDER || '';
                const order = orderStr.split(',').map(x => x.trim()).filter(Boolean);

                setProductContainers(containers);
                setHomeSectionsOrder(orderStr);

                // Build the block catalog: known statics + fetched product containers.
                // Display name uses the localized field for the MySft user's
                // current language, falling back to the other locales then to
                // the admin-internal `reference`. (`lang` is declared at the
                // top of the try block.)
                const localized = (c) =>
                    c[`loc_${lang}`] || c.loc_nl || c.loc_en || c.loc_fr || c.reference || `Container ${c.id}`;
                const containerBlocks = containers.map(c => ({
                    id:   `pd_cnt-${c.id}`,
                    name: localized(c),
                    isContainer: true,
                    configurable: true,
                    container: c,                          // raw DB row (for save flow later)
                    config: containerRowToFormData(c),     // pre-mapped to the modal's form shape
                }));
                const allBlocks = [...STATIC_BLOCKS, ...containerBlocks];
                const byId = Object.fromEntries(allBlocks.map(b => [b.id, b]));

                // Active = HOME_SECTIONS_ORDER, in order. Unknown ids (typically
                // stale references to deleted product containers) are dropped
                // silently here — the next save will rewrite HOME_SECTIONS_ORDER
                // without them, cleaning up the legacy data.
                const activeList = order
                    .map(id => byId[id])
                    .filter(Boolean);
                const placedIds = new Set(activeList.map(b => b.id));
                const availableList = allBlocks.filter(b => (!b.onlyOn || b.onlyOn === 'home') && !placedIds.has(b.id));

                setActive(activeList);
                setAvailable(availableList);

                // 404 page: its own active + available lists, independent of the
                // Front page (landing). Active comes from PAGE_404_SECTIONS_ORDER
                // (default 'text'); available = all 404-allowed blocks (no onlyOn,
                // or onlyOn==='page404') not already placed.
                const order404Str  = s.PAGE_404_SECTIONS_ORDER || '';
                const order404      = order404Str.split(',').map(x => x.trim()).filter(Boolean);
                const active404List = order404.map(id => byId[id]).filter(Boolean);
                const placed404     = new Set(active404List.map(b => b.id));
                const available404List = allBlocks.filter(b => (!b.onlyOn || b.onlyOn === 'page404') && !placed404.has(b.id));
                setPage404SectionsOrder(order404Str);
                setActive404(active404List);
                setAvailable404(available404List);

                // ---- Map legacy colour settings into styleSettings ----------
                // Pulls the storefront's saved colour values into the Style tab
                // so both the mockup and the ColorField inputs reflect what's
                // actually live on the shop. Falls back to the existing default
                // if a key is missing or empty.
                const hex = (v, fallback) => {
                    if (v == null || v === '') return fallback;
                    const str = String(v).trim();
                    // Expand short hex (#fff → #ffffff) so <input type="color"> accepts it.
                    if (/^#[0-9a-f]{3}$/i.test(str)) {
                        return '#' + str[1] + str[1] + str[2] + str[2] + str[3] + str[3];
                    }
                    return str;
                };
                // Font: Google fonts store the name in FONT_FAMILY_GOOGLE, system
                // stacks the full CSS stack in FONT_FAMILY. Surface whichever is
                // active as the dropdown value.
                const useGoogleFont = readBool(s.FONT_FAMILY_USE_GOOGLE, false);
                const loadedFont = useGoogleFont
                    ? (s.FONT_FAMILY_GOOGLE || s.FONT_FAMILY || 'Roboto')
                    : (s.FONT_FAMILY || 'Roboto');
                // Back-to-top: backend CIRCLE_LEFT/MIDDLE/RIGHT ↔ left/center/right.
                const backToTopFromBackend = (v) => {
                    const u = String(v || '').toUpperCase();
                    if (u.includes('LEFT'))   return 'left';
                    if (u.includes('MIDDLE') || u.includes('CENTER')) return 'center';
                    return 'right';
                };
                setStyleSettings(prev => {
                    const next = ({
                    ...prev,
                    global: {
                        ...prev.global,
                        background: hex(s.COLOR_BODY_BACKGROUND, prev.global.background),
                        text:       hex(s.COLOR_BODY_TEXT,       prev.global.text),
                        primary:    hex(s.COLOR_PRIMARY,         prev.global.primary),
                        font:       loadedFont,
                    },
                    visibility: {
                        ...prev.visibility,
                        languageBar:       readBool(s.SHOW_LANGUAGE_BAR, prev.visibility.languageBar),
                        footer:            readBool(s.SHOW_FOOTER,       prev.visibility.footer),
                        backToTopPosition: backToTopFromBackend(s.BACK_TO_TOP_BUTTON),
                        // Storefront-active languages; admin-available defaults to
                        // the active set (then all) when ADMIN_LANGUAGES is unset.
                        visibleLanguages:  parseLangCsv(s.VISIBILE_LANGUAGES) || prev.visibility.visibleLanguages,
                        adminLanguages:    parseLangCsv(s.ADMIN_LANGUAGES) || parseLangCsv(s.VISIBILE_LANGUAGES) || prev.visibility.adminLanguages,
                    },
                    sections: {
                        ...prev.sections,
                        language: {
                            ...prev.sections.language,
                            bgColor:     hex(s.COLOR_BACKGROUND_LANG,  prev.sections.language.bgColor),
                            textColor:   hex(s.COLOR_TEXT_LANG,        prev.sections.language.textColor),
                            activeColor: hex(s.COLOR_TEXT_ACTIVE_LANG, prev.sections.language.activeColor),
                        },
                        header: {
                            ...prev.sections.header,
                            bgColor:   hex(s.COLOR_BACKGROUND_HEADER, prev.sections.header.bgColor),
                            textColor: hex(s.COLOR_TEXT_HEADER,       prev.sections.header.textColor),
                        },
                        menu: {
                            ...prev.sections.menu,
                            // Navigation bar = SECONDARY pair. The cart-badge bubble
                            // has its own dedicated setting (COLOR_IN_CART_BACKGROUND)
                            // — distinct from COLOR_PRIMARY which now drives the
                            // Add-to-cart button + main price colour.
                            bgColor:     hex(s.COLOR_SECONDARY,          prev.sections.menu.bgColor),
                            textColor:   hex(s.COLOR_SECONDARY_INVERTED, prev.sections.menu.textColor),
                            accentColor: hex(s.COLOR_IN_CART_BACKGROUND, prev.sections.menu.accentColor),
                            // Custom hover colours (legacy: COLOR_USE_CUSTOM_HOVER gate +
                            // COLOR_PRIMARY_HOVER/_TEXT + COLOR_SECONDARY_HOVER/_TEXT).
                            useCustomHover:     readBool(s.COLOR_USE_CUSTOM_HOVER,   prev.sections.menu.useCustomHover),
                            primaryHover:       hex(s.COLOR_PRIMARY_HOVER,           prev.sections.menu.primaryHover),
                            primaryHoverText:   hex(s.COLOR_PRIMARY_HOVER_TEXT,      prev.sections.menu.primaryHoverText),
                            secondaryHover:     hex(s.COLOR_SECONDARY_HOVER,         prev.sections.menu.secondaryHover),
                            secondaryHoverText: hex(s.COLOR_SECONDARY_HOVER_TEXT,    prev.sections.menu.secondaryHoverText),
                        },
                        // Title / subtitle / description default to the global body
                        // text colour (`COLOR_BODY_TEXT`) so that changing the Global
                        // panel's "Text color" field is visibly reflected in the mockup.
                        // Per-section overrides via the cogwheel still win.
                        title: {
                            ...prev.sections.title,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.title.textColor),
                        },
                        subtitle: {
                            ...prev.sections.subtitle,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.subtitle.textColor),
                        },
                        text: {
                            ...prev.sections.text,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.text.textColor),
                        },
                        // Price now uses g.primary for the main number — only the
                        // discount/sales colour is editable per-section.
                        price: {
                            ...prev.sections.price,
                            discountColor: hex(s.COLOR_SALES_VALUE_PRIMARY, prev.sections.price.discountColor),
                        },
                        // Explorer-only: the "Groepen" sidebar's border colour.
                        // Not used on the product detail page in the real storefront,
                        // so we keep it scoped to this Explorer section instead of
                        // a global field.
                        sideFilter: {
                            ...prev.sections.sideFilter,
                            borderColor: hex(s.COLOR_BORDERS, prev.sections.sideFilter.borderColor),
                        },
                        footer: {
                            ...prev.sections.footer,
                            bgColor:   hex(s.COLOR_BACKGROUND_FOOTER, prev.sections.footer.bgColor),
                            textColor: hex(s.COLOR_TEXT_FOOTER,       prev.sections.footer.textColor),
                        },
                        footerExtra: {
                            ...prev.sections.footerExtra,
                            bgColor:   hex(s.COLOR_BACKGROUND_FOOTER_EXTRA, prev.sections.footerExtra.bgColor),
                            textColor: hex(s.COLOR_TEXT_FOOTER_EXTRA,       prev.sections.footerExtra.textColor),
                        },
                    },
                });
                    // Snapshot the loaded shape so the dirty check has a
                    // baseline. Mutating the ref inside the functional setState
                    // callback is safe — React guarantees the callback runs
                    // synchronously when the state update is dispatched.
                    originalStyleSettingsRef.current = next;
                    return next;
                });

                // ---- Product grid + variant grouping settings -----------
                // Maps the legacy mini-api setting keys to the modal's nested
                // config shape. `showCardOutline` has no backend key yet — it
                // stays at its default until the storefront supports it.
                // (readNum/readBool/readStr are module-scope helpers now.)
                // Pagination is stored as 'top' / 'bottom' / 'top-and-bottom'
                // on the backend; the modal uses 'above' / 'below' / 'both'.
                const paginationFromBackend = (v) =>
                    v === 'top' ? 'above' :
                    v === 'top-and-bottom' ? 'both' :
                    v === 'bottom' ? 'below' :
                    DEFAULT_PRODUCT_GRID_CONFIG.productGrid.paginationPosition;

                const loadedGrid = {
                    productGrid: {
                        maxColumns:         readNum (s.MAX_COLUMNS_EXPLORE,        DEFAULT_PRODUCT_GRID_CONFIG.productGrid.maxColumns),
                        paginationPosition: paginationFromBackend(s.EXPLORE_PAGINATION_POSITION),
                        showTotalProducts:  readBool(s.EXPLORE_SHOW_TOTALS,         DEFAULT_PRODUCT_GRID_CONFIG.productGrid.showTotalProducts),
                        showCardOutline:    DEFAULT_PRODUCT_GRID_CONFIG.productGrid.showCardOutline,
                    },
                    variantGrouping: {
                        groupAsOne:   readBool(s.EXPLORE_GROUP_VARIANTS,         DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.groupAsOne),
                        showSwatches: readBool(s.EXPLORE_SHOW_PRODUCT_VARIANTS,  DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.showSwatches),
                        maxVariants:  readNum (s.PRODUCT_SHOW_VARIANTS_AMOUNT,   DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.maxVariants),
                    },
                };
                setProductGridConfig(loadedGrid);
                originalProductGridConfigRef.current = loadedGrid;

                // ---- Product card config (Product information modal) ---
                // PRODUCT_DISPLAY_INFO stores three lines of CSV-of-keys
                // joined by ';' (e.g. "type,brand;reference;"). The modal
                // works with arrays of {id, key} chips — we round-trip on
                // save/load. Chip ids are regenerated each load; the dirty
                // check normalizes them away.
                let chipCounter = 0;
                const newId = (kind) => `${kind}-${Date.now()}-${++chipCounter}`;
                // ALL_FIELDS lives in ProductCardModal — duplicating just the
                // key set here avoids an import cycle.
                const KNOWN_KEYS = new Set([
                    'id','brand','name','reference','season','color','color_brand',
                    'description_1','description_2','description_3','description_4','description_5',
                    'category_1','category_2','category_3','category_4','category_5','category_6','category_7',
                    'detail_1','detail_2','detail_3','detail_4','detail_5',
                ]);
                const parseLines = (raw, fallback) => {
                    if (raw == null || raw === '') return fallback;
                    // PRODUCT_DISPLAY_INFO format: lines joined by ';'. Within a
                    // line, tokens are whitespace-separated; any non-recognised
                    // segment becomes a text chip so the user keeps their
                    // literal text (separators, parens, etc.) intact. The
                    // storefront's renderer does its own \b(known)\b regex
                    // replacement, so a text chip containing "(id)" still gets
                    // `id` swapped in on the actual product card.
                    const parts = String(raw).split(';');
                    return [0, 1, 2].map(i => {
                        const segment = parts[i] || '';
                        return segment
                            .split(/\s+/)
                            .filter(Boolean)
                            .map(tok => KNOWN_KEYS.has(tok)
                                ? { id: newId('chip'), key:  tok }
                                : { id: newId('txt'),  text: tok });
                    });
                };
                const fallbackLines = [
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line1.map(c => ({ ...c, id: newId('chip') })),
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line2.map(c => ({ ...c, id: newId('chip') })),
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line3.map(c => ({ ...c, id: newId('chip') })),
                ];
                const lines = s.PRODUCT_DISPLAY_INFO != null
                    ? parseLines(s.PRODUCT_DISPLAY_INFO, fallbackLines)
                    : fallbackLines;
                // EXPLORE_USE_IMAGE_FIT_RATIO is "W/H" (sometimes with spaces).
                // Default to whatever the modal's defaults are if unparseable.
                let aspW = DEFAULT_PRODUCT_CARD_CONFIG.image.aspectWidth;
                let aspH = DEFAULT_PRODUCT_CARD_CONFIG.image.aspectHeight;
                if (s.EXPLORE_USE_IMAGE_FIT_RATIO) {
                    const m = String(s.EXPLORE_USE_IMAGE_FIT_RATIO).match(/(\d+)\s*\/\s*(\d+)/);
                    if (m) { aspW = parseInt(m[1], 10); aspH = parseInt(m[2], 10); }
                }
                // The legacy admin only supports 'zoom' and 'second_image';
                // map second_image → fadeAlt for the modal's richer enum.
                const animTypeFromBackend = (v) =>
                    v === 'second_image' ? 'fadeAlt' :
                    v === 'zoom'         ? 'zoom'    :
                    DEFAULT_PRODUCT_CARD_CONFIG.image.animationType;

                const readColor = (v, fb) => (v == null || v === '' ? fb : String(v));

                const loadedCard = {
                    productInfo: {
                        line1: lines[0],
                        line2: lines[1],
                        line3: lines[2],
                        showDivider: readBool(s.PRODUCT_SHOW_DIVIDER,      DEFAULT_PRODUCT_CARD_CONFIG.productInfo.showDivider),
                        align:       readStr (s.PRODUCT_DETAILS_ALIGNMENT, DEFAULT_PRODUCT_CARD_CONFIG.productInfo.align),
                    },
                    image: {
                        useAlternativeAsMain: readBool(s.EXPLORE_USE_SECOND_IMAGE,      DEFAULT_PRODUCT_CARD_CONFIG.image.useAlternativeAsMain),
                        fillsCard:            readBool(s.EXPLORE_USE_IMAGE_FIT,         DEFAULT_PRODUCT_CARD_CONFIG.image.fillsCard),
                        aspectWidth:          aspW,
                        aspectHeight:         aspH,
                        cornerRounding:       readNum (s.PRODUCT_IMG_RADIUS,            DEFAULT_PRODUCT_CARD_CONFIG.image.cornerRounding),
                        animateOnHover:       readBool(s.ENABLE_PRODUCT_ITEM_ANIMATION, DEFAULT_PRODUCT_CARD_CONFIG.image.animateOnHover),
                        animationType:        animTypeFromBackend(s.PRODUCT_HOVER_ANIM_TYPE),
                    },
                    sizePreview: {
                        enabled:    readBool(s.PRODUCT_HOVER_SIZES,         DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.enabled),
                        // PRODUCT_HOVER_MAX_SIZES comes back as a parsed array
                        // (mini-api explodes the CSV). Index [0] is the Y axis
                        // ("lengtematen" / number of sizes shown in the hover
                        // badge) — what the modal's "Maximum sizes shown"
                        // control edits. Index [1] (X / breedtematen / widths
                        // per size) is stashed in a ref so save preserves it.
                        maxShown:   (() => {
                            const arr = Array.isArray(s.PRODUCT_HOVER_MAX_SIZES) ? s.PRODUCT_HOVER_MAX_SIZES : [];
                            const y   = parseInt(arr[0], 10);
                            return Number.isFinite(y) ? y : DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.maxShown;
                        })(),
                        position:   readStr (s.PRODUCT_SIZE_OVERLAY_POSITION, DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.position),
                        badgeBg:    readColor(s.PRODUCT_SIZE_OVERLAY_BGC,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.badgeBg),
                        badgeText:  readColor(s.PRODUCT_SIZE_OVERLAY_FGC,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.badgeText),
                        textSize:   readNum (s.PRODUCT_SIZE_OVERLAY_SIZE,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.textSize),
                        showBorder: readBool(s.PRODUCT_SIZE_SIZE_BORDER,      DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.showBorder),
                    },
                };
                setProductCardConfig(loadedCard);
                originalProductCardConfigRef.current = loadedCard;
                // Stash original [1] ("breedtematen" / widths per size) so save
                // can rebuild the CSV without dropping it. Defaults to 5 to
                // match the install migration's '3,5' seed.
                {
                    const arr = Array.isArray(s.PRODUCT_HOVER_MAX_SIZES) ? s.PRODUCT_HOVER_MAX_SIZES : [];
                    const x   = parseInt(arr[1], 10);
                    productHoverMaxWidthsRef.current = Number.isFinite(x) ? x : 5;
                }

                // ---- Groups filter + sorting config -----------------------
                // The Filters & sortering modal maps to a handful of legacy
                // setting keys. CSV strings (orders) split on commas;
                // JSON-coerced settings (SIDE_FILTER_SLICE/STATES) come back
                // as objects already.
                const csvList = (raw) => String(raw == null ? '' : raw)
                    .split(',').map(s => s.trim()).filter(Boolean);
                // Modal field → legacy sort-rule field. Best-effort: gender
                // and type priorities are typically category_1/_2 on most
                // shops; createdAt has no legacy equivalent, fall back to id.
                const sortFieldFromBackend = (raw) => ({
                    price:           'price',
                    category_1_prio: 'genderPriority',
                    category_2_prio: 'typePriority',
                    id:              'id',
                    discount_perc:   'discount',
                    total_stock:     'stock',
                }[raw] || null);
                const parseSortRule = (raw, fallback) => {
                    if (raw == null || raw === '') return fallback;
                    const tokens = csvList(raw);
                    if (tokens.length === 0) return fallback;
                    let counter = 0;
                    const out = [];
                    for (const tok of tokens) {
                        const desc = tok.startsWith('-');
                        const name = (desc ? tok.slice(1) : tok).trim();
                        const mapped = sortFieldFromBackend(name);
                        if (mapped) out.push({ id: `r${++counter}`, field: mapped, direction: desc ? 'desc' : 'asc' });
                    }
                    return out.length ? out : fallback;
                };
                // Walk the loaded SIDE_FILTER_SLICE / SIDE_FILTER_STATES JSON
                // blobs into the modal's per-filter settings shape. Unknown
                // keys are ignored; missing keys fall back to the modal's
                // baseline (8 items, expanded).
                const sliceObj  = (typeof s.SIDE_FILTER_SLICE  === 'object' && s.SIDE_FILTER_SLICE)  ? s.SIDE_FILTER_SLICE  : {};
                const statesObj = (typeof s.SIDE_FILTER_STATES === 'object' && s.SIDE_FILTER_STATES) ? s.SIDE_FILTER_STATES : {};
                const activeFilters = s.SIDE_FILTER_ORDER ? csvList(s.SIDE_FILTER_ORDER) : DEFAULT_GROUPS_FILTER_CONFIG.activeFilters;
                const filterSettings = {};
                for (const fk of activeFilters) {
                    const fallback = DEFAULT_GROUPS_FILTER_CONFIG.filterSettings[fk] || { itemsBeforeMore: 8, expandedByDefault: true };
                    filterSettings[fk] = {
                        itemsBeforeMore: sliceObj[fk] != null && sliceObj[fk] !== '' ? Number(sliceObj[fk]) : fallback.itemsBeforeMore,
                        expandedByDefault: statesObj[fk] != null ? !!statesObj[fk] : fallback.expandedByDefault,
                    };
                }
                // Layout's `wherefilters` uses 'topbar'/'sidebar' in the modal;
                // legacy FILTER_POSITION uses 'top'/'side'.
                const wherefiltersFromBackend = (v) =>
                    v === 'top'  ? 'topbar'  :
                    v === 'side' ? 'sidebar' :
                    DEFAULT_GROUPS_FILTER_CONFIG.layout.wherefilters;
                // Modal: 'swatches'/'names'; legacy COLOR_FILTER_DISPLAY:
                // 'color'/'text' (default 'color' per mig032_20190412.php).
                const colorStyleFromBackend = (v) =>
                    v === 'color' ? 'swatches' :
                    v === 'text'  ? 'names'    :
                    DEFAULT_GROUPS_FILTER_CONFIG.layout.colorStyle;

                const loadedGroups = {
                    layout: {
                        wherefilters:     wherefiltersFromBackend(s.FILTER_POSITION),
                        sizeStyle:        readStr (s.FILTER_SIZE_STYLE,        DEFAULT_GROUPS_FILTER_CONFIG.layout.sizeStyle),
                        colorStyle:       colorStyleFromBackend(s.COLOR_FILTER_DISPLAY),
                        splitWidthLength: readBool(s.FILTER_TOP_SPLIT_SIZES,    DEFAULT_GROUPS_FILTER_CONFIG.layout.splitWidthLength),
                        showProductCount: readBool(s.SIDE_FILTER_SHOW_COUNTERS, DEFAULT_GROUPS_FILTER_CONFIG.layout.showProductCount),
                        hoverStyle:       readStr (s.FILTER_HOVER,              DEFAULT_GROUPS_FILTER_CONFIG.layout.hoverStyle),
                    },
                    activeFilters,
                    sortFiltersBy: readStr(s.ORDER_SIDE_FILTER_BY, DEFAULT_GROUPS_FILTER_CONFIG.sortFiltersBy),
                    filterSettings,
                    mobileHierarchy: s.FILTER_TREE_LEVELS ? csvList(s.FILTER_TREE_LEVELS) : DEFAULT_GROUPS_FILTER_CONFIG.mobileHierarchy,
                    sortOptions:     s.EXPLORE_SORT_ORDER ? csvList(s.EXPLORE_SORT_ORDER) : DEFAULT_GROUPS_FILTER_CONFIG.sortOptions,
                    defaultSortRule: parseSortRule(s.EXPLORE_SORT_DEFAULT_RULE, DEFAULT_GROUPS_FILTER_CONFIG.defaultSortRule),
                };
                setGroupsFilterConfig(loadedGroups);
                originalGroupsFilterConfigRef.current = loadedGroups;

                // ---- Global width settings -------------------------------
                // Maps the flat SHOP_MAX_WIDTH_* keys back into the modal's
                // nested {applyToWholeSite, productPagesWidth, perElement.*}
                // shape. Width keys are coerced to numbers by the mini-api
                // (now that they're in ADMIN_SETTINGS_INT_KEYS); FW_BG keys
                // are coerced to booleans.
                const widthRow = (widthKey, fwbgKey, fallback) => ({
                    width:       readNum (s[widthKey], fallback.width),
                    bgFullWidth: readBool(s[fwbgKey],  fallback.bgFullWidth ?? true),
                });
                const loadedWidths = {
                    applyToWholeSite:  readBool(s.SHOP_MAX_WIDTH_COMPLETE, false),
                    productPagesWidth: readNum (s.SHOP_MAX_WIDTH,          1500),
                    buttonBorderRadius: readNum(s.BUTTON_BORDER_RADIUS,    DEFAULT_GLOBAL_WIDTHS.buttonBorderRadius),
                    perElement: {
                        languageBar:     widthRow('SHOP_MAX_WIDTH_TOP',         'SHOP_MAX_WIDTH_TOP_FW_BG',         DEFAULT_GLOBAL_WIDTHS.perElement.languageBar),
                        header:          widthRow('SHOP_MAX_WIDTH_HEADER',      'SHOP_MAX_WIDTH_HEADER_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.header),
                        infoBannerBelow: widthRow('SHOP_MAX_WIDTH_BANNERS',     'SHOP_MAX_WIDTH_BANNERS_FW_BG',     DEFAULT_GLOBAL_WIDTHS.perElement.infoBannerBelow),
                        infoBannerTop:   widthRow('SHOP_MAX_WIDTH_BANNERS_TOP', 'SHOP_MAX_WIDTH_BANNERS_TOP_FW_BG', DEFAULT_GLOBAL_WIDTHS.perElement.infoBannerTop),
                        // brands & explore have no BG-stretch toggle in the modal
                        brands:          { width: readNum(s.SHOP_MAX_WIDTH_BRANDS,  DEFAULT_GLOBAL_WIDTHS.perElement.brands.width)  },
                        explore:         { width: readNum(s.SHOP_MAX_WIDTH_EXPLORE, DEFAULT_GLOBAL_WIDTHS.perElement.explore.width) },
                        recentlyViewed:  widthRow('SHOP_MAX_WIDTH_RECENT',      'SHOP_MAX_WIDTH_RECENT_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.recentlyViewed),
                        footer:          widthRow('SHOP_MAX_WIDTH_FOOTER',      'SHOP_MAX_WIDTH_FOOTER_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.footer),
                    },
                };
                setGlobalWidths(loadedWidths);
                originalGlobalWidthsRef.current = loadedWidths;

                // ---- Header / Logo (only height for now) ----------------
                // logoBarHeight ↔ HEADER_HEIGHT. The width/bgFullWidth fields
                // overlap with globalWidths.header and are not duplicated here.
                // Image URLs come from img_store_logo/header/icon.
                // display_name (the shop URL) frequently has NO protocol — see
                // Header.js, which prepends https:// the same way. Without it the
                // <img> src is a protocol-less RELATIVE url that 404s against the
                // admin domain (→ placeholder SVG, blank carousel/grid tiles).
                const headerImgBase = shopImageBase(props.selectedWebshop?.display_name);
                const headerImgPrefix = (u) => (u ? `${headerImgBase}${u}` : '');
                const loadedHeaderLogo = {
                    size: {
                        ...DEFAULT_HEADER_LOGO_CONFIG.size,
                        logoBarHeight: readNum(s.HEADER_HEIGHT, DEFAULT_HEADER_LOGO_CONFIG.size.logoBarHeight),
                        maxContentWidth:     loadedWidths.perElement.header.width,
                        backgroundFullWidth: loadedWidths.perElement.header.bgFullWidth,
                    },
                    // img_store_* are root-relative to the storefront; prefix the
                    // webshop base so the <img> previews resolve in the admin app.
                    images: {
                        logoUrl:       headerImgPrefix(s.img_store_logo),
                        backgroundUrl: headerImgPrefix(s.img_store_header),
                        faviconUrl:    headerImgPrefix(s.img_store_icon),
                    },
                };
                setHeaderLogoConfig(loadedHeaderLogo);
                originalHeaderLogoConfigRef.current = loadedHeaderLogo;

                // ---- Navigation (menu bar) config ----------------------
                // Maps headerContent toggles + outlet mode + a handful of
                // layout/menu fields back from their legacy SHOW_HEADER_* /
                // CENTER_* / HEADER_FILTER_* / HEADER_TYPE /
                // HEADER_ITEM_BREAK_AMOUNT / HOME_* keys.
                const navDefault = DEFAULT_NAVIGATION_CONFIG;
                const loadedNavigation = {
                    headerContent: {
                        contact:     readBool(s.SHOW_HEADER_ABOUTUS,  navDefault.headerContent.contact),
                        brands:      readBool(s.SHOW_HEADER_BRANDS,   navDefault.headerContent.brands),
                        shopTheLook: readBool(s.SHOW_HEADER_LOOKS,    navDefault.headerContent.shopTheLook),
                        outlet:      readBool(s.SHOW_SALES_BUTTON,    navDefault.headerContent.outlet),
                        giftVoucher: readBool(s.SHOW_HEADER_CHEQUES,  navDefault.headerContent.giftVoucher),
                        search:      readBool(s.SHOW_HEADER_SEARCH,   navDefault.headerContent.search),
                        account:     readBool(s.SHOW_HEADER_ACCOUNT,  navDefault.headerContent.account),
                        cart:        readBool(s.SHOW_HEADER_CART,     navDefault.headerContent.cart),
                        searchExpandedByDefault: readBool(s.DEFAULT_SEARCH_STATE_VISIBLE, navDefault.headerContent.searchExpandedByDefault),
                        showHomeButton:     readBool(s.HOME_ENABLED,  navDefault.headerContent.showHomeButton),
                        homeUrl:     s.HOME_URL || navDefault.headerContent.homeUrl,
                    },
                    outletMode: {
                        minDiscount:     readNum (s.MIN_DISCOUNT_FOR_SALES, navDefault.outletMode.minDiscount),
                        hideFromExplore: readBool(s.HIDE_SALES_FROM_EXPLORE, navDefault.outletMode.hideFromExplore),
                    },
                    layout: {
                        ...navDefault.layout,
                        menuStyle:               s.HEADER_TYPE              || navDefault.layout.menuStyle,
                        actionButtonsPosition:   s.HEADER_ACCOUNT_POSITION  || navDefault.layout.actionButtonsPosition,
                        centerTopHeaderButtons:  readBool(s.CENTER_HEADER,                navDefault.layout.centerTopHeaderButtons),
                        centerTopButtonInMiddle: readBool(s.CENTER_LOGO,                  navDefault.layout.centerTopButtonInMiddle),
                        alignActionButtonsRight: readBool(s.HEADER_FILTER_SPACE_ACTIONS, navDefault.layout.alignActionButtonsRight),
                    },
                    navigationMenu: {
                        ...navDefault.navigationMenu,
                        categoriesShown: s.NAVBAR_ITEMS_TO_LIST != null && s.NAVBAR_ITEMS_TO_LIST !== '0' && s.NAVBAR_ITEMS_TO_LIST !== ''
                            ? [String(s.NAVBAR_ITEMS_TO_LIST)]
                            : [],
                        itemsPerRow:  readNum(s.HEADER_ITEM_BREAK_AMOUNT, navDefault.navigationMenu.itemsPerRow),
                        menuHeight:   readNum(s.HEADER_FILTER_HEIGHT,    navDefault.navigationMenu.menuHeight),
                        menuTextSize: readNum(s.HEADER_FILTER_FONT_SIZE, navDefault.navigationMenu.menuTextSize),
                    },
                    behaviour: {
                        keepMenuVisibleWhileScrolling: readBool(s.SHOW_HEADER_FILTER_ON_SCROLL, navDefault.behaviour.keepMenuVisibleWhileScrolling),
                    },
                };
                setNavigationConfig(loadedNavigation);
                originalNavigationConfigRef.current = loadedNavigation;
                setNavFilterStyle(String(s.FILTER_STYLE || ''));

                // Navbar filter order — fetch the category rows for the chosen
                // main-filter level (skip when "none"). Sequential after the
                // dump because the level comes out of it.
                const navOrderLevel = (loadedNavigation.navigationMenu.categoriesShown && loadedNavigation.navigationMenu.categoriesShown[0]) || '0';
                navbarOrderLevelRef.current = navOrderLevel;
                if (navOrderLevel !== '0') {
                    try {
                        const noRes = await SftGlobalData.post('webshop&action=proxy', {
                            key:        props.api,
                            webshop_id: webshopId,
                            sftUserId:  props.login?.sft_user_id,
                            user:       props.login?.login,
                            method:     'GET',
                            path:       '/admin/navbar-order',
                            query:      { group: navOrderLevel },
                        });
                        const noRows = (noRes?.success && Array.isArray(noRes.data)) ? noRes.data : [];
                        setNavbarOrder(noRows);
                        originalNavbarOrderRef.current = noRows;
                    } catch {
                        setNavbarOrder([]);
                        originalNavbarOrderRef.current = [];
                    }
                } else {
                    setNavbarOrder([]);
                    originalNavbarOrderRef.current = [];
                }

                // Footer modal — newsletter (settings) + customLinks /
                // socialMedia (external_links rows split by `position`).
                // Width overlaps GlobalWidthSettingsModal (left as default).
                // Pages + contact still need separate endpoints (contents
                // table / lic_clients_webshop) and stay frontend-only.
                const footerDefault = DEFAULT_FOOTER_CONFIG;
                const allLinks = Array.isArray(s.externalLinks) ? s.externalLinks : [];
                const linkRowToFooter = (r) => ({
                    id:       `cl_${r.id}`,
                    serverId: r.id,
                    url:      r.url || '',
                    labels:   { nl: r.loc_nl || '', en: r.loc_en || '', fr: r.loc_fr || '' },
                });
                const linkRowToSocial = (r) => ({
                    id:       `sm_${r.id}`,
                    serverId: r.id,
                    url:      r.url || '',
                });
                const loadedFooter = {
                    ...footerDefault,
                    newsletter: {
                        enabled:                readBool(s.SHOW_NEWSLETTER,               footerDefault.newsletter.enabled),
                        showInPopup:            readBool(s.NEWSLETTER_POPUP,              footerDefault.newsletter.showInPopup),
                        openPopupAutomatically: readBool(s.NEWSLETTER_POPUP_FIRST_VISIT,  footerDefault.newsletter.openPopupAutomatically),
                    },
                    // Per-page external-URL override. Empty value means
                    // "use the internal page" (TEXT_ABOUT etc.).
                    pages: {
                        about:      s.SF_LINK_ABOUT       || '',
                        terms:      s.SF_LINK_TERMS       || '',
                        privacy:    s.SF_LINK_PRIVACY     || '',
                        disclaimer: s.SF_LINK_DISCLAIMER  || '',
                    },
                    contact: {
                        shopName:    s.SF_INFO_NAME     || '',
                        addressLine: s.SF_INFO_ADDRESS  || '',
                        city:        s.SF_INFO_CITY     || '',
                        vatNumber:   s.SF_INFO_VAT      || '',
                        phone:       s.SF_INFO_PHONE    || '',
                        email:       s.SF_INFO_EMAIL    || '',
                        website:     s.SF_INFO_WEBSITE  || '',
                    },
                    customLinks: allLinks
                        .filter(r => String(r.position || '').toUpperCase() !== 'SOCIAL')
                        .map(linkRowToFooter),
                    socialMedia: allLinks
                        .filter(r => String(r.position || '').toUpperCase() === 'SOCIAL')
                        .map(linkRowToSocial),
                };
                setFooterConfig(loadedFooter);
                originalFooterConfigRef.current = loadedFooter;

                // Extra footer — payment toggle + per-column wysiwyg content.
                // Column content lives in the `contents` table (rows
                // TEXT_FOOTER_COL_{1,2,3}), exposed via the
                // /admin/settings dump as s.contents_footer_{1,2,3}.
                const extraFooterDefault = DEFAULT_EXTRA_FOOTER_CONFIG;
                const columnFromContents = (row) => {
                    const r = (row && typeof row === 'object') ? row : {};
                    const designs = (r.designs && typeof r.designs === 'object') ? r.designs : {};
                    // Auto-pick the unlayer editor when any per-language
                    // design JSON exists on disk. Mirrors the legacy admin
                    // behaviour: if there's a saved design we open the
                    // visual builder; if not, the plain wysiwyg editor.
                    const hasAnyDesign = !!(designs.nl || designs.en || designs.fr);
                    return {
                        editor: hasAnyDesign ? 'unlayer' : 'wysiwyg',
                        html:   { nl: r.body_nl || '', en: r.body_en || '', fr: r.body_fr || '' },
                        design: { nl: designs.nl || null, en: designs.en || null, fr: designs.fr || null },
                    };
                };
                const loadedExtraFooter = {
                    ...extraFooterDefault,
                    showPaymentMethods: readBool(s.FOOTER_EXTRA_PAYMENT_METHODS, extraFooterDefault.showPaymentMethods),
                    columns: {
                        1: columnFromContents(s.contents_footer_1),
                        2: columnFromContents(s.contents_footer_2),
                        3: columnFromContents(s.contents_footer_3),
                    },
                };
                setExtraFooterConfig(loadedExtraFooter);
                originalExtraFooterConfigRef.current = loadedExtraFooter;

                // Static text pages (About / Disclaimer / Privacy) — each from
                // its `contents` row. Auto-pick the unlayer editor when a
                // per-language design exists on disk (mirrors Extra footer).
                const pageFromContents = (row) => {
                    const r = (row && typeof row === 'object') ? row : {};
                    const designs = (r.designs && typeof r.designs === 'object') ? r.designs : {};
                    const hasAnyDesign = !!(designs.nl || designs.en || designs.fr);
                    return {
                        iframe:       !!Number(r.use_iframe),
                        editor:       hasAnyDesign ? 'unlayer' : 'wysiwyg',
                        html:         { nl: r.body_nl || '', en: r.body_en || '', fr: r.body_fr || '' },
                        designs:      { nl: designs.nl || null, en: designs.en || null, fr: designs.fr || null },
                        isCustomHtml: !!Number(r.is_custom_html),
                    };
                };
                const loadedStaticPages = {
                    about:      pageFromContents(s.contents_about),
                    disclaimer: pageFromContents(s.contents_disclaimer),
                    privacy:    pageFromContents(s.contents_privacy),
                    page404:    pageFromContents(s.contents_404),
                    intro:      pageFromContents(s.contents_intro),
                };
                setStaticPages(loadedStaticPages);
                originalStaticPagesRef.current = loadedStaticPages;

                // Image gallery — combines two settings rows + a JSON blob.
                // GALLERY_THUMBS_POSITION uses 'top'/'bottom'; the modal uses
                // 'above'/'below'. SHOW_GALLERY_THUMBS=false collapses the
                // position dropdown into 'hidden'.
                const imgGalDefault = DEFAULT_IMAGE_GALLERY_CONFIG;
                const legacyPos = (s.GALLERY_THUMBS_POSITION || 'left').toString().toLowerCase();
                const modalPos  = legacyPos === 'top' ? 'above'
                                : legacyPos === 'bottom' ? 'below'
                                : ['left','right','hidden'].includes(legacyPos) ? legacyPos
                                : imgGalDefault.thumbnailPosition;
                const thumbsOn  = readBool(s.SHOW_GALLERY_THUMBS, true);
                const ngc = s.NEW_GALLERY_CONFIG && typeof s.NEW_GALLERY_CONFIG === 'object'
                    ? s.NEW_GALLERY_CONFIG
                    : {};
                const mainArrows  = ngc.main_arrows  || {};
                const thumbArrows = ngc.thumb_arrows || {};
                const loadedImgGal = {
                    thumbnailPosition:      thumbsOn ? modalPos : 'hidden',
                    useGrid:                readBool(s.DETAIL_USE_NEW_GALLERY, imgGalDefault.useGrid),
                    maxThumbnails:          readNum(s.NEW_GALLERY_THUMBS_COUNT, imgGalDefault.maxThumbnails),
                    showArrows:             mainArrows.display !== undefined ? !!mainArrows.display : imgGalDefault.showArrows,
                    arrowColor:             mainArrows.color      || imgGalDefault.arrowColor,
                    arrowBgColor:           mainArrows.background || imgGalDefault.arrowBgColor,
                    showFullscreen:         thumbArrows.display !== undefined ? !!thumbArrows.display : imgGalDefault.showFullscreen,
                    fullscreenArrowColor:   thumbArrows.color      || imgGalDefault.fullscreenArrowColor,
                    fullscreenArrowBgColor: thumbArrows.background || imgGalDefault.fullscreenArrowBgColor,
                };
                setImageGalleryConfig(loadedImgGal);
                originalImageGalleryConfigRef.current = loadedImgGal;

                // Product Detail Information — seven of the eight cards
                // round-trip to legacy settings (layout, sectionsOpen,
                // detailsList, related, stock, titleAndUrl, breadcrumb).
                // Only `descriptionTexts` (per-language placement of
                // description fields) has no backend mapping yet — its
                // card surfaces a "planned" banner and edits stay local
                // until the storefront supports per-language toggles.
                const pdiDefault = DEFAULT_PRODUCT_DETAIL_INFO_CONFIG;

                // Layout — PRODUCT_DETAILS_ORDER is a CSV of legacy block
                // keys. Blocks not in the CSV are hidden on the storefront
                // (its component sets order=null and *ngIfs the section
                // away). Map legacy ↔ modal both ways.
                const legacyToModalBlock = {
                    header:          'header',
                    sizeSelector:    'sizeSelector',
                    addToCart:       'addToCart',
                    locationsStock:  'stockByLocation',
                    variantsList:    'variants',
                    related:         'relatedProducts',
                    productInfo:     'productInfo',
                    extraText:       'extraText',
                };
                const orderCsv     = String(s.PRODUCT_DETAILS_ORDER || '').trim();
                const legacyOrder  = orderCsv ? orderCsv.split(',').map(k => k.trim()).filter(Boolean) : [];
                const modalOrder   = legacyOrder.map(k => legacyToModalBlock[k]).filter(Boolean);
                const usedSet      = new Set(modalOrder);
                const loadedBlocks = Object.fromEntries(
                    Object.keys(pdiDefault.layout.blocks).map(k => [k, { used: usedSet.has(k) }])
                );

                // Container states (JSON-coerced). Modal sections map to
                // legacy keys via a short table:
                //   productDetails ↔ details, variants ↔ variants,
                //   relatedProducts ↔ related, articleInfo ↔ information,
                //   washInstructions ↔ washAndCare, stockByStore ↔ storeStock
                const sectionsMap = {
                    productDetails:   'details',
                    variants:         'variants',
                    relatedProducts:  'related',
                    articleInfo:      'information',
                    washInstructions: 'washAndCare',
                    stockByStore:     'storeStock',
                };
                const readStates = (raw, fallback) => {
                    const obj = (raw && typeof raw === 'object') ? raw : {};
                    return Object.fromEntries(
                        Object.entries(sectionsMap).map(([modalK, legacyK]) =>
                            [modalK, obj[legacyK] !== undefined ? !!obj[legacyK] : !!fallback[modalK]]
                        )
                    );
                };

                // Variants display: legacy DETAILS_COLOR_DISPLAY only knows
                // 'color' (chips) and 'image'. The modal extends with 3 more
                // values, so round-trip raw and only translate the legacy pair.
                const legacyToModalVariant = (v) => v === 'image' ? 'colorImage'
                                              : v === 'color' ? 'colorChips'
                                              : (v || pdiDefault.related.variantsDisplay);

                // Details list — SHOW_FIELDS_ON_DETAIL_PAGE is a flat CSV
                // of legacy keys. Whichever group a key lives in modal-side,
                // the underlying CSV stays flat; load splits the CSV into
                // the same three groups by key.
                const fieldsCsv = String(s.SHOW_FIELDS_ON_DETAIL_PAGE || '').trim();
                const fieldsSet = new Set(
                    fieldsCsv ? fieldsCsv.split(',').map(k => k.trim()).filter(Boolean) : []
                );
                const readGroup = (groupDefault) => Object.fromEntries(
                    Object.keys(groupDefault).map(k => [k, fieldsSet.has(k)])
                );

                const loadedPdi = {
                    ...pdiDefault,
                    layout: {
                        blocks: loadedBlocks,
                        order:  modalOrder.length ? modalOrder : pdiDefault.layout.order,
                    },
                    detailsList: fieldsCsv ? {
                        identity:     readGroup(pdiDefault.detailsList.identity),
                        categories:   readGroup(pdiDefault.detailsList.categories),
                        descriptions: readGroup(pdiDefault.detailsList.descriptions),
                    } : pdiDefault.detailsList,
                    sectionsOpen: {
                        desktop: readStates(s.PRODUCT_DETAILS_CONTAINER_STATES, pdiDefault.sectionsOpen.desktop),
                        mobile:  readStates(s.PRODUCT_MOBILE_CONTAINER_STATES,  pdiDefault.sectionsOpen.mobile),
                    },
                    related: {
                        maxToShow:         readNum (s.PRODUCT_RELATED_ARTICLES_AMOUNT,    pdiDefault.related.maxToShow),
                        variantsDisplay:   legacyToModalVariant(s.DETAILS_COLOR_DISPLAY),
                        showOutOfStock:    readBool(s.DETAILS_COLOR_SHOW_OUT_OF_STOCK,    pdiDefault.related.showOutOfStock),
                        highlightSelected: readBool(s.DETAILS_COLOR_DISPLAY_BORDER,       pdiDefault.related.highlightSelected),
                    },
                    stock: {
                        showStockByStore:    readBool(s.SHOW_AVAILABLE_STOCK_STORES, pdiDefault.stock.showStockByStore),
                        hideWhenSizeUnknown: readBool(s.HIDE_STORE_IF_NO_RECORD,     pdiDefault.stock.hideWhenSizeUnknown),
                    },
                    // Title & URL slots — three free-form templates parse
                    // into mixed token+text chip arrays; URL slug is CSV-
                    // only and only carries token chips.
                    titleAndUrl: {
                        browserTab:     { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_TAB) },
                        pageHeading:    { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_TITLE) },
                        pageSubheading: { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_SUBHEADING) },
                        urlSlug:        { used: parseCsvToChips    (s.DETAIL_PAGE_URL_SECTION) },
                    },
                    // Breadcrumb — CSV of legacy field keys from
                    // DETAIL_PAGE_NAV_SECTION. Unknown keys are dropped.
                    breadcrumb: {
                        used: String(s.DETAIL_PAGE_NAV_SECTION || '')
                            .split(',').map(k => k.trim()).filter(Boolean),
                    },
                };
                setProductDetailInfoConfig(loadedPdi);
                originalProductDetailInfoConfigRef.current = loadedPdi;

                // Carousel slides — `s.carousel` already lives in the
                // /admin/settings dump (one row per slide, with `lang` for
                // language scoping). Group by lang and translate the row
                // shape to what the modal expects. `serverId` tracks the
                // backend row id so save can diff which slides need POST/
                // PATCH/DELETE.
                //
                // Image URLs: the mini-api hands back a root-relative
                // `carouselImageUrl` (e.g. `/docroot/img-repo/home-gallery/`)
                // which works on the storefront's own domain but not in this
                // admin app. We prefix it with the webshop base (stripped of
                // the `/server/v1/mini-api` suffix) so <img> tags resolve.
                const carouselRows = Array.isArray(s.carousel) ? s.carousel : [];
                // Same protocol-normalisation as the header logo (display_name
                // often lacks https://) so carousel/grid image previews resolve.
                const webshopBase  = shopImageBase(props.selectedWebshop?.display_name);
                const carouselUrlBase = s.carouselImageUrl || '';
                carouselImgBaseRef.current = `${webshopBase}${carouselUrlBase}`;
                const buildImage = (filename, mime) => filename
                    ? { name: filename, url: `${webshopBase}${carouselUrlBase}${filename}`, mime: mime || '' }
                    : null;
                const rowToSlide = (r, idx) => ({
                    id:               `s_${r.id}`,
                    serverId:         r.id,
                    order:            r.order != null ? Number(r.order) : (idx + 1),
                    active:           !!Number(r.active),
                    desktopImage:     buildImage(r.image,        r.mime_type),
                    alternativeImage: buildImage(r.second_image, r.second_mime_type),
                    linkUrl:          r.link         || '',
                    hoverDesktop:     r.text_desktop || '',
                    hoverMobile:      r.text_mobile  || '',
                });
                const byLang = (lang) => carouselRows
                    .filter(r => String(r.lang || '').toLowerCase() === lang)
                    .map(rowToSlide);
                // Carousel settings — PER LANGUAGE since 2026-06-04. Each
                // language has its own HOME_GALLERY_CONFIG_{L} JSON +
                // STRETCH_/GALLERY_HOVER_ scalar keys +
                // HOME_GALLERY_OVERLAY_CONFIG_{L}; the modal's language pills
                // now apply to the Settings tab too. `settings` is keyed
                // { nl, en, fr }.
                const carouselDefault = DEFAULT_CAROUSEL_CONFIG.settings.nl;
                // Position is space-separated on the backend ('bottom center')
                // vs hyphen in the modal; 'center' (no axis) is the legacy
                // shorthand for 'middle center'.
                const carPositionFromBackend = (p, fb) => {
                    if (typeof p !== 'string' || !p) return fb;
                    if (p === 'center') return 'middle-center';
                    if (p.includes(' ')) return p.replace(/\s+/g, '-');
                    return p;
                };
                const carReadDevice = (raw, fb) => {
                    const o = raw || {};
                    return {
                        background: o.background ?? fb.background,
                        textColor:  o.color      ?? fb.textColor,
                        position:   carPositionFromBackend(o.position, fb.position),
                        // backend fontSize = font size; backend size = overlay band height %.
                        size:       o.fontSize ?? fb.size,
                        width:      o.size     ?? fb.width,
                        bold:       !!o.styleBold,
                        italic:     !!o.styleItalic,
                        underline:  !!o.styleLined,
                        strike:     !!o.styleStrike,
                    };
                };
                // L = 'NL' | 'EN' | 'FR' (settings-key suffix).
                const parseCarouselLang = (L) => {
                    const galleryConfig = (s[`HOME_GALLERY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_CONFIG_${L}`] === 'object')
                        ? s[`HOME_GALLERY_CONFIG_${L}`]
                        : {};
                    // The storefront reads the caption overlay from the SEPARATE
                    // HOME_GALLERY_OVERLAY_CONFIG_{L} key (StartupController merges
                    // it in as `overlayConfig`); fall back to the legacy inline spot.
                    const overlayRaw = (s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] === 'object')
                        ? s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`]
                        : (galleryConfig.overlayConfig || {});
                    const deskRaw = overlayRaw.desktop || {};
                    // margin = [top, bottom] (mig164).
                    const margin = Array.isArray(galleryConfig.margin) ? galleryConfig.margin : null;
                    return {
                        // Raw JSONs kept per language so save can merge tier-1
                        // values without dropping unknown legacy fields.
                        __rawGalleryConfig: galleryConfig,
                        __rawOverlayConfig: overlayRaw,
                        display: {
                            ...carouselDefault.display,
                            fullWidth:     readBool(s[`STRETCH_GALLERY_FULL_WIDTH_${L}`], carouselDefault.display.fullWidth),
                            maxHeight:     galleryConfig.maxHeight != null ? Number(galleryConfig.maxHeight) : carouselDefault.display.maxHeight,
                            maxHeightUnit: galleryConfig.maxHeightUnit || carouselDefault.display.maxHeightUnit,
                            spacingTop:    margin ? (Number(margin[0]) || 0) : carouselDefault.display.spacingTop,
                            spacingBottom: margin ? (Number(margin[1]) || 0) : carouselDefault.display.spacingBottom,
                            imageFit:      galleryConfig.imageSize || carouselDefault.display.imageFit,
                        },
                        hover: {
                            ...carouselDefault.hover,
                            effect:         s[`GALLERY_HOVER_ANIM_TYPE_${L}`] || carouselDefault.hover.effect,
                            zoomLevel:      readNum(s[`GALLERY_HOVER_ANIM_ZOOM_SCALE_${L}`], carouselDefault.hover.zoomLevel),
                            animationSpeed: s[`GALLERY_HOVER_ANIM_ZOOM_SPEED_${L}`] != null
                                ? Number(s[`GALLERY_HOVER_ANIM_ZOOM_SPEED_${L}`])
                                : carouselDefault.hover.animationSpeed,
                        },
                        behavior: {
                            ...carouselDefault.behavior,
                            showArrows:       galleryConfig.showControls           != null ? !!galleryConfig.showControls           : carouselDefault.behavior.showArrows,
                            arrowsOnHover:    galleryConfig.showControlsOnHover    != null ? !!galleryConfig.showControlsOnHover    : carouselDefault.behavior.arrowsOnHover,
                            arrowsBackground: galleryConfig.showControlsBackground != null ? !!galleryConfig.showControlsBackground : carouselDefault.behavior.arrowsBackground,
                            autoAdvance:      galleryConfig.autoSlide              != null ? !!galleryConfig.autoSlide              : carouselDefault.behavior.autoAdvance,
                            // Interval is in ms backend-side, seconds in the modal.
                            // Per-lang slideInterval (storefront reads the per-lang
                            // JSON) with the legacy global key as fallback.
                            timePerSlide:     (galleryConfig.slideInterval != null
                                ? Number(galleryConfig.slideInterval)
                                : readNum(s.HOME_GALLERY_INTERVAL, carouselDefault.behavior.timePerSlide * 1000)) / 1000,
                            transition:       galleryConfig.animation || carouselDefault.behavior.transition,
                        },
                        textOverlay: {
                            desktop: carReadDevice(deskRaw,           carouselDefault.textOverlay.desktop),
                            mobile:  carReadDevice(overlayRaw.mobile, carouselDefault.textOverlay.mobile),
                            onlyOnHover:        !!deskRaw.onlyOnHover,
                            alwaysVisible:      !!deskRaw.keepTextDisplayed,
                            useDesktopOnMobile: !!deskRaw.useDesktopTextFallback,
                        },
                    };
                };
                const loadedCarousel = {
                    ...DEFAULT_CAROUSEL_CONFIG,
                    slides:   { nl: byLang('nl'), en: byLang('en'), fr: byLang('fr') },
                    settings: { nl: parseCarouselLang('NL'), en: parseCarouselLang('EN'), fr: parseCarouselLang('FR') },
                };
                setCarouselConfig(loadedCarousel);
                originalCarouselConfigRef.current = loadedCarousel;

                // Image grid cells — same shape as carousel, different
                // image directory (`image-grid/` instead of `home-gallery/`).
                // The mini-api doesn't expose a separate `imageGridImageUrl`,
                // so we compose `${webshopBase}/${img_path}/image-grid/` from
                // the same pieces.
                const gridRows = Array.isArray(s.imageGrid) ? s.imageGrid : [];
                const gridUrlBase = s.imageGridImageUrl || `${s.img_path || ''}/image-grid/`;
                gridImgBaseRef.current = `${webshopBase}${gridUrlBase}`;
                const buildGridImage = (filename, mime) => filename
                    ? { name: filename, url: `${webshopBase}${gridUrlBase}${filename}`, mime: mime || '' }
                    : null;
                const rowToCell = (r, idx) => ({
                    id:               `c_${r.id}`,
                    serverId:         r.id,
                    order:            r.order != null ? Number(r.order) : (idx + 1),
                    active:           !!Number(r.active),
                    // Storefront's <mat-grid-tile> uses these as [colspan]/[rowspan]
                    // (home.component.html). Defaults to 1×1 when the row is
                    // missing them (older records pre-dating the column).
                    width:            Math.max(1, Number(r.width)  || 1),
                    height:           Math.max(1, Number(r.height) || 1),
                    desktopImage:     buildGridImage(r.image,        r.mime_type),
                    alternativeImage: buildGridImage(r.second_image, r.second_mime_type),
                    linkUrl:          r.link         || '',
                    hoverDesktop:     r.text_desktop || '',
                    hoverMobile:      r.text_mobile  || '',
                });
                const gridByLang = (lang) => gridRows
                    .filter(r => String(r.lang || '').toLowerCase() === lang)
                    .map(rowToCell);
                // Image grid settings — same canonical-NL approach as
                // carousel. Layout split between scalar per-lang keys
                // (IMAGE_GRID_COLS_NL, IMAGE_GRID_GUTTER_NL) and a JSON
                // blob (IMAGE_GRID_CONFIG_NL).
                const gridDefault    = DEFAULT_IMAGE_GRID_CONFIG.settings.nl;
                // Overlay caption — mig166 added `overlay.desktop` /
                // `overlay.mobile` sub-objects to IMAGE_GRID_CONFIG_{lang}.
                // Modal has matching dual editors (same shape as carousel).
                // Position uses space-separated tokens on the backend
                // ('bottom center', 'middle center', …) vs hyphen in the
                // modal ('bottom-center', 'middle-center', …) — storefront's
                // home.component.html does `.includes('top ')` matching, so
                // the space format is mandatory on save. A bare 'center'
                // (no axis) was a pre-mig166 shorthand for 'middle center';
                // alias it so old configs land on the correct dropdown option.
                const positionFromBackend = (p, fb) => {
                    if (typeof p !== 'string' || !p) return fb;
                    if (p === 'center') return 'middle-center';
                    if (p.includes(' ')) return p.replace(/\s+/g, '-');
                    return p;
                };
                const readDevice = (raw, fb) => {
                    const o = raw || {};
                    return {
                        background: o.background ?? fb.background,
                        textColor:  o.color      ?? fb.textColor,
                        position:   positionFromBackend(o.position, fb.position),
                        // Backend `fontSize` = font size; backend `size` = overlay band
                        // height %. Modal calls those `size` and `width` respectively.
                        size:       o.fontSize ?? fb.size,
                        width:      o.size     ?? fb.width,
                        bold:       !!o.styleBold,
                        italic:     !!o.styleItalic,
                        underline:  !!o.styleLined,
                        strike:     !!o.styleStrike,
                    };
                };
                // PER LANGUAGE since 2026-06-04 — every key already had per-lang
                // variants (IMAGE_GRID_COLS_{L}, GRID_HOVER_*_{L}, KEEP_GRID_{L},
                // IMAGE_GRID_CONFIG_{L}); we now read/write each independently.
                const parseGridLang = (L) => {
                    const gridJson = (s[`IMAGE_GRID_CONFIG_${L}`] && typeof s[`IMAGE_GRID_CONFIG_${L}`] === 'object')
                        ? s[`IMAGE_GRID_CONFIG_${L}`]
                        : {};
                    const rawOverlay = gridJson.overlay || {};
                    const desk       = rawOverlay.desktop || {};
                    // Margin: IMAGE_GRID_CONFIG_{lang}.margin = [top, bottom] (mig164).
                    const marginArr  = Array.isArray(gridJson.margin)
                        ? gridJson.margin
                        : gridDefault.layout.margin;
                    return {
                        __rawGridConfig: gridJson,
                        layout: {
                            ...gridDefault.layout,
                            columns:          readNum(s[`IMAGE_GRID_COLS_${L}`],   gridDefault.layout.columns),
                            gap:              readNum(s[`IMAGE_GRID_GUTTER_${L}`], gridDefault.layout.gap),
                            // KEEP_GRID_ON_MOBILE is per-lang (mig158). Default true
                            // per mig112 ('true' string). readBool handles 'true'/1/'1'.
                            keepGridOnMobile: readBool(s[`IMAGE_GRID_KEEP_GRID_ON_MOBILE_${L}`], gridDefault.layout.keepGridOnMobile),
                            margin:           [Number(marginArr[0]) || 0, Number(marginArr[1]) || 0],
                        },
                        hover: {
                            ...gridDefault.hover,
                            effect:         s[`GRID_HOVER_ANIM_TYPE_${L}`] || gridDefault.hover.effect,
                            zoomLevel:      readNum(s[`GRID_HOVER_ANIM_ZOOM_SCALE_${L}`], gridDefault.hover.zoomLevel),
                            animationSpeed: s[`GRID_HOVER_ANIM_ZOOM_SPEED_${L}`] != null
                                ? Number(s[`GRID_HOVER_ANIM_ZOOM_SPEED_${L}`])
                                : gridDefault.hover.animationSpeed,
                        },
                        textOverlay: {
                            desktop: readDevice(desk,              gridDefault.textOverlay.desktop),
                            mobile:  readDevice(rawOverlay.mobile, gridDefault.textOverlay.mobile),
                            // Shared flags only live on the desktop sub-object
                            // backend-side; modal exposes them as top-level.
                            onlyOnHover:        !!desk.onlyOnHover,
                            alwaysVisible:      !!desk.keepTextDisplayed,
                            useDesktopOnMobile: !!desk.useDesktopTextFallback,
                        },
                    };
                };
                const loadedImageGrid = {
                    ...DEFAULT_IMAGE_GRID_CONFIG,
                    cells:    { nl: gridByLang('nl'), en: gridByLang('en'), fr: gridByLang('fr') },
                    settings: { nl: parseGridLang('NL'), en: parseGridLang('EN'), fr: parseGridLang('FR') },
                };
                setImageGridConfig(loadedImageGrid);
                originalImageGridConfigRef.current = loadedImageGrid;
            } catch (e) {
                if (!cancelled) setConfigError({ message: e?.message || 'Network error', code: 'error_network' });
            } finally {
                if (!cancelled) setConfigLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, props.login?.sft_user_id, props.login?.login, props.selectedWebshop?.id]);

    // Build the order string from the current `active` list. IDs were
    // populated with canonical casing (`carousel`, `imageGrid`, `pd_cnt-<id>`)
    // when we built the catalog, so a plain join produces the storefront's
    // expected HOME_SECTIONS_ORDER format.
    const desiredHomeSectionsOrder = active.map(b => b.id).join(',');
    const orderDirty = desiredHomeSectionsOrder !== homeSectionsOrder;
    const desiredPage404Order = active404.map(b => b.id).join(',');
    const order404Dirty = desiredPage404Order !== page404SectionsOrder;

    // After a successful save we want the storefront's static caches
    // regenerated so visitors see the new values without a full sync. Mirrors
    // the legacy admin's behaviour and the iframe-fire pattern used in the
    // Actions tab. Best-effort: if the legacy script returns an error we
    // surface it but the save itself still counts as successful.
    // `detail` (e.g. '__generateCarousel') runs that ONE generator with
    // force=true — matches the legacy admin, which fires
    // `?detail=__generateCarousel` after a carousel image upload. Without it,
    // the plain run calls __generateCarousel(false), which only rewrites
    // carousel.json when the row changed in the last 11 min — and an image
    // upload's UPDATE doesn't bump `updated_at`, so the new image wouldn't show
    // until a forced regen. `silent` suppresses the toast for piggy-backed runs.
    const fireGenerateStatics = (detail = null, { silent = false } = {}) => {
        const base = shopImageBase(props.selectedWebshop?.display_name);
        if (!base) return;
        // The cron lives under server/v1/crons/, NOT at the domain root —
        // `base` has the `/server/v1/mini-api` suffix stripped, so we re-add
        // `/server/v1`. Matches the legacy admin's exact request:
        // {base}/server/v1/crons/generate_statics.php?lng=nl&__cb_=...
        const query   = detail ? `detail=${encodeURIComponent(detail)}` : 'lng=nl';
        const fullUrl = `${base}/server/v1/crons/generate_statics.php?${query}&__cb_=${Date.now()}`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        let done = false;
        const finish = (ok, msg) => {
            if (done) return;
            done = true;
            if (!silent && props.notify) props.notify(msg, ok ? 4000 : 6000, 'top-right', props.theme, ok ? 2 : 1);
            setTimeout(() => iframe.remove(), 100);
        };
        iframe.onload  = () => finish(true,  'Statics regenerated');
        iframe.onerror = () => finish(false, 'Statics regeneration failed');
        // Most storefront caches finish well under 60s; give it up to that
        // before reporting a timeout so the user knows to retry manually.
        setTimeout(() => finish(false, 'Statics regeneration timed out'), 60000);
        iframe.src = fullUrl;
        document.body.appendChild(iframe);
    };

    const saveConfig = async () => {
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) {
            if (props.notify) props.notify('No webshop selected', 4000, 'top-right', props.theme, 1);
            return;
        }
        if (!orderDirty && !productGridDirty && !productCardDirty && !styleSettingsDirty &&
            !groupsFilterDirty && !globalWidthsDirty && !headerLogoDirty && !navigationDirty &&
            !footerDirty && !extraFooterDirty && !imageGalleryDirty && !productDetailInfoDirty &&
            !carouselDirty && !imageGridDirty && !staticPagesDirty && !storesDirty && !separatorDirty && !mapSettingsDirty && !cardsDirty && !brandsConfigDirty && !dynamicPagesDirty && !bannersDirty && !looksConfigDirty && !overigeConfigDirty && !cadeaubonConfigDirty && !cadeaubonPlaceholdersDirty && !cadeaubonLayoutsDirty && !processingConfigDirty && !customerGroupsDirty && !syncConfigDirty && !order404Dirty && !navbarOrderDirty) {
            if (props.notify) props.notify('No changes to save', 3000, 'top-right', props.theme, 3);
            return;
        }
        setConfigSaving(true);
        try {
            // Build a single list of PATCH calls so all changes (HOME_SECTIONS_ORDER,
            // grid settings, future modal saves) ride the same proxy and the same
            // dirty-reset path. `b` for boolean → '1'/'0' coercion mirrors the
            // mini-api's expected string shape.
            const b = (v) => (v ? '1' : '0');
            const patches = [];
            // Set when the look CTA i18n string changes — triggers a localization
            // regenerate after the PATCH so the storefront JSON files update.
            let looksCtaSaved = false;
            // Set when a changed guest customer id fails validation — its setting
            // is then left out of the save (the rest still saves).
            let guestIdSkipped = false;
            if (orderDirty) {
                patches.push({
                    kind:  'home-order',
                    path:  '/admin/settings/HOME_SECTIONS_ORDER',
                    value: desiredHomeSectionsOrder,
                });
            }
            if (order404Dirty) {
                patches.push({
                    kind:  'page404-order',
                    path:  '/admin/settings/PAGE_404_SECTIONS_ORDER',
                    value: desiredPage404Order,
                });
            }
            if (productGridDirty) {
                const g  = productGridConfig.productGrid;
                const v  = productGridConfig.variantGrouping;
                const og = originalProductGridConfigRef.current.productGrid;
                const ov = originalProductGridConfigRef.current.variantGrouping;
                // Modal uses 'above'/'below'/'both'; backend expects
                // 'top'/'bottom'/'top-and-bottom'.
                const paginationToBackend = (vv) =>
                    vv === 'above' ? 'top' :
                    vv === 'both'  ? 'top-and-bottom' :
                    'bottom';
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'grid', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                push('MAX_COLUMNS_EXPLORE',          g.maxColumns,                                  og.maxColumns);
                push('EXPLORE_PAGINATION_POSITION',  paginationToBackend(g.paginationPosition),     paginationToBackend(og.paginationPosition));
                push('EXPLORE_SHOW_TOTALS',          b(g.showTotalProducts),                        b(og.showTotalProducts));
                // showCardOutline is a frontend-only future-feature flag —
                // no backend key, never patched.
                push('EXPLORE_GROUP_VARIANTS',       b(v.groupAsOne),                               b(ov.groupAsOne));
                push('EXPLORE_SHOW_PRODUCT_VARIANTS',b(v.showSwatches),                             b(ov.showSwatches));
                push('PRODUCT_SHOW_VARIANTS_AMOUNT', v.maxVariants,                                 ov.maxVariants);
            }
            if (productCardDirty) {
                const c  = productCardConfig;
                const oc = originalProductCardConfigRef.current;
                // line composition: chips → space-separated values per line,
                // joined by ';'. A token chip serialises to its field key, a
                // text chip to its literal text — exactly what the storefront
                // template renderer expects.
                const chipToToken = (c) => (c.text !== undefined ? c.text : c.key) || '';
                const linesToCsv = (cfg) => [
                    (cfg.productInfo?.line1 || []).map(chipToToken).filter(Boolean).join(' '),
                    (cfg.productInfo?.line2 || []).map(chipToToken).filter(Boolean).join(' '),
                    (cfg.productInfo?.line3 || []).map(chipToToken).filter(Boolean).join(' '),
                ].join(';');
                const animTypeToBackend = (vv) =>
                    vv === 'fadeAlt' ? 'second_image' :
                    vv === 'zoom'    ? 'zoom'         :
                    'zoom';
                const aspectStr = (cfg) => `${cfg.image.aspectWidth}/${cfg.image.aspectHeight}`;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'card', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                push('PRODUCT_DISPLAY_INFO',          linesToCsv(c),                          linesToCsv(oc));
                push('PRODUCT_SHOW_DIVIDER',          b(c.productInfo.showDivider),           b(oc.productInfo.showDivider));
                push('PRODUCT_DETAILS_ALIGNMENT',     c.productInfo.align,                    oc.productInfo.align);
                push('EXPLORE_USE_SECOND_IMAGE',      b(c.image.useAlternativeAsMain),        b(oc.image.useAlternativeAsMain));
                push('EXPLORE_USE_IMAGE_FIT',         b(c.image.fillsCard),                   b(oc.image.fillsCard));
                push('EXPLORE_USE_IMAGE_FIT_RATIO',   aspectStr(c),                           aspectStr(oc));
                push('PRODUCT_IMG_RADIUS',            c.image.cornerRounding,                 oc.image.cornerRounding);
                push('ENABLE_PRODUCT_ITEM_ANIMATION', b(c.image.animateOnHover),              b(oc.image.animateOnHover));
                push('PRODUCT_HOVER_ANIM_TYPE',       animTypeToBackend(c.image.animationType), animTypeToBackend(oc.image.animationType));
                push('PRODUCT_HOVER_SIZES',           b(c.sizePreview.enabled),               b(oc.sizePreview.enabled));
                push('PRODUCT_SIZE_OVERLAY_POSITION', c.sizePreview.position,                 oc.sizePreview.position);
                push('PRODUCT_SIZE_OVERLAY_BGC',      c.sizePreview.badgeBg,                  oc.sizePreview.badgeBg);
                push('PRODUCT_SIZE_OVERLAY_FGC',      c.sizePreview.badgeText,                oc.sizePreview.badgeText);
                push('PRODUCT_SIZE_OVERLAY_SIZE',     c.sizePreview.textSize,                 oc.sizePreview.textSize);
                push('PRODUCT_SIZE_SIZE_BORDER',      b(c.sizePreview.showBorder),            b(oc.sizePreview.showBorder));
                // PRODUCT_HOVER_MAX_SIZES — CSV "Y,X". The modal edits Y
                // (lengtematen / index [0] = number of sizes in the hover
                // badge); X (breedtematen / index [1]) was loaded into
                // productHoverMaxWidthsRef and is round-tripped untouched.
                {
                    const x = productHoverMaxWidthsRef.current;
                    const csvNow  = `${c.sizePreview.maxShown},${x}`;
                    const csvOrig = `${oc.sizePreview.maxShown},${x}`;
                    push('PRODUCT_HOVER_MAX_SIZES', csvNow, csvOrig);
                }
            }
            // Google-font loader writes (POST /admin/generate-font) — own op,
            // ride the shared dispatch below.
            const fontGenerateOps = [];
            if (styleSettingsDirty && originalStyleSettingsRef.current) {
                const orig = originalStyleSettingsRef.current;
                for (const p of STYLE_COLOR_PATHS) {
                    const cur  = readByPath(styleSettings,    p.path);
                    const oCol = readByPath(orig,             p.path);
                    if (cur !== oCol) {
                        patches.push({ kind: 'style', path: `/admin/settings/${p.setting}`, value: String(cur) });
                    }
                }
                // Font — system stack vs Google font. DEFAULT_FONTS hold the
                // system CSS stacks; anything else is a Google font name.
                const curFont = styleSettings.global?.font;
                if (curFont !== orig.global?.font) {
                    const isSystem = DEFAULT_FONTS.some(f => f.family === curFont);
                    if (isSystem) {
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY_USE_GOOGLE', value: '0' });
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY_GOOGLE',     value: '' });
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY',            value: String(curFont) });
                        fontGenerateOps.push({ kind: 'font', method: 'POST', path: '/admin/generate-font', body: { font: '' } });
                    } else {
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY_USE_GOOGLE', value: '1' });
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY_GOOGLE',     value: String(curFont) });
                        patches.push({ kind: 'style', path: '/admin/settings/FONT_FAMILY',            value: String(curFont) });
                        fontGenerateOps.push({ kind: 'font', method: 'POST', path: '/admin/generate-font', body: { font: String(curFont) } });
                    }
                }
                // Visibility toggles + back-to-top position.
                const v = styleSettings.visibility || {}, ov = orig.visibility || {};
                if (b(v.languageBar) !== b(ov.languageBar)) {
                    patches.push({ kind: 'style', path: '/admin/settings/SHOW_LANGUAGE_BAR', value: b(v.languageBar) });
                }
                if (b(v.footer) !== b(ov.footer)) {
                    patches.push({ kind: 'style', path: '/admin/settings/SHOW_FOOTER', value: b(v.footer) });
                }
                if (toLangCsv(v.visibleLanguages) !== toLangCsv(ov.visibleLanguages)) {
                    patches.push({ kind: 'style', path: '/admin/settings/VISIBILE_LANGUAGES', value: toLangCsv(v.visibleLanguages) });
                }
                if (toLangCsv(v.adminLanguages) !== toLangCsv(ov.adminLanguages)) {
                    patches.push({ kind: 'style', path: '/admin/settings/ADMIN_LANGUAGES', value: toLangCsv(v.adminLanguages) });
                }
                if (v.backToTopPosition !== ov.backToTopPosition) {
                    const bttMap = { left: 'CIRCLE_LEFT', center: 'CIRCLE_MIDDLE', right: 'CIRCLE_RIGHT' };
                    patches.push({ kind: 'style', path: '/admin/settings/BACK_TO_TOP_BUTTON', value: bttMap[v.backToTopPosition] || 'CIRCLE_RIGHT' });
                }
                // Custom-hover toggle (the 4 hover colours themselves ride the
                // STYLE_COLOR_PATHS loop above).
                const mCur = styleSettings.sections?.menu || {}, mOrig = orig.sections?.menu || {};
                if (b(mCur.useCustomHover) !== b(mOrig.useCustomHover)) {
                    patches.push({ kind: 'style', path: '/admin/settings/COLOR_USE_CUSTOM_HOVER', value: b(mCur.useCustomHover) });
                }
            }
            if (groupsFilterDirty) {
                const g  = groupsFilterConfig;
                const og = originalGroupsFilterConfigRef.current;
                // 'topbar'/'sidebar' (modal) ↔ 'top'/'side' (backend).
                const wherefiltersToBackend = (v) => v === 'topbar' ? 'top' : 'side';
                // Modal field → legacy sort-rule field. Inverse of the load-
                // time mapping; createdAt has no backend equivalent so we
                // drop it from the saved value (it'd otherwise become 'id'
                // which would silently override).
                const sortFieldToBackend = (f) => ({
                    price:           'price',
                    genderPriority:  'category_1_prio',
                    typePriority:    'category_2_prio',
                    id:              'id',
                    discount:        'discount_perc',
                    stock:           'total_stock',
                }[f] || null);
                const ruleToCsv = (rules) => (rules || [])
                    .map(r => {
                        const mapped = sortFieldToBackend(r.field);
                        if (!mapped) return null;
                        return r.direction === 'desc' ? `-${mapped}` : mapped;
                    })
                    .filter(Boolean)
                    .join(',');
                // SIDE_FILTER_SLICE / SIDE_FILTER_STATES are JSON-coerced on
                // the mini-api end, so we send the JS object as-is in the
                // body's `value` slot — the mini-api JSON.stringify's it.
                const sliceFromCfg  = (cfg) => Object.fromEntries(
                    (cfg.activeFilters || []).map(fk => [fk, (cfg.filterSettings?.[fk]?.itemsBeforeMore ?? '')])
                );
                const statesFromCfg = (cfg) => Object.fromEntries(
                    (cfg.activeFilters || []).map(fk => [fk, !!(cfg.filterSettings?.[fk]?.expandedByDefault)])
                );
                const push = (key, current, original) => {
                    // For JSON values, stringify both sides for an honest
                    // compare; the patcher will re-encode anyway.
                    const c = typeof current  === 'object' ? JSON.stringify(current)  : String(current);
                    const o = typeof original === 'object' ? JSON.stringify(original) : String(original);
                    if (c !== o) {
                        patches.push({ kind: 'groups', path: `/admin/settings/${key}`, value: current });
                    }
                };
                // Reverse the load-time mapping for COLOR_FILTER_DISPLAY.
                const colorStyleToBackend = (v) =>
                    v === 'swatches' ? 'color' :
                    v === 'names'    ? 'text'  :
                    'color';
                push('FILTER_POSITION',          wherefiltersToBackend(g.layout.wherefilters), wherefiltersToBackend(og.layout.wherefilters));
                push('FILTER_SIZE_STYLE',        g.layout.sizeStyle,                            og.layout.sizeStyle);
                push('COLOR_FILTER_DISPLAY',     colorStyleToBackend(g.layout.colorStyle),      colorStyleToBackend(og.layout.colorStyle));
                push('FILTER_TOP_SPLIT_SIZES',   b(g.layout.splitWidthLength),                  b(og.layout.splitWidthLength));
                push('SIDE_FILTER_SHOW_COUNTERS',b(g.layout.showProductCount),                  b(og.layout.showProductCount));
                push('FILTER_HOVER',             g.layout.hoverStyle,                           og.layout.hoverStyle);
                push('ORDER_SIDE_FILTER_BY',     g.sortFiltersBy || 'NAME',                     og.sortFiltersBy || 'NAME');
                push('SIDE_FILTER_ORDER',        (g.activeFilters || []).join(','),             (og.activeFilters || []).join(','));
                push('SIDE_FILTER_SLICE',        sliceFromCfg(g),                               sliceFromCfg(og));
                push('SIDE_FILTER_STATES',       statesFromCfg(g),                              statesFromCfg(og));
                push('FILTER_TREE_LEVELS',       (g.mobileHierarchy || []).join(','),           (og.mobileHierarchy || []).join(','));
                push('EXPLORE_SORT_ORDER',       (g.sortOptions || []).join(','),               (og.sortOptions || []).join(','));
                push('EXPLORE_SORT_DEFAULT_RULE',ruleToCsv(g.defaultSortRule),                  ruleToCsv(og.defaultSortRule));
            }
            if (globalWidthsDirty) {
                const w  = globalWidths;
                const ow = originalGlobalWidthsRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'widths', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                push('SHOP_MAX_WIDTH_COMPLETE',         b(w.applyToWholeSite),                b(ow.applyToWholeSite));
                push('SHOP_MAX_WIDTH',                  w.productPagesWidth,                  ow.productPagesWidth);
                // New-admin-only key — the mini-api PATCH creates the row on
                // first save (creatable whitelist).
                push('BUTTON_BORDER_RADIUS',            w.buttonBorderRadius,                 ow.buttonBorderRadius);
                push('SHOP_MAX_WIDTH_TOP',              w.perElement?.languageBar?.width,     ow.perElement?.languageBar?.width);
                push('SHOP_MAX_WIDTH_TOP_FW_BG',        b(w.perElement?.languageBar?.bgFullWidth), b(ow.perElement?.languageBar?.bgFullWidth));
                push('SHOP_MAX_WIDTH_HEADER',           w.perElement?.header?.width,          ow.perElement?.header?.width);
                push('SHOP_MAX_WIDTH_HEADER_FW_BG',     b(w.perElement?.header?.bgFullWidth), b(ow.perElement?.header?.bgFullWidth));
                push('SHOP_MAX_WIDTH_BANNERS',          w.perElement?.infoBannerBelow?.width, ow.perElement?.infoBannerBelow?.width);
                push('SHOP_MAX_WIDTH_BANNERS_FW_BG',    b(w.perElement?.infoBannerBelow?.bgFullWidth), b(ow.perElement?.infoBannerBelow?.bgFullWidth));
                push('SHOP_MAX_WIDTH_BANNERS_TOP',      w.perElement?.infoBannerTop?.width,   ow.perElement?.infoBannerTop?.width);
                push('SHOP_MAX_WIDTH_BANNERS_TOP_FW_BG',b(w.perElement?.infoBannerTop?.bgFullWidth), b(ow.perElement?.infoBannerTop?.bgFullWidth));
                push('SHOP_MAX_WIDTH_BRANDS',           w.perElement?.brands?.width,          ow.perElement?.brands?.width);
                push('SHOP_MAX_WIDTH_EXPLORE',          w.perElement?.explore?.width,         ow.perElement?.explore?.width);
                push('SHOP_MAX_WIDTH_RECENT',           w.perElement?.recentlyViewed?.width,  ow.perElement?.recentlyViewed?.width);
                push('SHOP_MAX_WIDTH_RECENT_FW_BG',     b(w.perElement?.recentlyViewed?.bgFullWidth), b(ow.perElement?.recentlyViewed?.bgFullWidth));
                push('SHOP_MAX_WIDTH_FOOTER',           w.perElement?.footer?.width,          ow.perElement?.footer?.width);
                push('SHOP_MAX_WIDTH_FOOTER_FW_BG',     b(w.perElement?.footer?.bgFullWidth), b(ow.perElement?.footer?.bgFullWidth));
            }
            if (headerLogoDirty) {
                const cur  = headerLogoConfig.size?.logoBarHeight;
                const orig = originalHeaderLogoConfigRef.current.size?.logoBarHeight;
                if (String(cur) !== String(orig)) {
                    patches.push({ kind: 'header-logo', path: '/admin/settings/HEADER_HEIGHT', value: String(cur) });
                }
            }
            if (navigationDirty) {
                const n  = navigationConfig;
                const on = originalNavigationConfigRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'nav', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                // headerContent toggles
                push('SHOW_HEADER_ABOUTUS', b(n.headerContent.contact),      b(on.headerContent.contact));
                push('SHOW_HEADER_BRANDS',  b(n.headerContent.brands),       b(on.headerContent.brands));
                push('SHOW_HEADER_LOOKS',   b(n.headerContent.shopTheLook),  b(on.headerContent.shopTheLook));
                push('SHOW_SALES_BUTTON',   b(n.headerContent.outlet),       b(on.headerContent.outlet));
                push('SHOW_HEADER_SEARCH',  b(n.headerContent.search),       b(on.headerContent.search));
                push('SHOW_HEADER_ACCOUNT', b(n.headerContent.account),      b(on.headerContent.account));
                push('SHOW_HEADER_CART',    b(n.headerContent.cart),         b(on.headerContent.cart));
                push('SHOW_HEADER_CHEQUES', b(n.headerContent.giftVoucher),  b(on.headerContent.giftVoucher));
                push('HOME_ENABLED',        b(n.headerContent.showHomeButton), b(on.headerContent.showHomeButton));
                push('HOME_URL',            n.headerContent.homeUrl || '',    on.headerContent.homeUrl || '');
                push('DEFAULT_SEARCH_STATE_VISIBLE', b(n.headerContent.searchExpandedByDefault), b(on.headerContent.searchExpandedByDefault));
                // outletMode
                push('MIN_DISCOUNT_FOR_SALES',  n.outletMode.minDiscount,      on.outletMode.minDiscount);
                push('HIDE_SALES_FROM_EXPLORE', b(n.outletMode.hideFromExplore), b(on.outletMode.hideFromExplore));
                // layout
                push('HEADER_TYPE',                  n.layout.menuStyle,                    on.layout.menuStyle);
                push('HEADER_ACCOUNT_POSITION',      n.layout.actionButtonsPosition,        on.layout.actionButtonsPosition);
                push('CENTER_HEADER',                b(n.layout.centerTopHeaderButtons),   b(on.layout.centerTopHeaderButtons));
                push('CENTER_LOGO',                  b(n.layout.centerTopButtonInMiddle),  b(on.layout.centerTopButtonInMiddle));
                push('HEADER_FILTER_SPACE_ACTIONS',  b(n.layout.alignActionButtonsRight),  b(on.layout.alignActionButtonsRight));
                // navigationMenu — legacy NAVBAR_ITEMS_TO_LIST is a single
                // string '0'-'7'. Modal stores categoriesShown as an array,
                // so we serialise the first element (or '0' for "none").
                const navbarCat = (n.navigationMenu.categoriesShown && n.navigationMenu.categoriesShown[0]) || '0';
                const navbarCatOrig = (on.navigationMenu.categoriesShown && on.navigationMenu.categoriesShown[0]) || '0';
                push('NAVBAR_ITEMS_TO_LIST',     navbarCat,                       navbarCatOrig);
                push('HEADER_ITEM_BREAK_AMOUNT', n.navigationMenu.itemsPerRow,    on.navigationMenu.itemsPerRow);
                push('HEADER_FILTER_HEIGHT',     n.navigationMenu.menuHeight,     on.navigationMenu.menuHeight);
                push('HEADER_FILTER_FONT_SIZE',  n.navigationMenu.menuTextSize,   on.navigationMenu.menuTextSize);
                // behaviour
                push('SHOW_HEADER_FILTER_ON_SCROLL', b(n.behaviour.keepMenuVisibleWhileScrolling), b(on.behaviour.keepMenuVisibleWhileScrolling));
            }
            // Navbar filter order — PATCH /admin/navbar-order/{categoryId} per
            // row whose order_index changed (category ids are strings, e.g. '0301').
            const navbarOrderOps = [];
            if (navbarOrderDirty) {
                const origById = Object.fromEntries(originalNavbarOrderRef.current.map(r => [r.id, r]));
                for (const r of navbarOrder) {
                    const o = origById[r.id];
                    if (!o || Number(r.order_index) !== Number(o.order_index)) {
                        navbarOrderOps.push({
                            kind:   'navbar-order',
                            method: 'PATCH',
                            path:   `/admin/navbar-order/${r.id}`,
                            body:   { order_index: Number(r.order_index) || 0 },
                        });
                    }
                }
            }
            if (footerDirty) {
                const f  = footerConfig;
                const of = originalFooterConfigRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'footer', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                push('SHOW_NEWSLETTER',              b(f.newsletter.enabled),                b(of.newsletter.enabled));
                push('NEWSLETTER_POPUP',             b(f.newsletter.showInPopup),            b(of.newsletter.showInPopup));
                push('NEWSLETTER_POPUP_FIRST_VISIT', b(f.newsletter.openPopupAutomatically), b(of.newsletter.openPopupAutomatically));
                // Per-page external link overrides.
                push('SF_LINK_ABOUT',      f.pages?.about      ?? '', of.pages?.about      ?? '');
                push('SF_LINK_TERMS',      f.pages?.terms      ?? '', of.pages?.terms      ?? '');
                push('SF_LINK_PRIVACY',    f.pages?.privacy    ?? '', of.pages?.privacy    ?? '');
                push('SF_LINK_DISCLAIMER', f.pages?.disclaimer ?? '', of.pages?.disclaimer ?? '');
                // Contact info.
                push('SF_INFO_NAME',    f.contact?.shopName    ?? '', of.contact?.shopName    ?? '');
                push('SF_INFO_ADDRESS', f.contact?.addressLine ?? '', of.contact?.addressLine ?? '');
                push('SF_INFO_CITY',    f.contact?.city        ?? '', of.contact?.city        ?? '');
                push('SF_INFO_VAT',     f.contact?.vatNumber   ?? '', of.contact?.vatNumber   ?? '');
                push('SF_INFO_PHONE',   f.contact?.phone       ?? '', of.contact?.phone       ?? '');
                push('SF_INFO_EMAIL',   f.contact?.email       ?? '', of.contact?.email       ?? '');
                push('SF_INFO_WEBSITE', f.contact?.website     ?? '', of.contact?.website     ?? '');
            }

            // External-links CRUD (footer custom links + social media). They
            // share an endpoint and only differ by the `position` column.
            // Pattern mirrors carousel/image-grid: POST for missing serverIds,
            // PATCH for changed rows, DELETE for rows dropped from the list.
            const externalLinkOps = [];
            if (footerDirty) {
                const buildOps = (currentList, originalList, position, labelMode) => {
                    const origById = new Map(
                        originalList.filter(l => l.serverId != null).map(l => [l.serverId, l])
                    );
                    const currentIds = new Set(
                        currentList.map(l => l.serverId).filter(id => id != null)
                    );

                    for (const l of currentList) {
                        const body = {
                            position,
                            url:    l.url || '',
                            loc_nl: labelMode ? (l.labels?.nl || '') : null,
                            loc_en: labelMode ? (l.labels?.en || '') : null,
                            loc_fr: labelMode ? (l.labels?.fr || '') : null,
                        };
                        if (l.serverId == null) {
                            externalLinkOps.push({
                                kind:   'extlink-create',
                                method: 'POST',
                                path:   '/admin/external-links',
                                body,
                            });
                            continue;
                        }
                        const o = origById.get(l.serverId);
                        if (!o) continue;
                        const changed = (
                            (l.url || '') !== (o.url || '') ||
                            (labelMode && (
                                (l.labels?.nl || '') !== (o.labels?.nl || '') ||
                                (l.labels?.en || '') !== (o.labels?.en || '') ||
                                (l.labels?.fr || '') !== (o.labels?.fr || '')
                            ))
                        );
                        if (changed) {
                            externalLinkOps.push({
                                kind:   'extlink-update',
                                method: 'PATCH',
                                path:   `/admin/external-links/${l.serverId}`,
                                body,
                            });
                        }
                    }
                    for (const o of originalList) {
                        if (o.serverId != null && !currentIds.has(o.serverId)) {
                            externalLinkOps.push({
                                kind:   'extlink-delete',
                                method: 'DELETE',
                                path:   `/admin/external-links/${o.serverId}`,
                                body:   null,
                            });
                        }
                    }
                };
                buildOps(footerConfig.customLinks || [], originalFooterConfigRef.current.customLinks || [], 'FOOTER', true);
                buildOps(footerConfig.socialMedia || [], originalFooterConfigRef.current.socialMedia || [], 'SOCIAL', false);
            }
            if (extraFooterDirty) {
                const e  = extraFooterConfig;
                const oe = originalExtraFooterConfigRef.current;
                if (b(e.showPaymentMethods) !== b(oe.showPaymentMethods)) {
                    patches.push({
                        kind:  'extra-footer',
                        path:  '/admin/settings/FOOTER_EXTRA_PAYMENT_METHODS',
                        value: b(e.showPaymentMethods),
                    });
                }
                // Per-column content — PATCH /admin/contents/TEXT_FOOTER_COL_N.
                // The endpoint accepts the same body shape for both editors:
                //   wysiwyg: body_nl/en/fr as strings,  is_via_file=false
                //   unlayer: body_nl/en/fr as { html, design } objects,
                //            is_via_file=true (the server writes both .html
                //            and .json to statics/content/{attr}/{lang}.*)
                for (const colIdx of [1, 2, 3]) {
                    const cur  = e.columns?.[colIdx]  || blankColumn();
                    const orig = oe.columns?.[colIdx] || blankColumn();
                    const htmlChanged =
                        (cur.html?.nl || '') !== (orig.html?.nl || '') ||
                        (cur.html?.en || '') !== (orig.html?.en || '') ||
                        (cur.html?.fr || '') !== (orig.html?.fr || '');
                    // Unlayer edits land in `design` before they hit the
                    // rendered HTML, so an html-only diff would miss them.
                    const designChanged =
                        JSON.stringify(cur.design?.nl ?? null) !== JSON.stringify(orig.design?.nl ?? null) ||
                        JSON.stringify(cur.design?.en ?? null) !== JSON.stringify(orig.design?.en ?? null) ||
                        JSON.stringify(cur.design?.fr ?? null) !== JSON.stringify(orig.design?.fr ?? null);
                    // Mode switch alone (unlayer → wysiwyg) needs to fire
                    // so the server deletes the design sidecars.
                    const editorChanged = (cur.editor || 'wysiwyg') !== (orig.editor || 'wysiwyg');
                    if (!htmlChanged && !designChanged && !editorChanged) continue;

                    const isUnlayer = cur.editor === 'unlayer';
                    const bodyFor = (lang) => {
                        const html   = cur.html?.[lang]   || '';
                        const design = cur.design?.[lang];
                        return isUnlayer ? { html, design } : html;
                    };
                    // PATCH body matches mini-api `/admin/contents/{attr}` shape.
                    // Custom-channel ops can ride alongside /admin/settings
                    // PATCHes because we render them through the same
                    // `path: ...` mechanism below — value is JSON-coerced
                    // server-side same as JSON_KEYS settings.
                    patches.push({
                        kind:  'extra-footer-content',
                        path:  `/admin/contents/TEXT_FOOTER_COL_${colIdx}`,
                        // The body field is the FULL PATCH body, not wrapped
                        // in a `value` key. We flag that so the Promise.all
                        // mapper below sends the right shape.
                        rawBody: {
                            body_nl:     bodyFor('nl'),
                            body_en:     bodyFor('en'),
                            body_fr:     bodyFor('fr'),
                            is_via_file: isUnlayer,
                            // is_custom_html / use_iframe stay at whatever
                            // the row already had — the modal doesn't
                            // surface those yet.
                        },
                    });
                }
            }
            if (staticPagesDirty) {
                // About / Disclaimer / Privacy → contents rows. Same PATCH
                // shape as the Extra footer columns: a wysiwyg body is a
                // plain string; an unlayer body is a { html, design } object
                // and rides is_via_file=true so the server writes the .html +
                // .json sidecars. use_iframe round-trips the page's toggle.
                const attrFor = { about: 'TEXT_ABOUT', disclaimer: 'TEXT_DISCLAIMER', privacy: 'TEXT_PRIVACY', page404: 'TEXT_404', intro: 'TEXT_INTRO' };
                for (const key of ['about', 'disclaimer', 'privacy', 'page404', 'intro']) {
                    const cur  = staticPages[key]                       || STATIC_PAGE_DEFAULT;
                    const orig = originalStaticPagesRef.current[key]    || STATIC_PAGE_DEFAULT;
                    if (JSON.stringify(cur) === JSON.stringify(orig)) continue;
                    const isUnlayer = cur.editor === 'unlayer';
                    const bodyFor = (lang) => {
                        const html   = cur.html?.[lang] || '';
                        const design = cur.designs?.[lang];
                        return isUnlayer ? { html, design } : html;
                    };
                    patches.push({
                        kind:  'static-page-content',
                        path:  `/admin/contents/${attrFor[key]}`,
                        rawBody: {
                            body_nl:     bodyFor('nl'),
                            body_en:     bodyFor('en'),
                            body_fr:     bodyFor('fr'),
                            is_via_file: isUnlayer,
                            use_iframe:  !!cur.iframe,
                        },
                    });
                }
            }
            if (cardsDirty) {
                // Cart-page account cards → contents rows (wysiwyg, so body is a
                // plain string and is_via_file=false). Only the changed cards.
                const origById = Object.fromEntries(originalCardsRef.current.map(c => [c.id, c]));
                for (const card of cards) {
                    const meta = CARD_CONTENT[card.id];
                    if (!meta) continue;
                    const orig = origById[card.id];
                    const changed = !orig
                        || card.contentNL !== orig.contentNL
                        || card.contentEN !== orig.contentEN
                        || card.contentFR !== orig.contentFR
                        || !!card.useIframe !== !!orig.useIframe;
                    if (!changed) continue;
                    patches.push({
                        kind:  'card-content',
                        path:  `/admin/contents/${meta.attr}`,
                        rawBody: {
                            body_nl:     card.contentNL || '',
                            body_en:     card.contentEN || '',
                            body_fr:     card.contentFR || '',
                            is_via_file: false,
                            use_iframe:  !!card.useIframe,
                        },
                    });
                }
            }
            if (brandsConfigDirty) {
                const b = brandsConfig, ob = originalBrandsConfigRef.current;
                // Flat keys.
                if (!!b.whatToShow.onlyOnline !== !!ob.whatToShow.onlyOnline) {
                    patches.push({ kind: 'brands', path: '/admin/settings/BRANDS_ONLY_ONLINE', value: b.whatToShow.onlyOnline ? '1' : '0' });
                }
                const catCur  = b.whatToShow.navigationCategory  || 'none';
                const catOrig = ob.whatToShow.navigationCategory || 'none';
                if (catCur !== catOrig) {
                    patches.push({ kind: 'brands', path: '/admin/settings/BRANDS_CATEGORY_FILTER', value: catCur });
                }
                if (b.layout !== ob.layout) {
                    patches.push({ kind: 'brands', path: '/admin/settings/BRANDS_PAGE_STYLE', value: BRANDS_LAYOUT_TO_STYLE[b.layout] || 'list' });
                }
                // BRANDS_PAGE_CONFIG JSON blob — only when a JSON-backed field
                // changed. Merge over the raw loaded blob to keep keys the tab
                // doesn't surface (idx, divider, moreLabel, …). Passed as an
                // object; the mini-api JSON-encodes it server-side.
                const jsonRelevant = (c) => JSON.stringify({ settings: c.settings, textStyles: c.textStyles });
                if (jsonRelevant(b) !== jsonRelevant(ob)) {
                    const toShort = (st) => ({ s: st.size, b: !!st.bold, i: !!st.italic, u: !!st.underline, c: st.color });
                    patches.push({
                        kind:  'brands-json',
                        path:  '/admin/settings/BRANDS_PAGE_CONFIG',
                        value: {
                            ...originalBrandsRawJsonRef.current,
                            lm:   b.settings.displayContinuous,
                            am:   b.settings.displayGrouped,
                            cols: Number(b.settings.letterColumns) || 4,
                            max:  Number(b.settings.brandsPerLetter) || 0,
                            sul:  !!b.settings.showLettersWithoutBrands,
                            d: { bn: toShort(b.textStyles.desktop.brandName), ai: toShort(b.textStyles.desktop.alphabetNav), al: toShort(b.textStyles.desktop.letterHeading) },
                            m: { bn: toShort(b.textStyles.mobile.brandName),  ai: toShort(b.textStyles.mobile.alphabetNav),  al: toShort(b.textStyles.mobile.letterHeading) },
                        },
                    });
                }
            }
            // Overige — custom scripts (POST per changed section) + GTM/Pixel
            // (settings PATCH + generate-file POST). Custom-scripts & generate
            // calls have their own bodies so they ride a dedicated ops list.
            const overigeOps = [];
            if (overigeConfigDirty) {
                const oc = overigeConfig, ooc = originalOverigeConfigRef.current;
                // Custom code → _scriptsHeader.php / _scriptsBody.php.
                if ((oc.customCode?.head || '') !== (ooc.customCode?.head || '')) {
                    overigeOps.push({ kind: 'scripts-head', method: 'POST', path: '/admin/custom-scripts', body: { content: oc.customCode?.head || '', section: 'head' } });
                }
                if ((oc.customCode?.body || '') !== (ooc.customCode?.body || '')) {
                    overigeOps.push({ kind: 'scripts-body', method: 'POST', path: '/admin/custom-scripts', body: { content: oc.customCode?.body || '', section: 'body' } });
                }
                // GTM / Meta Pixel — the setting (so the dump reflects it) plus
                // the generated include file (what the storefront pulls in).
                const gtmCur = toolId(oc, 'gtm'),       gtmOrig = toolId(ooc, 'gtm');
                if (gtmCur !== gtmOrig) {
                    patches.push({ kind: 'overige', path: '/admin/settings/GTM_ID', value: gtmCur });
                    if (gtmCur) overigeOps.push({ kind: 'gtm-file', method: 'POST', path: '/admin/generate-gtm', body: { gtmId: gtmCur } });
                }
                const pxCur = toolId(oc, 'metaPixel'),  pxOrig = toolId(ooc, 'metaPixel');
                if (pxCur !== pxOrig) {
                    patches.push({ kind: 'overige', path: '/admin/settings/PIXEL_ID', value: pxCur });
                    if (pxCur) overigeOps.push({ kind: 'pixel-file', method: 'POST', path: '/admin/generate-pixel', body: { pixelId: pxCur } });
                }
                // Cookie banner → cookie.js (the route merges onto the existing
                // config so per-language translations are preserved).
                const cc = oc.cookies || {}, occ = ooc.cookies || {};
                const cookiesChanged = cc.bannerStyle !== occ.bannerStyle
                    || cc.position !== occ.position
                    || !!cc.categories?.analytics !== !!occ.categories?.analytics
                    || !!cc.categories?.marketing !== !!occ.categories?.marketing;
                if (cookiesChanged) {
                    overigeOps.push({
                        kind: 'cookie-consent', method: 'POST', path: '/admin/cookie-consent',
                        body: {
                            layout:    OVERIGE_STYLE_TO_LAYOUT[cc.bannerStyle] || 'box',
                            position:  cc.position || 'bottom right',
                            analytics: !!cc.categories?.analytics,
                            marketing: !!cc.categories?.marketing,
                        },
                    });
                }
            }

            // Cadeaubon settings — diff the flat CHEQUES_* map and PATCH only the
            // keys that changed (same value shape the backend stores).
            if (cadeaubonConfigDirty) {
                const curChq  = cadeaubonToSettings(cadeaubonConfig);
                const origChq = cadeaubonToSettings(originalCadeaubonConfigRef.current);
                for (const key of Object.keys(curChq)) {
                    if (curChq[key] !== origChq[key]) {
                        patches.push({ kind: 'cadeaubon', path: `/admin/settings/${key}`, value: curChq[key] });
                    }
                }
            }

            // Cadeaubon translations (cheques_placeholders CRUD). New rows (no id)
            // POST — the backend generates the PLC_* key from the description;
            // existing rows PATCH only layout/loc_*; removed rows DELETE.
            const cadeaubonOps = [];
            if (cadeaubonPlaceholdersDirty) {
                const origPlc     = originalPlaceholdersRef.current;
                const origPlcById = Object.fromEntries(origPlc.filter(p => p.id != null).map(p => [p.id, p]));
                const curPlcIds   = new Set(cadeaubonPlaceholders.filter(p => p.id != null).map(p => p.id));
                for (const op of origPlc) {
                    if (op.id != null && !curPlcIds.has(op.id)) {
                        cadeaubonOps.push({ kind: 'plc-del', method: 'DELETE', path: `/cheque-placeholders/${op.id}`, body: {} });
                    }
                }
                for (const p of cadeaubonPlaceholders) {
                    if (p.id == null) {
                        if (!(p.description || '').trim()) continue; // skip empty new rows
                        cadeaubonOps.push({ kind: 'plc-new', method: 'POST', path: '/cheque-placeholders', body: {
                            description: p.description, layout: p.layout || 'GLOBAL',
                            loc_nl: p.nl || '', loc_en: p.en || '', loc_fr: p.fr || '',
                        } });
                    } else {
                        const o = origPlcById[p.id];
                        const changed = !o || o.nl !== p.nl || o.en !== p.en || o.fr !== p.fr || (o.layout || '') !== (p.layout || '');
                        if (changed) {
                            cadeaubonOps.push({ kind: 'plc-upd', method: 'PATCH', path: `/cheque-placeholders/${p.id}`, body: {
                                layout: p.layout || 'GLOBAL', loc_nl: p.nl || '', loc_en: p.en || '', loc_fr: p.fr || '',
                            } });
                        }
                    }
                }
            }

            // Cadeaubon voucher layouts (cheques_layouts CRUD). New rows (no
            // serverId) POST — with an optional copy_layout_from to clone a
            // system default; existing rows PATCH type/description/visible;
            // removed non-system rows DELETE. Image + design uploads run AFTER
            // (they need the fresh id), mapped via layoutRef. System defaults
            // (isSystem) are never POST/DELETE'd.
            const cadeaubonLayoutOps = [];
            if (cadeaubonLayoutsDirty) {
                const origLay     = originalCadeaubonLayoutsRef.current;
                const origLayById = Object.fromEntries(origLay.filter(l => l.serverId != null).map(l => [l.serverId, l]));
                const curLayIds   = new Set(cadeaubonLayouts.filter(l => l.serverId != null).map(l => l.serverId));
                for (const o of origLay) {
                    if (o.serverId != null && !o.isSystem && !curLayIds.has(o.serverId)) {
                        cadeaubonLayoutOps.push({ kind: 'lay-del', method: 'DELETE', path: `/cheques-layouts/${o.serverId}`, body: {} });
                    }
                }
                for (const l of cadeaubonLayouts) {
                    if (l.serverId == null) {
                        cadeaubonLayoutOps.push({
                            kind: 'lay-new', method: 'POST', path: '/cheques-layouts',
                            layoutRef: { tempId: l.id },
                            body: {
                                type:        l.type,
                                description: l.description || '',
                                visible:     l.visible ? 1 : 0,
                                ...(l.copyFrom != null ? { copy_layout_from: l.copyFrom } : {}),
                            },
                        });
                    } else if (!l.isSystem) {
                        const o = origLayById[l.serverId];
                        const changed = !o || o.type !== l.type || (o.description || '') !== (l.description || '') || !!o.visible !== !!l.visible;
                        if (changed) {
                            cadeaubonLayoutOps.push({
                                kind: 'lay-upd', method: 'PATCH', path: `/cheques-layouts/${l.serverId}`,
                                body: { type: l.type, description: l.description || '', visible: l.visible ? 1 : 0 },
                            });
                        }
                    }
                }
            }

            // Verwerking — scalar settings diff + the 3 CHEQUES_* discount JSON.
            if (processingConfigDirty) {
                const curProc  = processingToSettings(processingConfig);
                const origProc = processingToSettings(originalProcessingConfigRef.current);
                // Validate a changed guest customer id; an invalid one is left out
                // of the save (everything else still saves).
                const curGuest  = String(processingConfig.access?.guestCustomerId ?? '').trim();
                const origGuest = String(originalProcessingConfigRef.current.access?.guestCustomerId ?? '').trim();
                if (curGuest && curGuest !== origGuest && typeof validateCustomerId === 'function') {
                    const guestRes = await validateCustomerId(curGuest);
                    if (guestRes && guestRes.status === 'invalid') guestIdSkipped = true;
                }
                for (const key of Object.keys(curProc)) {
                    if (key.startsWith('__')) continue;                              // dirty-only signal
                    if (key === 'PROCESS_PAYMENT_METHODS' && curProc[key] === 'NOCHECKOUT') continue; // pending option, don't write
                    if (key === 'PROCESS_NO_ACCOUNT_ID' && guestIdSkipped) continue; // invalid id — don't persist
                    if (curProc[key] !== origProc[key]) {
                        patches.push({ kind: 'processing', path: `/admin/settings/${key}`, value: curProc[key] });
                    }
                }
                // Discount/voucher JSON — rebuild over the originals so codes not
                // shown in the UI rows are preserved, then patch the ones that changed.
                const orig = originalProcChequeRef.current;
                const buildObj = (base, field) => {
                    const out = { ...(base || {}) };
                    Object.keys(processingConfig.discountTypes || {}).forEach((code6) => {
                        out[`99${code6}`] = !!(processingConfig.discountTypes[code6] || {})[field];
                    });
                    return out;
                };
                const nextAllowed = buildObj(orig.allowed,      'enabled');
                const nextAsPay   = buildObj(orig.asPayment,    'acceptAsPayment');
                const nextReceipt = buildObj(orig.addToReceipt, 'showInEmail');
                if (JSON.stringify(nextAllowed) !== JSON.stringify(orig.allowed))      patches.push({ kind: 'processing', path: '/admin/settings/CHEQUES_ALLOWED',        value: nextAllowed });
                if (JSON.stringify(nextAsPay)   !== JSON.stringify(orig.asPayment))    patches.push({ kind: 'processing', path: '/admin/settings/CHEQUES_AS_PAYMENT',     value: nextAsPay });
                if (JSON.stringify(nextReceipt) !== JSON.stringify(orig.addToReceipt)) patches.push({ kind: 'processing', path: '/admin/settings/CHEQUES_ADD_TO_RECEIPT', value: nextReceipt });

                // "No checkout" ↔ PROCESS_TICKET_MODULE LOOKBOOK. Switching back to a
                // checkout location re-enables ordering (RESERVATION), preserving an
                // existing RESERVATION/RECEIPT mode otherwise.
                const procLoc    = processingConfig.payments?.location;
                const origModule = originalTicketModuleRef.current;
                const nextModule = (procLoc === 'noCheckout') ? 'LOOKBOOK' : (origModule === 'LOOKBOOK' ? 'RESERVATION' : origModule);
                if (nextModule && nextModule !== origModule) {
                    patches.push({ kind: 'processing', path: '/admin/settings/PROCESS_TICKET_MODULE', value: nextModule });
                }
            }

            // Synchronisatie — scalar settings diff (FETCH_*, MAP_*, PHOTO_*).
            if (syncConfigDirty) {
                const curSync  = syncToSettings(syncConfig, allStoreCodesForSync);
                const origSync = syncToSettings(originalSyncConfigRef.current, allStoreCodesForSync);
                for (const key of Object.keys(curSync)) {
                    if (curSync[key] !== origSync[key]) {
                        patches.push({ kind: 'sync', path: `/admin/settings/${key}`, value: curSync[key] });
                    }
                }
            }

            // Custom customer fields — customers_categories_groups CRUD. New rows
            // (no id) POST; existing rows PATCH changed columns; removed rows DELETE.
            const customerGroupOps = [];
            if (customerGroupsDirty) {
                const origGroups     = originalCustomerGroupsRef.current;
                const origGroupsById = Object.fromEntries(origGroups.filter(g => g.id != null).map(g => [g.id, g]));
                const curGroupIds    = new Set(customerCategoryGroups.filter(g => g.id != null).map(g => g.id));
                for (const og of origGroups) {
                    if (og.id != null && !curGroupIds.has(og.id)) {
                        customerGroupOps.push({ kind: 'ccg-del', method: 'DELETE', path: `/admin/customer-category-groups/${og.id}`, body: {} });
                    }
                }
                for (const g of customerCategoryGroups) {
                    const cols = customerGroupToColumns(g);
                    if (g.id == null) {
                        if (!cols.sft_id) continue; // skip new rows without a code group
                        customerGroupOps.push({ kind: 'ccg-new', method: 'POST', path: '/admin/customer-category-groups', body: cols });
                    } else {
                        const o = origGroupsById[g.id];
                        const origCols = o ? customerGroupToColumns(o) : null;
                        const body = {};
                        for (const c of Object.keys(cols)) {
                            if (!origCols || JSON.stringify(cols[c]) !== JSON.stringify(origCols[c])) body[c] = cols[c];
                        }
                        if (Object.keys(body).length) {
                            customerGroupOps.push({ kind: 'ccg-upd', method: 'PATCH', path: `/admin/customer-category-groups/${g.id}`, body });
                        }
                    }
                }
            }

            if (looksConfigDirty) {
                const l = looksConfig, ol = originalLooksConfigRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'looks', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                const b01 = (v) => (v ? '1' : '0');
                // Grid
                push('LOOKS_NAVIGATION_CODE', l.looksGrid.navigationCategory, ol.looksGrid.navigationCategory);
                push('LOOKS_LIST_SORTING',    l.looksGrid.displayOrder,       ol.looksGrid.displayOrder);
                push('MAX_COLUMNS_LOOKS',     l.looksGrid.looksPerRow,        ol.looksGrid.looksPerRow);
                // Overlay
                push('LOOKS_OVERLAY_ONLY_ON_HOVER', b01(l.overlay.onlyOnHover),  b01(ol.overlay.onlyOnHover));
                push('LOOKS_OVERLAY_BGC',           l.overlay.bandBg,            ol.overlay.bandBg);
                push('LOOKS_OVERLAY_SIZE',          l.overlay.bandHeightPct,     ol.overlay.bandHeightPct);
                push('LOOKS_OVERLAY_POSITION',      l.overlay.bandVerticalPosition, ol.overlay.bandVerticalPosition);
                push('LOOKS_OVERLAY_SHOW_TITLE',    b01(l.overlay.showTitle),    b01(ol.overlay.showTitle));
                push('LOOKS_OVERLAY_FGC',           l.overlay.titleColor,        ol.overlay.titleColor);
                push('LOOKS_OVERLAY_TITLE_FS',      l.overlay.titleFontSize,     ol.overlay.titleFontSize);
                push('LOOKS_OVERLAY_SHOW_BUTTON',   b01(l.overlay.showCta),      b01(ol.overlay.showCta));
                push('LOOKS_OVERLAY_BUTTON_BGC',    l.overlay.ctaBg,             ol.overlay.ctaBg);
                push('LOOKS_OVERLAY_BUTTON_FGC',    l.overlay.ctaTextColor,      ol.overlay.ctaTextColor);
                push('LOOKS_OVERLAY_BUTTON_FS',     l.overlay.ctaFontSize,       ol.overlay.ctaFontSize);
                // Detail page
                push('LOOKS_DETAIL_TEXT_LOCATION', l.detailPage.descriptionPosition, ol.detailPage.descriptionPosition);
                push('LOOK_SHOW_DIVIDER',          b01(l.detailPage.showDividerBetweenProducts), b01(ol.detailPage.showDividerBetweenProducts));
                push('LOOK_SHOW_OUT_OF_STOCK',     b01(l.detailPage.showOutOfStock),             b01(ol.detailPage.showOutOfStock));
                // Product title/subtitle — space-separated field tokens.
                push('SHOW_FIELDS_ON_LOOK_TITLE',      l.productTitle.join(' '),    ol.productTitle.join(' '));
                push('SHOW_FIELDS_ON_LOOK_SUBHEADING', l.productSubtitle.join(' '), ol.productSubtitle.join(' '));
                // Intro text + iframe flag → TEXT_LOOKS content row (wysiwyg,
                // plain strings). The "Show in iframe" toggle is this content
                // row's use_iframe (the looks page renders it via content-display).
                if (JSON.stringify(l.introText) !== JSON.stringify(ol.introText)
                    || !!l.looksGrid.showInIframe !== !!ol.looksGrid.showInIframe) {
                    patches.push({
                        kind:  'looks-content',
                        path:  '/admin/contents/TEXT_LOOKS',
                        rawBody: {
                            body_nl: l.introText.nl || '',
                            body_en: l.introText.en || '',
                            body_fr: l.introText.fr || '',
                            is_via_file: false,
                            use_iframe: !!l.looksGrid.showInIframe,
                        },
                    });
                }
                // Look CTA label → the shared GLOBAL.LOOKS_DETAILS i18n row
                // (per language). PATCH /localization/{id}; a regenerate runs
                // post-save so the storefront JSON picks it up.
                if (JSON.stringify(l.overlay.ctaText) !== JSON.stringify(ol.overlay.ctaText) && looksCtaIdRef.current != null) {
                    patches.push({
                        kind:   'looks-cta',
                        path:   `/localization/${looksCtaIdRef.current}`,
                        rawBody: {
                            nl: l.overlay.ctaText?.nl || '',
                            en: l.overlay.ctaText?.en || '',
                            fr: l.overlay.ctaText?.fr || '',
                        },
                    });
                    looksCtaSaved = true;
                }
            }
            if (imageGalleryDirty) {
                const g  = imageGalleryConfig;
                const og = originalImageGalleryConfigRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'img-gallery', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                // Thumbnail position: modal 'above'/'below' → legacy 'top'/'bottom';
                // 'hidden' drives SHOW_GALLERY_THUMBS=false but the position
                // value itself is left at whatever it was so the user gets
                // their old layout back when they unhide.
                const toLegacyPos = (p) => p === 'above' ? 'top'
                                       : p === 'below' ? 'bottom'
                                       : p === 'hidden' ? null
                                       : p;
                const legacyPos     = toLegacyPos(g.thumbnailPosition);
                const legacyPosOrig = toLegacyPos(og.thumbnailPosition);
                if (legacyPos && legacyPos !== legacyPosOrig) {
                    push('GALLERY_THUMBS_POSITION', legacyPos, legacyPosOrig || '');
                }
                push('SHOW_GALLERY_THUMBS',
                    b(g.thumbnailPosition !== 'hidden'),
                    b(og.thumbnailPosition !== 'hidden'));
                push('DETAIL_USE_NEW_GALLERY',  b(g.useGrid),       b(og.useGrid));
                push('NEW_GALLERY_THUMBS_COUNT', g.maxThumbnails,    og.maxThumbnails);

                // NEW_GALLERY_CONFIG is a JSON-coerced setting. Pass the
                // object as-is; the mini-api JSON.encodes it server-side
                // (same pattern as SIDE_FILTER_SLICE/STATES).
                const arrowKeys = ['showArrows','arrowColor','arrowBgColor','showFullscreen','fullscreenArrowColor','fullscreenArrowBgColor'];
                const arrowsChanged = arrowKeys.some(k => g[k] !== og[k]);
                if (arrowsChanged) {
                    patches.push({
                        kind: 'img-gallery-json',
                        path: '/admin/settings/NEW_GALLERY_CONFIG',
                        value: {
                            main_arrows:  { display: !!g.showArrows,     color: g.arrowColor,           background: g.arrowBgColor },
                            thumb_arrows: { display: !!g.showFullscreen, color: g.fullscreenArrowColor, background: g.fullscreenArrowBgColor },
                        },
                    });
                }
            }
            if (productDetailInfoDirty) {
                const p  = productDetailInfoConfig;
                const op = originalProductDetailInfoConfigRef.current;
                const push = (key, current, original) => {
                    if (String(current) !== String(original)) {
                        patches.push({ kind: 'pdi', path: `/admin/settings/${key}`, value: String(current) });
                    }
                };
                const pushJson = (key, currentObj, originalObj) => {
                    if (JSON.stringify(currentObj) !== JSON.stringify(originalObj)) {
                        patches.push({ kind: 'pdi-json', path: `/admin/settings/${key}`, value: currentObj });
                    }
                };

                // Layout — rebuild PRODUCT_DETAILS_ORDER from the modal's
                // blocks. We honour the existing CSV order (so any future
                // reorder UI just rewrites `layout.order`), filter out
                // blocks the user toggled off, and append any used blocks
                // that weren't in the original order (defensive — should
                // only happen when defaults are first applied). All modal
                // blocks have a legacy equivalent in modalToLegacyBlock
                // today; once a wash-instructions <section> ships on the
                // storefront, add the modal→legacy entry here.
                const modalToLegacyBlock = {
                    header:          'header',
                    sizeSelector:    'sizeSelector',
                    addToCart:       'addToCart',
                    stockByLocation: 'locationsStock',
                    variants:        'variantsList',
                    relatedProducts: 'related',
                    productInfo:     'productInfo',
                    extraText:       'extraText',
                };
                const orderCsv = (cfg) => {
                    const ord  = Array.isArray(cfg.layout.order) ? cfg.layout.order : [];
                    const used = Object.keys(modalToLegacyBlock).filter(k => cfg.layout.blocks[k]?.used);
                    const seq  = [...ord.filter(k => used.includes(k)), ...used.filter(k => !ord.includes(k))];
                    return seq.map(k => modalToLegacyBlock[k]).join(',');
                };
                push('PRODUCT_DETAILS_ORDER', orderCsv(p), orderCsv(op));

                // Details list — flatten the 3 groups into a single CSV
                // following the canonical legacy order (so toggling never
                // reshuffles unrelated fields).
                const FIELD_ORDER = [
                    'id','brand','name','reference','color','color_brand','season',
                    'category_1','category_2','category_3','category_4','category_5','category_6','category_7',
                    'description_1','description_2','description_3','description_4','description_5',
                    'detail_1','detail_2','detail_3','detail_4','detail_5',
                ];
                const fieldsCsvOf = (cfg) => {
                    const all = { ...cfg.detailsList.identity, ...cfg.detailsList.categories, ...cfg.detailsList.descriptions };
                    return FIELD_ORDER.filter(k => all[k]).join(',');
                };
                push('SHOW_FIELDS_ON_DETAIL_PAGE', fieldsCsvOf(p), fieldsCsvOf(op));
                // Sections open — reverse the same modal-key → legacy-key
                // mapping used by the loader.
                const sectionsMap = {
                    productDetails:   'details',
                    variants:         'variants',
                    relatedProducts:  'related',
                    articleInfo:      'information',
                    washInstructions: 'washAndCare',
                    stockByStore:     'storeStock',
                };
                const toLegacyStates = (modalObj) => Object.fromEntries(
                    Object.entries(sectionsMap).map(([modalK, legacyK]) => [legacyK, !!modalObj[modalK]])
                );
                pushJson('PRODUCT_DETAILS_CONTAINER_STATES', toLegacyStates(p.sectionsOpen.desktop), toLegacyStates(op.sectionsOpen.desktop));
                pushJson('PRODUCT_MOBILE_CONTAINER_STATES',  toLegacyStates(p.sectionsOpen.mobile),  toLegacyStates(op.sectionsOpen.mobile));

                // Related — DETAILS_COLOR_DISPLAY only knows 'color'/'image';
                // pass other values through unchanged so the modal's extra
                // options round-trip if the storefront ever learns them.
                const modalToLegacyVariant = (v) => v === 'colorImage' ? 'image'
                                              : v === 'colorChips' ? 'color'
                                              : v;
                push('PRODUCT_RELATED_ARTICLES_AMOUNT', p.related.maxToShow,                        op.related.maxToShow);
                push('DETAILS_COLOR_DISPLAY',           modalToLegacyVariant(p.related.variantsDisplay), modalToLegacyVariant(op.related.variantsDisplay));
                push('DETAILS_COLOR_SHOW_OUT_OF_STOCK', b(p.related.showOutOfStock),                b(op.related.showOutOfStock));
                push('DETAILS_COLOR_DISPLAY_BORDER',    b(p.related.highlightSelected),             b(op.related.highlightSelected));

                // Stock
                push('SHOW_AVAILABLE_STOCK_STORES', b(p.stock.showStockByStore),    b(op.stock.showStockByStore));
                push('HIDE_STORE_IF_NO_RECORD',     b(p.stock.hideWhenSizeUnknown), b(op.stock.hideWhenSizeUnknown));

                // Title & URL — three template strings + one CSV. Serialise
                // both sides so the dirty-compare honours chip reorder /
                // text edits but ignores fresh chip ids.
                const tau    = p.titleAndUrl  || {};
                const tauOrig = op.titleAndUrl || {};
                push('SHOW_FIELDS_ON_DETAIL_TAB',        serializeChipsToTemplate(tau.browserTab?.used),     serializeChipsToTemplate(tauOrig.browserTab?.used));
                push('SHOW_FIELDS_ON_DETAIL_TITLE',      serializeChipsToTemplate(tau.pageHeading?.used),    serializeChipsToTemplate(tauOrig.pageHeading?.used));
                push('SHOW_FIELDS_ON_DETAIL_SUBHEADING', serializeChipsToTemplate(tau.pageSubheading?.used), serializeChipsToTemplate(tauOrig.pageSubheading?.used));
                push('DETAIL_PAGE_URL_SECTION',          serializeChipsToCsv     (tau.urlSlug?.used),        serializeChipsToCsv     (tauOrig.urlSlug?.used));

                // Breadcrumb levels — CSV of legacy keys.
                const bcCsv     = (p.breadcrumb?.used  || []).join(',');
                const bcCsvOrig = (op.breadcrumb?.used || []).join(',');
                push('DETAIL_PAGE_NAV_SECTION', bcCsv, bcCsvOrig);
            }

            // Carousel slides — POST new, PATCH changed, DELETE removed.
            // These hit /admin/carousel/* (table CRUD) instead of
            // /admin/settings, so they ride a separate ops list. Image
            // fields are intentionally skipped: image upload runs via a
            // dedicated multipart endpoint in a later round, and PATCH on
            // the carousel route doesn't touch image columns either —
            // existing server-side images stay intact.
            const carouselOps = [];
            if (carouselDirty) {
                const langs = ['nl', 'en', 'fr'];
                for (const lang of langs) {
                    // pendingDelete rows drop out of `current`, so the
                    // orig-vs-current serverId diff below emits their DELETE.
                    const current  = (carouselConfig.slides?.[lang] || []).filter(s => !s.pendingDelete);
                    const original = originalCarouselConfigRef.current.slides?.[lang] || [];
                    const origById = new Map(
                        original.filter(s => s.serverId != null).map(s => [s.serverId, s])
                    );
                    const currentServerIds = new Set(
                        current.map(s => s.serverId).filter(id => id != null)
                    );

                    // POST for slides without a serverId (newly added).
                    for (const s of current) {
                        if (s.serverId == null) {
                            carouselOps.push({
                                kind:   'carousel-create',
                                method: 'POST',
                                path:   '/admin/carousel',
                                // Links the create response's new id back to this
                                // slide so image uploads can target it afterwards.
                                slideRef: { lang, tempId: s.id },
                                body: {
                                    lang,
                                    link:         s.linkUrl     || null,
                                    active:       s.active ? 1 : 0,
                                    order:        Number(s.order) || 0,
                                    text_desktop: s.hoverDesktop || null,
                                    text_mobile:  s.hoverMobile  || null,
                                },
                            });
                        }
                    }
                    // PATCH for slides whose tracked fields changed.
                    for (const s of current) {
                        if (s.serverId == null) continue;
                        const o = origById.get(s.serverId);
                        if (!o) continue;
                        const changed = (
                            s.linkUrl      !== o.linkUrl     ||
                            !!s.active     !== !!o.active    ||
                            Number(s.order) !== Number(o.order) ||
                            s.hoverDesktop !== o.hoverDesktop ||
                            s.hoverMobile  !== o.hoverMobile
                        );
                        if (changed) {
                            carouselOps.push({
                                kind:   'carousel-update',
                                method: 'PATCH',
                                path:   `/admin/carousel/${s.serverId}`,
                                body: {
                                    lang,
                                    link:         s.linkUrl     || null,
                                    active:       s.active ? 1 : 0,
                                    order:        Number(s.order) || 0,
                                    text_desktop: s.hoverDesktop || null,
                                    text_mobile:  s.hoverMobile  || null,
                                },
                            });
                        }
                    }
                    // DELETE for slides present in the original snapshot but
                    // not in the current list (removed by the user).
                    for (const o of original) {
                        if (o.serverId != null && !currentServerIds.has(o.serverId)) {
                            carouselOps.push({
                                kind:   'carousel-delete',
                                method: 'DELETE',
                                path:   `/admin/carousel/${o.serverId}`,
                                body:   null,
                            });
                        }
                    }
                }

                // Carousel settings (display + hover + behavior + overlay) —
                // PER LANGUAGE. Each language diffs and writes only its own
                // HOME_GALLERY_CONFIG_{L} / overlay / scalar keys.
                // Overlay writer — mirrors carReadDevice on load. Position
                // hyphen → space (storefront does `.includes('top ')`); the
                // desktop sub-object also carries the three shared flags.
                const carPositionToBackend = (p) => typeof p === 'string' ? p.replace(/-/g, ' ') : 'bottom center';
                const carWriteDevice = (ov, base, withFlags, parent) => ({
                    ...base,
                    background:  ov.background ?? base.background,
                    color:       ov.textColor  ?? base.color,
                    position:    carPositionToBackend(ov.position),
                    fontSize:    Number(ov.size)  || base.fontSize || 12,
                    size:        Number(ov.width) || base.size     || 10,
                    styleBold:   !!ov.bold,
                    styleItalic: !!ov.italic,
                    styleLined:  !!ov.underline,
                    styleStrike: !!ov.strike,
                    ...(withFlags ? {
                        onlyOnHover:            !!parent.onlyOnHover,
                        keepTextDisplayed:      !!parent.alwaysVisible,
                        useDesktopTextFallback: !!parent.useDesktopOnMobile,
                    } : {}),
                });
                // JSON blob per language — merge the modal-managed keys on top
                // of the raw server JSON (kept per lang on load) so legacy
                // fields like `showIndex` survive.
                const galleryJsonFor = (cfgSettings) => {
                    // overlayConfig lives in its OWN key (HOME_GALLERY_OVERLAY_CONFIG_*),
                    // so strip any legacy inline copy out of HOME_GALLERY_CONFIG.
                    const { overlayConfig: _legacyOverlay, ...baseNoOverlay } = cfgSettings.__rawGalleryConfig || {};
                    return {
                        ...baseNoOverlay,
                        autoSlide:              !!cfgSettings.behavior?.autoAdvance,
                        slideInterval:          (Number(cfgSettings.behavior?.timePerSlide) || 0) * 1000,
                        showControls:           !!cfgSettings.behavior?.showArrows,
                        showControlsOnHover:    !!cfgSettings.behavior?.arrowsOnHover,
                        showControlsBackground: !!cfgSettings.behavior?.arrowsBackground,
                        maxHeight:              Number(cfgSettings.display?.maxHeight) || 0,
                        maxHeightUnit:          cfgSettings.display?.maxHeightUnit || 'px',
                        // margin = [top, bottom]; storefront reads it for the
                        // carousel wrapper spacing.
                        margin:                 [Number(cfgSettings.display?.spacingTop) || 0, Number(cfgSettings.display?.spacingBottom) || 0],
                        animation:              cfgSettings.behavior?.transition || 'fade',
                        imageSize:              cfgSettings.display?.imageFit || 'scale',
                    };
                };
                const overlayJsonFor = (cfgSettings) => {
                    const rawOverlayBase = cfgSettings.__rawOverlayConfig || {};
                    const to = cfgSettings.textOverlay || {};
                    return {
                        ...rawOverlayBase,
                        desktop: carWriteDevice(to.desktop || {}, rawOverlayBase.desktop || {}, true,  to),
                        mobile:  carWriteDevice(to.mobile  || {}, rawOverlayBase.mobile  || {}, false, to),
                    };
                };
                for (const cl of ['nl', 'en', 'fr']) {
                    const L      = cl.toUpperCase();
                    const cs     = carouselConfig.settings?.[cl] || {};
                    const csOrig = originalCarouselConfigRef.current.settings?.[cl] || {};
                    const csChanged = (current, original) => String(current) !== String(original);

                    const jsonCurrent  = galleryJsonFor(cs);
                    const jsonOriginal = galleryJsonFor(csOrig);
                    if (JSON.stringify(jsonCurrent) !== JSON.stringify(jsonOriginal)) {
                        patches.push({ kind: 'carousel-cfg', path: `/admin/settings/HOME_GALLERY_CONFIG_${L}`, value: jsonCurrent });
                    }
                    // Caption overlay → its own per-lang key, matching the storefront
                    // (StartupController reads HOME_GALLERY_OVERLAY_CONFIG_{lang}).
                    const overlayCurrent  = overlayJsonFor(cs);
                    const overlayOriginal = overlayJsonFor(csOrig);
                    if (JSON.stringify(overlayCurrent) !== JSON.stringify(overlayOriginal)) {
                        patches.push({ kind: 'carousel-cfg', path: `/admin/settings/HOME_GALLERY_OVERLAY_CONFIG_${L}`, value: overlayCurrent });
                    }
                    // Per-lang scalar/bool keys.
                    const pushLang = (keyBase, valCur, valOrig, coerce) => {
                        if (csChanged(valCur, valOrig)) {
                            patches.push({ kind: 'carousel-cfg', path: `/admin/settings/${keyBase}_${L}`, value: coerce ? coerce(valCur) : valCur });
                        }
                    };
                    pushLang('STRETCH_GALLERY_FULL_WIDTH',    cs.display?.fullWidth,    csOrig.display?.fullWidth,    v => b(v));
                    pushLang('GALLERY_HOVER_ANIM_TYPE',       cs.hover?.effect,         csOrig.hover?.effect,         v => String(v || 'none'));
                    pushLang('GALLERY_HOVER_ANIM_ZOOM_SCALE', cs.hover?.zoomLevel,      csOrig.hover?.zoomLevel,      v => String(Number(v) || 0));
                    pushLang('GALLERY_HOVER_ANIM_ZOOM_SPEED', cs.hover?.animationSpeed, csOrig.hover?.animationSpeed, v => String(Number(v) || 0));
                }
                // Legacy global interval key — kept in sync from the NL value
                // (the per-lang slideInterval inside the JSON is what the
                // storefront actually reads per language).
                const nlTime     = Number(carouselConfig.settings?.nl?.behavior?.timePerSlide) || 0;
                const nlTimeOrig = Number(originalCarouselConfigRef.current.settings?.nl?.behavior?.timePerSlide) || 0;
                if (nlTime !== nlTimeOrig) {
                    patches.push({ kind: 'carousel-cfg', path: '/admin/settings/HOME_GALLERY_INTERVAL', value: String(nlTime * 1000) });
                }
            }

            // Image grid cells — same shape as carousel, different endpoint
            // (`/admin/image-grid/*`). Hits the same dirty-aware POST/PATCH/
            // DELETE flow.
            const imageGridOps = [];
            if (imageGridDirty) {
                const langs = ['nl', 'en', 'fr'];
                for (const lang of langs) {
                    // pendingDelete rows drop out of `current`, so the
                    // orig-vs-current serverId diff below emits their DELETE.
                    const current  = (imageGridConfig.cells?.[lang] || []).filter(c => !c.pendingDelete);
                    const original = originalImageGridConfigRef.current.cells?.[lang] || [];
                    const origById = new Map(
                        original.filter(c => c.serverId != null).map(c => [c.serverId, c])
                    );
                    const currentServerIds = new Set(
                        current.map(c => c.serverId).filter(id => id != null)
                    );

                    for (const c of current) {
                        if (c.serverId == null) {
                            imageGridOps.push({
                                kind:   'imagegrid-create',
                                method: 'POST',
                                path:   '/admin/image-grid',
                                // Links the create response's new id back to this
                                // cell so image uploads can target it afterwards.
                                cellRef: { lang, tempId: c.id },
                                body: {
                                    lang,
                                    link:         c.linkUrl     || null,
                                    active:       c.active ? 1 : 0,
                                    order:        Number(c.order) || 0,
                                    width:        Math.max(1, Number(c.width)  || 1),
                                    height:       Math.max(1, Number(c.height) || 1),
                                    text_desktop: c.hoverDesktop || null,
                                    text_mobile:  c.hoverMobile  || null,
                                },
                            });
                        }
                    }
                    for (const c of current) {
                        if (c.serverId == null) continue;
                        const o = origById.get(c.serverId);
                        if (!o) continue;
                        const changed = (
                            c.linkUrl      !== o.linkUrl     ||
                            !!c.active     !== !!o.active    ||
                            Number(c.order) !== Number(o.order) ||
                            Number(c.width)  !== Number(o.width)  ||
                            Number(c.height) !== Number(o.height) ||
                            c.hoverDesktop !== o.hoverDesktop ||
                            c.hoverMobile  !== o.hoverMobile
                        );
                        if (changed) {
                            imageGridOps.push({
                                kind:   'imagegrid-update',
                                method: 'PATCH',
                                path:   `/admin/image-grid/${c.serverId}`,
                                body: {
                                    lang,
                                    link:         c.linkUrl     || null,
                                    active:       c.active ? 1 : 0,
                                    order:        Number(c.order) || 0,
                                    width:        Math.max(1, Number(c.width)  || 1),
                                    height:       Math.max(1, Number(c.height) || 1),
                                    text_desktop: c.hoverDesktop || null,
                                    text_mobile:  c.hoverMobile  || null,
                                },
                            });
                        }
                    }
                    for (const o of original) {
                        if (o.serverId != null && !currentServerIds.has(o.serverId)) {
                            imageGridOps.push({
                                kind:   'imagegrid-delete',
                                method: 'DELETE',
                                path:   `/admin/image-grid/${o.serverId}`,
                                body:   null,
                            });
                        }
                    }
                }

                // Image grid settings — PER LANGUAGE. Each language diffs and
                // writes only its own IMAGE_GRID_CONFIG_{L} / scalar keys.
                // Position: modal hyphen → backend space (storefront's
                // home.component.html checks `.includes('top ')` etc.).
                const positionToBackend = (p) => typeof p === 'string'
                    ? p.replace(/-/g, ' ')
                    : 'bottom center';
                // Per-device writer — mirrors the load-time readDevice.
                // Mobile sub-object lacks the shared desktop flags backend-side;
                // the spread of `baseDevice` preserves whatever was stored there
                // (e.g. seeded by mig166) and only overrides the style fields
                // the modal owns.
                const writeDevice = (ovDevice, baseDevice, withDesktopFlags, ov) => ({
                    ...baseDevice,
                    background:  ovDevice.background ?? baseDevice.background,
                    color:       ovDevice.textColor  ?? baseDevice.color,
                    position:    positionToBackend(ovDevice.position),
                    fontSize:    Number(ovDevice.size)  || baseDevice.fontSize || 12,
                    size:        Number(ovDevice.width) || baseDevice.size     || 10,
                    styleBold:   !!ovDevice.bold,
                    styleItalic: !!ovDevice.italic,
                    styleLined:  !!ovDevice.underline,
                    styleStrike: !!ovDevice.strike,
                    ...(withDesktopFlags
                        ? {
                            onlyOnHover:            !!ov.onlyOnHover,
                            keepTextDisplayed:      !!ov.alwaysVisible,
                            useDesktopTextFallback: !!ov.useDesktopOnMobile,
                          }
                        : {}),
                });
                const gridJsonFor = (cfgSettings) => {
                    const rawGridBase = cfgSettings.__rawGridConfig || {};
                    const baseOverlay = rawGridBase.overlay || {};
                    const baseDesktop = baseOverlay.desktop || {};
                    const baseMobile  = baseOverlay.mobile  || {};
                    const ov          = cfgSettings.textOverlay         || {};
                    const ovDesktop   = ov.desktop                       || {};
                    const ovMobile    = ov.mobile                        || {};
                    const marginArr   = Array.isArray(cfgSettings.layout?.margin)
                        ? cfgSettings.layout.margin
                        : [20, 0];
                    return {
                        ...rawGridBase,
                        // Only the fields the storefront actually reads from
                        // `imageGridConfig[lang]` round-trip: `margin` (mig164)
                        // and the `overlay.{desktop,mobile}` sub-objects (mig166).
                        margin:      [Number(marginArr[0]) || 0, Number(marginArr[1]) || 0],
                        overlay: {
                            ...baseOverlay,
                            desktop: writeDevice(ovDesktop, baseDesktop, /*withDesktopFlags*/ true,  ov),
                            mobile:  writeDevice(ovMobile,  baseMobile,  /*withDesktopFlags*/ false, ov),
                        },
                    };
                };
                for (const gl of ['nl', 'en', 'fr']) {
                    const L      = gl.toUpperCase();
                    const gs     = imageGridConfig.settings?.[gl] || {};
                    const gsOrig = originalImageGridConfigRef.current.settings?.[gl] || {};
                    const gsChanged = (current, original) => String(current) !== String(original);

                    const gridJsonCurrent  = gridJsonFor(gs);
                    const gridJsonOriginal = gridJsonFor(gsOrig);
                    if (JSON.stringify(gridJsonCurrent) !== JSON.stringify(gridJsonOriginal)) {
                        patches.push({ kind: 'imagegrid-cfg', path: `/admin/settings/IMAGE_GRID_CONFIG_${L}`, value: gridJsonCurrent });
                    }
                    const pushLangGrid = (keyBase, valCur, valOrig, coerce) => {
                        if (gsChanged(valCur, valOrig)) {
                            patches.push({ kind: 'imagegrid-cfg', path: `/admin/settings/${keyBase}_${L}`, value: coerce ? coerce(valCur) : valCur });
                        }
                    };
                    pushLangGrid('IMAGE_GRID_COLS',                gs.layout?.columns,          gsOrig.layout?.columns,          v => String(Number(v) || 0));
                    pushLangGrid('IMAGE_GRID_GUTTER',              gs.layout?.gap,              gsOrig.layout?.gap,              v => String(Number(v) || 0));
                    // IMAGE_GRID_KEEP_GRID_ON_MOBILE per lang (mig158) — stored
                    // as the string "true"/"false" to match the seed in mig112.
                    pushLangGrid('IMAGE_GRID_KEEP_GRID_ON_MOBILE', gs.layout?.keepGridOnMobile, gsOrig.layout?.keepGridOnMobile, v => v ? 'true' : 'false');
                    pushLangGrid('GRID_HOVER_ANIM_TYPE',           gs.hover?.effect,            gsOrig.hover?.effect,            v => String(v || 'none'));
                    pushLangGrid('GRID_HOVER_ANIM_ZOOM_SCALE',     gs.hover?.zoomLevel,         gsOrig.hover?.zoomLevel,         v => String(Number(v) || 0));
                    pushLangGrid('GRID_HOVER_ANIM_ZOOM_SPEED',     gs.hover?.animationSpeed,    gsOrig.hover?.animationSpeed,    v => String(Number(v) || 0));
                }
            }

            // Stores — one PATCH /stores/{id} per changed store, with only the
            // columns that actually changed (mirrors the legacy admin, which
            // sends e.g. {hours_fri: "09:30 - 15:30"}).
            // Map settings — 4 settings PATCHes (ride the shared `patches` list)
            // + the style file via POST /admin/maps-style (own op, custom body).
            const mapStyleOps = [];
            if (mapSettingsDirty) {
                const m = mapSettings, om = originalMapSettingsRef.current;
                if (!!m.multi !== !!om.multi) {
                    patches.push({ kind: 'map', path: '/admin/settings/ABOUT_US_MAP_MULTI', value: m.multi ? '1' : '0' });
                }
                if ((m.position || 'bottom') !== (om.position || 'bottom')) {
                    patches.push({ kind: 'map', path: '/admin/settings/ABOUT_US_MAP_POSITION', value: m.position || 'bottom' });
                }
                if (String(m.zoom) !== String(om.zoom)) {
                    patches.push({ kind: 'map', path: '/admin/settings/ABOUT_US_MAP_ZOOM', value: String(m.zoom) });
                }
                if (String(m.zoomMax) !== String(om.zoomMax)) {
                    patches.push({ kind: 'map', path: '/admin/settings/ABOUT_US_MAP_ZOOM_MAX', value: String(m.zoomMax) });
                }
                if (String(m.style || '') !== String(om.style || '')) {
                    mapStyleOps.push({ kind: 'map-style', method: 'POST', path: '/admin/maps-style', body: { style: m.style || '[]' } });
                }
            }

            const storeOps = [];
            if (storesDirty || separatorDirty) {
                const origById = Object.fromEntries(originalStoresRef.current.map(st => [st.id, st]));
                for (const st of stores) {
                    const curCols  = storeFormToColumns(st, hoursSeparator);
                    const orig     = origById[st.id];
                    const origCols = orig ? storeFormToColumns(orig, originalHoursSeparatorRef.current) : null;
                    const body = {};
                    for (const col of Object.keys(curCols)) {
                        if (!origCols || JSON.stringify(curCols[col]) !== JSON.stringify(origCols[col])) {
                            body[col] = curCols[col];
                        }
                    }
                    if (Object.keys(body).length) {
                        storeOps.push({ kind: 'store', method: 'PATCH', path: `/stores/${st.id}`, body });
                    }
                }
            }

            // Dynamic pages — create (POST) / update (PATCH) / soft-delete.
            // Only edited languages (present in `content`) are written, with the
            // unlayer {html, design} body + is_via_file=1; untouched languages
            // are omitted so their existing sidecars stay intact.
            // Information banners — create / update changed columns only /
            // soft-delete removed. Content lives in loc_* (single banner) or
            // items_* (item list); the inactive side is cleared on save.
            const bannerFormToColumns = (b) => {
                const multi = !!b.multipleItems;
                return {
                    name:             b.referenceName || '',
                    active:           b.isActive ? 1 : 0,
                    date_from:        b.activeFrom || null,
                    date_until:       b.activeTo   || null,
                    position:         BANNER_POS_TO_BACKEND[b.position] || 'top',
                    background_color: b.bgColor   || '',
                    foreground_color: b.textColor || '',
                    height:           Number(b.height) || 0,
                    is_animated:      b.scrollText ? 1 : 0,
                    is_item_list:     multi ? 1 : 0,
                    loc_nl:    !multi ? (b.content?.nl || '') : '',
                    loc_en:    !multi ? (b.content?.en || '') : '',
                    loc_fr:    !multi ? (b.content?.fr || '') : '',
                    items_nl:   multi ? (b.content?.nl || '') : '',
                    items_en:   multi ? (b.content?.en || '') : '',
                    items_fr:   multi ? (b.content?.fr || '') : '',
                };
            };
            const bannerOps = [];
            if (bannersDirty) {
                const origBanners = originalBannersRef.current;
                const origById = Object.fromEntries(origBanners.filter(b => b.serverId != null).map(b => [b.serverId, b]));
                const curServerIds = new Set(banners.filter(b => b.serverId != null).map(b => b.serverId));
                for (const ob of origBanners) {
                    if (ob.serverId != null && !curServerIds.has(ob.serverId)) {
                        bannerOps.push({ kind: 'banner-del', method: 'DELETE', path: `/information-banner/${ob.serverId}`, body: {} });
                    }
                }
                for (const b of banners) {
                    const curCols = bannerFormToColumns(b);
                    if (b.serverId == null) {
                        bannerOps.push({ kind: 'banner-new', method: 'POST', path: '/information-banner', body: curCols });
                    } else {
                        const orig = origById[b.serverId];
                        const origCols = orig ? bannerFormToColumns(orig) : null;
                        const body = {};
                        for (const col of Object.keys(curCols)) {
                            if (!origCols || JSON.stringify(curCols[col]) !== JSON.stringify(origCols[col])) {
                                body[col] = curCols[col];
                            }
                        }
                        if (Object.keys(body).length) {
                            bannerOps.push({ kind: 'banner-upd', method: 'PATCH', path: `/information-banner/${b.serverId}`, body });
                        }
                    }
                }
            }

            const dynamicPageOps = [];
            if (dynamicPagesDirty) {
                const origDyn = originalDynamicPagesRef.current;
                const origById = Object.fromEntries(origDyn.filter(p => p.serverId != null).map(p => [p.serverId, p]));
                const curServerIds = new Set(dynamicPages.filter(p => p.serverId != null).map(p => p.serverId));
                // soft-deletes
                for (const op of origDyn) {
                    if (op.serverId != null && !curServerIds.has(op.serverId)) {
                        dynamicPageOps.push({ kind: 'dynpage-del', method: 'DELETE', path: `/dynamic-pages/${op.serverId}`, body: {} });
                    }
                }
                for (const p of dynamicPages) {
                    const bodyLangs = {};
                    DYN_LANGS.forEach((lang) => {
                        if (p.content && p.content[lang]) {
                            bodyLangs[`body_${lang}`] = { html: p.content[lang].html || '', design: p.content[lang].design || null };
                        }
                    });
                    const hasContent = Object.keys(bodyLangs).length > 0;
                    const meta = {
                        route:    p.slug || '',
                        position: DYN_POS_TO_BACKEND[p.position] || 'HIDDEN',
                        loc_nl:   p.titles?.nl || '',
                        loc_en:   p.titles?.en || '',
                        loc_fr:   p.titles?.fr || '',
                        active:   p.isActive ? 1 : 0,
                        use_iframe: p.iframe ? 1 : 0,
                    };
                    if (p.serverId == null) {
                        dynamicPageOps.push({ kind: 'dynpage-new', method: 'POST', path: '/dynamic-pages', body: { ...meta, is_via_file: 1, ...bodyLangs } });
                    } else {
                        const orig = origById[p.serverId];
                        const metaChanged = !orig
                            || p.slug !== orig.slug || p.position !== orig.position
                            || p.isActive !== orig.isActive || p.iframe !== orig.iframe
                            || JSON.stringify(p.titles) !== JSON.stringify(orig.titles);
                        if (metaChanged || hasContent) {
                            dynamicPageOps.push({
                                kind: 'dynpage-upd', method: 'PATCH', path: `/dynamic-pages/${p.serverId}`,
                                body: { ...meta, ...(hasContent ? { is_via_file: 1, ...bodyLangs } : {}) },
                            });
                        }
                    }
                }
            }

            const results = await Promise.all([
                ...patches.map(p =>
                SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: webshopId,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'PATCH',
                    path:       p.path,
                    // Most settings PATCHes are { value: ... }. Patches
                    // that target a different endpoint shape (e.g. the
                    // `/admin/contents/{attribute}` content rows) set
                    // `rawBody` instead and we forward it verbatim.
                    body:       p.rawBody !== undefined ? p.rawBody : { value: p.value },
                }).then(r => ({ ...p, response: r }))),
                ...carouselOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...navbarOrderOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...imageGridOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...externalLinkOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...storeOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...mapStyleOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...fontGenerateOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...dynamicPageOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...bannerOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...overigeOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...cadeaubonOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...cadeaubonLayoutOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
                ...customerGroupOps.map(op =>
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     op.method,
                        path:       op.path,
                        body:       op.body,
                    }).then(r => ({ ...op, response: r })),
                ),
            ]);

            const failed = results.filter(r => !r.response || !r.response.success);
            if (failed.length) {
                const msg = failed.map(f => `${f.path}: ${f.response?.message || 'failed'}`).join(' · ');
                if (props.notify) props.notify('Save failed: ' + msg, 6000, 'top-right', props.theme, 1);
                return;
            }

            // Carousel image/video uploads. Run AFTER the row CRUD so freshly
            // created slides have a server id; the file rides as base64 in the
            // JSON proxy body (mini-api decodes it). Done before the carousel
            // refetch below so the reload picks up the new image filenames.
            if (carouselDirty) {
                const newIdByTemp = new Map();
                for (const r of results) {
                    if (r.kind === 'carousel-create' && r.slideRef && r.response?.success && r.response?.data?.id != null) {
                        newIdByTemp.set(r.slideRef.tempId, r.response.data.id);
                    }
                }
                const uploadTasks = [];
                for (const lang of ['nl', 'en', 'fr']) {
                    for (const sl of (carouselConfig.slides?.[lang] || [])) {
                        if (sl.pendingDelete) continue; // row is being deleted — don't upload to it
                        const sid = sl.serverId ?? newIdByTemp.get(sl.id);
                        if (sid == null) continue;
                        if (sl.desktopImage?.file)     uploadTasks.push({ sid, slot: 'image',        img: sl.desktopImage });
                        if (sl.alternativeImage?.file) uploadTasks.push({ sid, slot: 'image-second', img: sl.alternativeImage });
                    }
                }
                const uploadFails = [];
                for (const task of uploadTasks) {
                    try {
                        const b64 = await fileToBase64(task.img.file);
                        const up = await SftGlobalData.post('webshop&action=proxy', {
                            key:        props.api,
                            webshop_id: webshopId,
                            sftUserId:  props.login?.sft_user_id,
                            user:       props.login?.login,
                            method:     'POST',
                            path:       `/admin/carousel/${task.sid}/${task.slot}`,
                            body:       { image_base64: b64, filename: task.img.name, mime: task.img.mime || '' },
                        });
                        if (!up || !up.success) uploadFails.push(task.img.name);
                    } catch {
                        uploadFails.push(task.img.name);
                    }
                }
                if (uploadFails.length && props.notify) {
                    props.notify('Some images failed to upload: ' + uploadFails.join(', '), 6000, 'top-right', props.theme, 1);
                }
            }

            // Image-grid image uploads — same base64 flow as the carousel, after
            // the cell CRUD so new cells have a server id (mapped via cellRef).
            if (imageGridDirty) {
                const newGridIdByTemp = new Map();
                for (const r of results) {
                    if (r.kind === 'imagegrid-create' && r.cellRef && r.response?.success && r.response?.data?.id != null) {
                        newGridIdByTemp.set(r.cellRef.tempId, r.response.data.id);
                    }
                }
                const gridUploadTasks = [];
                for (const lang of ['nl', 'en', 'fr']) {
                    for (const cl of (imageGridConfig.cells?.[lang] || [])) {
                        if (cl.pendingDelete) continue; // row is being deleted — don't upload to it
                        const cid = cl.serverId ?? newGridIdByTemp.get(cl.id);
                        if (cid == null) continue;
                        if (cl.desktopImage?.file)     gridUploadTasks.push({ cid, slot: 'image',        img: cl.desktopImage });
                        if (cl.alternativeImage?.file) gridUploadTasks.push({ cid, slot: 'image-second', img: cl.alternativeImage });
                    }
                }
                const gridUploadFails = [];
                for (const task of gridUploadTasks) {
                    try {
                        const b64 = await fileToBase64(task.img.file);
                        const up = await SftGlobalData.post('webshop&action=proxy', {
                            key:        props.api,
                            webshop_id: webshopId,
                            sftUserId:  props.login?.sft_user_id,
                            user:       props.login?.login,
                            method:     'POST',
                            path:       `/admin/image-grid/${task.cid}/${task.slot}`,
                            body:       { image_base64: b64, filename: task.img.name, mime: task.img.mime || '' },
                        });
                        if (!up || !up.success) gridUploadFails.push(task.img.name);
                    } catch {
                        gridUploadFails.push(task.img.name);
                    }
                }
                if (gridUploadFails.length && props.notify) {
                    props.notify('Some images failed to upload: ' + gridUploadFails.join(', '), 6000, 'top-right', props.theme, 1);
                }
            }

            // Header/logo image uploads (logo, header background, favicon). These
            // hit /admin/images/general/{TYPE}, which moves the file AND updates
            // the matching IMG_{TYPE} setting server-side — no row CRUD needed.
            if (headerLogoDirty) {
                const HDR_IMG_TYPES = { logoUrl: 'LOGO', backgroundUrl: 'HEADER', faviconUrl: 'ICON' };
                const imgs = headerLogoConfig.images || {};
                const hdrFails = [];
                for (const field of Object.keys(HDR_IMG_TYPES)) {
                    const file = imgs[`${field}__file`];
                    if (!file) continue;
                    try {
                        const b64 = await fileToBase64(file);
                        const up = await SftGlobalData.post('webshop&action=proxy', {
                            key:        props.api,
                            webshop_id: webshopId,
                            sftUserId:  props.login?.sft_user_id,
                            user:       props.login?.login,
                            method:     'POST',
                            path:       `/admin/images/general/${HDR_IMG_TYPES[field]}`,
                            body:       { image_base64: b64, filename: file.name, mime: file.type || '' },
                        });
                        if (!up || !up.success) hdrFails.push(file.name);
                    } catch {
                        hdrFails.push(file.name);
                    }
                }
                if (hdrFails.length && props.notify) {
                    props.notify('Some images failed to upload: ' + hdrFails.join(', '), 6000, 'top-right', props.theme, 1);
                }
            }

            // Cadeaubon voucher-layout assets — preview image, physical layout
            // image (base64), and the digital Unlayer design. Run AFTER the
            // layout CRUD so new layouts have a server id (mapped via layoutRef).
            if (cadeaubonLayoutsDirty) {
                const newLayIdByTemp = new Map();
                for (const r of results) {
                    if (r.kind === 'lay-new' && r.layoutRef && r.response?.success && r.response?.data?.id != null) {
                        newLayIdByTemp.set(r.layoutRef.tempId, r.response.data.id);
                    }
                }
                const layFails = [];
                const postLayout = (sid, slot, body) => SftGlobalData.post('webshop&action=proxy', {
                    key: props.api, webshop_id: webshopId, sftUserId: props.login?.sft_user_id,
                    user: props.login?.login, method: 'POST', path: `/cheques-layouts/${sid}/${slot}`, body,
                });
                for (const l of cadeaubonLayouts) {
                    const sid = l.serverId ?? newLayIdByTemp.get(l.id);
                    if (sid == null) continue;
                    if (l.imageFile) {
                        try {
                            const b64 = await fileToBase64(l.imageFile);
                            const up = await postLayout(sid, 'image', { image_base64: b64, filename: l.imageFile.name, mime: l.imageFile.type || '' });
                            if (!up || !up.success) layFails.push(l.imageFile.name);
                        } catch { layFails.push(l.imageFile.name); }
                    }
                    if (l.layoutImageFile && l.type === 'PHYSICAL') {
                        try {
                            const b64 = await fileToBase64(l.layoutImageFile);
                            const up = await postLayout(sid, 'layout-image', { image_base64: b64, filename: l.layoutImageFile.name, mime: l.layoutImageFile.type || '' });
                            if (!up || !up.success) layFails.push(l.layoutImageFile.name);
                        } catch { layFails.push(l.layoutImageFile.name); }
                    }
                    if (l.designDirty && l.type === 'DIGITAL') {
                        try {
                            const up = await postLayout(sid, 'layout', { html: l.html || '', design: l.design || {} });
                            if (!up || !up.success) layFails.push(`${l.description || 'layout'} (design)`);
                        } catch { layFails.push(`${l.description || 'layout'} (design)`); }
                    }
                }
                if (layFails.length && props.notify) {
                    props.notify('Some voucher-layout assets failed to save: ' + layFails.join(', '), 6000, 'top-right', props.theme, 1);
                }
            }

            // Regenerate the storefront i18n JSON when a translation changed
            // (the look CTA label). Best-effort — the DB row is already saved.
            if (looksCtaSaved) {
                try {
                    await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/localization/generate',
                    });
                } catch { /* swallow — translation row is saved; JSON regen can be retried */ }
            }

            // Sync "saved" references so the dirty flags clear.
            if (orderDirty)         setHomeSectionsOrder(desiredHomeSectionsOrder);
            if (order404Dirty)      setPage404SectionsOrder(desiredPage404Order);
            if (productGridDirty)   originalProductGridConfigRef.current = productGridConfig;
            if (productCardDirty)   originalProductCardConfigRef.current = productCardConfig;
            if (styleSettingsDirty) originalStyleSettingsRef.current     = styleSettings;
            if (groupsFilterDirty)  originalGroupsFilterConfigRef.current = groupsFilterConfig;
            if (globalWidthsDirty)  originalGlobalWidthsRef.current        = globalWidths;
            // headerLogo: snapshot happens in the refetch block below (so newly
            // uploaded image URLs land and the pending `__file` markers clear).
            if (navigationDirty)    originalNavigationConfigRef.current    = navigationConfig;
            // Navbar order: plain snapshot — the PATCHed order_index values are
            // already what the rows hold (no server-assigned ids involved).
            if (navbarOrderDirty)   originalNavbarOrderRef.current         = navbarOrder;
            // Footer: settings parts are picked up by the simple ref sync
            // below; the external-links arrays need a refetch so freshly
            // POSTed rows get their serverId (and any DELETEd id is cleared).
            // We do the actual refetch in the block further down once all
            // ops have settled — for now just snapshot non-list fields.
            if (footerDirty)        originalFooterConfigRef.current        = {
                ...footerConfig,
                customLinks: originalFooterConfigRef.current.customLinks,
                socialMedia: originalFooterConfigRef.current.socialMedia,
            };
            if (extraFooterDirty)   originalExtraFooterConfigRef.current   = extraFooterConfig;
            if (staticPagesDirty)   originalStaticPagesRef.current         = staticPages;
            if (storesDirty)        originalStoresRef.current              = stores;
            if (separatorDirty)     originalHoursSeparatorRef.current      = hoursSeparator;
            if (mapSettingsDirty)   originalMapSettingsRef.current         = mapSettings;
            if (cardsDirty)         originalCardsRef.current               = cards;
            if (brandsConfigDirty)  originalBrandsConfigRef.current        = brandsConfig;
            if (looksConfigDirty)   originalLooksConfigRef.current         = looksConfig;
            if (overigeConfigDirty) originalOverigeConfigRef.current       = overigeConfig;
            // Synchronisatie: scalar settings only, no server-assigned ids.
            if (syncConfigDirty)    originalSyncConfigRef.current          = syncConfig;
            // Cadeaubon settings have no server-assigned ids, so a plain
            // snapshot clears the dirty flag.
            if (cadeaubonConfigDirty) originalCadeaubonConfigRef.current   = cadeaubonConfig;
            // Verwerking: snapshot the config and the rebuilt discount JSON so the
            // dirty flags clear (no server-assigned ids, so no refetch needed).
            if (processingConfigDirty) {
                // If the guest id was skipped (invalid), keep its original value in
                // the snapshot so the field stays flagged dirty/unsaved until fixed.
                const prevGuest = originalProcessingConfigRef.current.access?.guestCustomerId ?? '';
                originalProcessingConfigRef.current = guestIdSkipped
                    ? { ...processingConfig, access: { ...processingConfig.access, guestCustomerId: prevGuest } }
                    : processingConfig;
                const prev = originalProcChequeRef.current;
                const rebuild = (base, field) => {
                    const out = { ...(base || {}) };
                    Object.keys(processingConfig.discountTypes || {}).forEach((c6) => {
                        out[`99${c6}`] = !!(processingConfig.discountTypes[c6] || {})[field];
                    });
                    return out;
                };
                originalProcChequeRef.current = {
                    allowed:      rebuild(prev.allowed,      'enabled'),
                    asPayment:    rebuild(prev.asPayment,    'acceptAsPayment'),
                    addToReceipt: rebuild(prev.addToReceipt, 'showInEmail'),
                };
                // Sync the ticket-module baseline to what was effectively written.
                const om = originalTicketModuleRef.current;
                originalTicketModuleRef.current = (processingConfig.payments?.location === 'noCheckout')
                    ? 'LOOKBOOK'
                    : (om === 'LOOKBOOK' ? 'RESERVATION' : om);
            }
            // Cadeaubon translations: refetch so freshly-POSTed placeholders pick
            // up their server id + generated PLC_* key (otherwise the next save
            // would re-POST them).
            if (cadeaubonPlaceholdersDirty) {
                try {
                    const refreshPlc = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheque-placeholders',
                    });
                    const plcRows2 = (refreshPlc?.success && Array.isArray(refreshPlc.data)) ? refreshPlc.data : null;
                    const reloadedPlc = plcRows2 ? plcRows2.map(placeholderRowToForm) : cadeaubonPlaceholders;
                    setCadeaubonPlaceholders(reloadedPlc);
                    originalPlaceholdersRef.current = reloadedPlc;
                } catch {
                    originalPlaceholdersRef.current = cadeaubonPlaceholders;
                }
            }
            // Voucher layouts: refetch so new POSTs get their server id + the
            // new image filenames land (and pending picks/design flags clear).
            if (cadeaubonLayoutsDirty) {
                try {
                    const refreshLay = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheques-layouts',
                    });
                    const layRows2 = (refreshLay?.success && Array.isArray(refreshLay.data)) ? refreshLay.data : null;
                    // Image prefix captured at load time — no dump round-trip.
                    const reloadedLay = layRows2 ? layRows2.map(r => layoutRowToForm(r, layoutImgBaseRef.current)) : cadeaubonLayouts;
                    setCadeaubonLayouts(reloadedLay);
                    originalCadeaubonLayoutsRef.current = reloadedLay;
                } catch {
                    originalCadeaubonLayoutsRef.current = cadeaubonLayouts;
                }
            }
            // Custom customer fields: refetch the settings dump so freshly-POSTed
            // groups get their server id (else the next save re-POSTs them).
            if (customerGroupsDirty) {
                try {
                    // Dedicated list endpoint (mini-api GET added 2026-06-04) —
                    // avoids re-pulling the whole /admin/settings dump.
                    const refreshCcg = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/customer-category-groups',
                    });
                    const ccgRows2 = (refreshCcg?.success && Array.isArray(refreshCcg.data))
                        ? refreshCcg.data
                        : null;
                    const reloadedCcg = ccgRows2 ? ccgRows2.map(customerGroupRowToForm) : customerCategoryGroups;
                    setCustomerCategoryGroups(reloadedCcg);
                    originalCustomerGroupsRef.current = reloadedCcg;
                } catch {
                    originalCustomerGroupsRef.current = customerCategoryGroups;
                }
            }
            // Dynamic pages: refetch so freshly-POSTed pages pick up their real
            // id and edited `content` resets (designs re-lazy-load on next view).
            if (dynamicPagesDirty) {
                try {
                    const refreshDyn = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/dynamic-pages',
                    });
                    const dynRows2 = (refreshDyn?.success && Array.isArray(refreshDyn.data)) ? refreshDyn.data : null;
                    const reloaded = dynRows2 ? dynRows2.map(dynRowToPage) : dynamicPages;
                    setDynamicPages(reloaded);
                    originalDynamicPagesRef.current = reloaded;
                } catch {
                    originalDynamicPagesRef.current = dynamicPages;
                }
            }
            // Banners: there's no admin-wide GET list endpoint (the public
            // /information-banner filters by active+date), so refetch the
            // collections bundle and re-extract `informationBanners` to pick
            // up fresh ids for newly-POSTed banners.
            if (bannersDirty) {
                try {
                    const refreshSet = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings-collections',
                    });
                    const bannerRows2 = (refreshSet?.success && Array.isArray(refreshSet.data?.informationBanners))
                        ? refreshSet.data.informationBanners
                        : null;
                    const reloaded = bannerRows2 ? bannerRows2.map(bannerRowToForm) : banners;
                    setBanners(reloaded);
                    originalBannersRef.current = reloaded;
                } catch {
                    originalBannersRef.current = banners;
                }
            }
            if (imageGalleryDirty)  originalImageGalleryConfigRef.current  = imageGalleryConfig;
            if (productDetailInfoDirty) originalProductDetailInfoConfigRef.current = productDetailInfoConfig;
            // For carousel, refetch /admin/settings after CRUD to repopulate
            // serverIds (new POSTs need their fresh row id; without it the
            // next save would re-POST them). Falls back to the working
            // carouselConfig if the refetch hits an error.
            if (carouselDirty) {
                try {
                    // Dedicated list endpoint — much lighter than re-pulling
                    // the whole /admin/settings dump for one table.
                    const refresh = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/carousel',
                    });
                    const rows = (refresh?.success && Array.isArray(refresh?.data)) ? refresh.data : null;
                    if (rows) {
                        const buildImage2 = (fn, mime) => fn
                            ? { name: fn, url: `${carouselImgBaseRef.current}${fn}`, mime: mime || '' }
                            : null;
                        const rowToSlide2 = (r, idx) => ({
                            id:               `s_${r.id}`,
                            serverId:         r.id,
                            order:            r.order != null ? Number(r.order) : (idx + 1),
                            active:           !!Number(r.active),
                            desktopImage:     buildImage2(r.image,        r.mime_type),
                            alternativeImage: buildImage2(r.second_image, r.second_mime_type),
                            linkUrl:          r.link         || '',
                            hoverDesktop:     r.text_desktop || '',
                            hoverMobile:      r.text_mobile  || '',
                        });
                        const byLang2 = (lang) => rows
                            .filter(r => String(r.lang || '').toLowerCase() === lang)
                            .map(rowToSlide2);
                        const refreshedCarousel = {
                            ...carouselConfig,
                            slides: { nl: byLang2('nl'), en: byLang2('en'), fr: byLang2('fr') },
                        };
                        setCarouselConfig(refreshedCarousel);
                        originalCarouselConfigRef.current = refreshedCarousel;
                    } else {
                        originalCarouselConfigRef.current = carouselConfig;
                    }
                } catch {
                    originalCarouselConfigRef.current = carouselConfig;
                }
            }
            // Same dance for the image-grid: refetch after CRUD so new POSTs
            // get their server-assigned row ids before the next save.
            if (imageGridDirty) {
                try {
                    // Dedicated list endpoint — much lighter than re-pulling
                    // the whole /admin/settings dump for one table.
                    const refresh = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/image-grid',
                    });
                    const rows = (refresh?.success && Array.isArray(refresh?.data)) ? refresh.data : null;
                    if (rows) {
                        const buildGridImage3 = (fn, mime) => fn
                            ? { name: fn, url: `${gridImgBaseRef.current}${fn}`, mime: mime || '' }
                            : null;
                        const rowToCell3 = (r, idx) => ({
                            id:               `c_${r.id}`,
                            serverId:         r.id,
                            order:            r.order != null ? Number(r.order) : (idx + 1),
                            active:           !!Number(r.active),
                            // Keep in sync with the load-time rowToCell — without
                            // these the post-save refetch resets every cell to 1×1
                            // in memory until a full page reload re-reads them.
                            width:            Math.max(1, Number(r.width)  || 1),
                            height:           Math.max(1, Number(r.height) || 1),
                            desktopImage:     buildGridImage3(r.image,        r.mime_type),
                            alternativeImage: buildGridImage3(r.second_image, r.second_mime_type),
                            linkUrl:          r.link         || '',
                            hoverDesktop:     r.text_desktop || '',
                            hoverMobile:      r.text_mobile  || '',
                        });
                        const gridByLang3 = (lang) => rows
                            .filter(r => String(r.lang || '').toLowerCase() === lang)
                            .map(rowToCell3);
                        const refreshedImageGrid = {
                            ...imageGridConfig,
                            cells: { nl: gridByLang3('nl'), en: gridByLang3('en'), fr: gridByLang3('fr') },
                        };
                        setImageGridConfig(refreshedImageGrid);
                        originalImageGridConfigRef.current = refreshedImageGrid;
                    } else {
                        originalImageGridConfigRef.current = imageGridConfig;
                    }
                } catch {
                    originalImageGridConfigRef.current = imageGridConfig;
                }
            }
            // Header/logo: refetch after image uploads so the new IMG_ URLs land
            // and the pending `__file` markers clear (otherwise it stays dirty).
            // The core bundle carries the computed img_store_* URLs.
            if (headerLogoDirty) {
                try {
                    const refresh = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings-core',
                    });
                    if (refresh?.success && refresh.data) {
                        const s4 = refresh.data;
                        const hdrBase4 = shopImageBase(props.selectedWebshop?.display_name);
                        const px = (u) => (u ? `${hdrBase4}${u}` : '');
                        const refreshedHeaderLogo = {
                            size: { ...headerLogoConfig.size },
                            images: {
                                logoUrl:       px(s4.img_store_logo),
                                backgroundUrl: px(s4.img_store_header),
                                faviconUrl:    px(s4.img_store_icon),
                            },
                        };
                        setHeaderLogoConfig(refreshedHeaderLogo);
                        originalHeaderLogoConfigRef.current = refreshedHeaderLogo;
                    } else {
                        originalHeaderLogoConfigRef.current = headerLogoConfig;
                    }
                } catch {
                    originalHeaderLogoConfigRef.current = headerLogoConfig;
                }
            }
            // Footer external-links: refetch so freshly POSTed rows get a
            // serverId. We only need to refresh the customLinks/socialMedia
            // arrays — newsletter is already snapshotted above.
            if (footerDirty && externalLinkOps.length > 0) {
                try {
                    const refresh = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings-collections',
                    });
                    const links = (refresh?.success && Array.isArray(refresh?.data?.externalLinks)) ? refresh.data.externalLinks : null;
                    if (links) {
                        const toFooter = (r) => ({ id: `cl_${r.id}`, serverId: r.id, url: r.url || '', labels: { nl: r.loc_nl || '', en: r.loc_en || '', fr: r.loc_fr || '' } });
                        const toSocial = (r) => ({ id: `sm_${r.id}`, serverId: r.id, url: r.url || '' });
                        const refreshedFooter = {
                            ...footerConfig,
                            customLinks: links.filter(r => String(r.position || '').toUpperCase() !== 'SOCIAL').map(toFooter),
                            socialMedia: links.filter(r => String(r.position || '').toUpperCase() === 'SOCIAL').map(toSocial),
                        };
                        setFooterConfig(refreshedFooter);
                        originalFooterConfigRef.current = refreshedFooter;
                    }
                } catch { /* swallow — snapshot already in place */ }
            }

            if (props.notify) props.notify('Saved — regenerating statics…', 4000, 'top-right', props.theme, 2);
            if (guestIdSkipped && props.notify) props.notify('Guest customer ID was not saved — no customer with that ID exists.', 6000, 'top-right', props.theme, 1);
            // Fire the legacy generate_statics.php script so the storefront
            // picks up the new config without waiting for the next cron tick.
            fireGenerateStatics();
            // Carousel/image-grid JSON is only force-rewritten via ?detail (the
            // plain run skips it unless the row changed in the last 11 min, and
            // an image upload doesn't bump updated_at). Mirror the legacy admin
            // and force those generators so new images show immediately.
            if (carouselDirty)  fireGenerateStatics('__generateCarousel',  { silent: true });
            if (imageGridDirty) fireGenerateStatics('__generateImageGrid', { silent: true });
            // Baseline refs were just updated — force every dirty useMemo to
            // recompute so the Save button clears (see savedTick declaration).
            setSavedTick(t => t + 1);
        } catch (e) {
            if (props.notify) props.notify('Save failed: ' + (e?.message || 'network error'), 6000, 'top-right', props.theme, 1);
        } finally {
            setConfigSaving(false);
        }
    };

    // Track inline slot's position so the editor overlay can follow it.
    // The editor lives in a single portal target (document.body) so its iframe
    // never remounts; we just reposition with CSS. We also track the DialogContent
    // rect so we can clip the overlay to the dialog's visible area (prevents the
    // editor from overlapping modal content when the user scrolls).
    useLayoutEffect(() => {
        if (!containerModalOpen || !editorInlineSlot || editorFullscreen) return;

        const updateRect = () => {
            if (!editorInlineSlot) return;
            const rect = editorInlineSlot.getBoundingClientRect();
            const dialogRect = dialogContentRef.current?.getBoundingClientRect();
            setSlotRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                dialogTop: dialogRect?.top ?? 0,
                dialogBottom: dialogRect?.bottom ?? window.innerHeight,
            });
        };

        updateRect();

        const ro = new ResizeObserver(updateRect);
        ro.observe(editorInlineSlot);

        window.addEventListener('resize', updateRect);
        // capture=true to catch scroll events from DialogContent
        window.addEventListener('scroll', updateRect, true);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
        };
    }, [containerModalOpen, editorInlineSlot, editorFullscreen, editorLang]);

    // Style tab state
    const [primaryColor, setPrimaryColor] = useState('#1976d2');
    const [secondaryColor, setSecondaryColor] = useState('#9acfbf');
    const [fontFamily, setFontFamily] = useState('Inter');
    const [borderRadius, setBorderRadius] = useState(8);

    // Style tab: new layout (mockup + gear-modals)
    const [styleSettings, setStyleSettings] = useState({
        global: {
            background: '#ffffff',
            text: '#111111',
            border: '#e4e7eb',
            primary: '#1976d2',
            font: 'Roboto',
        },
        visibility: {
            languageBar: true,
            footer: true,
            extra: true,
            backToTopPosition: 'right',
            // Languages: visibleLanguages = active on storefront (VISIBILE_LANGUAGES),
            // adminLanguages = editable in the admin (ADMIN_LANGUAGES, a superset).
            visibleLanguages: ['NL', 'EN', 'FR'],
            adminLanguages:   ['NL', 'EN', 'FR'],
        },
        sections: {
            language:    { bgColor: '#f5f5f5', textColor: '#111111', activeColor: '#dcdcdc', position: 'right', widthPx: 0, enabledLanguages: ['NL', 'EN', 'FR'] },
            header:      { bgColor: '#000000', textColor: '#ffffff' },
            menu:        { bgColor: '#000000', textColor: '#ffffff', accentColor: '#e53935', alignment: 'center', showCartBadge: true, useCustomHover: false, primaryHover: '#1565c0', primaryHoverText: '#ffffff', secondaryHover: '#333333', secondaryHoverText: '#ffffff' },
            image:       { width: 50, shownData: 'productImage' },
            title:       { textColor: '#111111', fontSize: 18, position: 'left' },
            subtitle:    { textColor: '#666666', fontSize: 14 },
            text:        { textColor: '#333333', fontSize: 13 },
            price:       { mainColor: '#111111', discountColor: '#e53935' },
            addToCart:   { bgColor: '#1976d2', textColor: '#ffffff' },
            sideFilter:  { borderColor: '#e4e7eb' },
            footer:      { bgColor: '#f5f5f5', textColor: '#111111', columns: 3, showPaymentMethods: false },
            footerExtra: { bgColor: '#ffffff', textColor: '#666666', columns: 3 },
        },
    });
    // Dirty check for style settings — compares every legacy-mapped colour
    // path against the snapshot we took at load time.
    const styleSettingsDirty = useMemo(() => {
        const orig = originalStyleSettingsRef.current;
        if (!orig) return false;
        if (STYLE_COLOR_PATHS.some(p => readByPath(styleSettings, p.path) !== readByPath(orig, p.path))) return true;
        // Non-colour fields in the Global / Visibility panel.
        if ((styleSettings.global?.font) !== (orig.global?.font)) return true;
        // Custom-hover toggle (the 4 hover colours ride STYLE_COLOR_PATHS above).
        if ((styleSettings.sections?.menu?.useCustomHover) !== (orig.sections?.menu?.useCustomHover)) return true;
        const v = styleSettings.visibility || {}, ov = orig.visibility || {};
        if (v.languageBar !== ov.languageBar) return true;
        if (v.footer !== ov.footer) return true;
        if (v.backToTopPosition !== ov.backToTopPosition) return true;
        if (toLangCsv(v.visibleLanguages) !== toLangCsv(ov.visibleLanguages)) return true;
        if (toLangCsv(v.adminLanguages) !== toLangCsv(ov.adminLanguages)) return true;
        return false;
    }, [styleSettings, savedTick]);
    // { section: 'language'|'title'|..., mode: 'color'|'config', anchorPos? } or null
    const [activeStyleSection, setActiveStyleSection] = useState(null);
    const openStyleModal = (section, mode, anchorPos = null) => setActiveStyleSection({ section, mode, anchorPos });
    // Timestamp of the last color-picker interaction; used to suppress the Popover
    // from closing while the native OS color dialog is open (it steals focus and
    // MUI interprets the return click as a backdrop click).
    const colorPickerInteractionRef = useRef(0);

    // Google Fonts catalog (fetched from backend, cached in localStorage for 7 days)
    // Cache stores only Google's API data; DEFAULT_FONTS is prepended at read time.
    const [googleFonts, setGoogleFonts] = useState([]);
    useEffect(() => {
        const CACHE_KEY = 'sft_google_fonts_v1';
        const TTL_MS = 7 * 24 * 60 * 60 * 1000;
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed?.fetchedAt && Date.now() - parsed.fetchedAt < TTL_MS && Array.isArray(parsed.fonts)) {
                    // Strip any stale DEFAULT_FONTS from old cache entries before prepending fresh ones
                    const pureGoogle = parsed.fonts.filter(f => f.category !== 'default');
                    setGoogleFonts([...DEFAULT_FONTS, ...pureGoogle]);
                    return;
                }
            }
        } catch (e) { /* ignore cache errors */ }

        const url = SftGlobalData.baseURL_API + 'google&action=fetchFonts';
        axios.post(url, { key: props.api }, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        }).then((resp) => {
            const fonts = resp?.data?.data;
            if (Array.isArray(fonts) && fonts.length > 0) {
                // Stable sort by category (preserves popularity order within each group)
                const sorted = fonts
                    .map((f, i) => ({ ...f, _i: i }))
                    .sort((a, b) => {
                        const c = (a.category || '').localeCompare(b.category || '');
                        return c !== 0 ? c : a._i - b._i;
                    })
                    .map(({ _i, ...rest }) => rest);
                setGoogleFonts([...DEFAULT_FONTS, ...sorted]);
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), fonts: sorted }));
                } catch (e) { /* quota/serialization — skip */ }
            }
        }).catch(() => { /* silent — the fallback options still render */ });
    }, []);

    // Inject a <link> for the currently selected global font so the mockup
    // preview actually renders in that typeface. Each unique font is injected once.
    // System fonts (CSS stacks like "Arial, Helvetica, sans-serif") are skipped —
    // the browser resolves those locally and Google Fonts doesn't host them.
    const loadedFontLinksRef = useRef(new Set());
    useEffect(() => {
        const family = styleSettings?.global?.font;
        if (!family) return;
        if (loadedFontLinksRef.current.has(family)) return;
        // CSS font-family stacks contain commas — they're system fonts, not Google fonts.
        if (family.indexOf(',') !== -1) {
            loadedFontLinksRef.current.add(family);
            return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}&display=swap`;
        document.head.appendChild(link);
        loadedFontLinksRef.current.add(family);
    }, [styleSettings?.global?.font]);

    const updateStyleGlobal = (field, value) => {
        setStyleSettings(prev => ({ ...prev, global: { ...prev.global, [field]: value } }));
    };
    const updateStyleVisibility = (field, value) => {
        setStyleSettings(prev => ({ ...prev, visibility: { ...prev.visibility, [field]: value } }));
    };
    const updateStyleSection = (section, field, value) => {
        setStyleSettings(prev => ({
            ...prev,
            sections: { ...prev.sections, [section]: { ...prev.sections[section], [field]: value } },
        }));
    };

    // Features tab state
    const [shopName, setShopName] = useState('Demowwww');
    const [shopDomain, setShopDomain] = useState('shop.demowww.be');
    const [shopLanguage, setShopLanguage] = useState('nl');
    const [shopActive, setShopActive] = useState(true);

    // Processing tab state
    const [orderPrefix, setOrderPrefix] = useState('MOD-');
    const [autoConfirm, setAutoConfirm] = useState(true);
    const [stockSync, setStockSync] = useState(true);
    const [syncInterval, setSyncInterval] = useState(15);


    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop-config');
    }, []);

    // startTransition keeps the current tab visible while a lazy sub-tab's chunk
    // loads, instead of flashing the Suspense skeleton (which caused a visible
    // flicker + layout jump on fast/cached loads).
    const handleTabChange = (_e, newValue) => startTransition(() => setTabValue(newValue));

    // Hidden navigation search — easter-egg unlocked by clicking the
    // Shortcuts main tab 10 times. All search behaviour, the index, and the
    // visual bar live in ./search/WebshopConfigSearch.js.
    // Open a Pages front-page block's gear dialog from the search bar (carousel /
    // image grid). Looks the block up in the canonical catalog so the dialog gets
    // the real {id, i18nKey} (its title + which editor to render).
    const openBlockConfig = (blockId) => {
        const blk = STATIC_BLOCKS.find(b => b.id === blockId);
        if (blk) setConfigBlock(blk);
    };
    const search = useWebshopSearch({
        t:        props.t,
        theme:    props.theme,
        notify:   props.notify,
        setTabValue, setFeaturesTab, setPagesTab, setStyleTab, setProcTab, setSyncTab,
        openStyleModal, openBlockConfig,
    });

    // Helpers to find which list/index an item lives in
    // 'active' resolves to whichever page's active list is currently visible.
    // The Front page tab uses `active`; the 404 page tab uses `active404`.
    // The 404 page is the second sub-tab under the Pages main tab
    // (tabValue 0 = Pages, pagesTab 1 = 404) — NOT a top-level tab. The old
    // `tabValue === 2` check pointed at the Features tab, so drags on the 404
    // page were silently mutating the Front page's active list.
    const isPage404Tab = () => tabValue === 0 && pagesTab === 1;
    const getActiveList = () => (isPage404Tab() ? active404 : active);
    const setActiveList = (updater) => {
        if (isPage404Tab()) setActive404(updater);
        else setActive(updater);
    };
    // Available list is per-page too (Front page vs 404), so a drag on one
    // tab never mutates the other tab's catalog.
    const getAvailableList = () => (isPage404Tab() ? available404 : available);
    const setAvailableList = (updater) => {
        if (isPage404Tab()) setAvailable404(updater);
        else setAvailable(updater);
    };

    const findContainer = (id) => {
        if (getAvailableList().some(i => i.id === id)) return 'available';
        if (getActiveList().some(i => i.id === id)) return 'active';
        if (id === 'available' || id === 'active') return id;
        return null;
    };

    const getList = (column) => (column === 'active' ? getActiveList() : getAvailableList());
    const setList = (column, updater) => {
        if (column === 'active') setActiveList(updater);
        else setAvailableList(updater);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragStart = (event) => setActiveId(event.active.id);

    const handleDragOver = (event) => {
        const { active: activeDrag, over } = event;
        if (!over) return;

        const activeContainer = findContainer(activeDrag.id);
        const overContainer = findContainer(over.id);
        if (!activeContainer || !overContainer) return;
        if (activeContainer === overContainer) return;

        // Cross-column: move item between lists during drag for live feedback
        const activeItems = getList(activeContainer);
        const overItems = getList(overContainer);
        const activeIndex = activeItems.findIndex(i => i.id === activeDrag.id);
        const overIndex = overItems.findIndex(i => i.id === over.id);
        if (activeIndex < 0) return;

        const movedItem = activeItems[activeIndex];
        setList(activeContainer, prev => prev.filter(i => i.id !== activeDrag.id));
        setList(overContainer, prev => {
            const insertAt = overIndex >= 0 ? overIndex : prev.length;
            const withNew = [...prev];
            withNew.splice(insertAt, 0, movedItem);
            return withNew;
        });
    };

    const handleDragEnd = (event) => {
        const { active: activeDrag, over } = event;
        setActiveId(null);
        if (!over) return;

        const activeContainer = findContainer(activeDrag.id);
        const overContainer = findContainer(over.id);
        if (!activeContainer || !overContainer) return;

        if (activeContainer === overContainer) {
            const list = getList(activeContainer);
            const fromIndex = list.findIndex(i => i.id === activeDrag.id);
            const toIndex = list.findIndex(i => i.id === over.id);
            if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
                setList(activeContainer, arrayMove(list, fromIndex, toIndex));
            }
        }
    };

    const handleDragCancel = () => setActiveId(null);

    const openNewContainerModal = () => {
        setEditingContainerId(null);
        setContainerData(DEFAULT_CONTAINER);
        setEditorLang(0);
        setEditorFullscreen(false);
        setContainerModalOpen(true);
    };

    const openEditContainerModal = (item) => {
        setEditingContainerId(item.id);
        setContainerData(item.config || DEFAULT_CONTAINER);
        setEditorLang(0);
        setEditorFullscreen(false);
        setContainerModalOpen(true);
    };

    const closeContainerModal = () => {
        setContainerModalOpen(false);
        setEditingContainerId(null);
        setEditorFullscreen(false);
    };

    const captureEditorDesigns = () => {
        return new Promise((resolve) => {
            const designs = {};
            const refs = [
                { lang: 'NL', ref: editorRefNL },
                { lang: 'EN', ref: editorRefEN },
                { lang: 'FR', ref: editorRefFR },
            ];
            let completed = 0;
            refs.forEach(({ lang, ref }) => {
                if (ref.current?.editor) {
                    ref.current.editor.saveDesign((design) => {
                        designs[`design${lang}`] = design;
                        completed++;
                        if (completed === refs.length) resolve(designs);
                    });
                } else {
                    completed++;
                    if (completed === refs.length) resolve(designs);
                }
            });
            // Timeout safety in case editor refs aren't ready
            setTimeout(() => resolve(designs), 1500);
        });
    };

    const saveContainer = async () => {
        const designs = await captureEditorDesigns();
        const finalData = { ...containerData, ...designs };

        if (editingContainerId) {
            const updater = (list) => list.map(i => (i.id === editingContainerId
                ? { ...i, name: finalData.reference || 'Container', config: finalData }
                : i));
            setAvailable(updater);
            setAvailable404(updater);
            setActive(updater);
            setActive404(updater);
        } else {
            const newContainer = {
                id: `container_${Date.now()}`,
                name: finalData.reference || 'New container',
                isContainer: true,
                configurable: true,
                config: finalData,
            };
            // New containers are available on both the Front page and the 404 page.
            setAvailable(prev => [...prev, newContainer]);
            setAvailable404(prev => [...prev, newContainer]);
        }
        closeContainerModal();
    };

    const deleteContainer = (id) => {
        setAvailable(prev => prev.filter(i => i.id !== id));
        setAvailable404(prev => prev.filter(i => i.id !== id));
        setActive(prev => prev.filter(i => i.id !== id));
        setActive404(prev => prev.filter(i => i.id !== id));
    };

    const updateContainerField = (field, value) => {
        setContainerData(prev => ({ ...prev, [field]: value }));
    };

    const getEditorRef = (lang) => {
        if (lang === 'NL') return editorRefNL;
        if (lang === 'EN') return editorRefEN;
        if (lang === 'FR') return editorRefFR;
        return null;
    };

    const copyEditorDesign = (fromLang, toLang) => {
        const fromRef = getEditorRef(fromLang);
        const toRef = getEditorRef(toLang);
        if (!fromRef?.current?.editor || !toRef?.current?.editor) return;
        fromRef.current.editor.saveDesign((design) => {
            toRef.current.editor.loadDesign(design);
            updateContainerField(`design${toLang}`, design);
        });
    };

    const onEditorReady = (lang) => () => {
        const ref = getEditorRef(lang);
        const design = containerData[`design${lang}`];
        if (design && ref?.current?.editor) {
            try {
                ref.current.editor.loadDesign(design);
            } catch (e) { /* ignore */ }
        }
    };

    // Screen-wide translate helper (the section modals have their own local
    // copy inside renderSectionModalFields; this one serves the main render).
    const trs = (key, fb) => (props.t ? props.t(key, fb) : fb);
    const isDark = props.theme === 'dark';
    const panelBg = isDark ? '#22272e' : '#f7f8fa';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    // Sub-tab bar styling — "inverted selection" look: the whole band takes
    // the selected-blue (primary = sft_blue, the same blue the active MAIN tab
    // uses), and the tabs sit flat on top of it — inactive labels in a soft
    // grey, the active one in solid white with a white indicator. This makes
    // the sub-tabs read as one connected toolbar belonging to the active main
    // tab. The main tab bar's bottom border is removed so the white main tabs
    // flow straight into this blue band.
    // Sub-tab bar: blue band (the same blue an active MAIN tab uses for its
    // text). The selected sub-tab is made to LOOK like a selected main tab —
    // blue text — which on the blue band needs a white surface behind it. So
    // the active sub-tab becomes a white tab with blue text (like the active
    // tab in a coloured tab strip); inactive tabs are soft white on the blue.
    // The band itself has no padding; rounded top corners make it read as a
    // panel attached under the main tabs.
    const subTabBarSx = {
        mb: 2, mx: -3,
        backgroundColor: Colors.sft_blue,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    };
    const subTabsSx = {
        // Belt-and-braces: also paint the Tabs root blue so the band is solid
        // even where the Box behind it might not show through. It must carry
        // the same rounded top corners as the wrapping Box, otherwise its
        // square blue rectangle paints over and hides the Box's radius.
        backgroundColor: Colors.sft_blue,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        // The white active tab conveys selection — no underline needed.
        '& .MuiTabs-indicator':        { display: 'none' },
        // Spacing lives on the items (margin), not the band. Only the TOP
        // corners are rounded and there's no bottom margin, so the active
        // white tab reads like a real tab whose flat bottom connects down to
        // the content — framed by blue on the top and sides.
        '& .MuiTab-root':              { color: 'rgba(255,255,255,0.78)', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', mt: 0.75, mx: 0.5 },
        // MUI forces selected → primary.main with high specificity; override
        // with !important so the blue text sits on the white tab surface.
        '& .MuiTab-root.Mui-selected': { color: `${Colors.sft_blue} !important`, backgroundColor: cardBg, fontWeight: 700 },
        '& .MuiTabs-scrollButtons':    { color: 'rgba(255,255,255,0.78)' },
    };

    const blockLabel = (item) => {
        // Product-container blocks carry the raw DB row; derive their label from
        // the localized field for the CURRENT admin language at render time so
        // it follows a language switch (item.name was frozen at load time).
        if (item?.isContainer && item.container) {
            const c = item.container;
            const l = String(props.lang || i18n.language || 'nl').slice(0, 2).toLowerCase();
            return c[`loc_${l}`] || c.loc_nl || c.loc_en || c.loc_fr || c.reference || item.name;
        }
        return (props.t && item.i18nKey ? props.t(item.i18nKey, item.name) : item.name);
    };

    const BlockRowContent = ({ item, column }) => (
        <>
            <DragIndicatorIcon sx={{ color: mutedText, mr: 1.5, fontSize: 20 }} />
            <Typography sx={{ flexGrow: 1, color: textColor, fontSize: '1rem' }}>
                {blockLabel(item)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {item.isContainer && (
                    <IconButton
                        size="small"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); openEditContainerModal(item); }}
                        sx={{ color: mutedText }}
                        title="Configure container"
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                )}
                {item.isContainer && (
                    <IconButton
                        size="small"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); deleteContainer(item.id); }}
                        sx={{ color: Colors.def_red }}
                        title="Delete container"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
                {column === 'active' && item.configurable && !item.isContainer && (
                    <IconButton
                        size="small"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); setConfigBlock(item); }}
                        sx={{ color: mutedText }}
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </>
    );

    const BlockRow = ({ item, column }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: item.id, data: { column } });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0 : 1,
        };

        return (
            <Paper
                ref={setNodeRef}
                variant="outlined"
                style={style}
                {...attributes}
                {...listeners}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    mb: 1.5,
                    cursor: 'grab',
                    backgroundColor: cardBg,
                    borderColor: borderColor,
                    borderRadius: 2,
                    userSelect: 'none',
                    '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }
                }}
            >
                <BlockRowContent item={item} column={column} />
            </Paper>
        );
    };

    const BlockRowOverlay = ({ item, column }) => (
        <Paper
            variant="outlined"
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                cursor: 'grabbing',
                backgroundColor: cardBg,
                borderColor: borderColor,
                borderRadius: 2,
                userSelect: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            }}
        >
            <BlockRowContent item={item} column={column} />
        </Paper>
    );

    const DroppableColumn = ({ column, items, label }) => {
        const { setNodeRef, isOver } = useDroppable({ id: column });
        const isEmpty = items.length === 0;
        // Highlight the whole column whenever a block is dragged over it (not
        // only when empty), so the drop target reads clearly during a drag.
        const highlight = isOver;

        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    <Typography variant="h6" sx={{ color: textColor, fontWeight: 500 }}>
                        {label}
                    </Typography>
                    {column === 'available' && (
                        <IconButton
                            size="small"
                            onClick={openNewContainerModal}
                            sx={{ color: Colors.sft_blue }}
                            title="Add product container"
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                <Paper
                    ref={setNodeRef}
                    variant="outlined"
                    sx={{
                        backgroundColor: highlight
                            ? (isDark ? '#2a3340' : '#eef4ff')
                            : panelBg,
                        borderColor: highlight ? Colors.sft_blue : borderColor,
                        borderRadius: 2,
                        p: 2,
                        minHeight: 420,
                        borderStyle: highlight || isEmpty ? 'dashed' : 'solid',
                        borderWidth: highlight ? 2 : 1,
                        transition: 'background-color 200ms, border-color 200ms'
                    }}
                >
                    <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                        {items.map(item => (
                            <BlockRow key={item.id} item={item} column={column} />
                        ))}
                    </SortableContext>
                    {isEmpty && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: mutedText, fontStyle: 'italic' }}>
                            Drop blocks here
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    };

    const activeItem = activeId
        ? [...available, ...available404, ...active, ...active404].find(i => i.id === activeId)
        : null;

    // Features sub-tabs render placeholders for now — the underlying schema
    // for each (Gift voucher / Shop the look / Brands / Banners / Algemeen)
    // is still being defined. Slots are reserved here so the routing stays
    // stable while we wire each one to real backend settings.
    const renderFeaturesTab = () => (
        <>
            <Box sx={subTabBarSx}>
                <Tabs
                    value={featuresTab}
                    onChange={(_e, v) => startTransition(() => setFeaturesTab(v))}
                    variant="scrollable"
                    scrollButtons="auto"
                    className={props.theme}
                    sx={subTabsSx}
                >
                    <Tab label={props.t ? props.t('cfg_features_tab_cadeaubon', 'Cadeaubon')     : 'Cadeaubon'}     icon={<CardGiftcardIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_stl',       'Shop the look') : 'Shop the look'} icon={<CheckroomIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_banners',   'Banners')       : 'Banners'}       icon={<ViewCarouselIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_overige',   'Overige')       : 'Overige'}       icon={<InfoOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                </Tabs>
            </Box>
            {configLoading ? renderConfigSkeleton() : (<>
            <TabPanel value={featuresTab} index={0}><CadeaubonTab    theme={props.theme} t={props.t} config={cadeaubonConfig} onConfigChange={setCadeaubonConfig} placeholders={cadeaubonPlaceholders} onPlaceholdersChange={setCadeaubonPlaceholders} layouts={cadeaubonLayouts} onLayoutsChange={setCadeaubonLayouts} login={props.login} api={props.api} selectedWebshop={props.selectedWebshop} /></TabPanel>
            <TabPanel value={featuresTab} index={1}><ShopTheLookTab  theme={props.theme} t={props.t} config={looksConfig} onConfigChange={setLooksConfig} shopFieldLabels={shopFieldLabels} /></TabPanel>
            <TabPanel value={featuresTab} index={2}><BannersTab      theme={props.theme} t={props.t} banners={banners} onBannersChange={setBanners} /></TabPanel>
            <TabPanel value={featuresTab} index={3}><OverigeTab      theme={props.theme} t={props.t} config={overigeConfig} onConfigChange={setOverigeConfig} /></TabPanel>
            </>)}
        </>
    );

    // Items whose .onlyOn === 'page404' should only surface on the 404 tab's
    // Available column. Container/sales items (no onlyOn flag) show on both.
    const availableForFrontPage = available.filter(i => i.onlyOn !== 'page404');
    const availableForPage404   = available404.filter(i => !i.onlyOn || i.onlyOn === 'page404');

    const renderFrontPageTab = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                <DroppableColumn column="available" items={availableForFrontPage} label={props.t ? props.t('cfg_block_available', 'Available') : 'Available'} />
                <DroppableColumn column="active" items={active} label={props.t ? props.t('cfg_block_active', 'Active') : 'Active'} />
            </Box>
            <DragOverlay>
                {activeItem ? (
                    <BlockRowOverlay item={activeItem} column={findContainer(activeId)} />
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    // 404 page — same DnD as Front page but operates on its OWN `active404` +
    // `available404` lists, so composing the 404 page never affects the Front
    // page (landing) catalog. Per-container settings still differ because each
    // list entry carries its own .config.
    const renderPage404Tab = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                <DroppableColumn column="available" items={availableForPage404} label={props.t ? props.t('cfg_block_available', 'Available') : 'Available'} />
                <DroppableColumn column="active" items={active404} label={props.t ? props.t('cfg_block_active', 'Active') : 'Active'} />
            </Box>
            <DragOverlay>
                {activeItem ? (
                    <BlockRowOverlay item={activeItem} column={findContainer(activeId)} />
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    // --- Style tab helpers ---
    const markPickerInteraction = () => {
        colorPickerInteractionRef.current = Date.now();
    };
    // colorFieldProps is spread on each <ColorField .../> usage — passing them
    // as stable theme values keeps the module-level memo component stable.
    const colorFieldProps = { mutedText, borderColor, markPickerInteraction };

    const MockIconBtn = ({ onClick, top, right, left, bottom, children, title, color = 'rgba(0,0,0,0.55)', hoverColor = 'rgba(0,0,0,0.8)' }) => (
        <IconButton
            className="mock-config-icon"
            size="small"
            onClick={onClick}
            title={title}
            sx={{
                position: 'absolute',
                top, right, left, bottom,
                backgroundColor: color,
                color: '#ffffff',
                width: 26,
                height: 26,
                '&:hover': { backgroundColor: hoverColor },
                zIndex: 2,
                '& svg': { fontSize: 16 },
            }}
        >
            {children}
        </IconButton>
    );

    const MockGear = (props) => (
        <MockIconBtn {...props} title="Settings"><SettingsIcon /></MockIconBtn>
    );
    const MockBrush = (props) => (
        <MockIconBtn
            {...props}
            title="Colors"
            color="rgba(25,118,210,0.85)"
            hoverColor={Colors.sft_blue}
        >
            <BrushIcon />
        </MockIconBtn>
    );

    // Stack of icons anchored at a corner — auto-spaces multiple icons
    const getAnchorPos = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return { top: rect.bottom, left: rect.right };
    };

    const MockIcons = ({ section, color, config, top = 4, right = 4 }) => (
        <>
            {/* Visual order (left → right): brush, then gear. 'right' increases = more to the left. */}
            {color && <MockBrush top={top} right={config ? right + 32 : right} onClick={(e) => openStyleModal(section, 'color', getAnchorPos(e))} />}
            {config && <MockGear top={top} right={right} onClick={(e) => openStyleModal(section, 'config', getAnchorPos(e))} />}
        </>
    );

    const SECTION_LABELS = {
        language: 'Language bar',
        header: 'Header (logo)',
        menu: 'Menu bar',
        image: 'Product image',
        title: 'Product title',
        subtitle: 'Product subtitle',
        text: 'Description text',
        price: 'Price',
        addToCart: 'Add to cart button',
        footer: 'Footer',
        footerExtra: 'Footer (extra)',
        sideFilter: 'Groepen sidebar',
        groups: 'Filters and sorting',
        productCard: 'Product information (Verkenner)',
        productDetailInfo: 'Product detail information',
        globalWidths: 'Global width settings',
        grid: 'Product grid',
    };

    // Modal sizing tiers. Multi-card sections get a wide canvas; the richest
    // (multi-column / visual-preview) ones scale with the viewport so even a
    // 1920px screen is used. Everything else stays a comfortable form width.
    const STYLE_MULTICARD_SECTIONS = ['menu', 'footer', 'header', 'productDetailInfo', 'image', 'footerExtra', 'globalWidths', 'groups', 'productCard', 'grid'];
    // Every 2-column-masonry modal rides the viewport-wide tier — a single
    // column of stretched cards at lg looked sparse, two columns fill it.
    // image/footerExtra/globalWidths are single-card bodies → they stay lg.
    const STYLE_RICHEST_SECTIONS = ['menu', 'grid', 'footer', 'header', 'productDetailInfo', 'groups', 'productCard'];
    const styleSec = activeStyleSection?.section;
    const styleIsRichest = STYLE_RICHEST_SECTIONS.includes(styleSec);
    const styleIsMultiCard = STYLE_MULTICARD_SECTIONS.includes(styleSec);
    // A collapsed sidebar frees up horizontal room → let the modals grow a bit more.
    const sidebarCollapsed = !!props.hideSidebar;

    const renderSectionModalFields = () => {
        if (!activeStyleSection) return null;
        const { section, mode } = activeStyleSection;
        const s = styleSettings.sections[section];
        const setField = (field, value) => updateStyleSection(section, field, value);
        // Local i18n helper for the inline modal field labels — same shape
        // as the rest of the admin (`tr(key, fallback)`).
        const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

        if (mode === 'color') {
            switch (section) {
                case 'language':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',          'Background')}      value={s.bgColor}    onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text',        'Text color')}      value={s.textColor}  onChange={(v) => setField('textColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_active_lang', 'Active language')} value={s.activeColor} onChange={(v) => setField('activeColor', v)} />
                        </Stack>
                    );
                case 'header':
                case 'footer':
                case 'footerExtra':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',   'Background')} value={s.bgColor}   onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text', 'Text color')} value={s.textColor} onChange={(v) => setField('textColor', v)} />
                        </Stack>
                    );
                case 'menu':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_bg',     'Background')}          value={s.bgColor}     onChange={(v) => setField('bgColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text',   'Text color')}          value={s.textColor}   onChange={(v) => setField('textColor', v)} />
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_accent', 'Accent (cart badge)')} value={s.accentColor} onChange={(v) => setField('accentColor', v)} />
                            <Divider />
                            {/* Custom hover colours — gated by COLOR_USE_CUSTOM_HOVER.
                                Primary = COLOR_PRIMARY_HOVER(_TEXT), Secondary =
                                COLOR_SECONDARY_HOVER(_TEXT). Shown only when enabled. */}
                            <FormControlLabel
                                control={<Switch size="small" checked={!!s.useCustomHover} onChange={(e) => setField('useCustomHover', e.target.checked)} />}
                                label={tr('cfg_style_color_custom_hover', 'Gebruik eigen aangepaste zweefkleuren')}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                            />
                            {s.useCustomHover && (
                                <Stack spacing={2}>
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_primary_hover',        'Primary hover')}        value={s.primaryHover}       onChange={(v) => setField('primaryHover', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_primary_hover_text',   'Primary hover text')}   value={s.primaryHoverText}   onChange={(v) => setField('primaryHoverText', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_secondary_hover',      'Secondary hover')}      value={s.secondaryHover}     onChange={(v) => setField('secondaryHover', v)} />
                                    <ColorField {...colorFieldProps} label={tr('cfg_style_color_secondary_hover_text', 'Secondary hover text')} value={s.secondaryHoverText} onChange={(v) => setField('secondaryHoverText', v)} />
                                </Stack>
                            )}
                        </Stack>
                    );
                case 'text':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_text', 'Text color')} value={s.textColor} onChange={(v) => setField('textColor', v)} />
                        </Stack>
                    );
                case 'price':
                    // "Main price" colour was removed — that one's now the
                    // global Primary colour. Only the discount/sale tint
                    // stays section-editable.
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_discount', 'Discount / sale')} value={s.discountColor} onChange={(v) => setField('discountColor', v)} />
                        </Stack>
                    );
                case 'sideFilter':
                    return (
                        <Stack spacing={2}>
                            <ColorField {...colorFieldProps} label={tr('cfg_style_color_border', 'Border color')} value={s.borderColor} onChange={(v) => setField('borderColor', v)} />
                        </Stack>
                    );
                default:
                    return null;
            }
        }

        // mode === 'config'
        switch (section) {
            case 'language': {
                // Two language scopes (stored in styleSettings.visibility):
                //   adminLanguages   = editable in the admin (ADMIN_LANGUAGES)
                //   visibleLanguages = active on the storefront (VISIBILE_LANGUAGES, ⊆ admin)
                const visLangs = styleSettings.visibility.visibleLanguages || [];
                const admLangs = styleSettings.visibility.adminLanguages || [];
                const toggleAdmin = (code) => {
                    if (admLangs.includes(code)) {
                        updateStyleVisibility('adminLanguages', orderLangs(admLangs.filter(c => c !== code)));
                        // No longer editable → can't stay live on the storefront either.
                        if (visLangs.includes(code)) updateStyleVisibility('visibleLanguages', orderLangs(visLangs.filter(c => c !== code)));
                    } else {
                        updateStyleVisibility('adminLanguages', orderLangs([...admLangs, code]));
                    }
                };
                const toggleVisible = (code) => {
                    if (visLangs.includes(code)) {
                        updateStyleVisibility('visibleLanguages', orderLangs(visLangs.filter(c => c !== code)));
                    } else {
                        updateStyleVisibility('visibleLanguages', orderLangs([...visLangs, code]));
                    }
                };
                const langPill = (code, checked, onClick) => (
                    <Box
                        key={code}
                        onClick={() => onClick(code)}
                        sx={{
                            px: 1.5, py: 0.75, borderRadius: 1,
                            border: `1.5px solid ${checked ? Colors.sft_blue : borderColor}`,
                            backgroundColor: checked ? (isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)') : 'transparent',
                            color: checked ? Colors.sft_blue : textColor,
                            fontSize: '0.85rem', fontWeight: checked ? 600 : 400,
                            cursor: 'pointer', userSelect: 'none', transition: 'all 120ms',
                        }}
                    >
                        {code}
                    </Box>
                );
                return (
                    <ConfigCard anchor="cfg-card-language-bar" title={tr('cfg_lang_config_title', 'Taalbalk')}>
                    <Stack spacing={2}>
                        <TextField
                            select
                            autoFocus
                            label={tr('cfg_style_position', 'Position')}
                            size="small"
                            value={s.position}
                            onChange={(e) => setField('position', e.target.value)}
                        >
                            <MenuItem value="left">{tr('cfg_style_position_left',     'Left')}</MenuItem>
                            <MenuItem value="center">{tr('cfg_style_position_center', 'Center')}</MenuItem>
                            <MenuItem value="right">{tr('cfg_style_position_right',   'Right')}</MenuItem>
                        </TextField>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_style_max_width', 'Max-width')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Slider
                                    value={s.widthPx}
                                    min={0}
                                    max={2500}
                                    step={10}
                                    onChange={(_e, v) => setField('widthPx', v)}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(v) => `${v}px`}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    type="number"
                                    size="small"
                                    value={s.widthPx}
                                    onChange={(e) => setField('widthPx', Math.max(0, Math.min(2500, parseInt(e.target.value) || 0)))}
                                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    sx={{ width: 110 }}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_lang_admin_available', 'Beschikbaar in de admin')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {ALL_LANGUAGE_CODES.map(code => langPill(code, admLangs.includes(code), toggleAdmin))}
                            </Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75 }}>
                                {tr('cfg_lang_admin_available_hint', 'Talen waarvoor je teksten kan invullen in de admin.')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_lang_storefront_active', 'Actief op de webshop')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 32, alignItems: 'center' }}>
                                {admLangs.length
                                    ? orderLangs(admLangs).map(code => langPill(code, visLangs.includes(code), toggleVisible))
                                    : <Typography variant="caption" sx={{ color: mutedText }}>{tr('cfg_lang_none_available', 'Maak eerst een taal beschikbaar.')}</Typography>}
                            </Box>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75 }}>
                                {tr('cfg_lang_storefront_active_hint', 'Welke van die talen klanten effectief zien op de webshop.')}
                            </Typography>
                        </Box>
                    </Stack>
                    </ConfigCard>
                );
            }
            case 'header':
                // Header cog opens the Header / Logo modal — Size + Images cards.
                return (
                    <HeaderLogoModal
                        value={headerLogoConfig}
                        onChange={updateHeaderLogo}
                        onUploadImage={uploadHeaderImage}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'productDetailInfo':
                // The big one — eight cards covering layout, sections,
                // details list, language texts, title/url, breadcrumb,
                // related products, stock visibility.
                return (
                    <ProductDetailInformationModal
                        value={productDetailInfoConfig}
                        onChange={updateProductDetailInfo}
                        shopFieldLabels={shopFieldLabels}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'groups':
                // Filters & sorting — opened from the cog on the GROEPEN
                // sidebar in the Verkenner mockup.
                return (
                    <GroupsFilterModal
                        value={groupsFilterConfig}
                        onChange={setGroupsFilterConfig}
                        shopFieldLabels={shopFieldLabels}
                        filterStyle={navFilterStyle}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'productCard':
                // Product card layout — opened from the cog on the IMG
                // block of the last product card in the Verkenner mockup.
                // Controlled by hoisted state so the Verkenner preview
                // reflects edits live.
                return (
                    <ProductCardModal
                        value={productCardConfig}
                        onChange={setProductCardConfig}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'grid':
                // Product grid + variant grouping — opened from the small
                // cog that floats between cards 2 and 3 in the Verkenner
                // mockup (i.e. centred above the 4 mock cards).
                return (
                    <ProductGridModal
                        value={productGridConfig}
                        onChange={setProductGridConfig}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'globalWidths':
                // Centralised max-width settings — opened from the dedicated
                // sidebar button (too tall to embed inline in the sidebar).
                return (
                    <GlobalWidthSettingsModal
                        value={globalWidths}
                        onChange={updateGlobalWidths}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'image':
                // Image cog now opens the full Image Gallery modal —
                // thumbnail position picker, grid/scroll mode, max thumbs,
                // arrows + per-element colours.
                return (
                    <ImageGalleryModal
                        value={imageGalleryConfig}
                        onChange={updateImageGallery}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'text':
                return (
                    <Stack spacing={2}>
                        <TextField
                            autoFocus
                            label={tr('cfg_style_font_size', 'Font size')}
                            type="number"
                            size="small"
                            value={s.fontSize}
                            onChange={(e) => setField('fontSize', parseInt(e.target.value) || 0)}
                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        />
                    </Stack>
                );
            case 'title':
                return (
                    <Stack spacing={2}>
                        <TextField
                            autoFocus
                            label={tr('cfg_style_font_size', 'Font size')}
                            type="number"
                            size="small"
                            value={s.fontSize}
                            onChange={(e) => setField('fontSize', parseInt(e.target.value) || 0)}
                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        />
                        <TextField
                            select
                            label={tr('cfg_style_position', 'Position')}
                            size="small"
                            value={s.position}
                            onChange={(e) => setField('position', e.target.value)}
                        >
                            <MenuItem value="left">{tr('cfg_style_position_left',     'Left')}</MenuItem>
                            <MenuItem value="center">{tr('cfg_style_position_center', 'Center')}</MenuItem>
                            <MenuItem value="right">{tr('cfg_style_position_right',   'Right')}</MenuItem>
                        </TextField>
                    </Stack>
                );
            case 'subtitle':
                return (
                    <Stack spacing={2}>
                        <TextField
                            autoFocus
                            label={tr('cfg_style_font_size', 'Font size')}
                            type="number"
                            size="small"
                            value={s.fontSize}
                            onChange={(e) => setField('fontSize', parseInt(e.target.value) || 0)}
                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        />
                    </Stack>
                );
            case 'menu':
                // The menu cog now opens the full Navigation modal — composed
                // of Header content / Layout / Navigation menu / Behaviour
                // cards (each in its own file under style/cards/).
                return (
                    <NavigationModal
                        value={navigationConfig}
                        onChange={updateNavigation}
                        shopFieldLabels={shopFieldLabels}
                        navbarOrder={navbarOrder}
                        onNavbarOrderChange={setNavbarOrder}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'footer':
                // Footer cog opens the full Footer modal (Width / Pages /
                // Custom links / Social media / Contact / Newsletter cards).
                // Colour-only settings stay in the brush popover above.
                return (
                    <FooterModal
                        value={footerConfig}
                        onChange={updateFooter}
                        globalFooterWidth={globalWidths.perElement?.footer}
                        onChangeGlobalFooterWidth={(patch) => {
                            if (patch.width !== undefined)        updateGlobalWidths('perElement.footer.width',       patch.width);
                            if (patch.bgFullWidth !== undefined)  updateGlobalWidths('perElement.footer.bgFullWidth', patch.bgFullWidth);
                        }}
                        onEditPage={(slug) => {
                            // The "Edit page" buttons in PagesAndLinksCard
                            // are navigation shortcuts to the dedicated
                            // page editors in the Pages tab. We close the
                            // FooterModal dialog and jump to the matching
                            // sub-tab. Slugs without an internal page
                            // ('terms') have their button disabled in the
                            // card so this handler never sees them.
                            const PAGE_SUB_INDEX = { about: 4, disclaimer: 5, privacy: 6 };
                            const idx = PAGE_SUB_INDEX[slug];
                            if (idx === undefined) return;
                            setConfigBlock(null);
                            startTransition(() => {
                                setTabValue(0);     // Pages tab
                                setPagesTab(idx);
                            });
                        }}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            case 'footerExtra':
                // Extra-footer cog opens the full Extra Footer modal:
                // 3 columns × 3 languages, with column 1 unlayer and
                // columns 2-3 wysiwyg, plus a payment-methods toggle.
                return (
                    <ExtraFooterModal
                        value={extraFooterConfig}
                        onChange={updateExtraFooter}
                        theme={props.theme}
                        t={props.t}
                    />
                );
            default:
                return null;
        }
    };

    const renderStyleTab = (pageMode = 'productDetail') => {
        const g = styleSettings.global;
        const vis = styleSettings.visibility;
        const sec = styleSettings.sections;
        const tr  = (key, fb) => (props.t ? props.t(key, fb) : fb);

        return (
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
                gap: 3,
                alignItems: 'start',
            }}>

                {/* ===== LEFT COLUMN: Global + Visibility ===== */}
                {/* Compact panels — caption-sized titles, tighter spacing,
                    compact ColorFields, smaller switches. The whole sidebar
                    fits in roughly half the vertical space the v1 design
                    used. */}
                <Stack spacing={1.5}>
                    {/* Global */}
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6, color: mutedText, display: 'block', mb: 1, lineHeight: 1.4 }}>
                            {tr('cfg_style_global_title', 'Global')}
                        </Typography>
                        <Stack spacing={1}>
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_bg',      'Background color')} value={g.background} onChange={(v) => updateStyleGlobal('background', v)} />
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_text',    'Text color')}       value={g.text}       onChange={(v) => updateStyleGlobal('text', v)} />
                            <ColorField {...colorFieldProps} compact label={tr('cfg_style_global_primary', 'Primary color')}    value={g.primary}    onChange={(v) => updateStyleGlobal('primary', v)} />
                            <Autocomplete
                                size="small"
                                fullWidth
                                disableClearable
                                options={googleFonts.length > 0 ? googleFonts : [
                                    ...DEFAULT_FONTS,
                                    { family: 'Roboto',      category: 'sans-serif' },
                                    { family: 'Inter',       category: 'sans-serif' },
                                    { family: 'Open Sans',   category: 'sans-serif' },
                                    { family: 'Lato',        category: 'sans-serif' },
                                    { family: 'Nunito',      category: 'sans-serif' },
                                    { family: 'Montserrat',  category: 'sans-serif' },
                                    { family: 'Poppins',     category: 'sans-serif' },
                                ]}
                                value={googleFonts.find(f => f.family === g.font) || { family: g.font, category: 'sans-serif' }}
                                onChange={(_, newValue) => {
                                    if (newValue?.family) updateStyleGlobal('font', newValue.family);
                                }}
                                isOptionEqualToValue={(opt, val) => opt.family === val.family}
                                getOptionLabel={(opt) => opt?.family || ''}
                                groupBy={(opt) => opt.category}
                                renderOption={(props, opt) => (
                                    <li {...props} key={opt.family} style={{ fontFamily: `"${opt.family}"`, fontSize: '0.85rem' }}>
                                        {opt.family}
                                    </li>
                                )}
                                renderInput={(params) => <TextField {...params} label={tr('cfg_style_global_font', 'Font')} sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }} />}
                            />
                            {/* Button border-radius (BUTTON_BORDER_RADIUS) — a
                                new-admin-only key, created on first save via the
                                mini-api creatable whitelist. State rides on
                                globalWidths so load/save/dirty stay in one place. */}
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label={tr('cfg_style_global_button_radius', 'Button rounding')}
                                title={tr('cfg_style_global_button_radius_help', 'Border-radius applied to buttons across the webshop (e.g. add to cart). 0 = square corners.')}
                                value={globalWidths.buttonBorderRadius ?? 4}
                                onChange={(e) => updateGlobalWidths('buttonBorderRadius', Math.max(0, parseInt(e.target.value) || 0))}
                                InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>px</Typography></InputAdornment> }}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
                            />
                        </Stack>
                    </Paper>

                    {/* Visibility */}
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6, color: mutedText, display: 'block', mb: 0.5, lineHeight: 1.4 }}>
                            {tr('cfg_style_visibility_title', 'Visibility')}
                        </Typography>
                        <Stack spacing={0}>
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.languageBar} onChange={(e) => updateStyleVisibility('languageBar', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_language_bar', 'Language bar')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.footer} onChange={(e) => updateStyleVisibility('footer', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_footer', 'Footer')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <FormControlLabel
                                control={<Switch color="success" size="small" checked={vis.extra} onChange={(e) => updateStyleVisibility('extra', e.target.checked)} />}
                                label={<Typography variant="caption" sx={{ fontSize: '0.78rem' }}>{tr('cfg_style_visibility_extra_footer', 'Extra footer')}</Typography>}
                                labelPlacement="start"
                                sx={{ ml: 0, mr: 0, justifyContent: 'space-between', width: '100%' }}
                            />
                            <Box sx={{ mt: 0.75 }}>
                                <TextField
                                    select
                                    label={tr('cfg_style_visibility_back_to_top', 'Back-to-top position')}
                                    size="small"
                                    value={vis.backToTopPosition}
                                    onChange={(e) => updateStyleVisibility('backToTopPosition', e.target.value)}
                                    fullWidth
                                    sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
                                >
                                    <MenuItem value="left">{tr('cfg_style_visibility_btt_left',   'Bottom left')}</MenuItem>
                                    <MenuItem value="center">{tr('cfg_style_visibility_btt_center', 'Bottom center')}</MenuItem>
                                    <MenuItem value="right">{tr('cfg_style_visibility_btt_right',  'Bottom right')}</MenuItem>
                                    <MenuItem value="hidden">{tr('cfg_style_visibility_btt_hidden', 'Hidden')}</MenuItem>
                                </TextField>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Global width settings — opens its own modal because
                        the full per-element list is too tall for the sidebar.
                        Same data is also reachable from the per-section
                        modals (Header / Footer / banners). */}
                    <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                            onClick={() => openStyleModal('globalWidths', 'config')}
                            fullWidth
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.5 }}
                        >
                            {props.t ? props.t('cfg_widths_open', 'Global width settings') : 'Global width settings'}
                        </Button>
                    </Paper>

                </Stack>

                {/* ===== RIGHT COLUMN: Webshop mockup ===== */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${borderColor}`,
                        backgroundColor: g.background,
                        color: g.text,
                        fontFamily: g.font,
                        overflow: 'hidden',
                        position: 'relative',
                        // Force every descendant to inherit the mockup font.
                        // MUI Typography variants (h1/body1/etc.) have more specific
                        // selectors than "& *", so we need !important to win the
                        // cascade. Scoped to this Paper only.
                        '& *': { fontFamily: 'inherit !important' },
                        // Hide the gear/brush config icons until the user hovers
                        // the mockup — lets you see a clean preview.
                        '& .mock-config-icon': {
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.15s ease',
                        },
                        '&:hover .mock-config-icon': {
                            opacity: 1,
                            pointerEvents: 'auto',
                        },
                    }}
                >
                    {/* Language bar */}
                    {vis.languageBar && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.language.bgColor,
                            color: sec.language.textColor,
                            px: 2, py: 0.75,
                            fontSize: '0.8rem',
                            borderBottom: `1px solid ${borderColor}`,
                            pr: 10, // reserve space for icons so they never overlap
                        }}>
                            <Box sx={{
                                display: 'flex',
                                gap: 1.5,
                                // 0 = no constraint (natural content width); otherwise cap at the chosen max.
                                ...(sec.language.widthPx > 0 ? { maxWidth: `${sec.language.widthPx}px`, width: '100%' } : {}),
                                marginLeft: sec.language.position === 'left' ? 0 : 'auto',
                                marginRight: sec.language.position === 'right' ? 0 : 'auto',
                                // Inner alignment is opposite of outer position, so increasing
                                // width pushes the whole (still-packed) language group inward.
                                justifyContent: sec.language.position === 'right' ? 'flex-start'
                                    : sec.language.position === 'left' ? 'flex-end'
                                        : 'center',
                            }}>
                                {(styleSettings.visibility.visibleLanguages || []).map((lang, idx) => (
                                    <span
                                        key={lang}
                                        // COLOR_TEXT_ACTIVE_LANG is a TEXT colour (default #fff). Apply it as
                                        // `color` rather than `backgroundColor` so the active language is just
                                        // visually emphasised, not painted as a solid block.
                                        style={idx === 0 ? { color: sec.language.activeColor, fontWeight: 600 } : undefined}
                                    >
                                        {lang}
                                    </span>
                                ))}
                            </Box>
                            <MockIcons section="language" color config top={4} right={6} />
                        </Box>
                    )}

                    {/* Header */}
                    <Box sx={{
                        position: 'relative',
                        backgroundColor: sec.header.bgColor,
                        color: sec.header.textColor,
                        py: 3,
                        textAlign: 'center',
                        letterSpacing: 1,
                    }}>
                        <Typography sx={{ color: sec.header.textColor, fontWeight: 600, fontSize: '1.1rem' }}>
                            [LOGO]
                        </Typography>
                        <MockIcons section="header" color config top={8} right={8} />
                    </Box>

                    {/* Menu */}
                    <Box sx={{
                        position: 'relative',
                        backgroundColor: sec.menu.bgColor,
                        color: sec.menu.textColor,
                        py: 1.25,
                        px: 2,
                        display: 'flex',
                        justifyContent: sec.menu.alignment === 'left' ? 'flex-start'
                            : sec.menu.alignment === 'right' ? 'flex-end'
                                : 'center',
                        gap: 3,
                        fontSize: '0.85rem',
                        pr: 10,
                    }}>
                        <span>Category</span>
                        <span>|</span>
                        <span>Category</span>
                        <span>|</span>
                        <span>Category</span>
                        <span>|</span>
                        <span style={{ position: 'relative' }}>
                            Cart
                            {sec.menu.showCartBadge && (
                                <span style={{
                                    position: 'absolute',
                                    top: -6, right: -10,
                                    backgroundColor: sec.menu.accentColor,
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    width: 14, height: 14,
                                    borderRadius: '50%',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>3</span>
                            )}
                        </span>
                        <span>|</span>
                        <span>Account</span>
                        <MockIcons section="menu" color config top={6} right={8} />
                    </Box>

                    {/* ── Page-specific body ─────────────────────────────────
                        Shared sections above (language/header/menu) and below
                        (footer/footerExtra) render the same for both sub-tabs.
                        Editing them on one tab automatically reflects on the
                        other because they read from the same styleSettings
                        state. The body below is the only thing that swaps
                        between Explorer and Product detail. */}
                    {pageMode === 'explorer' ? (
                    <>
                        {/* Sidebar + grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 2, p: 2 }}>
                            {/* Sidebar — only the HEADER strip uses the menu's
                                bg + text colours; the body stays transparent so
                                the page background shows through. The outer
                                border is editable (maps to COLOR_BORDERS). */}
                            <Paper sx={{
                                position: 'relative',
                                borderRadius: 1,
                                backgroundColor: 'transparent',
                                border: `1px solid ${sec.sideFilter.borderColor}`,
                                boxShadow: 'none',
                                overflow: 'hidden',
                            }}>
                                {/* Two separate sections in the same corner —
                                    brush opens the sideFilter color modal, cog
                                    opens the groups filters & sorting modal.
                                    Cog stays rightmost; brush sits to its left. */}
                                <MockIcons section="sideFilter" color  top={4} right={36} />
                                <MockIcons section="groups"     config top={4} right={4}  />
                                {/* Coloured header strip */}
                                <Box sx={{
                                    backgroundColor: sec.menu.bgColor,
                                    color: sec.menu.textColor,
                                    px: 1.5, py: 1,
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                }}>
                                    GROEPEN
                                </Box>
                                {/* Body — uses the global text colour so the
                                    category list reads against the page bg, not
                                    the menu's textColor. */}
                                <Stack spacing={0.5} sx={{ fontSize: '0.8rem', color: g.text, p: 1.5 }}>
                                    <span style={{ fontWeight: 600 }}>KLEDING</span>
                                    {['Bh', 'Blazer', 'Blouse', 'Body', 'Cardigan', 'Chino', 'Dress', 'Hoody', 'Jacket & coat'].map((c) => (
                                        <span key={c} style={{ paddingLeft: 8 }}>{c}</span>
                                    ))}
                                    <span style={{ fontWeight: 600, marginTop: 6 }}>SCHOENEN</span>
                                    <span style={{ fontWeight: 600 }}>ACCESSOIRES</span>
                                </Stack>
                            </Paper>

                            {/* Right column: sort bar + grid */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Sort/filter toolbar */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {['MERKEN', 'MATEN', 'PRIJS', 'CATEGORIE'].map((label) => (
                                        <Box key={label} sx={{
                                            px: 1.25, py: 0.5,
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: 0.75,
                                            fontSize: '0.7rem',
                                            color: sec.title.textColor,
                                            backgroundColor: '#ffffff',
                                        }}>
                                            {label} ▾
                                        </Box>
                                    ))}
                                    <Box sx={{ flex: 1 }} />
                                    <Box sx={{
                                        px: 1.25, py: 0.5,
                                        border: `1px solid ${borderColor}`,
                                        borderRadius: 0.75,
                                        fontSize: '0.7rem',
                                        color: sec.title.textColor,
                                        backgroundColor: '#ffffff',
                                    }}>
                                        Sorteren ▾
                                    </Box>
                                </Box>

                                {/* Product grid (4 mockup cards) — wrapped in a
                                    relatively-positioned Box so the grid-config
                                    cog can float at the top center, sitting
                                    visually between cards 2 and 3.

                                    FUTURE: when the storefront supports an
                                    outlined product-card wrapper, flip
                                    productGridConfig.productGrid.showCardOutline
                                    on (and expose the toggle in the modal) to
                                    light up the bordered look below. Today the
                                    flag stays false so the preview matches what
                                    the live shop renders. */}
                                <Box sx={{ position: 'relative' }}>
                                    {/* Grid cog: sits at the seam between card 2
                                        and card 3. Slight negative top moves it
                                        above the cards so it doesn't crowd the
                                        IMG content. */}
                                    <IconButton
                                        className="mock-config-icon"
                                        size="small"
                                        onClick={(e) => openStyleModal('grid', 'config', getAnchorPos(e))}
                                        title="Grid settings"
                                        sx={{
                                            position: 'absolute',
                                            top: -14,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 26, height: 26,
                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                            color: '#fff',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                            '& svg': { fontSize: 15 },
                                            zIndex: 3,
                                        }}
                                    >
                                        <SettingsIcon />
                                    </IconButton>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: 1.5,
                                }}>
                                    {[1, 2, 3, 4].map((i) => {
                                        const showOutline = !!productGridConfig?.productGrid?.showCardOutline;
                                        return (
                                        <Paper key={i} variant={showOutline ? 'outlined' : 'elevation'} elevation={0} sx={{
                                            p: 1.25,
                                            borderRadius: showOutline ? 1 : 0,
                                            backgroundColor: showOutline ? '#ffffff' : 'transparent',
                                            border: showOutline ? undefined : 'none',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}>
                                            {i === 4 && (
                                                <>
                                                    {/* Discount badge sits one position inward so the
                                                        brush can take the rightmost corner — same visual
                                                        rhythm as the productDetail subtab where the brush
                                                        sits to the right of the discount. */}
                                                    <Box sx={{
                                                        position: 'absolute', top: 6, right: 36,
                                                        backgroundColor: sec.price.discountColor,
                                                        color: '#fff',
                                                        px: 0.5, py: 0.1,
                                                        borderRadius: 0.5,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 700,
                                                        zIndex: 2,
                                                    }}>-30%</Box>
                                                    {/* Brush at top-right corner, just to the right of
                                                        the discount badge — opens the price section's
                                                        color modal. */}
                                                    <IconButton
                                                        className="mock-config-icon"
                                                        size="small"
                                                        onClick={(e) => openStyleModal('price', 'color', getAnchorPos(e))}
                                                        title="Colors"
                                                        sx={{
                                                            position: 'absolute', top: 4, right: 6,
                                                            width: 22, height: 22,
                                                            backgroundColor: 'rgba(25,118,210,0.85)',
                                                            color: '#fff',
                                                            '&:hover': { backgroundColor: Colors.sft_blue },
                                                            '& svg': { fontSize: 13 },
                                                            zIndex: 2,
                                                        }}
                                                    >
                                                        <BrushIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                            <Box sx={{
                                                position: 'relative',
                                                width: '100%', aspectRatio: '3 / 4',
                                                backgroundColor: '#f3f3f3',
                                                borderRadius: 0.5,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#999', fontSize: '0.7rem',
                                                mb: 0.75,
                                            }}>
                                                IMG
                                                {i === 4 && (
                                                    /* Cog overlays at center, offset right of "IMG" text —
                                                        absolute positioning keeps IMG truly centered. */
                                                    <IconButton
                                                        className="mock-config-icon"
                                                        size="small"
                                                        onClick={(e) => openStyleModal('productCard', 'config', getAnchorPos(e))}
                                                        title="Settings"
                                                        sx={{
                                                            position: 'absolute',
                                                            left: 'calc(50% + 18px)',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            width: 22, height: 22,
                                                            backgroundColor: 'rgba(0,0,0,0.55)',
                                                            color: '#fff',
                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                                                            '& svg': { fontSize: 13 },
                                                        }}
                                                    >
                                                        <SettingsIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            {/* Product info — title, optional divider, subtitle, price.
                                                Layout pulled from productCardConfig so live edits in the
                                                Product information modal are reflected here. */}
                                            <Typography sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: sec.title.textColor,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                                pb: productCardConfig.productInfo.showDivider ? 0.4 : 0,
                                                borderBottom: productCardConfig.productInfo.showDivider ? '1px solid lightgrey' : 'none',
                                                mb: productCardConfig.productInfo.showDivider ? 0.4 : 0,
                                            }}>
                                                Product {i}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.65rem',
                                                color: sec.subtitle.textColor,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                            }}>
                                                ARTICLE.{i}0010101
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: '0.8rem',
                                                color: g.primary,
                                                fontWeight: 600,
                                                mt: 0.25,
                                                textAlign: productCardConfig.productInfo.align || 'left',
                                            }}>
                                                € {(99 + i * 10).toFixed(2)}
                                            </Typography>
                                        </Paper>
                                        );
                                    })}
                                </Box>
                                </Box>
                            </Box>
                        </Box>
                    </>
                    ) : (
                    /* Product row */
                    <>
                    {/* The page-level cog used to live in a spacer band above
                        the breadcrumb. It now sits inline with the product title
                        (rendered inside the product row below) so it aligns
                        with the focal point of the page. */}
                    {/* Breadcrumb — driven by the Product detail info modal's
                        breadcrumb settings. Shown above the product so the
                        preview honestly reflects how much vertical space the
                        crumb-row eats. */}
                    {productDetailInfoConfig.breadcrumb?.enabled !== false && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, fontSize: '0.78rem', color: g.text }}>
                            <span style={{ opacity: 0.7 }}>◀ Vorige</span>
                            <span style={{ opacity: 0.3 }}>|</span>
                            {productDetailInfoConfig.breadcrumb?.showRootLink && (
                                <>
                                    {/* Crumb links use the regular breadcrumb text
                                        colour (inherited from the row's `color: g.text`),
                                        not the primary web colour — matches the live
                                        storefront where the whole trail is one colour
                                        and only the current page is emphasised. */}
                                    <span>Shop</span>
                                    <span style={{ opacity: 0.5 }}>{productDetailInfoConfig.breadcrumb?.separator || '/'}</span>
                                </>
                            )}
                            <span>KLEDING</span>
                            <span style={{ opacity: 0.5 }}>{productDetailInfoConfig.breadcrumb?.separator || '/'}</span>
                            <span style={{ fontWeight: 600 }}>Blouse</span>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, p: 3, borderBottom: `1px solid ${borderColor}`, position: 'relative' }}>
                        {/* Page-level cog (Product detail information modal).
                            Aligned with the language/header/menu cogs above
                            (right: 8) so the right column of cogs reads as a
                            consistent vertical line. */}
                        <MockIcons section="productDetailInfo" config top={8} right={8} />
                        {/* Image gallery — main image + thumbnail strip,
                            laid out per the Image gallery modal's
                            thumbnailPosition setting. */}
                        <Box sx={{
                            position: 'relative',
                            width: `${sec.image.width}%`,
                            minWidth: 160,
                        }}>
                            {(() => {
                                const pos        = imageGalleryConfig.thumbnailPosition || 'left';
                                const maxThumbs  = Math.max(1, Math.min(8, Number(imageGalleryConfig.maxThumbnails) || 3));
                                const showArrows = imageGalleryConfig.showArrows;
                                const arrowFg    = imageGalleryConfig.arrowColor   || '#000';
                                const arrowBg    = imageGalleryConfig.arrowBgColor || '#fff';

                                const main = (
                                    <Box sx={{
                                        position: 'relative',
                                        flex: 1,
                                        aspectRatio: '1 / 1',
                                        backgroundColor: '#f3f3f3',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999',
                                        fontSize: '0.8rem',
                                    }}>
                                        IMG
                                        {showArrows && (
                                            <>
                                                <Box sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', backgroundColor: arrowBg, color: arrowFg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>‹</Box>
                                                <Box sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', backgroundColor: arrowBg, color: arrowFg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>›</Box>
                                            </>
                                        )}
                                    </Box>
                                );
                                const thumbs = (orientation) => (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: orientation === 'col' ? 'column' : 'row',
                                        gap: 0.75,
                                        flexShrink: 0,
                                        ...(orientation === 'col'
                                            ? { width: 56 }
                                            : { width: '100%' }),
                                    }}>
                                        {Array.from({ length: maxThumbs }).map((_, i) => (
                                            <Box key={i} sx={{
                                                ...(orientation === 'col' ? { width: '100%' } : { flex: 1 }),
                                                aspectRatio: '1 / 1',
                                                backgroundColor: '#ececec',
                                                borderRadius: 0.5,
                                                border: i === 0 ? `1px solid ${g.primary}` : '1px solid transparent',
                                            }} />
                                        ))}
                                    </Box>
                                );

                                if (pos === 'hidden') return main;
                                if (pos === 'above')  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{thumbs('row')}{main}</Box>;
                                if (pos === 'below')  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{main}{thumbs('row')}</Box>;
                                if (pos === 'right')  return <Box sx={{ display: 'flex', gap: 1 }}>{main}{thumbs('col')}</Box>;
                                /* default 'left' */    return <Box sx={{ display: 'flex', gap: 1 }}>{thumbs('col')}{main}</Box>;
                            })()}
                            <MockIcons section="image" config top={6} right={6} />
                        </Box>

                        {/* Info */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {/* Title + subtitle — tight pair, subtitle sits
                                directly under the title. */}
                            <Box sx={{ position: 'relative', textAlign: sec.title.position }}>
                                <Typography sx={{
                                    color: sec.title.textColor,
                                    fontSize: sec.title.fontSize,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                }}>
                                    Product title
                                </Typography>
                                <Typography sx={{
                                    color: sec.subtitle.textColor,
                                    fontSize: sec.subtitle.fontSize,
                                    fontStyle: 'italic',
                                    mt: 0.25,
                                    lineHeight: 1.2,
                                }}>
                                    Product subtitle
                                </Typography>
                            </Box>

                            {/* Price — own line, larger, with € sign */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                                <Typography sx={{
                                    color: g.primary,
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                }}>
                                    € 90,00
                                </Typography>
                                <Typography sx={{
                                    textDecoration: 'line-through',
                                    color: '#aaa',
                                    fontSize: '0.95rem',
                                }}>
                                    € 100,00
                                </Typography>
                                <Box sx={{
                                    backgroundColor: sec.price.discountColor,
                                    color: '#fff',
                                    px: 0.75, py: 0.25,
                                    borderRadius: 0.5,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    alignSelf: 'center',
                                }}>-10%</Box>
                                {/* Brush sits inline next to the discount badge,
                                    on the same vertical line — opens the price
                                    section's color modal. */}
                                <IconButton
                                    className="mock-config-icon"
                                    size="small"
                                    onClick={(e) => openStyleModal('price', 'color', getAnchorPos(e))}
                                    title="Colors"
                                    sx={{
                                        alignSelf: 'center',
                                        width: 22, height: 22,
                                        backgroundColor: 'rgba(25,118,210,0.85)',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: Colors.sft_blue },
                                        '& svg': { fontSize: 13 },
                                    }}
                                >
                                    <BrushIcon />
                                </IconButton>
                            </Box>

                            {/* Description */}
                            <Box sx={{
                                backgroundColor: '#f3f3f3',
                                borderRadius: 1,
                                p: 1.25,
                                minHeight: 60,
                            }}>
                                <Typography sx={{ color: sec.text.textColor, fontSize: sec.text.fontSize }}>
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                                </Typography>
                            </Box>

                            {/* Add to cart — colour is driven by the global Primary
                                colour, no per-section editor (text colour is
                                computed for contrast). */}
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Box sx={{
                                        backgroundColor: g.primary,
                                        color: getContrastText(g.primary),
                                        px: 2.5, py: 1,
                                        borderRadius: 1,
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        cursor: 'default',
                                    }}>
                                        Add to cart
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    </>
                    )}

                    {/* Footer */}
                    {vis.footer && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.footer.bgColor,
                            color: sec.footer.textColor,
                            py: 2, px: 3,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${sec.footer.columns || 3}, 1fr)`,
                            gap: 2,
                            fontSize: '0.85rem',
                            textAlign: 'center',
                        }}>
                            {Array.from({ length: sec.footer.columns || 3 }).map((_, i) => (
                                <Box key={i}>
                                    {i === 0 && (<>Contact<br/>Address<br/>Name</>)}
                                    {i === 1 && (<>Privacy<br/>Facebook<br/>Instagram</>)}
                                    {i === 2 && (<>Extra<br/>Link</>)}
                                    {i >= 3 && (<>Column {i + 1}<br/>Link<br/>Link</>)}
                                </Box>
                            ))}
                            {sec.footer.showPaymentMethods && (
                                <Box sx={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 1,
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: `1px solid rgba(0,0,0,0.08)`,
                                }}>
                                    {['VISA', 'MC', 'PayPal', 'Bancontact', 'iDEAL'].map((pm) => (
                                        <Box key={pm} sx={{
                                            px: 1, py: 0.25,
                                            backgroundColor: 'rgba(0,0,0,0.06)',
                                            borderRadius: 0.5,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                        }}>{pm}</Box>
                                    ))}
                                </Box>
                            )}
                            <MockIcons section="footer" color config top={6} right={8} />
                        </Box>
                    )}

                    {/* Extra footer */}
                    {vis.extra && (
                        <Box sx={{
                            position: 'relative',
                            backgroundColor: sec.footerExtra.bgColor,
                            color: sec.footerExtra.textColor,
                            py: 1.5, px: 3,
                            display: 'grid',
                            gridTemplateColumns: `repeat(${sec.footerExtra.columns || 3}, 1fr)`,
                            gap: 2,
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            borderTop: `1px solid ${borderColor}`,
                        }}>
                            {Array.from({ length: sec.footerExtra.columns || 3 }).map((_, i) => (
                                <span key={i}>Extra {i + 1}</span>
                            ))}
                            <MockIcons section="footerExtra" color config top={6} right={8} />
                        </Box>
                    )}
                </Paper>

                {/* Color settings — tiny popover anchored at the clicked brush icon */}
                <Popover
                    open={activeStyleSection?.mode === 'color' && !!activeStyleSection?.anchorPos}
                    anchorReference="anchorPosition"
                    anchorPosition={activeStyleSection?.anchorPos || { top: 0, left: 0 }}
                    onClose={() => {
                        // Safety net: if the user literally just opened the native
                        // color picker, swallow this close — MUI's click-away can
                        // fire right as the OS dialog appears.
                        if (Date.now() - colorPickerInteractionRef.current < 500) return;
                        setActiveStyleSection(null);
                    }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                mt: 0.5,
                                minWidth: 240,
                                border: `1px solid ${borderColor}`,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            },
                        },
                    }}
                >
                    <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${borderColor}` }}>
                        <BrushIcon sx={{ color: Colors.sft_blue, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                        {activeStyleSection?.mode === 'color' && renderSectionModalFields()}
                    </Box>
                </Popover>

                {/* Config settings — Dialog widens to `md` for sections that
                    host a multi-card modal (Navigation), and stays compact
                    for the simpler ones (image width, language position, …). */}
                <Dialog
                    open={activeStyleSection?.mode === 'config'}
                    onClose={() => setActiveStyleSection(null)}
                    maxWidth={styleIsRichest ? false : (styleIsMultiCard ? (sidebarCollapsed ? 'xl' : 'lg') : 'sm')}
                    fullWidth
                    PaperProps={{ sx: {
                        borderRadius: 3,
                        maxHeight: '92vh',
                        // Richest modals: viewport-based width (capped) so wide
                        // laptop/desktop screens (incl. 1920px) are actually used —
                        // and a touch wider still when the sidebar is collapsed.
                        ...(styleIsRichest ? { width: sidebarCollapsed ? '96vw' : '94vw', maxWidth: sidebarCollapsed ? 1850 : 1700 } : {}),
                    } }}
                >
                    {/* Title is slightly larger than the rest of the chrome
                        so admins can scan which section they're configuring;
                        actions/content stay compact. */}
                    <DialogTitle sx={{ py: 1.25, px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SettingsIcon sx={{ color: mutedText, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                                {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: mutedText, display: 'block', mt: 0.25, fontSize: '0.78rem' }}>
                            {props.t ? props.t('cfg_style_modal_subtitle', 'Layout & content settings for this section only.') : 'Layout & content settings for this section only.'}
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ py: 2 }}>
                        {activeStyleSection?.mode === 'config' && renderSectionModalFields()}
                    </DialogContent>
                    <Divider />
                    {/* Same action bar as the Pages block-config dialog:
                        Cancel just closes (edits stay in state, the floating
                        save still has them), Save runs the global saveConfig
                        and closes on success. */}
                    <DialogActions sx={{ px: 1.5, py: 0.75 }}>
                        <Button onClick={() => setActiveStyleSection(null)}>
                            {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                        </Button>
                        <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={() => setActiveStyleSection(null)} t={props.t} />
                    </DialogActions>
                </Dialog>

                {/* Colour settings opened via the SEARCH bar. The brush Popover
                    above needs an anchor position from the clicked icon, which a
                    search jump doesn't have — so when colour mode is opened
                    WITHOUT an anchor we render the same colour fields in a
                    centered Dialog instead. */}
                <Dialog
                    open={activeStyleSection?.mode === 'color' && !activeStyleSection?.anchorPos}
                    onClose={() => setActiveStyleSection(null)}
                    maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3, width: 320, maxWidth: '90vw' } }}
                >
                    <DialogTitle sx={{ py: 1.25, px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BrushIcon sx={{ color: mutedText, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: '1.1rem' }}>
                                {activeStyleSection ? (props.t ? props.t('cfg_style_section_' + activeStyleSection.section, SECTION_LABELS[activeStyleSection.section]) : SECTION_LABELS[activeStyleSection.section]) : ''}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: mutedText, display: 'block', mt: 0.25, fontSize: '0.78rem' }}>
                            {props.t ? props.t('cfg_style_colors_subtitle', 'Colours for this section.') : 'Colours for this section.'}
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ py: 2 }}>
                        {activeStyleSection?.mode === 'color' && !activeStyleSection?.anchorPos && renderSectionModalFields()}
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ px: 1.5, py: 0.75 }}>
                        <Button onClick={() => setActiveStyleSection(null)}>
                            {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                        </Button>
                        <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={() => setActiveStyleSection(null)} t={props.t} />
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };

    // Generic "not yet wired" placeholder for the new sub-tabs / top-level
    // tabs we've reserved a slot for. Kept intentionally minimal — content
    // arrives as we wire each one to a real backend setting.
    const renderPagePlaceholder = (label) => (
        <Box sx={{ maxWidth: 720, py: 4 }}>
            <Typography variant="h6" sx={{ color: textColor, mb: 1 }}>{label}</Typography>
            <Typography variant="body2" sx={{ color: mutedText }}>
                {label} configuration is not wired up yet. The slot is reserved here so the
                routing stays stable while we add the backing settings.
            </Typography>
        </Box>
    );

    const renderShortcutsTab = () => (
        <Box sx={{ maxWidth: 720, py: 4 }}>
            <Typography variant="h6" sx={{ color: textColor, mb: 1 }}>Shortcuts</Typography>
            <Typography variant="body2" sx={{ color: mutedText }}>
                Keyboard shortcuts and quick-access links for admin actions. Not wired up yet.
            </Typography>
        </Box>
    );


    // Aggregate dirty flag across every wired config block — drives both the
    // floating save button and the new header save button so they stay in
    // sync. `canSave` also guards against saving while loading or with no
    // shop selected.
    const configDirty = orderDirty || productGridDirty || productCardDirty || styleSettingsDirty ||
        groupsFilterDirty || globalWidthsDirty || headerLogoDirty || navigationDirty ||
        footerDirty || extraFooterDirty || imageGalleryDirty ||
        productDetailInfoDirty || carouselDirty || imageGridDirty || staticPagesDirty || storesDirty || separatorDirty || mapSettingsDirty || cardsDirty || brandsConfigDirty || dynamicPagesDirty || bannersDirty || looksConfigDirty || overigeConfigDirty || cadeaubonConfigDirty || cadeaubonPlaceholdersDirty || cadeaubonLayoutsDirty || processingConfigDirty || customerGroupsDirty || syncConfigDirty || order404Dirty || navbarOrderDirty;
    const canSave = !!props.selectedWebshop && !configLoading && !configError && !configSaving && configDirty;

    // Unsaved-changes guard — the sidebar navigation / webshop picker /
    // browser-close consult this registry before throwing the state away.
    // The check returns LABELS of the dirty sections so the confirmation
    // dialog can show exactly what would be lost. A ref keeps the registered
    // check reading the LIVE list (the register call itself runs once).
    const lbl = (k, fb) => (props.t ? props.t(k, fb) : fb);
    const unsavedItems = [];
    // Only report unsaved changes once the screen has actually loaded. During the
    // async load the baseline refs (originalXxxRef.current) are set synchronously
    // while the matching setState is still pending, so for a render or two the
    // dirty useMemos read "default state ≠ loaded ref" = dirty. Without this gate,
    // clicking away mid-load pops the unsaved-changes modal though nothing was
    // edited. `canSave` already guards on !configLoading the same way.
    // `!configError` matters: when the mini-api load fails the effect returns
    // early (before the per-section baseline refs are set), so the dirty
    // useMemos read default-state ≠ unset-ref = dirty and would pop a FALSE
    // "unsaved changes" warning on a shop the user never edited.
    if (!configLoading && !configError && !!props.selectedWebshop) {
        const P  = lbl('cfg_main_pages',      "Pagina's");
        const S  = lbl('cfg_main_style',      'Opmaak');
        const F  = lbl('cfg_main_features',   'Features');
        // Each item carries its main-tab name + an `icon` key the dialog maps
        // to the SAME icon the tab uses, so the user sees where the change is.
        const push = (icon, tab, section) => unsavedItems.push({ icon, tab, section });
        if (orderDirty)             push('pages', P, lbl('cfg_pages_tab_front',   'Startpagina'));
        if (order404Dirty)          push('pages', P, lbl('cfg_pages_tab_404',     '404-pagina'));
        if (carouselDirty)          push('pages', P, lbl('cfg_carousel_slides_summary', 'Carousel'));
        if (imageGridDirty)         push('pages', P, lbl('cfg_imagegrid_tab_images', 'Afbeeldingen-grid'));
        if (staticPagesDirty)       push('pages', P, `${lbl('cfg_pages_tab_about', 'About')} / ${lbl('cfg_pages_tab_disclaimer', 'Terms & conditions')} / Privacy / 404 / ${lbl('cfg_block_intro', 'Welkom')}`);
        if (dynamicPagesDirty)      push('pages', P, lbl('cfg_pages_tab_dynamic', 'Dynamische pagina'));
        if (brandsConfigDirty)      push('pages', P, lbl('cfg_pages_tab_merken',  'Merken'));
        if (cardsDirty)             push('pages', P, lbl('cfg_pages_tab_cart',    'Winkelmand'));
        if (productGridDirty)       push('style', S, lbl('cfg_style_section_grid', 'Product grid'));
        if (productCardDirty)       push('style', S, lbl('cfg_style_section_productCard', 'Product card'));
        if (styleSettingsDirty)     push('style', S, lbl('cfg_style_global_title', 'Globaal'));
        if (groupsFilterDirty)      push('style', S, lbl('cfg_style_section_groups', 'Filters & sortering'));
        if (globalWidthsDirty)      push('style', S, lbl('cfg_widths_title', 'Globale breedtes'));
        if (headerLogoDirty)        push('style', S, lbl('cfg_style_section_header', 'Header (logo)'));
        if (navigationDirty)        push('style', S, lbl('cfg_style_section_menu', 'Menubalk'));
        if (navbarOrderDirty)       push('style', S, lbl('cfg_nav_sorting_title', 'Weergavevolgorde filters'));
        if (footerDirty)            push('style', S, lbl('cfg_style_section_footer', 'Footer'));
        if (extraFooterDirty)       push('style', S, lbl('cfg_style_section_footerExtra', 'Footer (extra)'));
        if (imageGalleryDirty)      push('style', S, lbl('cfg_style_section_image', 'Afbeeldingengalerij'));
        if (productDetailInfoDirty) push('style', S, lbl('cfg_style_section_productDetailInfo', 'Product detail'));
        if (bannersDirty)           push('features', F, lbl('cfg_features_tab_banners', 'Banners'));
        if (looksConfigDirty)       push('features', F, lbl('cfg_features_tab_stl', 'Shop the look'));
        if (overigeConfigDirty)     push('features', F, lbl('cfg_features_tab_overige', 'Overige'));
        if (cadeaubonConfigDirty || cadeaubonPlaceholdersDirty || cadeaubonLayoutsDirty)
                                    push('features', F, lbl('cfg_features_tab_cadeaubon', 'Cadeaubon'));
        if (processingConfigDirty || customerGroupsDirty)
                                    push('processing', lbl('cfg_main_processing', 'Verwerking'), '');
        if (syncConfigDirty)        push('sync', lbl('cfg_main_sync', 'Synchronisatie'), '');
        if (storesDirty || separatorDirty || mapSettingsDirty)
                                    push('stores', lbl('unsaved_about_stores', 'Over ons / winkels'), '');
    }
    const unsavedItemsRef = useRef([]);
    unsavedItemsRef.current = unsavedItems;
    // Live ref to saveConfig so the guard's "Save & continue" calls the current
    // closure (the register effect runs once on mount).
    const saveConfigRef = useRef(null);
    saveConfigRef.current = saveConfig;
    useEffect(() => {
        registerUnsavedCheck(
            'webshop-config',
            () => unsavedItemsRef.current,
            () => saveConfigRef.current && saveConfigRef.current(),
        );
        return () => unregisterUnsavedCheck('webshop-config');
    }, []);

    // Loading placeholder: card-shaped skeletons while the webshop's config is
    // being fetched, so the screen reads as "loading" instead of flashing empty
    // defaults before the real data lands.
    const renderConfigSkeleton = () => (
        <Box sx={{ pt: 3 }}>
            <Stack spacing={2}>
                {[0, 1, 2].map(i => (
                    <Paper key={i} elevation={0} sx={{ borderRadius: 2, p: 2.5, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Skeleton variant="text" width="32%" height={28} />
                        <Skeleton variant="text" width="55%" height={18} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5, borderRadius: 1 }} />
                        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
                    </Paper>
                ))}
            </Stack>
        </Box>
    );

    // Gate the whole editor on a usable webshop. No row → "no webshop linked";
    // mini-api load failed → the (now visible) reason. Both replace the tabs
    // entirely so the user can't edit empty defaults that silently no-op on
    // save. Hooks above already ran, so these conditional returns are safe.
    if (!props.selectedWebshop || (configError && !configLoading)) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ backgroundColor: cardBg, borderRadius: 2, p: 3, border: `1px solid ${borderColor}` }}>
                    {!props.selectedWebshop
                        ? <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                        : <WebshopErrorState error={configError} t={props.t} lang={props.lang} />}
                </Box>
            </div>
        );
    }

    return (
        <LanguagesProvider value={{ adminLanguages: styleSettings.visibility.adminLanguages, visibleLanguages: styleSettings.visibility.visibleLanguages }}>
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ backgroundColor: cardBg, borderRadius: 2, px: 3, pb: 3, pt: 0, border: `1px solid ${borderColor}` }}>

                {/* No bottom border here — the white main-tab row flows
                    straight into the tinted sub-tab tray below, so the two
                    read as one connected header instead of two stacked bars. */}
                <Box sx={{
                    mx: -3,
                    display: 'flex', alignItems: 'center', gap: 2,
                    pr: 3,
                    // Same surface colour the tabs sit on (the `.light`/`.dark`
                    // class uses var(--light_basic)/var(--dark_basic)), so the
                    // search + save area no longer reads as a separate white
                    // block. Rounded top corners match the card.
                    backgroundColor: isDark ? 'var(--dark_basic)' : 'var(--light_basic)',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                }}>
                    <Tabs value={tabValue} onChange={handleTabChange} className={props.theme} sx={{ flex: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTab-root.Mui-selected': { fontWeight: 700 } }}>
                        {can('webshop.config.tab.pages')      && <Tab value={0} label={props.t ? props.t('cfg_main_pages',      'Pages')          : 'Pages'}          icon={<ArticleIcon />}         iconPosition="top" />}
                        {can('webshop.config.tab.style')      && <Tab value={1} label={props.t ? props.t('cfg_main_style',      'Style')          : 'Style'}          icon={<PaletteIcon />}         iconPosition="top" />}
                        {can('webshop.config.tab.features')   && <Tab value={2} label={props.t ? props.t('cfg_main_features',   'Features')       : 'Features'}       icon={<InfoOutlinedIcon />}    iconPosition="top" />}
                        {can('webshop.config.tab.processing') && <Tab value={3} label={props.t ? props.t('cfg_main_processing', 'Processing')     : 'Processing'}     icon={<ShoppingBasketIcon />}  iconPosition="top" />}
                        {can('webshop.config.tab.sync')       && <Tab value={4} label={props.t ? props.t('cfg_main_sync',       'Sync-settings')  : 'Sync-settings'}  icon={<SyncIcon />}            iconPosition="top" />}
                        {/* Shortcuts tab hidden for now — re-enable once it has real content. */}
                    </Tabs>
                    <WebshopSearchBar search={search} />
                    {/* Persistent save in the header, to the right of the search
                        field. Mirrors the floating save button's behaviour.
                        Sized to roughly match the 260px search field so the two
                        read as a balanced pair. */}
                    <Button
                        ref={headerSaveRef}
                        variant="contained"
                        onClick={saveConfig}
                        disabled={!canSave}
                        sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 1.5, minWidth: 220, height: 40 }}
                    >
                        {props.t ? props.t('save_changes', 'Wijzigingen opslaan') : 'Wijzigingen opslaan'}
                    </Button>
                </Box>

                {/* Floating save — only when there are unsaved changes AND the
                    header save button has scrolled out of view, so long pages
                    can always be saved. Portaled to <body> so position:fixed is
                    relative to the viewport (the screen scrolls in an inner
                    container whose ancestors may create a containing block). */}
                {configDirty && !headerSaveVisible && createPortal(
                    <Fab
                        variant="extended"
                        color="primary"
                        onClick={saveConfig}
                        disabled={!canSave}
                        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300, textTransform: 'none' }}
                    >
                        {configSaving
                            ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            : <SaveIcon sx={{ mr: 1 }} />}
                        {props.t ? props.t('save_changes', 'Wijzigingen opslaan') : 'Wijzigingen opslaan'}
                    </Fab>,
                    document.body,
                )}

                <Suspense fallback={renderConfigSkeleton()}>
                <TabPanel value={tabValue} index={2} sx={{ pt: 0 }}>{renderFeaturesTab()}</TabPanel>
                <TabPanel value={tabValue} index={0} sx={{ pt: 0 }}>
                    {/* Pages: groups every page-level composition under one tab so
                        the top bar stays compact. Front page + 404 + Cart used to
                        live as their own top-level tabs; About/Disclaimer/Privacy/
                        Dynamic are placeholders for now. pt:0 on the outer panel
                        so the sub-tabs hug the main tabs without a gap. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={pagesTab} onChange={(_e, v) => startTransition(() => setPagesTab(v))} variant="scrollable" scrollButtons="auto" className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_pages_tab_front',      'Front page')   : 'Front page'}   icon={<GridViewIcon />}            iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_404',        '404 page')     : '404 page'}     icon={<ReportGmailerrorredIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_merken',     'Merken')       : 'Merken'}       icon={<LocalOfferIcon />}          iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_cart',       'Cart page')    : 'Cart page'}    icon={<ShoppingCartIcon />}        iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_about',      'About')        : 'About'}        icon={<DescriptionIcon />}         iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_disclaimer', 'Terms & conditions')   : 'Terms & conditions'}   icon={<GavelIcon />}               iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_privacy',    'Privacy')      : 'Privacy'}      icon={<LockIcon />}                iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_dynamic',    'Dynamic page') : 'Dynamic page'} icon={<DynamicFeedIcon />}         iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (<>
                    <TabPanel value={pagesTab} index={0}>{renderFrontPageTab()}</TabPanel>
                    <TabPanel value={pagesTab} index={1}>{renderPage404Tab()}</TabPanel>
                    <TabPanel value={pagesTab} index={2}><MerkenTab       theme={props.theme} t={props.t} config={brandsConfig} onConfigChange={setBrandsConfig} shopFieldLabels={shopFieldLabels} /></TabPanel>
                    <TabPanel value={pagesTab} index={3}>
                        <CartPageTab theme={props.theme} t={props.t} cards={cards} onCardsChange={setCards} />
                    </TabPanel>
                    <TabPanel value={pagesTab} index={4}>
                        <AboutPageTab
                            theme={props.theme} t={props.t}
                            value={staticPages.about}
                            onChange={setStaticPage('about')}
                            stores={stores}
                            onStoresChange={setStores}
                            hoursSeparator={hoursSeparator}
                            onHoursSeparatorChange={setHoursSeparator}
                            gmapsApiKey={gmapsApiKey}
                            mapSettings={mapSettings}
                            onMapSettingsChange={setMapSettings}
                        />
                    </TabPanel>
                    <TabPanel value={pagesTab} index={5}>
                        <StaticPageTab
                            theme={props.theme} t={props.t}
                            pageKey="disclaimer"
                            defaultTitle="Terms & conditions"
                            defaultDescription="This text appears on the Terms & conditions page."
                            value={staticPages.disclaimer}
                            onChange={setStaticPage('disclaimer')}
                        />
                    </TabPanel>
                    <TabPanel value={pagesTab} index={6}>
                        <StaticPageTab
                            theme={props.theme} t={props.t}
                            pageKey="privacy"
                            defaultTitle="Privacy"
                            defaultDescription="This text appears on the general Privacy page."
                            value={staticPages.privacy}
                            onChange={setStaticPage('privacy')}
                        />
                    </TabPanel>
                    <TabPanel value={pagesTab} index={7}>
                        <DynamicPageTab
                            theme={props.theme} t={props.t}
                            baseUrl={shopImageBase(props.selectedWebshop?.display_name)}
                            pages={dynamicPages}
                            onPagesChange={setDynamicPages}
                            fetchDesign={fetchDynDesign}
                        />
                    </TabPanel>
                    </>)}
                </TabPanel>
                <TabPanel value={tabValue} index={1} sx={{ pt: 0 }}>
                    {/* Style: Explorer + Product detail share most settings
                        (language bar, header, menu, footer) so editing one
                        propagates to the other automatically — both panels
                        read from the same styleSettings state. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={styleTab} onChange={(_e, v) => startTransition(() => setStyleTab(v))} className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_style_tab_explorer',       'Explorer')       : 'Explorer'}       sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_style_tab_product_detail', 'Product detail') : 'Product detail'} sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (<>
                    <TabPanel value={styleTab} index={0} sx={{ pt: 0 }}>{renderStyleTab('explorer')}</TabPanel>
                    <TabPanel value={styleTab} index={1} sx={{ pt: 0 }}>{renderStyleTab('productDetail')}</TabPanel>
                    </>)}
                </TabPanel>
                <TabPanel value={tabValue} index={3} sx={{ pt: 0 }}>
                    {/* Processing: General / Payments / Shipping & pickup / Customer fields.
                        Same pattern as Pages and Sync — sub-tabs hug the main tabs
                        with mx:-3 to extend edge-to-edge. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={procTab} onChange={(_e, v) => startTransition(() => setProcTab(v))} className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_proc_tab_general',  'General')          : 'General'}          icon={<SettingsOutlinedIcon />}     iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_proc_tab_payments', 'Payments')         : 'Payments'}         icon={<CreditCardOutlinedIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_proc_tab_shipping', 'Shipping & pickup'): 'Shipping & pickup'} icon={<LocalShippingOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_proc_tab_fields',   'Customer fields')  : 'Customer fields'}  icon={<PersonAddAlt1OutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (
                    <ProcessingTab theme={props.theme} t={props.t} subTab={procTab} config={processingConfig} onConfigChange={setProcessingConfig} posOptions={posOptions} warehouseOptions={warehouseOptions} salutationOptions={salutationOptions} countryOptions={countryOptions} paymentProviderAvailability={paymentProviderAvailability} paymentTypeOptions={paymentTypeOptions} customerCategoryGroups={customerCategoryGroups} onCustomerCategoryGroupsChange={setCustomerCategoryGroups} stores={stores} onStoresChange={setStores} validateCustomerId={validateCustomerId} onApplyCountryPreset={applyCountryPreset} />
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={4} sx={{ pt: 0 }}>
                    {/* Sync-settings: General / Field mapping / Images.
                        Same pattern as Pages — sub-tabs hug the main tabs
                        with mx:-3 to extend edge-to-edge. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={syncTab} onChange={(_e, v) => startTransition(() => setSyncTab(v))} className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_sync_tab_general', 'General') : 'General'}             icon={<RestartAltIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_sync_tab_field',   'Field mapping') : 'Field mapping'} icon={<HubOutlinedIcon />}   iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_sync_tab_images',  'Images') : 'Images'}               icon={<ImageOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (
                    <SyncSettingsTab theme={props.theme} t={props.t} subTab={syncTab} config={syncConfig} onConfigChange={setSyncConfig} warehouseOptions={warehouseOptions} shopFieldLabels={shopFieldLabels} />
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={5}>{renderShortcutsTab()}</TabPanel>
                </Suspense>

            </Box>

            {/* The floating save button is gone — the persistent save button
                in the header (next to the search field) is the single save
                entry point now. */}

            <Dialog
                open={!!configBlock}
                onClose={() => setConfigBlock(null)}
                /* intro and carousel both render rich modals (unlayer editor,
                   image upload grid) so they need a wider dialog. Other blocks
                   fall back to the small placeholder form. */
                maxWidth={['intro', 'carousel', 'imagegrid', 'imageGrid', 'text'].includes(configBlock?.id) ? 'lg' : 'sm'}
                fullWidth
            >
                <DialogTitle sx={{ pr: 6 }}>
                    {props.t ? props.t('cfg_block_configure', 'Configure') : 'Configure'} {configBlock ? blockLabel(configBlock) : ''}
                </DialogTitle>
                <DialogContent>
                    <Suspense fallback={<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}>
                    {configBlock?.id === 'intro' && (
                        <Box sx={{ pt: 1 }}>
                            <StaticPageTab
                                theme={props.theme}
                                t={props.t}
                                pageKey="intro"
                                defaultTitle={blockLabel(configBlock)}
                                defaultDescription="This text appears on the landing page as a kind of welcome message."
                                value={staticPages.intro}
                                onChange={setStaticPage('intro')}
                            />
                        </Box>
                    )}
                    {configBlock?.id === 'carousel' && (
                        <Box sx={{ pt: 1 }}>
                            <CarouselConfigModal
                                value={carouselConfig}
                                onChange={setCarouselConfig}
                                theme={props.theme}
                                t={props.t}
                            />
                        </Box>
                    )}
                    {(configBlock?.id === 'imagegrid' || configBlock?.id === 'imageGrid') && (
                        <Box sx={{ pt: 1 }}>
                            {/* Classic / Visueel switch lives in the dialog's
                                action bar (bottom-left) so it doesn't add empty
                                space at the top. Both editors share the same
                                imageGridConfig, so flipping never loses edits. */}
                            {useImageGridV2 ? (
                                <ImageGridConfigModalV2
                                    value={imageGridConfig}
                                    onChange={setImageGridConfig}
                                    theme={props.theme}
                                    t={props.t}
                                />
                            ) : (
                                <ImageGridConfigModal
                                    value={imageGridConfig}
                                    onChange={setImageGridConfig}
                                    theme={props.theme}
                                    t={props.t}
                                />
                            )}
                        </Box>
                    )}
                    {/* 404 "text" block → real editor for the contents_404 /
                        TEXT_404 row, controlled the same way as Disclaimer/Privacy
                        (value + onChange → staticPages.page404 → staticPagesDirty →
                        saved on the main Save as a /admin/contents/TEXT_404 PATCH). */}
                    {configBlock?.id === 'text' && (
                        <Box sx={{ pt: 1 }}>
                            <StaticPageTab
                                theme={props.theme} t={props.t}
                                pageKey="page404"
                                defaultTitle={blockLabel(configBlock)}
                                defaultDescription="This text appears on the 404 (page not found) page."
                                value={staticPages.page404}
                                onChange={setStaticPage('page404')}
                            />
                        </Box>
                    )}
                    {configBlock && !['intro', 'carousel', 'imagegrid', 'imageGrid', 'text'].includes(configBlock.id) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField label="Title" defaultValue={blockLabel(configBlock)} fullWidth />
                            <TextField label="Subtitle" defaultValue="" fullWidth />
                            <FormControlLabel control={<Switch color="success" defaultChecked />} label="Show on mobile" />
                            <FormControlLabel control={<Switch color="success" defaultChecked />} label="Animate on scroll" />
                            <Box>
                                <Typography sx={{ mb: 1, color: mutedText }}>Items to display</Typography>
                                <Slider defaultValue={6} min={1} max={12} valueLabelDisplay="auto" />
                            </Box>
                        </Box>
                    )}
                    </Suspense>
                </DialogContent>
                <DialogActions>
                    {/* Classic / Visueel toggle — only for the image-grid
                        block. `mr: auto` pushes it to the left so Cancel/Save
                        stay right-aligned. */}
                    {(configBlock?.id === 'imagegrid' || configBlock?.id === 'imageGrid') && (
                        <Box sx={{
                            mr: 'auto',
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            p: 0.25, borderRadius: 999,
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            border: '1px solid', borderColor: 'divider',
                        }}>
                            {[
                                { v: false, label: props.t ? props.t('cfg_imagegridv2_toggle_classic', 'Klassiek') : 'Klassiek' },
                                { v: true,  label: props.t ? props.t('cfg_imagegridv2_toggle_visual',  'Visueel')  : 'Visueel'  },
                            ].map(o => (
                                <ButtonBase
                                    key={String(o.v)}
                                    onClick={() => setUseImageGridV2(o.v)}
                                    sx={{
                                        px: 1.5, py: 0.5, borderRadius: 999,
                                        fontSize: '0.72rem', fontWeight: 700,
                                        color:           useImageGridV2 === o.v ? 'primary.main' : 'text.secondary',
                                        backgroundColor: useImageGridV2 === o.v ? '#fff' : 'transparent',
                                        boxShadow:       useImageGridV2 === o.v ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                    }}
                                >
                                    {o.label}
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                    <Button onClick={() => setConfigBlock(null)}>
                        {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                    </Button>
                    <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={() => setConfigBlock(null)} t={props.t} />
                </DialogActions>
            </Dialog>

            <Dialog
                open={containerModalOpen}
                onClose={(_event, reason) => {
                    // When editor is in fullscreen, ESC / backdrop should only exit fullscreen,
                    // not close the whole modal.
                    if (editorFullscreen && (reason === 'escapeKeyDown' || reason === 'backdropClick')) {
                        setEditorFullscreen(false);
                        return;
                    }
                    closeContainerModal();
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
            >
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {editingContainerId ? trs('cfg_container_edit_title', 'Edit product container') : trs('cfg_container_new_title', 'New product container')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        {trs('cfg_container_subtitle', 'Configure how a product container displays on the shop front page.')}
                    </Typography>
                </DialogTitle>
                <Divider />
                <DialogContent ref={dialogContentRef} sx={{ backgroundColor: panelBg, py: 3 }}>
                    <Stack spacing={2}>

                        {/* Section: Basics */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <InfoOutlinedIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {trs('cfg_container_basics', 'Basics')}
                                </Typography>
                            </Stack>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        label={trs('cfg_container_reference', 'Reference')}
                                        size="small"
                                        value={containerData.reference}
                                        onChange={(e) => updateContainerField('reference', e.target.value)}
                                        fullWidth
                                        helperText={trs('cfg_container_reference_help', 'Internal identifier used in logs and exports')}
                                    />
                                    <TextField
                                        label={trs('cfg_container_link', 'Link to discovery/lookup page')}
                                        size="small"
                                        value={containerData.link}
                                        onChange={(e) => updateContainerField('link', e.target.value)}
                                        fullWidth
                                        placeholder="/explore?sort=most_recent"
                                        helperText={trs('cfg_container_link_help', 'Where the button takes visitors')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3, pt: 0.5 }}>
                                    <FormControlLabel
                                        control={<Switch color="success" checked={containerData.active} onChange={(e) => updateContainerField('active', e.target.checked)} />}
                                        label={trs('cfg_container_active', 'Active')}
                                    />
                                    <FormControlLabel
                                        control={<Switch color="success" checked={containerData.keepVisible} onChange={(e) => updateContainerField('keepVisible', e.target.checked)} />}
                                        label={trs('cfg_container_keep_visible', 'Keep visible when list is empty')}
                                    />
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Section: Layout */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <TuneIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {trs('cfg_container_layout', 'Layout')}
                                </Typography>
                            </Stack>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                    <TextField
                                        label={trs('cfg_container_max_articles', 'Max. articles')}
                                        type="number"
                                        size="small"
                                        value={containerData.maxArticles}
                                        onChange={(e) => updateContainerField('maxArticles', parseInt(e.target.value) || 0)}
                                    />
                                    <TextField
                                        label={trs('cfg_container_top_margin', 'Top margin')}
                                        type="number"
                                        size="small"
                                        value={containerData.topMargin}
                                        onChange={(e) => updateContainerField('topMargin', parseInt(e.target.value) || 0)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    />
                                    <TextField
                                        label={trs('cfg_container_bottom_margin', 'Bottom margin')}
                                        type="number"
                                        size="small"
                                        value={containerData.bottomMargin}
                                        onChange={(e) => updateContainerField('bottomMargin', parseInt(e.target.value) || 0)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1, ml: 0.5 }}>
                                        {trs('cfg_container_max_width', 'Max width')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 1 }}>
                                        <Slider
                                            value={typeof containerData.maxWidth === 'number' ? containerData.maxWidth : 0}
                                            onChange={(_e, v) => updateContainerField('maxWidth', v)}
                                            min={0}
                                            max={2000}
                                            step={10}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(v) => `${v}px`}
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={containerData.maxWidth}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value) || 0;
                                                updateContainerField('maxWidth', Math.max(0, Math.min(2000, v)));
                                            }}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            sx={{ width: 120 }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_view_method', 'View method')}
                                        </Typography>
                                        <Select
                                            value={containerData.viewMethod}
                                            onChange={(e) => updateContainerField('viewMethod', e.target.value)}
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value="horizontal">{trs('cfg_container_view_horizontal', 'Horizontal list')}</MenuItem>
                                            <MenuItem value="grid">{trs('cfg_container_view_grid', 'Grid')}</MenuItem>
                                            <MenuItem value="carousel">{trs('cfg_container_view_carousel', 'Carousel')}</MenuItem>
                                        </Select>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_scroll_method', 'Scroll method')}
                                        </Typography>
                                        <Select
                                            value={containerData.scrollMethod}
                                            onChange={(e) => updateContainerField('scrollMethod', e.target.value)}
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value="arrows_large">{trs('cfg_container_scroll_arrows_large', 'Arrows (large)')}</MenuItem>
                                            <MenuItem value="arrows_small">{trs('cfg_container_scroll_arrows_small', 'Arrows (small)')}</MenuItem>
                                            <MenuItem value="dots">{trs('cfg_container_scroll_dots', 'Dots')}</MenuItem>
                                            <MenuItem value="none">{trs('cfg_container_scroll_none', 'None')}</MenuItem>
                                        </Select>
                                    </Box>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Section: Content */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <TextFieldsIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {trs('cfg_container_content', 'Content')}
                                </Typography>
                            </Stack>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: textColor }}>
                                        {trs('cfg_container_title_label', 'Container title')}
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {[
                                            { code: 'NL', field: 'titleNL' },
                                            { code: 'EN', field: 'titleEN' },
                                            { code: 'FR', field: 'titleFR' },
                                        ].filter(({ code }) => (styleSettings.visibility.adminLanguages || ALL_LANGUAGE_CODES).includes(code)).map(({ code, field }) => (
                                            <TextField
                                                key={field}
                                                size="small"
                                                value={containerData[field]}
                                                onChange={(e) => updateContainerField(field, e.target.value)}
                                                fullWidth
                                                placeholder={`${trs('cfg_container_title_ph', 'Title')} (${code})`}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box sx={{
                                                                px: 1,
                                                                py: 0.25,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                color: mutedText,
                                                                backgroundColor: panelBg,
                                                                borderRadius: 1,
                                                                letterSpacing: 0.5,
                                                            }}>
                                                                {code}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: textColor }}>
                                        {trs('cfg_container_button_text', 'Button text')}
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {[
                                            { code: 'NL', field: 'buttonNL' },
                                            { code: 'EN', field: 'buttonEN' },
                                            { code: 'FR', field: 'buttonFR' },
                                        ].filter(({ code }) => (styleSettings.visibility.adminLanguages || ALL_LANGUAGE_CODES).includes(code)).map(({ code, field }) => (
                                            <TextField
                                                key={field}
                                                size="small"
                                                value={containerData[field]}
                                                onChange={(e) => updateContainerField(field, e.target.value)}
                                                fullWidth
                                                placeholder={`${trs('cfg_container_button_ph', 'Button')} (${code})`}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box sx={{
                                                                px: 1,
                                                                py: 0.25,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                color: mutedText,
                                                                backgroundColor: panelBg,
                                                                borderRadius: 1,
                                                                letterSpacing: 0.5,
                                                            }}>
                                                                {code}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Section: Styling */}
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <PaletteIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {trs('cfg_container_styling', 'Styling')}
                                </Typography>
                            </Stack>
                            <Stack spacing={3}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_title_font_size', 'Title font size')}
                                        </Typography>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={containerData.titleFontSize}
                                            onChange={(e) => updateContainerField('titleFontSize', parseFloat(e.target.value) || 0)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_title_position', 'Title position')}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={containerData.titlePosition}
                                            exclusive
                                            onChange={(_e, v) => v && updateContainerField('titlePosition', v)}
                                            size="small"
                                            fullWidth
                                            sx={{ '& .MuiToggleButton-root': { height: 43, py: 0 } }}
                                        >
                                            <ToggleButton value="left"><FormatAlignLeftIcon /></ToggleButton>
                                            <ToggleButton value="center"><FormatAlignCenterIcon /></ToggleButton>
                                            <ToggleButton value="right"><FormatAlignRightIcon /></ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1, ml: 0.5 }}>
                                        {trs('cfg_container_button_color', 'Button text color')}
                                    </Typography>
                                    <Stack direction="row" spacing={1.5}>
                                        {[
                                            { value: 'primary', label: trs('cfg_container_color_primary', 'Primary color'), color: Colors.sft_blue },
                                            { value: 'secondary', label: trs('cfg_container_color_secondary', 'Secondary color'), color: '#9acfbf' },
                                        ].map(({ value, label, color }) => {
                                            const isSelected = containerData.buttonColor === value;
                                            return (
                                                <Box
                                                    key={value}
                                                    onClick={() => updateContainerField('buttonColor', value)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.25,
                                                        cursor: 'pointer',
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: 1.5,
                                                        border: `2px solid ${isSelected ? Colors.sft_blue : borderColor}`,
                                                        backgroundColor: isSelected ? (isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)') : 'transparent',
                                                        transition: 'all 150ms',
                                                        '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '50%',
                                                            backgroundColor: color,
                                                            border: `1px solid ${borderColor}`,
                                                        }}
                                                    />
                                                    <Typography variant="body2" sx={{ color: isSelected ? Colors.sft_blue : textColor, fontWeight: isSelected ? 600 : 400 }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Section: Page Template (Unlayer editor) — header + inline slot.
                            Actual content is rendered via portal below, so it can be moved to
                            document.body when fullscreen without unmounting the editors. */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                border: `1px solid ${borderColor}`,
                                backgroundColor: cardBg,
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <WebIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {trs('cfg_container_page_template', 'Page template')}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <IconButton
                                    size="small"
                                    onClick={toggleEditorFullscreen}
                                    title={editorFullscreen ? trs('cfg_container_exit_fullscreen', 'Exit fullscreen') : trs('cfg_container_fullscreen', 'Fullscreen')}
                                >
                                    {editorFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                            </Stack>
                            {/* Portal target for the editor UI when not fullscreen. */}
                            <Box
                                ref={setEditorInlineSlot}
                                sx={{ minHeight: editorFullscreen ? 0 : 700 }}
                            />
                        </Paper>
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, backgroundColor: cardBg }}>
                    <Button onClick={closeContainerModal}>{trs('cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={saveContainer}>
                        {editingContainerId ? trs('cfg_container_save', 'Save changes') : trs('cfg_container_create', 'Create container')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Editor content always lives in a portal to document.body. The DOM never
                moves — only CSS coordinates change. This keeps the Unlayer iframe mounted
                across fullscreen toggles so user edits are preserved. When not fullscreen,
                the overlay follows the inline slot's bounding rect; when fullscreen, it
                covers the viewport. */}
            {containerModalOpen && (editorFullscreen || slotRect) && createPortal(
                (() => {
                    // Compute clipping region so the overlay is only visible inside
                    // the Dialog's viewport area when inline.
                    let clipTop = 0;
                    let clipBottom = 0;
                    if (!editorFullscreen && slotRect) {
                        clipTop = Math.max(0, slotRect.dialogTop - slotRect.top);
                        const overlayBottom = slotRect.top + Math.max(slotRect.height, 0);
                        clipBottom = Math.max(0, overlayBottom - slotRect.dialogBottom);
                    }
                    return (
                        <Box
                            sx={editorFullscreen ? {
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                zIndex: 2000,
                                backgroundColor: cardBg,
                                p: 3,
                                overflow: 'auto',
                            } : {
                                position: 'fixed',
                                top: slotRect?.top,
                                left: slotRect?.left,
                                width: slotRect?.width,
                                minHeight: slotRect?.height,
                                zIndex: 1400, // above Dialog Paper (default 1300)
                                pointerEvents: 'auto',
                                clipPath: `inset(${clipTop}px 0px ${clipBottom}px 0px)`,
                                WebkitClipPath: `inset(${clipTop}px 0px ${clipBottom}px 0px)`,
                            }}
                        >
                    {editorFullscreen && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <WebIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {trs('cfg_container_page_template', 'Page template')}
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton size="small" onClick={toggleEditorFullscreen} title={trs('cfg_container_exit_fullscreen', 'Exit fullscreen')}>
                                <FullscreenExitIcon />
                            </IconButton>
                        </Stack>
                    )}

                    {!editorFullscreen && (
                        <FormControlLabel
                            sx={{ mb: 2 }}
                            control={
                                <Switch color="success"
                                    checked={containerData.useIframe}
                                    onChange={(e) => updateContainerField('useIframe', e.target.checked)}
                                />
                            }
                            label={trs('cfg_container_iframe', 'Render in iframe (recommended when using advanced styling or scripts)')}
                        />
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Tabs
                            value={editorLang}
                            onChange={(_e, v) => setEditorLang(v)}
                            sx={{ minHeight: 36 }}
                        >
                            <Tab label="NL · Nederlands" sx={{ minHeight: 36, textTransform: 'none' }} />
                            <Tab label="EN · English" sx={{ minHeight: 36, textTransform: 'none' }} />
                            <Tab label="FR · Français" sx={{ minHeight: 36, textTransform: 'none' }} />
                        </Tabs>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" sx={{ color: mutedText }}>
                                {trs('cfg_container_copy_from', 'Copy layout from:')}
                            </Typography>
                            {editorLang === 0 && (
                                <>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('EN', 'NL')}>EN</Button>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('FR', 'NL')}>FR</Button>
                                </>
                            )}
                            {editorLang === 1 && (
                                <>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('NL', 'EN')}>NL</Button>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('FR', 'EN')}>FR</Button>
                                </>
                            )}
                            {editorLang === 2 && (
                                <>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('NL', 'FR')}>NL</Button>
                                    <Button size="small" variant="outlined" onClick={() => copyEditorDesign('EN', 'FR')}>EN</Button>
                                </>
                            )}
                        </Stack>
                    </Box>

                    <Box sx={{ display: editorLang === 0 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefNL}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('NL')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            console.log(data.text); // Log or manipulate text editor data
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                    
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: editorLang === 1 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefEN}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('EN')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            console.log(data.text); // Log or manipulate text editor data
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: editorLang === 2 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefFR}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('FR')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            console.log(data.text); // Log or manipulate text editor data
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
                    );
                })(),
                document.body
            )}
        </div>
        </LanguagesProvider>
    );
};

export default WebshopConfigScreen;
